import { type Translations } from './vi';

/**
 * Bản dự phòng. Kiểu `Translations` khiến việc thêm khoá ở `vi.ts` mà quên
 * `en.ts` trở thành lỗi typecheck, thay vì một chuỗi tiếng Việt lọt vào giao
 * diện tiếng Anh mà không ai để ý.
 */
export const en: Translations = {
  assistantChat: {
    modelLevel: 'Answer model level',
    modelLevelHint: 'The assistant answers with {{model}}. Levels and models follow Settings → AI key.',
    modelUnknown: 'model unknown',
    autoSummarize: 'Summarize for me',
    quickActions: 'Quick actions',
    thinking: 'Thinking…',
    reading: 'Reading context…',
    validating: 'Checking the result…',
    keyboardHint: 'Enter to send · Shift+Enter for a new line',

    suggested: 'Suggested from your history and habits',
    loadingSuggestions: 'Reading your history for suggestions…',
    noOptions: 'No available choices. Check your permissions and project settings, or skip this optional step.',
    deleteOther: 'Delete chat {{n}} from another organization',
    chat: 'Chat',
    activity: 'Activity',
    sessions: 'Conversations',
    new: 'New chat',
    switchSession: 'Switch conversation',
    deleteSession: 'Delete conversation',
    noSession: 'No conversation yet',
    deleteConfirm: 'Delete this conversation? Created items will be kept.',
    limit:
      'Up to 3 conversations per account. Delete one to create another. Manage conversations in their original organization.',
    empty: 'Start a conversation to ask about ChillPM or create an item step by step.',
    you: 'You',
    bot: 'ChillPM assistant',
    source: 'Guide source',
    working: 'Working…',
    preview: 'Draft — nothing created yet',
    confirm: 'Confirm creation',
    back: 'Previous step',
    skip: 'Skip',
    cancel: 'Cancel draft',
    placeholder: 'Ask about ChillPM or answer the current step…',
    message: 'Message',
    send: 'Send',
    choose: 'Choose a value',
    yes: 'Yes',
    no: 'No',
    next: 'Continue',
    actions: {
      todo: 'Create personal to-do',
      project: 'New project',
      task: 'New task',
      sprint: 'New sprint',
      issue: 'New issue',
      report: 'New report',
    },
    fields: {
      startTime: 'Start time',
      dueTime: 'Due time',
      priority: 'Priority',
      typeId: 'Project issue type',
      name: 'Name',
      key: 'Project key',
      methodology: 'Methodology',
      description: 'Description',
      startDate: 'Start date',
      endDate: 'End date',
      techStackIds: 'Technologies',
      lineManagerId: 'Line manager',
      projectId: 'Project',
      title: 'Title',
      taskTypeId: 'Task type',
      dueDate: 'Due date',
      estimateHours: 'Estimated hours',
      hasChecklist: 'Add checklist',
      checklist: 'Checklist (one item per line)',
      goal: 'Goal',
      color: 'Color',
      kind: 'Issue kind',
      impact: 'Impact',
      urgency: 'Urgency',
      from: 'From date',
      to: 'Through date',
      detail: 'Detail level',
      note: 'Note',
    },
  },
  common: {
    updatedAt: 'Updated {{at}}',
    previous: 'Previous page',
    next: 'Next page',
    delete: 'Delete',
    saving: 'Saving…',
    loadMore: 'Load more',
    appName: 'Chill PM',
    loading: 'Loading…',
    retry: 'Retry',
    save: 'Save',
    edit: 'Edit',
    cancel: 'Cancel',
    close: 'Close',
    search: 'Search',
    signOut: 'Sign out',
    discardChanges: {
      title: 'Leave without saving?',
      body: 'Your edits have not been saved. If you leave now, these changes will be lost.',
      keep: 'Keep editing',
      discard: 'Leave and discard',
    },
  },
  auth: {
    signInTitle: 'Sign in',
    signInSubtitle: 'Agile and Waterfall project management, with AI assistance.',
    email: 'Email',
    emailPlaceholder: 'you@company.com',
    submitting: 'Signing in…',
    restoringSession: 'Restoring your session…',
    signedOut: 'You have been signed out.',
    sessionExpired: 'Your session has ended. Please sign in again.',
    signInWithGoogle: 'Sign in with Google',
    googleNotConfigured:
      'Google sign-in is not configured yet. Ask your operator to enable it, or explore with a demo account below.',
    googleError: {
      state:
        'That Google sign-in expired or was interrupted. Press the Google button to start over.',
      denied:
        'Could not sign in with this Google account. If your account has been deactivated, contact your organisation administrator.',
      suspended:
        'You are currently suspended in the organization you belong to. Contact its admin to be reinstated.',
    },
    demoTitle: 'Explore with a demo account',
    demoPersona: {
      org_admin: 'Org admin',
      director: 'Org director',
      leader: 'Leader',
      pm: 'PM',
      member: 'Employee',
    },
    demoOr: 'or',
    demoHelp: 'Pick a role to explore right away — no sign-up, no password.',
    demoReset:
      'Demo data is shared and resets itself about every 30 minutes. Feel free to add, edit or delete — even while others are looking around too.',
  },
  onboarding: {
    title: 'Welcome — one more step',
    subtitle: 'Choose how you want to get started.',
    signedInAs: 'Signed in as',
    displayName: 'Display name',
    displayNamePlaceholder: 'The name colleagues will see',
    orgName: 'Organisation name',
    orgNamePlaceholder: 'Your company or team name',
    orgNameHelp: 'You will be the administrator of this organisation.',
    creating: 'Creating…',
    expiredTitle: 'Sign-up session expired',
    expiredHelp: 'The sign-up ticket only lasts 24 hours. Please sign in with Google again.',
    backToLogin: 'Back to sign in',
    // Three choices — APM-PSPACE-m (docs/plan-personal-space.md §0.3).
    needOrgTitle: 'You need to belong to an organisation',
    needOrg: 'Every account lives in an organisation. Choose how to get started.',
    choicesLabel: 'How to get started',
    choice: {
      personal: {
        title: 'Just me',
        hint: 'Projects, my work, my issues. Up to 50 tasks, no inviting people, no file uploads.',
        submit: 'Start on my own',
      },
      full: {
        title: 'A full new organisation',
        hint: "Every feature; you're the administrator and can invite others.",
        badge: 'Will be paid later',
        submit: 'Create and continue',
      },
      wait: {
        title: 'Wait for an invite',
        hint: 'Your organisation already uses this product: wait for an administrator to invite your email.',
        submit: 'Wait for an invite',
      },
      guestOnly: {
        title: 'Only join the project I was invited to',
        hint: 'No organisation of your own. You see project {{project}} once the project manager approves.',
        submit: 'Send join request',
      },
    },
    // Approval link, no account yet — APM-ORGJR-c: one button, not three cards.
    requestTitle: 'Join {{org}}',
    requestSubtitle:
      'This link needs an administrator’s approval. Send your request; once approved you can sign in.',
    requestSubmit: 'Send join request',
    guestLinkPending:
      "You're asking to join project {{project}} ({{org}}). The project manager will review the request whichever option you pick.",
    // Waiting room — card 3 (APM-GAUTH-e, restored in APM-PSPACE-m).
    waitingTitle: 'Waiting for an invite',
    waitingSubtitle:
      'Once an administrator invites this email, press "Check again" to get in — no need to sign in again.',
    waitSendEmail: 'Send this address to your organisation administrator:',
    waitThenCheck:
      'They can invite you by email (then press "Check again") or send an invite link.',
    checkAgain: 'Check again',
    checking: 'Checking…',
    notInvitedYet:
      'No invitation for this email yet. Try again once the administrator says they invited you.',
    readyTitle: 'You have been added to an organisation.',
    enterNow: 'Enter now',
    requestsLabel: 'Organisation join requests',
    request: {
      pending: 'Waiting for {{org}} to approve — sent {{at}}.',
      declined: 'Your request to join {{org}} was not approved.',
    },
    pendingTitle: 'Request sent',
    pendingHelpWait:
      'An administrator of {{org}} will review your request. You can close this page; once approved, come back and press "Check again".',
    waitClosable:
      'You can close this page. We remember you for 24 hours, so coming back needs no new sign-in.',
    backToChoices: 'Back to the options',
    invitedTitle: "You've been invited to an organisation",
    invitedSubtitle: 'Confirm your display name, then join.',
    inviteDead: 'This invite link no longer works',
    inviteDeadHelp:
      'It expired or was revoked. Ask whoever sent it for a new one — or pick a way to get started below.',
    join: 'Join organisation',
  },
  /** Keep or delete the "just me" org after joining another org — APM-PSPACE-n. */
  personalOrgDecision: {
    title: 'You joined {{org}}',
    body: 'You still have your "just me" organisation — where you worked on your own before. Keep it, or delete it?',
    projectCount_one: 'It holds {{count}} project.',
    projectCount_other: 'It holds {{count}} projects.',
    keep: 'Keep',
    delete: 'Delete…',
    keepHelp:
      'Kept, it stays in the organisation switcher. You can still delete it later in Settings → My organisations.',
    confirmTitle: 'Delete your "just me" organisation?',
    confirmBody:
      'These projects will no longer show anywhere, together with the tasks and to-dos in that organisation:',
    noProjects: 'No projects.',
    confirm: 'Delete',
    kept: 'Kept your "just me" organisation.',
    deleted: 'Deleted your "just me" organisation.',
    settingsTitle: '"Just me" organisation',
    settingsHelp: 'Delete your own organisation and every project in it.',
    settingsIsHome:
      'This is still your main organisation — join another organisation before deleting it.',
    settingsButton: 'Delete "just me" organisation',
  },
  /** "Personal space" card on the Projects page — APM-PSPACE-e. */
  personalSpaceCard: {
    title: 'Personal space',
    description:
      'This is your own space. Once an organisation invites you, you get every feature there.',
    requestsLabel: 'Pending requests',
    orgRequest: {
      pending: 'Waiting for {{org}} to approve your request to join — sent {{at}}.',
      declined: '{{org}} did not approve your request to join.',
    },
    guestRequest: {
      pending: 'Waiting for approval to join project {{project}} ({{org}}) — sent {{at}}.',
      declined: 'Not approved for project {{project}} ({{org}}).',
    },
    memberOf: 'You are already a member of:',
    enter: 'Enter',
    checkInvites: 'Check invitations',
    checking: 'Checking…',
    noInvites:
      'No organisation has added {{email}} yet. Send this address to your organisation administrator, then check again.',
    createOrg: 'Create an organisation',
    orgName: 'Organisation name',
    orgNamePlaceholder: 'Your company or team name',
    create: 'Create organisation',
    createHelp: 'You will administer this organisation; the screen switches to it.',
  },
  portal: {
    title: 'My issues',
    greeting: 'Hello {{name}}',
    report: 'Report an issue',
    created: 'Sent. We will respond within the agreed time.',
    confirm: 'Confirm resolved',
    confirmed: 'Thanks for confirming',
    reopen: 'Not resolved',
    reopenReason: 'What is still wrong?',
    dueAt: 'Resolution committed by {{at}}',
    noProject: {
      title: 'You have not been invited to any project',
      description: 'Please contact the project owner to get access.',
    },
    empty: {
      title: 'You have not reported anything yet',
      description:
        'Hit a problem or need help? Report it here to have it tracked and resolved on time.',
    },
  },
  /** Issue settings section in project settings — APM-ITYPE-b. */
  issueTypes: {
    title: 'Issue settings',
    subtitle: "The project's issue types and how each one is handled.",
    intro:
      'Each type belongs to a handling mode. The mode decides priority, linking to a root cause and maintenance reporting; the names and number of types are yours.',
    add: 'Add type',
    editTitle: 'Edit issue type',
    editNamed: 'Edit type {{name}}',
    deleteNamed: 'Delete type {{name}}',
    deleteTitle: 'Delete type "{{name}}"',
    delete: 'Delete type',
    deleteUnused: 'No issue uses this type yet. Delete it?',
    moveTo_one: '{{count}} issue with this type will move to:',
    moveTo_other: '{{count}} issues with this type will move to:',
    replacement: 'Replacement type',
    name: 'Type name',
    baseKind: 'Handling mode',
    color: 'Color',
    issueCount_one: '{{count}} issue',
    issueCount_other: '{{count}} issues',
    emptyKind: 'No type for this handling mode yet.',
    lastOfKind: 'Each handling mode needs at least one type.',
    kindLockedInUse:
      'Issues use this type, so its handling mode cannot change — their priority and reports would shift.',
    kindLockedLast: 'This is the only type for this handling mode, so it cannot change.',
    created: 'Issue type added',
    saved: 'Issue type saved',
    deleted: 'Type "{{name}}" deleted',
    kind: { incident: 'Incident', request: 'Request', problem: 'Root cause' },
    kindHelp: {
      incident: 'Something broken. Priority from impact × urgency; can link to a root cause.',
      request:
        'Something one person asks for (account, access, data export). Impact always counts as one person.',
      problem:
        'The root cause of several incidents. Only issue handlers can create it; incidents link to it.',
    },
    errors: {
      ISSUE_TYPE_NAME_TAKEN: 'The project already has an issue type with this name.',
      ISSUE_TYPE_IN_USE: 'Issues use this type, so its handling mode cannot change.',
      ISSUE_TYPE_LAST_OF_KIND: 'Each handling mode needs at least one type.',
      ISSUE_TYPE_REPLACEMENT_REQUIRED: 'Issues use this type — choose a replacement.',
      ISSUE_TYPE_REPLACEMENT_MISMATCH: 'The replacement must have the same handling mode.',
    },
  },
  /** Client invite link page — APM-CLINV-c. */
  clientInvite: {
    title: 'Project invitation',
    invalidTitle: 'This invite link cannot be used',
    invalid: 'The invite link has expired or been revoked. Ask the project manager for a new one.',
    heading: 'Join {{project}}',
    subheading: '{{org}} invites you to report and follow issues on project {{key}}.',
    signedInAs: 'You are signed in as {{name}}.',
    apply: 'Request to join',
    notYou: 'Not you? Sign out',
    registerHelp:
      'Create an account to send your request. Once the project manager approves it, you can report issues.',
    name: 'Full name',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 12 characters.',
    register: 'Create account and send request',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    toPortal: 'Go to my issues',
    googleHelp:
      'Sign in with Google to send your request. A new account is created in the next step.',
    toHome: 'Go to home',
    outcome: {
      ok: 'Request sent. The project manager will review it — you will be notified.',
      pending: 'You already sent a request; it is waiting for the project manager.',
      already_member: 'You are already a client of this project.',
      staff:
        'Your account belongs to a member of the organization, so the client invite link does not apply. Ask the project manager to add you to the project.',
      demo_account: 'Demo accounts cannot join projects of a real organization.',
    },
    mine: {
      label: 'Project join requests',
      pending: 'Waiting for approval to join {{project}} ({{org}}) — sent {{at}}.',
      declined: 'Your request to join {{project}} was not approved.',
      note: 'Reason: {{note}}',
    },
  },

  // Guest collaborators from outside the organization — APM-GUEST-g, docs/plan-guest-collaborator.md.
  projectGuests: {
    badge: 'Guest',
    badgeHint:
      'Collaborator from outside the organization — may do what their role grants, except administering the project and approving / committing',
    inviteLink: {
      hint: 'Send this link to collaborators. Anyone who opens it can create an account, but joins the project only after you approve them under Pending.',
      empty: 'No active invite link.',
      create: 'Create invite link',
      url: 'Collaborator invite link',
      meta: 'Expires {{at}} · {{count}} requests sent',
      revoke: 'Revoke',
      revokeConfirm:
        'Revoke the link? People who have not opened it yet will no longer be able to use it.',
      regenerate: 'Create new link',
      regenerateConfirm: 'The current link will stop working immediately. Create a new one?',
      created: 'Invite link created',
      revoked: 'Invite link revoked',
    },
    requests: {
      empty: {
        title: 'No pending requests',
        description: 'People who open the invite link appear here for you to approve.',
      },
      sentAt: 'Sent at',
      accept: 'Approve',
      decline: 'Decline',
      declineTitle: 'Decline the request from {{name}}',
      note: 'Reason (the requester will see it, optional)',
      accepted: '{{name}} has joined the project',
      declined: 'Request declined',
      decided: 'This request has already been handled.',
    },
    errors: {
      staff:
        'This person is a member of the organization — add them as a regular project member instead.',
      clientAccount:
        'This email belongs to a client account of the organization — it cannot be added as a collaborator.',
      alreadyMember: 'This person is already in the project.',
      roleTooWide:
        'This role is wider than a guest collaborator may have (exceeds: {{permissions}}). Guests may do whatever their role grants, except administering the project and approving / committing (changes, AI suggestions, documents, issue settings).',
    },
    title: 'Guest collaborators',
    subtitle:
      'People outside the organization working in this project with the role you pick — except administering the project and approving / committing. They do not appear in the directory, performance or org chart.',
    invite: 'Invite collaborator',
    name: 'Display name',
    email: 'Email',
    role: 'Role',
    openTasks: 'Open tasks',
    googleReady:
      'Added {{email}}. Send them the link below and ask them to sign in with Google using this exact email.',
    loginLink: 'Sign-in link',
    joined: 'Joined',
    hint: 'Collaborators join the project right away. They do not appear in the directory, performance views or org chart.',
    send: 'Invite',
    linkReady:
      'Account created for {{email}}. Send the link below so they can set a password — it is shown only once.',
    link: 'Set-password link',
    copy: 'Copy link',
    copied: 'Link copied',
    copyFailed: 'Could not copy — select and copy it manually',
    expires: 'Link expires at {{at}}',
    existing:
      '{{email}} already has an account — they were added to the project and will see it when they sign in.',
    remove: 'Remove',
    removeConfirm:
      'Remove {{name}} from the project? Their account stays; they just lose access to this project.',
    removed: 'Collaborator removed from the project',
    cannotRemove: 'They still have open tasks — reassign them first.',
    empty: {
      title: 'No collaborators yet',
      description:
        'Invite people from outside the organization to take tasks and update progress right in the project.',
    },
  },

  /** Project invite links section — APM-PSPACE-l, two cards, one default role each. */
  inviteLinks: {
    activeTitle: 'Active links',
    activeHint:
      "Every invite link of this project that still works. Copy one again any time; deleting a link revokes that link only — people who applied through it stay in the approval queue.",
    empty: 'No active links.',
    createStaff: 'Create member invite link',
    createGuest: 'Create collaborator invite link',
    created: 'Invite link created',
    kind: { staff: 'Member', guest: 'Collaborator' },
    meta: 'Created {{created}} · expires {{expires}}',
    legacy:
      'Created before links could be stored — it cannot be copied again. If you no longer have the copy you sent, delete it and create a new one.',
    copy: 'Copy link',
    copied: 'Link copied',
    copyFailed: 'Could not copy — select the link and copy it by hand',
    revoke: 'Delete',
    revokeLabel: 'Delete the {{kind}} link created {{created}}',
    revokeConfirm:
      'Delete this link? Anyone opening it will see it was revoked. Requests already sent through it stay for you to review.',
    revoked: 'Link deleted',
    roleLabel: 'Default role',
    joinersTitle: 'Applied through this link ({{count}})',
    joinersEmpty: 'Nobody has applied through this link yet.',
    joinerStatus: {
      pending: 'Awaiting approval',
      accepted: 'Joined the project',
      declined: 'Declined',
      withdrawn: 'Withdrawn',
    },
    linksTitle: 'Invite links',
    guestBlocked:
      'The showcase organization cannot invite outsiders — its data resets every 30 minutes.',
    directTitle: 'Invite by name',
    directHint:
      'Pick someone in the organization to invite directly, or invite a collaborator by email — no link needed.',
    inviteGuestByEmail: 'Invite a collaborator by email',
  },
  projectInviteCards: {
    sectionSubtitle:
      'The active member and collaborator invite links — copy them again, see who applied, delete any one — and invitations by name.',
    staff: {
      title: 'Members of the organization',
      subtitle:
        'People who already have an account in the organization: invite them by name, or send a link so they apply. You settle role, dates and allocation before they join.',
    },
    guest: {
      title: 'Outside collaborators',
      demoOrg:
        'The showcase organization cannot invite outsiders — its data resets every 30 minutes. Try collaborators in a real organization.',
    },
    defaultRole: 'Default role',
    staffNoDefault: 'Let the person choose',
    staffDefaultHint:
      'Preselected on the sign-up form of whoever opens the link, and when inviting by name. You still settle it on approval.',
    guestDefaultHint:
      'The role a collaborator gets when you approve a request from the link (you can still change it then), and the preset when inviting by email. Changing it keeps the link.',
    staffLinkTitle: 'Sign-up link',
    staffLinkHint:
      'Members open the link, sign in and apply with their wishes. Requests show in the Pending block of the Members section. The link creates no new accounts.',
    roleSaved: 'Default role of the link changed',
  },

  guestInvite: {
    title: 'Collaboration invite',
    invalidTitle: 'This invite link cannot be used',
    invalid: 'The invite link has expired or been revoked. Ask the project manager for a new one.',
    heading: 'Collaborate on {{project}}',
    subheading: '{{org}} invites you to take and update tasks on project {{key}}.',
    signedInAs: 'You are signed in as {{name}}.',
    apply: 'Request to join',
    notYou: 'Not you? Sign out',
    registerHelp:
      'Create an account to send your request. Once the project manager approves it, you will see the tasks assigned to you.',
    name: 'Full name',
    email: 'Email',
    password: 'Password',
    passwordHint: 'At least 12 characters.',
    register: 'Create account and send request',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    toWork: 'Go to my work',
    googleHelp:
      'Sign in with Google to send your request. A new account is created in the next step.',
    toHome: 'Go to home',
    outcome: {
      ok: 'Request sent. The project manager will review it — you will be notified.',
      pending: 'You already sent a request; it is waiting for the project manager.',
      already_member: 'You are already in this project.',
      staff:
        'Your account belongs to a member of the organization, so the collaborator invite link does not apply. Ask the project manager to add you to the project.',
      client_account:
        'Your account is a client of this organization, so the collaborator invite link cannot be used.',
      demo_account: 'Demo accounts cannot join projects of a real organization.',
    },
    mine: {
      label: 'Project join requests',
      pending: 'Waiting for approval to join {{project}} ({{org}}) — sent {{at}}.',
      declined: 'Your request to join {{project}} was not approved.',
      note: 'Reason: {{note}}',
    },
  },
  issueClients: {
    /** Nút mở popup ở header tab Issue (APM-CLINV-a). */
    open: 'Clients',
    pendingBadge: '{{count}} awaiting approval',
    tabs: {
      label: 'Client management sections',
      clients: 'Clients',
      link: 'Invite link',
      pending: 'Awaiting approval',
      pendingCount: 'Awaiting approval ({{count}})',
    },
    inviteLink: {
      hint: 'Send this link to clients. Anyone who opens it can create an account, but they can only report and view issues after you approve them under Awaiting approval.',
      empty: 'No active invite link.',
      create: 'Create invite link',
      url: 'Client invite link',
      meta: 'Expires {{at}} · {{count}} people requested access',
      revoke: 'Revoke',
      revokeConfirm:
        'Revoke the link? People who have not opened it yet will no longer be able to use it.',
      regenerate: 'New link',
      regenerateConfirm: 'The current link stops working immediately. Create a new one?',
      created: 'Invite link created',
      revoked: 'Invite link revoked',
    },
    requests: {
      empty: {
        title: 'No pending requests',
        description: 'People who join through the invite link show up here for approval.',
      },
      sentAt: 'Sent',
      accept: 'Approve',
      decline: 'Decline',
      declineTitle: 'Decline the request from {{name}}',
      note: 'Reason (shown to the client, optional)',
      accepted: '{{name}} was added to the project',
      declined: 'Request declined',
      decided: 'This request has already been handled.',
      staff: 'This person is a member of the organization and cannot be added as a client.',
    },
    title: 'Project clients',
    subtitle:
      'People outside the organization invited to report and follow issues on this project.',
    invite: 'Invite a client',
    name: 'Display name',
    email: 'Email',
    reported: 'Issues reported',
    googleReady:
      'Added {{email}}. Send them the link below and ask them to sign in with Google using this exact email.',
    loginLink: 'Sign-in link',
    joined: 'Joined',
    hint: 'Clients only see issues they reported or that were shared with them, never internal notes.',
    send: 'Invite',
    linkReady:
      'An account was created for {{email}}. Send them the link below to set a password — it is shown only once.',
    link: 'Set-password link',
    copy: 'Copy link',
    copied: 'Link copied',
    copyFailed: 'Could not copy — select and copy it manually',
    expires: 'The link expires at {{at}}',
    existing:
      '{{email}} already has an account — they were added to the project and will see it when they sign in.',
    empty: {
      title: 'No clients invited yet',
      description:
        'Invite clients so they can report issues and follow progress on their "My issues" page.',
    },
    watchersOf: 'Watchers of {{code}}',
  },
  myIssues: {
    title: 'My issues',
    subtitleStaff:
      'Issues you reported or that were shared with you, across every project you belong to.',
    /** Limited shell, no role with `issue:read` in any project — APM-PSPACE-g. */
    noAccess: {
      title: 'Your role cannot see issues yet',
      description: 'The project manager can let your role read issues in the project settings.',
    },
    scopeLabel: 'Scope',
    scope: { all: 'All', reported: 'Reported by me', watching: 'Shared with me' },
    stateLabel: 'Status',
    state: { open: 'Open', closed: 'Closed', all: 'Any status' },
    projectLabel: 'Project',
    allProjects: 'All projects',
    projectOption: '{{name}} ({{count}})',
    search: 'Search by title or number',
    report: 'Report an issue',
    created: 'Issue reported',
    more: 'Load more',
    shared: 'Shared',
    needsConfirm: 'Awaiting your confirmation',
    dueOverdue: 'Overdue',
    dueSoon: 'Under 2 hours left',
    col: {
      code: 'Code',
      title: 'Title',
      project: 'Project',
      state: 'Status',
      priority: 'Priority',
      due: 'Due',
    },
    empty: {
      title: 'No issues here yet',
      description:
        'Issues you report or that others share with you appear here, with their status.',
    },
    noProject: {
      title: 'You are not in any project that takes issues',
      description: 'Once you are a member of a project, you report and follow its issues here.',
    },
    detail: 'Issue details',
    dueAt: 'due {{at}}',
    resolvedAsk: 'The team marked this as resolved. Is your problem solved?',
    confirm: 'Confirm resolved',
    confirmed: 'Confirmed — the issue is closed',
    reopen: 'Not solved, reopen',
    reopenReason: 'What is still not working?',
    timeline: 'History',
    // Public thread with the handling team — APM-ISC-e.
    conversation: 'Conversation with the team',
    entry: { created: 'Reported' },
    actor: { reporter: 'Reporter', team: 'Support team', system: 'System' },
    watchers: 'Watchers',
    noWatchers: 'Not shared with anyone.',
    client: 'Client',
    addWatcher: 'Add a watcher',
    watcherEmail: 'Email of the person to share with',
    share: 'Share',
    shareHint:
      'You can only share with people who are already members of this project. They see the issue and get notified when its status changes.',
    removeWatcher: 'Remove {{name}} from watchers',
  },
  nav: {
    documents: 'Documents',
    dashboard: 'Overview',
    myTasks: 'My tasks',
    myIssues: 'My issues',
    projects: 'Projects',
    reports: 'Reports',
    orgChart: 'Org chart',
    performance: 'Performance',
    velocity: 'Estimation calibration',
    okr: 'OKR',
    admin: 'Administration',
    system: 'System',
    aiConsole: 'AI console',
    aiHarness: 'Harness',
    settings: 'My settings',
    privacy: 'Privacy',
    mainLabel: 'Main navigation',
    adminGroup: 'Features',
    viewAsUser: 'View as a regular user',
    viewAsAdmin: 'Back to system administration',
    account: 'My account',
  },
  projects: {
    goneToast: 'This project no longer exists, or you no longer have access to it.',
    period: {
      both: '{{from}} – {{to}}',
      fromOnly: 'from {{from}}',
      untilOnly: 'until {{to}}',
    },
    collapse: 'Collapse sidebar',
    expand: 'Expand sidebar',
    quickAccess: 'Quick access',
    myRole: 'Your role in this project',
    sidebarEmpty: 'No projects yet.',
    sidebarAllHidden:
      'You have hidden every project from here. Open “All projects” to show them again.',
    sidebarLabel: 'Project list',
    archivedBadge: 'Archived',
    filterPlaceholder: 'Find a project…',
    filterEmpty: 'No project matches.',
    rag: {
      green: 'On track',
      amber: 'Issues appearing',
      red: 'Serious problems',
      unknown: 'Not enough data',
    },
    ragNoData:
      'No figures yet — the project has not started, or you cannot read portfolio reports.',
    ragProgress: '{{done}}/{{total}} items done',
    ragLate: 'Late: {{count}} items',
    ragRisk: 'Open risks: {{count}} (top score {{top}}/25)',
    ragNextMilestone: 'Next milestone: {{title}} — {{date}}',
  },
  rag: {
    red: 'At risk',
    amber: 'Needs attention',
    green: 'On track',
    unknown: 'Not enough data',
  },
  personalSpaceLimits: {
    notice:
      'Personal space: up to {{limit}} tasks, no inviting people, no file uploads. Join or create an organisation to use everything.',
    taskLimit:
      'A personal space holds up to {{limit}} tasks (you have {{current}}). Join or create an organisation to add more.',
    restricted: {
      invite:
        'A personal space cannot invite other people. Join or create an organisation to work with others.',
      document:
        'A personal space cannot add project documents. Join or create an organisation to use documents.',
      task_attachment:
        'A personal space cannot attach files. Join or create an organisation to upload files.',
      coordination:
        'A personal space has no People page. Join or create an organisation to use everything.',
      change_request:
        'A personal space has no change requests. Join or create an organisation to use everything.',
      design:
        'A personal space has no Design page. Join or create an organisation to use everything.',
      org_restructure: 'A personal space cannot merge, split or be part of a proposal.',
    },
  },
  errors: {
    // Git tab — APM-GIT-b, docs/plan-git-integration.md §3, §5.1.
    GIT_HOST_NOT_FOUND: 'This git server no longer exists.',
    GIT_HOST_UNKNOWN:
      'This link does not belong to any declared git server — ask your organisation admin to declare it.',
    GIT_HOST_IN_USE:
      '{{connections}} project connections and {{identities}} linked accounts still point to this server, so it cannot be deleted — turn it off instead.',
    GIT_HOST_DISABLED: 'This git server has been turned off by your organisation admin.',
    GIT_HOST_UNREACHABLE: 'Could not reach the git server, or that address is not its API.',
    GIT_TOKEN_INVALID: 'The git server rejected the token — wrong, expired or revoked.',
    GIT_FORBIDDEN: 'The token lacks permission for this action on the git server.',
    GIT_REPO_NOT_FOUND: 'Repository not found, or the token cannot see it.',
    GIT_BRANCH_EXISTS: 'This branch already exists in the repository.',
    GIT_RATE_LIMITED: 'Calling the git server too fast — try again in a few minutes.',
    GIT_NOT_SUPPORTED:
      'This kind of git server, or this action, is not supported by this installation yet.',
    // Project connections and repositories — APM-GIT-d.
    GIT_CONNECTION_IN_USE:
      'This connection still has linked repositories. Unlink them first, or rotate the token if you only want to replace it.',
    GIT_CONNECTION_REQUIRED:
      'This project has no usable connection to the git server of this repository. Create a connection (paste a token) first.',
    GIT_REPO_ALREADY_LINKED: 'This repository is already linked to the project.',
    // Progress ↔ commit — APM-PCOM.
    GIT_NOT_CONNECTED:
      'The project has no usable git repository (a linked repository with a valid token) — set up Git first.',
    GIT_COMMIT_NOT_FOUND: 'This commit is not on the selected branch — reload the commit list and pick again.',
    // Linking a whole group — APM-GIT-s.
    GIT_GROUP_NOT_FOUND: 'Group not found, or the token cannot see it.',
    GIT_GROUP_ALREADY_LINKED: 'This group is already linked to the project.',
    // APM-ORGINT-d/-e — read by organisation admins; only a super admin can allow it.
    PRIVATE_ADDRESS_BLOCKED:
      'This address is on an internal network — ask the system administrator to turn on "allow internal addresses" for your organisation.',
    // Project sync — APM-SYNC-a/b, docs/plan-project-sync.md.
    SYNC_PROVIDER_NOT_FOUND: 'This sync source no longer exists.',
    SYNC_PROVIDER_DISABLED: 'This sync source has been turned off by your organisation admin.',
    SYNC_PROVIDER_IN_USE:
      'This source is still used by {{n}} project(s), so it cannot be deleted — turn it off instead.',
    SYNC_PROVIDER_UNREACHABLE: 'Could not reach the sync source, or that address is not its API.',
    SYNC_KEY_INVALID:
      'The source rejected the API key — it is wrong, revoked, or its issuer left the project there.',
    SYNC_EXTERNAL_PROJECT_TAKEN: 'This external project is already connected to another project.',
    SYNC_RATE_LIMITED: 'Calling the source too fast — try again in a few minutes.',
    SYNC_REMOTE_ERROR: 'The sync source reported an error. Try again later.',
    SYNC_REMOTE_REJECTED: 'The source rejected the data we sent.',
    SYNC_REMOTE_NOT_FOUND: 'The source cannot find this task (deleted, or in another project).',
    SYNC_RESPONSE_TOO_LARGE: 'The source returned too much data at once.',
    SYNC_PRIMARY_EXISTS: 'This project already has a primary source.',
    SYNC_CONNECTION_NOT_FOUND: 'The sync connection does not exist.',
    SYNC_RUNNING: 'A sync run is already in progress for this connection — wait for it to finish.',
    SYNC_PROVIDER_KIND_UNSUPPORTED:
      'This kind of sync source is not supported by this installation yet.',
    SYNC_NO_TASK_TYPE:
      'This project has no task type yet, so tasks cannot be imported — add a task type to the project, then run again.',
    // Decision sheets — APM-DSHEET-c.
    DECISION_ANSWER_CONFLICT:
      'Someone else just answered this question — review it and choose again.',
    DECISION_IMPORT_INVALID: 'The decision sheet file is not valid.',
    DECISION_SHEET_NOT_READY:
      'A sheet needs at least one question and one decider before it can open.',
    DECISION_SHEET_CLOSED: 'The sheet is closed — reopen it to edit or answer.',
    DECISION_SHEET_NOT_CLOSED: 'The sheet is not closed — close it to export the final document.',
    DECISION_NOT_DECIDER: 'You are not a decider on this sheet.',
    DECISION_OPTION_IN_USE:
      'This option is the chosen answer — clear the answer before deleting or renaming the option.',
    DECISION_QUESTION_ANSWERED: 'This question is answered — clear the answer before deleting it.',
    DECISION_DECIDER_NO_ACCESS:
      'This person cannot read project documents, so they cannot be a decider.',
    DECISION_SHEET_NOT_YET: 'The sheet did not exist at that time.',
    DECISION_SUGGESTION_REVIEWED:
      'This suggestion was already handled — someone may have just created a sheet from it or discarded it.',
    DECISION_SUGGESTION_UNUSABLE:
      'This suggestion cannot become a decision sheet — run the AI again.',
    // Public link of a decision sheet — APM-DSX-b.
    DECISION_SHARE_UNAVAILABLE:
      'This link no longer works — it was revoked, has expired or is wrong.',
    DECISION_SHARE_READ_ONLY: 'This link is view-only — it cannot be used to answer.',
    DECISION_SHARE_RATE_LIMITED: 'Too many answers in a few minutes — wait a moment and try again.',
    DECISION_RESPONDENT_REQUIRED: 'Give your name and email before answering.',
    // Organisation merge / split — APM-ORGMS-f (L13).
    ORG_RESTRUCTURE_IN_PROGRESS:
      'The organisation is already part of an unfinished merge / split proposal.',
    ORG_RESTRUCTURE_DEMO: 'Demo organisations cannot merge, split or be part of a proposal.',
    ORG_RESTRUCTURE_NOT_APPROVER: 'You are not one of the approvers of this proposal.',
    ORG_RESTRUCTURE_STALE_VERSION:
      'Someone else just edited the configuration — review the new version and try again.',
    ORG_RESTRUCTURE_BLOCKED:
      'Your approval is recorded, but the proposal still has blockers so it cannot run yet.',
    ORG_RESTRUCTURING:
      'The organisation is being merged / split — this usually takes a few minutes, try again later.',
    STORAGE_QUOTA_EXCEEDED:
      'Your organisation has used {{used}} of its {{quota}} quota, so this {{requested}} file cannot be uploaded. Delete files on a project’s Files page, or ask the system operator to raise the quota.',
    UNAUTHENTICATED: 'Please sign in to continue.',
    TOKEN_EXPIRED: 'Your session has expired.',
    FORBIDDEN: 'You do not have permission to do that.',
    TASK_NOT_EDITABLE: 'You can only edit tasks assigned to you or created by you.',
    ACTUAL_HOURS_NOT_EDITABLE:
      "Only the PM, an admin or the assignee's manager can edit actual hours.",
    TASK_PLAN_MISSING: 'This task has no due date or hour estimate yet, so it cannot start or finish.',
    TASK_STATUS_FORBIDDEN: 'This project does not let you change the status of this task.',
    TASK_DONE_FORBIDDEN:
      'This project does not let you move this task into or out of the done column.',
    MEMBERSHIP_SUSPENDED:
      'You are currently suspended in {{orgs}}. Contact the organization admin to be reinstated.',
    NOT_FOUND: 'We could not find what you asked for.',
    VALIDATION_FAILED: 'Some fields need attention.',
    RATE_LIMITED: 'Too many attempts. Please wait a moment.',
    CONFLICT: 'This data changed somewhere else.',
    EMAIL_ALREADY_REGISTERED:
      'That email already has an account. Sign in instead, or use another email.',
    INTERNAL: 'Something went wrong. Please try again.',
    FIGMA_NOT_CONFIGURED:
      'Your organization has no Figma token yet. Ask an org admin to add one under Admin › Figma.',
    FIGMA_FORBIDDEN: 'Figma rejected this token, or the token cannot view the file.',
    FIGMA_FILE_NOT_FOUND: 'Figma could not find this file. Check the link.',
    FIGMA_RATE_LIMITED: 'Figma API calls are used up for now. Wait a moment and try again.',
    FIGMA_UNAVAILABLE: 'Could not reach Figma.',
    DESIGN_PAGE_TOO_LARGE:
      'The Figma data is too large to read at once. Try capturing a smaller section.',
    TASK_NOT_DETAILED_ENOUGH:
      'Describe the task in more detail to use AI features: add a checklist, or write a description longer than 50 words.',
    TEAM_REPORT_FINALIZED:
      'This report is finalised and can no longer be edited. Create a new one for different figures or narrative.',
    TRANSLATION_TOO_LONG:
      'This passage is too long to translate in one go. Split it into shorter paragraphs and translate again.',
    TRANSLATION_SAME_LANGUAGE:
      'This passage is already in the target language — nothing to translate.',
    TRANSLATION_BLOCK_FAILED:
      'The AI could not translate this passage in the shared pass — select it, then press Translate this block again in the page bar to translate it on its own.',
    SHARE_UNAVAILABLE:
      'This share link can no longer be opened — it may have been revoked or expired, or the document was deleted.',
    SHARE_ORG_ONLY:
      'This document is shared only within the organisation of the person who created the link.',
    SHARE_EMAIL_NOT_FOUND: 'There is no ChillPM account with this email.',
    // Project sync connection — APM-SYNC-c.
    SYNC_EXTERNAL_PROJECT_MISMATCH:
      'This key belongs to a different project at the source. To switch source projects, disconnect and connect again.',
    SYNC_CONNECTION_DISCONNECTED:
      'This connection is disconnected — reconnect by adding the source again with the same key.',
    network: 'Could not reach the server.',
  },
  systemMetrics: {
    orgs: 'Organisations',
    users: 'Users',
    projects: 'Projects',
    unitOrg: ' orgs',
    unitUser: ' people',
    unitProject: ' projects',
    day: 'Today',
    week: '7 days',
    month: '30 days',
    perOrg: 'How much each organisation uses',
    colOrg: 'Organisation',
    colProjects: 'Projects',
    colUsers: 'People',
    colSince: 'Created',
    demoTag: 'showcase',
    omitted: '{{n}} more organisations not listed here.',
    active: 'Active users',
    activeHint:
      'People who signed in or used the app (session refresh) in the window — locked accounts excluded.',
    activeDemo: 'Showcase organisations only: {{day}} today · {{month}} in 30 days',
    files: 'Files',
    filesHint: 'Only files uploaded by users, fully stored and not deleted.',
    unitFile: ' files',
    colActive: 'Active 30 days',
    colFiles: 'Files',
    colBytes: 'Storage',
  },
  systemMonitor: {
    title: 'Background work',
    subtitle: 'Scheduled and event-driven jobs of the running process. Refreshes every 30 seconds.',
    onlyProblems: 'Only jobs with problems',
    noProblems: 'No job has a problem.',
    noJobs: 'No jobs are registered yet.',
    colJob: 'Job',
    colStatus: 'Status',
    colLast: 'Last run',
    colDuration: 'Took',
    colP95: 'p95 7 days',
    colProjects: 'Projects',
    colProjectsHint: 'Last run / 7-day average',
    colNext: 'Next',
    colFailures: 'Errors 7 days',
    colQueue: 'Queue',
    p95: 'p95 {{value}}',
    avg: 'avg {{value}}',
    onEvent: 'on event',
    neverRan: 'never ran',
    queue: '{{pending}} waiting · {{running}} running',
    health: {
      green: 'OK',
      amber: 'Check',
      red: 'Failing',
    },
    issues: {
      dead_24h: 'A run failed permanently in the last 24 hours',
      overdue: 'Missed 2 or more scheduled ticks',
      stale_running: 'A run has been running for over 15 minutes (likely stuck)',
      late: 'Missed the latest scheduled tick',
      retrying: 'Last run failed, waiting to retry',
      slow: 'Last run took over 3× the median',
    },
    runStatus: {
      queued: 'Queued',
      running: 'Running',
      succeeded: 'Succeeded',
      retrying: 'Failed, retrying',
      dead: 'Dead',
    },
    runsTitle: 'Recent runs',
    runsEmpty: 'This job has no runs in the queue (old rows are pruned after 30 days).',
    runAttempts: 'attempt {{n}}',
    runOrg: 'org {{id}}',
    // "Project sync" summary line — APM-SYNC-j.
    projectSync: {
      title: 'Project sync',
      active: '{{n}} active',
      error: '{{n}} failing',
      paused: '{{n}} paused',
      disabled: '{{n}} connections on a disabled source',
      none: 'no project connected yet',
      providerOff: 'Disabled',
    },
    loopsTitle: 'Background loops',
    loopsHint: 'In-memory heartbeat of the process answering — lost on restart, which is expected.',
    loopLastTick: 'Last tick',
    loopEvery: 'Expected every',
    loopLastError: 'Last error',
    durMs: '{{n}} ms',
    durSec: '{{n}} s',
    durMin: '{{m}} min {{s}} s',
    durHour: '{{h}} h {{m}} min',
    decimalSep: '.',
    justNow: 'just now',
    agoMin: '{{n}} min ago',
    agoHour: '{{n}} h ago',
    agoDay: '{{n}} days ago',
    inMin: 'in {{n}} min',
    inHour: 'in {{n}} h',
    inDay: 'in {{n}} days',
    nProjects: '{{n}} projects',
    nOrgs: '{{n}} orgs',
    nItems: '{{n}} items',
    loops: {
      jobs: { worker: 'Job queue worker' },
      telegram: { polling: 'Telegram receiver (long poll)' },
    },
    jobs: {
      ai: {
        alerts: 'AI alerts (cost, error rate)',
        'prune-runs': 'Prune old AI runs',
        'reap-stuck-runs': 'Reap stuck AI runs',
        'rollup-daily': 'Daily AI cost rollup',
        'scan-models': 'Scan AI model list',
      },
      core: { 'prune-job-queue': 'Prune job queue' },
      demo: {
        'reset-org': 'Reset showcase organisation',
        'simulate-activity': 'Simulate showcase activity',
      },
      design: {
        'capture-snapshot': 'Capture design snapshot',
        'compare-snapshots': 'Compare design snapshots',
        'detect-file-changes': 'Detect design file changes',
      },
      document: {
        'backfill-extraction': 'Extract text from older documents',
        'extract-text': 'Extract document text',
      },
      events: {
        archive: 'Archive old events',
        'maintain-partitions': 'Maintain event_log partitions',
      },
      files: {
        'cleanup-pending': 'Clean up unfinished uploads',
        'prune-draft-images': 'Prune draft images',
      },
      issue: {
        actuals: 'Recompute issue measures',
        auto_close: 'Auto-close issues',
      },
      notification: {
        fanout: 'Fan out notifications',
        prune: 'Prune old notifications',
        'scan-due-soon': 'Due-soon reminders',
      },
      offboarding: { 'execute-handover': 'Offboarding handover' },
      okr: { 'report-reminder': 'OKR report reminder' },
      org_restructure: {
        execute: 'Execute org restructure',
        expire: 'Expire restructure proposals',
      },
      performance: { goal_progress: 'Measure personal goal progress' },
      plan: { 'discard-stale-drafts': 'Discard stale plan drafts' },
      'project-sync': {
        pull: 'Pull tasks from sync sources',
        schedule: 'Project sync schedule',
        push: 'Push changes to sync sources',
        reconcile: 'Weekly reconcile with sync sources',
        'prune-runs': 'Prune old sync logs',
      },
      raid: { 'scan-risk-signals': 'Scan risk signals' },
      reports: { 'project-health': 'Daily project health' },
      snapshots: {
        'tier-cold': 'Move snapshots to cold storage',
        write: 'Write replay snapshots',
      },
      tasks: {
        'clear-milestone-types': 'Clear old milestone task types',
        'generate-recurring': 'Generate recurring tasks',
        'handover-reminder': 'Pre-leave handover reminder',
        'task-actuals': 'Recompute task measures',
      },
      telegram: { send: 'Send Telegram messages' },
      timesheet: {
        'auto-pause': 'Auto-pause timers',
        'start-reminder': 'Remind to start the timer at shift start (Telegram)',
      },
      users: { 'deactivate-offboarded': 'Lock offboarded accounts' },
      velocity: {
        'drift-scan': 'Velocity drift scan',
        'sync-samples': 'Sync velocity samples',
      },
    },
  },
  system: {
    title: 'System',
    probesTitle: 'Connection chain',
    web: 'Web (Vite)',
    api: 'API (NestJS)',
    database: 'MySQL',
    running: 'running',
    stalled: 'Stalled',
    connected: 'connected',
    disconnected: 'not reachable',
    mysqlHint: 'MySQL not running? Run',
    apiHint: 'Cannot reach the API — did you run pnpm dev?',
  },
  storage: {
    title: 'File storage (S3)',
    subtitle: 'Files do not live on the app server. Configure an S3, R2 or MinIO bucket here.',
    notConfigured: 'Not configured',
    notConfiguredHelp:
      'The app keeps working, but uploads and the document library stay locked until a valid configuration exists.',
    active: 'In use',
    verified: 'Verified',
    unverified: 'Not verified',
    noConfigs: 'No configurations yet',
    noConfigsHelp: 'Add one below, test the connection, then activate it.',
    provider: 'Provider',
    endpoint: 'Endpoint',
    endpointOptional: 'Leave empty to use the default AWS endpoint for the region.',
    endpointRequired: 'Required for this provider.',
    awsDefaultEndpoint: 'default AWS endpoint',
    region: 'Region',
    bucket: 'Bucket',
    accessKey: 'Access key ID',
    secretKey: 'Secret access key',
    sse: 'Server-side encryption',
    forcePathStyle: 'Use path-style addressing (MinIO, some R2 setups)',
    browserBaseUrl: 'Browser base URL (optional)',
    browserBaseUrlHint:
      'Only set this when the bucket cannot be given a CORS policy. Example: https://app.example.com/_s3 — a same-origin reverse proxy must forward this path to the bucket. Leave empty for the browser to call the bucket directly.',
    browserBaseUrlDirect: 'browser calls the bucket directly',
    browserBaseUrlVia: 'browser goes through {{url}}',
    browserBaseUrlEdit: 'Edit browser base URL',
    browserBaseUrlSave: 'Save',
    browserBaseUrlCancel: 'Cancel',
    browserBaseUrlSaved: 'Browser base URL saved. Upload a file to check the proxy route.',
    addConfig: 'Add configuration',
    testBeforeActivate:
      'New configurations start inactive. They must pass a connection test first.',
    testConnection: 'Test connection',
    testPassed: 'Connection healthy: write, read and delete all succeeded.',
    testFailed: 'Test failed. See the per-step details below.',
    mustTestFirst: 'The connection test must pass before activating.',
    activate: 'Activate',
    activated: 'Configuration activated.',
    created: 'Configuration added. Test the connection next.',
  },
  usage: {
    title: 'Storage by tier',
    subtitle:
      'The hot tier lives in MySQL and slows backups down; the cold tier lives on S3 and only costs storage.',
    hot: 'Hot tier (MySQL)',
    hotHint: 'Instant access. Replay over the last 30 days reads from here.',
    cold: 'Cold tier (S3)',
    coldHint: 'Older events and snapshots. Slower, rarely needed.',
    files: 'User files (S3)',
    filesHint: 'Attachments, documents, exports.',
    eventLog: 'event_log table',
    events: 'Events',
    snapshots: 'Snapshots',
    archived: 'Archived',
    committed: 'Committed',
    fileCount: 'Files',
    pending: 'Pending',
    partitions: 'Partitions',
    pendingWarning: '{{count}} stalled uploads — check the cleanup job',
  },
  knowledge: {
    title: 'Knowledge base',
    subtitle:
      'Project-management and estimation experience the AI assistant reads when it judges. Changing an entry drops the cache of every skill sharing its tag.',
    help: '"Active" entries labelled "prompt" go into the system prompt of skills with a matching tag — as reference data, not as instructions.',
    add: 'Add entry',
    empty: 'No knowledge entries yet.',
    saved: 'Knowledge base saved',
    disable: 'Disable',
    enable: 'Enable',
    approve: 'Approve',
    source: 'Source',
    usedBy: 'Read by skills: {{skills}}',
    usedByNone: 'No skill reads this entry right now',
    status: {
      active: 'Active',
      disabled: 'Disabled',
      ai_suggested: 'Suggested by AI — pending review',
    },
    tag: {
      estimate: 'estimate',
      assign: 'assign',
      plan: 'plan',
      risk: 'risk',
      retro: 'retro',
      okr: 'OKR',
      critic: 'critic (read only by the critic)',
      'harness:invariant': 'harness lesson (layer 1 of every skill, 300-token cap)',
    },
    lives: { prompt: 'prompt', code: 'code', metric: 'metric' },
    metric: {
      overdue_task_pct: 'Share of open tasks past due',
      late_start_pct: 'Share of tasks past planned start not yet started',
    },
    form: {
      title: 'Title',
      body: 'Body',
      bodyHint:
        'One to three paragraphs: what holds, why, what happens if ignored — then "how the system uses it".',
      tags: 'Tags (which skills read it)',
      lives: 'Where it lives',
      livesHint:
        'Only entries with "prompt" enter the system prompt. "code" is a rule already in the solver; "metric" needs measuring — pick a metric below to attach the organisation\'s number.',
      source: 'Source',
      metric: 'Attached metric',
      noMetric: 'No metric',
      since: 'Since (YYYY-MM)',
    },
    impact: {
      none: 'No skill reads this tag yet — saving drops no cache.',
      some: 'Saving changes the system prompt of: {{skills}}. {{calls}} calls in the last 24 hours will miss the cache next turn.',
      overBudget:
        '⚠ Over the knowledge block budget — the longest entries will be dropped from the prompt: {{skills}}',
    },
  },
  aiMemory: {
    title: 'Project AI memory',
    subtitle:
      'Durable facts the AI assistant reads on every run in this project — confirmed by people, only proposed by AI.',
    userTitle: 'My AI memory',
    userSubtitle: "Notes that only enter your own prompts, never other people's.",
    help: {
      project:
        'Each note is one line in the memory layer of the prompt (a snapshot, not a transcript). Delete lines individually.',
      user: 'Personal notes: only enter the prompt when you run the assistant yourself.',
      org: 'Organisation notes: enter the prompt of every run in the organisation.',
    },
    empty: 'No notes yet.',
    add: 'Add',
    placeholder: 'e.g. the team works four days a week',
    saved: 'Remembered',
    remembered: 'Remembered',
    rememberThis: 'Remember this?',
    stillTrue: 'Still true',
    confirmed_one: 'confirmed {{count}} time',
    confirmed_other: 'confirmed {{count}} times',
    origin: {
      manual: 'added manually',
      answer: 'from a clarification answer',
      rejection: 'from a rejection reason',
    },
    durability: { durable: 'durable', temporary: 'temporary (30 days)' },
  },
  aiConsole: {
    title: 'AI console',
    subtitle:
      'Everything an admin manages about the AI assistant, on one page: knowledge, memory, prompts, policy, models, budgets.',
    forbidden: 'You lack permission to manage AI configuration.',
    lastChanged: 'last changed {{at}}',
    tabs: {
      knowledge: 'Knowledge',
      memory: 'Memory',
      prompts: 'Prompts',
      policy: 'Policy',
      models: 'Models',
      budgets: 'Budgets',
    },
    history: { show: 'History', hide: 'Hide history', empty: 'No changes yet.', system: 'system' },
    memory: {
      org: 'Organisation notes',
      project: 'Project notes',
      noProject: 'No projects yet.',
      userNote:
        'Personal notes are visible only to their owner under "My settings" — admins cannot read them.',
    },
    prompts: {
      skill: 'Skill',
      noSkill: 'No skill registered.',
      active: 'In use: {{version}}',
      codeDefault: 'prompt in code',
      impact:
        '{{calls}} calls / {{runs}} runs in the last 24 hours will miss the cache once a new version is activated',
      codePrompt: 'Show the prompt in code',
      status: { draft: 'draft', active: 'active', retired: 'retired' },
      forced: 'Forced past golden: {{reason}}',
      diff: 'Compare',
      diffBase: 'Active version (or code)',
      diffNext: 'Selected version',
      activate: 'Activate',
      force: 'Force',
      forceHint: 'Needs a reason of at least 5 characters below. Recorded on the version.',
      retire: 'Retire',
      retired: 'Version retired',
      goldenProject: 'Test project to run the golden set on before activation',
      goldenProjectNone: '— not selected (activation is blocked if the skill has a golden set) —',
      forceReason: 'Reason to force past a red golden set',
      forceReasonHint: 'e.g. internal experiment, risk understood',
      goldenFailed: 'Golden set failed — this version was not activated:',
      activated: 'Activated {{version}}',
      activatedWithGolden: 'Activated {{version}} — golden {{passed}}/{{ran}} green',
      newDraft: 'New draft (system prompt)',
      draftHint: 'The system prompt of the skill. Harness rules are appended automatically.',
      draftHintDynamic:
        'This skill has prepare(): the draft must contain {{dynamic}} — where the code-built dynamic part goes.',
      notes: 'Notes for this version (optional)',
      saveDraft: 'Save draft',
      draftSaved: 'Draft {{version}} saved',
      // Evidence record required on activation — APM-H-ao.
      experience: {
        title: 'Evidence record (required to activate)',
        help: 'A green golden set says "not broken", not "why it changed". Write: on which cue, which lesson, and which sandbox runs showed it. Save it with the draft or send it on activation — the one sent on activation wins. Forcing past golden still needs evidence.',
        cue: 'Cue',
        cueHint: 'e.g. the model merges two unrelated tasks into one',
        lesson: 'Lesson',
        lessonHint: 'e.g. ask to split by data boundary before splitting by order of work',
        runIds: 'Sandbox runs as evidence',
        runIdsHint: 'Run the draft on the Harness page (sandbox mode) and paste the run ids — one per line or comma-separated',
        confidence: 'Confidence',
        confidenceOf: { low: 'low', medium: 'medium', high: 'high' },
        scope: 'Lesson scope',
        scopeOf: { invariant: 'every skill', domain: 'this skill' },
        incomplete: 'Fill in the cue, the lesson and at least one run id (or a draft that already has them) before activating.',
        stored: 'This draft already carries an evidence record — leave the fields below empty to use it.',
        runs: '{{count}} runs',
        golden: 'golden {{before}} → {{after}}',
        none: 'No evidence record',
      },
    },
    models: {
      help: 'One row per model: current price and capabilities (thinking, output_format, cache_control). "Verify" sends three tiny real calls.',
      empty: 'No model has a price or capability yet.',
      price: 'in {{input}} · out {{output}} USD/1M tokens',
      noPrice: 'no price — calls will skip this model',
      capability:
        'thinking: {{thinking}} · output_format: {{format}} · cache_control: {{cache}} · vision: {{vision}}',
      yes: 'yes',
      no: 'no',
      verifiedAt: 'verified {{at}}',
      manual: 'declared manually {{at}}',
      unknownCapability: 'Capability unknown — provider defaults apply.',
      verify: 'Verify',
      verifyHint: 'Three tiny pings; costs real money (feature: ping).',
      verified: 'Verified {{model}}',
      check: { thinking: 'thinking', outputFormat: 'output_format', cacheControl: 'cache_control' },
      editPrice: 'Edit price',
      priceTitle: 'Model price',
      priceHelp:
        'Calls skip any model without a price. Prices are USD per 1M tokens, from the provider\u2019s published price list; effective immediately, and the old price row is closed rather than edited. Cache factor: cache-read price relative to input (blank ⇒ keep current, 0.1 for a new model).',
      priceSaved: 'Saved price for {{model}}',
      manualTitle: 'Declare capability manually',
      manualHelp:
        'When the gateway does not allow pings. Declaring manually clears the previous verification mark.',
      saved: 'Capability saved',
      field: {
        modelId: 'Model id',
        provider: 'Provider',
        thinkingStyle: 'Thinking style',
        supportsThinking: 'Has thinking',
        supportsOutputFormat: 'Has output_format',
        supportsCacheControl: 'Has cache_control',
        supportsVision: 'Reads images',
        inputPrice: 'Input (USD/1M)',
        outputPrice: 'Output (USD/1M)',
        cacheRead: 'Cache-read factor',
      },
    },
  },
  harness: {
    versions: {
      label: 'Harness version',
      default: 'System default ({{version}})',
      active: 'Default for new runs: {{version}}',
      history: 'Harness changelog',
      setDefault: 'Set as default',
      saved: 'Default version updated. Running sessions keep their pinned version.',
      used: 'Executed version: {{version}}',
      // Lesson library — APM-H-ao.
      experience: {
        title: 'Prompt versions activated with evidence',
        empty: 'No prompt version has been activated with evidence yet.',
        openRun: 'Open run in the compare tab',
        replaced: 'replaced',
        golden: 'golden {{before}} → {{after}}',
        scope: { invariant: 'every skill', domain: 'this skill' },
        confidence: { low: 'low confidence', medium: 'medium confidence', high: 'high confidence' },
      },
    },
    title: 'Harness',
    subtitle:
      'Run skills, read traces in six blocks, compare two runs, registry and metrics — for operators.',
    forbidden: 'You lack permission to use the Harness page.',
    tabs: {
      run: 'Run',
      direct: 'Head-to-head',
      compare: 'Compare runs',
      registry: 'Registry',
      prompts: 'Prompts',
      metrics: 'Metrics',
      models: 'Models & benchmarks',
    },
    // "Models & benchmarks" tab — APM-MDL-c/-d.
    models: {
      sourceTitle: 'Public benchmark source',
      sourceHelp:
        'Artificial Analysis key (free, 1,000 requests/day). The 04:00 scan downloads per-model indices to recommend model + reasoning for hard / everyday / easy work.',
      sourceStatus: 'Key …{{last4}} · {{count}} benchmark rows · last fetched {{at}}',
      notConfigured: 'No benchmark source key — recommendations use internal measurements only.',
      keyPlaceholder: 'Artificial Analysis API key',
      saveKey: 'Save key',
      fetchNow: 'Fetch now',
      saved: 'Benchmark source key saved.',
      fetched: 'Fetched {{count}} benchmark rows.',
      tableTitle: 'Public benchmarks on file',
      empty: 'No rows yet.',
      col: {
        model: 'Model',
        effort: 'Reasoning',
        intelligence: 'Intelligence',
        coding: 'Coding',
        price: 'Price/1M (blended)',
      },
      attribution: 'Source: Artificial Analysis (artificialanalysis.ai).',
    },
    stage: {
      context: 'Context',
      llm: 'LLM',
      policy: 'Policy',
      runtime: 'Runtime',
      verify: 'Verify',
      result: 'Result',
    },
    trace: {
      turn: 'turn',
      allTurns: 'all turns',
      turnLabel: 'turn {{turn}}',
      dropped: '{{count}} blocks dropped',
      rawPrompt: 'Show raw prompt',
      hidden: 'hidden tools',
    },
    usage: { none: 'No usage steps yet (persisted trace not loaded).', turn: 'turn' },
    run: {
      skill: 'Skill',
      project: 'Project',
      mode: 'Mode',
      modeHint: {
        sandbox:
          'Suggestions are flagged sandbox and hidden from business screens; your prompt draft (if any) is used.',
        real: 'Exactly like a user click — to reproduce a reported bug.',
        replay:
          "Tools return the original run's results, only the model is called again — measure a new prompt on the same input.",
      },
      sourceRun: 'Source run (replay)',
      prompt: 'Request',
      params: 'Params',
      start: 'Run',
      running: 'Running…',
      done: 'Run finished',
      streaming: 'receiving steps…',
      needProject: 'Pick a project first.',
      compare: 'Open in Compare',
      saveFixture: 'Save fixture',
    },
    compare: {
      left: 'Run A',
      right: 'Run B',
      pickTwo: 'Pick two runs to compare.',
      contentDiff: 'Content diff',
    },
    direct: {
      help: 'Same skill, same input, both at once: a harness run (sandbox, with tools/hooks/checks) and one direct model call (only the skill system prompt + request, JSON forced). Both cost real money with your key.',
      runBoth: 'Run both',
      harness: 'Harness',
      direct: 'Direct model call',
      harnessTrace: 'Harness run trace',
      yes: 'yes',
      no: 'no',
      row: {
        valid: 'Valid output (Zod)',
        items: 'Items',
        ungrounded: 'Ungrounded ids',
        rejected: 'Rejected by finalize',
        verifyRetries: 'Failed submissions',
        turns: 'Turns · tools',
        cost: 'Cost',
        latency: 'Latency',
        tokens: 'Tokens',
        model: 'Model',
        prompt: 'Prompt version',
      },
    },
    registry: {
      skills: 'Skills ({{count}})',
      tools: 'Tools ({{count}})',
      manual: 'manual path',
      usedBy: 'used by',
      writes: 'WRITES',
      crossProject: 'cross-project',
    },
    metrics: {
      window: 'Window {{from}} → {{to}}',
      allSkills: 'all skills',
      noBreach: 'No metric above threshold.',
      sparkline: 'Runs per day',
      sparklineHint: 'bar = runs, red = failed runs',
    },
  },
  aiUsage: {
    title: 'AI usage',
    subtitle: 'Tokens, cost and budgets. The figures reconcile with your Anthropic invoice.',
    lastDays: '{{count}} days',
    exportCsv: 'Export CSV',
    cost: 'Cost',
    calls: 'calls',
    cacheHit: 'Cache hit rate',
    cacheHitHint: 'Share of input tokens served from cache — higher is cheaper.',
    avgLatency: 'Average latency',
    overTime: 'Cost per day',
    byFeature: 'By feature',
    byModel: 'By model',
    topUsers: 'Top spenders',
    byProject: 'By project',
    noData: 'No data yet',
    empty: 'No AI calls yet',
    emptyHelp: 'Configure an AI key, then try a feature to see figures here.',
    perOrg: 'organisation-wide',
    perUser: 'per user',
    nearLimit: 'Approaching the limit',
    overLimit: 'Over the limit — new calls are blocked',
    rollupLagging: '{{count}} days not rolled up — check the rollup job',
    period: { day: 'Daily limit', month: 'Monthly limit' },
  },
  miniApp: {
    loading: 'Opening…',
    outside:
      'This screen only runs inside Telegram. Open the bot and use the menu button next to the message box.',
    openWeb: 'Open the web app',
    notLinked: {
      title: 'This Telegram account is not connected yet',
      help: 'Open your browser to connect — you are usually signed in there already, so no need to sign in again inside Telegram. Come back here when done.',
      openBrowser: 'Open browser to connect',
      retry: 'I have connected, try again',
    },
    reopen: 'Close and reopen',
    error: {
      title: 'Could not open the Mini App',
      openBrowser: 'Open in browser',
      closeManually:
        'If this window did not close, tap the × at the top, then reopen it from the bot menu button.',
    },
    tabTimer: 'Timer',
    tabsLabel: 'Mini App sections',
    timerRunning: 'Timer running',
    timerPaused: 'Timer paused',
    tabInbox: 'Notifications',
    greeting: 'Hi {{name}}',
  },
  telegramTypes: {
    'task.assigned': 'Assigned a new task',
    'task.unassigned': 'My task moved to someone else',
    'task.edited': 'Someone edited a task I am working on',
    'task.time_running_out': 'A task I am working on has only 10% of its time left',
    'comment.mentioned': 'Someone mentioned me',
    'announcement.sent': 'Announcement from a manager',
    'membership_request.invited': 'Invitation to join a project',
    'membership_request.applied': 'Someone applies to a project I manage',
    'project_approval.requested': 'A new project is waiting for my approval',
    'project_approval.approved': 'My project was approved',
    'project_approval.rejected': 'My project was rejected',
    'project_approval.decided_by_other': 'Someone else decided on a project waiting for me',
    'project.member_left': 'A member left my project',
    'raid.risk_high': 'A risk crossed the high threshold',
    'raid.signals_detected': 'New risk signals',
    'schedule.drift_detected': 'The project schedule is drifting',
    'schedule.impact_detected': 'A schedule change affects the plan',
    'schedule.deadline_changed': 'A project, sprint or milestone deadline moved',
    'sprint.ending_soon': 'A sprint I manage is about to end',
    'sprint.last_day': 'Last day of a sprint I manage',
    'sprint.auto_closed': 'A sprint I manage closed automatically at its end date',
    'handover.suggested': 'Handover suggested before an absence',
    'design_file.remote_version_detected': 'A Figma file has a new version',
    'timesheet.timer_overdue': 'Timer running on an overdue task',
    'timesheet.session_capped': 'Timer auto-stopped at 12 hours',
    'timesheet.overtime_capped': 'Overtime timer auto-stopped',
    'timesheet.day_capped': 'Timer auto-stopped: daily limit reached',
    'timesheet.start_reminder': 'Shift started but timer not running (with quick leave buttons)',
    'absence.requested': 'A direct report requests leave (with Approve button)',
    'absence.retracted': 'A direct report withdraws a leave request',
    'org_join_request.created': 'Someone asks to join the organisation via an invite link (org admins)',
    'ai_budget.soft_limit_reached': 'AI spend crossed the warning threshold',
    'issue.state_changed': 'An issue I reported or watch changed status',
    'issue.resolved': 'An issue is resolved and awaits my confirmation',
    'issue.reopened': 'An issue I handle was reopened',
    'issue.watcher_added': 'An issue was shared with me',
    // Issue conversation — APM-ISC-c.
    'issue.replied': 'The team replied on an issue I reported or watch',
    'issue.customer_replied': 'The reporter replied on an issue I handle',
    'issue.internal_comment': 'Internal discussion on an issue I handle',
    'issue.assigned': 'An issue was assigned to me',
    'org_restructure.updated': 'An organisation merge / split proposal needs my attention',
    'okr.report_due': 'Key results in my OKRs need a value',
    // Testing bug flow — APM-TEST2-b.
    'bug.assigned': 'A test bug was assigned to me',
    'bug.fixed': 'A bug I reported is fixed and awaits my retest',
    'bug.still_failing': 'A bug I fixed was reported as still failing',
    'bug.closed': 'A bug I fixed was verified and closed',
    'decision_sheet.invited': 'Invited to decide options in a sheet',
    'document.review_requested': 'A document in a project I manage was submitted for review',
    'document.mentioned': 'Someone mentioned me in a document',
  },
  telegramGroups: {
    work: 'My work',
    timesheet: 'Time tracking',
    issue: 'Service issues',
    bug: 'Test bugs',
    document: 'Documents & design',
    project: 'Projects & members',
    planning: 'Planning & risk (for PMs)',
    org: 'Organization',
    count: '{{on}}/{{total}} on',
    toggleAll: 'Turn the whole {{group}} group on / off',
  },
  telegramLink: {
    reminder: {
      dot: 'Telegram not set up',
      badge: 'Telegram',
      cardBadge: 'Needs setup',
    },
    title: 'Telegram',
    subtitle:
      'Get work notifications on Telegram, and update progress right there — no need to open the web app.',
    botMissing: 'An administrator has not configured a Telegram bot yet, so this is switched off.',
    intro:
      'Press the button below and Telegram opens. Press “Start” there and you are done — no code to type.',
    connect: 'Connect Telegram',
    connecting: 'Creating link…',
    waiting:
      'Waiting for you to press “Start” in Telegram. Scan the code with your phone if Telegram is there.',
    qrAlt: 'QR code that opens the chat with the bot',
    openTelegram: 'Open Telegram again',
    expiresIn: 'Link expires in {{mm}}:{{ss}}',
    expired: 'The link has expired. Press “Connect Telegram” for a new one.',
    since: 'Connected since {{at}}',
    unnamed: 'Telegram account',
    blocked:
      'You are blocking the bot, or the chat was deleted — notifications cannot get through. Reopen the chat with the bot and send /start to resume.',
    test: 'Send a test message',
    testSent: 'Sent. Check your Telegram.',
    unlink: 'Disconnect',
    unlinkConfirm: 'Disconnecting stops Telegram notifications. You can reconnect any time.',
    unlinked: 'Telegram disconnected.',
    prefsTitle: 'Send me these',
    prefsSave: 'Save preferences',
    prefsSaving: 'Saving…',
    prefsSaved: 'Preferences saved.',
    quietTitle: 'Quiet hours',
    quietFrom: 'From',
    quietTo: 'To',
    quietHelp:
      'Telegram stays silent during this window. Messages are **not** held and sent later — they remain in full in the web inbox.',
  },
  // Project sync with external systems — APM-SYNC-*, docs/plan-project-sync.md.
  // Named `projectSync`, not `sync` (top-level key collision trap).
  // Git tab — docs/plan-git-integration.md. EVERY key of the feature lives under this
  // block; later tasks append their own sub-block at the END, without reordering.
  gitTab: {
    // APM-GIT-o — Git setup wizard under Project settings → Git repositories.
    wizard: {
      title: 'Git repositories',
      subtitle:
        'Connect the project to GitLab or GitHub so ChillPM sees the branches, merge requests and commits of every item.',
      intro:
        'Do the four steps below in order — about 5 minutes. Each step opens once the one before it is done.',
      stepLabel: 'Step {{n}}',
      status: { done: 'Done', current: 'In progress', locked: 'Not yet' },
      lockedHint: 'Finish the step above to open this one.',
      optionalLater:
        'Extra options (branch names, automatic status changes, instant updates…) show up here once the four steps are done.',
      openSettings: 'Git settings',
      host: {
        title: 'Choose the git server',
        pick: 'Which server hosts the project code?',
        kind: { gitlab: 'GitLab', github: 'GitHub' },
        chosen: 'Server: {{name}}',
        change: 'Choose another server',
        noneTitle: 'There is no git server to choose yet',
        noneBody:
          'Your organisation admin has to register the company GitLab / GitHub address first — done once for the whole organisation. Send the message below to your organisation admin and come back here afterwards.',
        message:
          'Hi, project «{{project}}» needs to connect to our company GitLab/GitHub. Could you register the git server in ChillPM under Admin → Git servers: {{url}} . Thank you!',
        copyMessage: 'Copy the message',
        copied: 'Message copied.',
        copyFailed: 'Could not copy — select the message above and copy it by hand.',
        orgAdminHint: 'You are an organisation admin, so you can register it right away.',
        openAdmin: 'Open Admin → Git servers',
      },
      token: {
        title: 'Create a token on the git server',
        whatIsToken:
          'A token is a long string of characters, like a private key: it lets ChillPM read the project code without anyone’s password.',
        levelQuestion: 'What should ChillPM do with the code?',
        level: { read: 'View only', write: 'View + create branches/MRs from ChillPM' },
        levelHint: {
          read: 'ChillPM only reads: it sees branches, merge requests (MR — a request to merge code into the main branch) and commits, and links them to items. It changes nothing in the repository. Right for most projects.',
          write:
            'Same as "View only", plus: one click on an item creates a branch or merge request in the repository, under the token’s account.',
        },
        repoUrlLabel: 'Repository link (optional)',
        repoUrlHint:
          'Paste the repository link and the guide gets a button that opens the right page, and the repository step is pre-filled with it.',
        repoUrlOther: 'This link is not on {{host}} — check it, or leave it empty.',
        followSteps: 'Follow these steps on {{host}}:',
        ready: 'I have the token — next step',
        chosenLevel: 'Chosen access: {{level}}',
        review: 'See the guide again',
        showGuide: 'How to create a token',
      },
      guide: {
        openHost: 'Open the git server',
        gitlab: {
          openKnown:
            'Use the button below to open the token page of **{{path}}**. Sign in if GitLab asks; your account needs the **Maintainer** role on the repository.',
          openFind:
            'Open GitLab, go to the project repository, then in the left sidebar choose **Settings** → **Access tokens**. If you cannot see it, your account lacks access to the repository — ask its maintainer (**Maintainer** role) to do this step, or use a personal token at the end of this guide.',
          openTokens: 'Open the repository Access tokens page',
          name: 'Click **Add new token**. In **Token name**, type **ChillPM**.',
          role: 'In **Select a role**, choose **{{role}}**.',
          scope: 'Under **Select scopes**, tick only **{{scope}}**.',
          expiry:
            'In **Expiration date**, pick an expiry date (GitLab allows up to one year). No need to remember it: ChillPM reminds you 14 days before the token expires.',
          create:
            'Click **Create project access token**. The token appears at the top of the page — click the copy icon next to it. ⚠️ The token is shown **only once**: close the page and it is gone, you would have to create a new one.',
          personal:
            'Cannot create a repository token because you lack access? Use your **personal token** instead: the button below opens the personal token page with the name and scope filled in — pick an expiry date and click **Create token**. Note: ChillPM then sees every repository your account sees, and anything written to git is under your name.',
          openPersonal: 'Open the personal token page',
          groupNote:
            'Several repositories in the same group? Do exactly the same on the **group** page (group → **Settings** → **Access tokens**): one token covers the whole group.',
          maintainerNote:
            'Want ChillPM to install the webhook itself (data arrives within seconds instead of waiting for the sync interval)? Choose the **Maintainer** role instead of the one above. Optional.',
        },
        github: {
          open: 'Use the button below to open the GitHub new token page; sign in if asked.',
          openNew: 'Open the GitHub token page',
          name: 'In **Token name**, type **ChillPM**.',
          expiry:
            'In **Expiration**, pick an expiry date. No need to remember it: ChillPM reminds you 14 days before the token expires.',
          owner:
            'In **Resource owner**: if the repository belongs to an organization, choose that organization; if it is your own, keep your name. Some organizations require an admin to approve tokens — GitHub will tell you.',
          repos:
            'Under **Repository access**, choose **Only select repositories** and pick the project repository.',
          reposKnown:
            'Under **Repository access**, choose **Only select repositories** and pick **{{path}}**.',
          permissions:
            'Under **Permissions** → **Repository permissions**, turn on exactly these; leave everything else at **No access**:',
          generate:
            'Click **Generate token** at the bottom of the page and copy the token shown. ⚠️ The token is shown **only once**: close the page and it is gone.',
          webhookNote:
            'Want ChillPM to install the webhook itself (data within seconds)? Also turn on **Webhooks** → **Read and write**. Optional.',
          classicNote:
            'Already have a **classic** token with the **repo** scope? It works as is, no need to create a new one.',
          permissionColumn: 'Permission',
          accessColumn: 'Set to',
          access: { read: 'Read-only', readWrite: 'Read and write' },
        },
      },
      connect: {
        title: 'Paste the token and check it',
        tokenLabel: 'Paste the token here',
        tokenHint:
          'The token is stored encrypted and never shown again — after saving only its last 4 characters are visible.',
        submit: 'Check and save',
        checking: 'Asking {{host}}…',
        defaultLabel: 'ChillPM · {{host}}',
        result: 'Connected with the account **{{account}}**.',
        resultNoAccount: 'Connected.',
        access: 'Access: {{access}}.',
        accessValue: {
          read: 'view only',
          write: 'can create branches and merge requests',
          unknown: 'GitHub tells when the repository is linked in step 4',
        },
        expires: 'The token expires on {{date}}.',
        noExpiry: 'The token has no expiry date (or the server does not say).',
        expiresSoon:
          'The token expires soon — create a new one and click "Replace token" before that date so the repository keeps updating.',
        writeMissing:
          'You chose "View + create branches/MRs" but this token is **view only**: it lacks the **api** scope or the **Developer** role. ChillPM still works in view-only mode; to create branches from ChillPM, create a new token as in step 2 and click "Paste another token".',
        writeUnknown:
          'GitHub does not say whether this kind of token can write until a repository is linked — ChillPM checks it in step 4.',
        replace: 'Paste another token',
        broken: {
          invalid:
            'The git server rejects the saved token — it may have been revoked. Paste a new token to continue.',
          expired: 'The saved token has expired. Create a new one as in step 2 and paste it here.',
          hostDisabled:
            'Your organisation admin has turned this git server off. Ask them to turn it back on — nothing to do here.',
        },
        errorTitle: 'Not connected yet',
        fix: {
          GIT_TOKEN_INVALID:
            '{{host}} does not accept this token. Most common causes: characters missing when copying, the token expired or was revoked, or it was created on another server (e.g. a GitHub token pasted for GitLab). Copy the whole token again, or create a new one as in step 2.',
          GIT_FORBIDDEN:
            'The token is valid but lacks access. Check the role and permissions in step 2 (GitLab: Reporter role or higher and the read_api scope; GitHub: read access to Contents and Pull requests), then create a new token.',
          GIT_HOST_UNREACHABLE:
            'ChillPM cannot reach {{host}} right now. Try again in a few minutes; if it keeps failing, ask your organisation admin to check the server address under Admin → Git servers.',
          GIT_HOST_DISABLED: 'Your organisation admin has turned this git server off — ask them to turn it back on.',
          GIT_RATE_LIMITED:
            '{{host}} is busy because of too many calls. Wait a few minutes, then check again.',
          VALIDATION_FAILED:
            'A token is one unbroken string, without spaces, at least 8 characters long. Check that no extra space or line break was copied.',
        },
        saved: 'Connected — the token belongs to {{account}}.',
        rotated: 'Token replaced.',
        rotateSubmit: 'Check and replace',
        newToken: 'New token',
      },
      repository: {
        title: 'Choose the repository',
        how: 'How to choose the repository',
        mode: { list: 'Pick from the list', url: 'Paste the link' },
        listHint: 'These are the repositories this token can see.',
        searchPlaceholder: 'Search by repository name, then press Enter',
        empty:
          'This token sees no matching repository. Check the repository selection when the token was created, or switch to "Paste the link".',
        pick: 'Link this repository',
        alreadyLinked: 'Linked',
        urlLabel: 'Repository link',
        urlHint: 'Open the repository in the browser and copy the address bar, e.g. {{example}}.',
        linkByUrl: 'Link repository',
        linking: 'Linking…',
        linked: 'Linked {{path}} — ChillPM is loading its history for the first time.',
        errorTitle: 'The repository is not linked yet',
        urlAlreadyLinked: '{{path}} is already linked to this project — pick another repository.',
        // APM-GIT-s-b — one repository or a whole group.
        scope: {
          label: 'Link one repository or a whole group',
          single: 'One repository',
          group: { gitlab: 'Whole group', github: 'Whole organization' },
        },
      },
      // APM-GIT-s-b — link a whole GitLab group / GitHub organization.
      group: {
        intro: {
          gitlab:
            'No need to link repositories one by one: pick a group and ChillPM links every repository in it. Repositories created later show up as suggestions for you to approve.',
          github:
            'No need to link repositories one by one: pick an organization and ChillPM links every repository of it the token can see. Repositories created later show up as suggestions for you to approve.',
        },
        how: 'How to choose the group',
        mode: { list: 'Pick from the list', url: 'Paste the group link' },
        searchPlaceholder: 'Search by group name, then press Enter',
        empty: {
          gitlab:
            'This token sees no matching group. A token created inside one repository only sees that repository — to link a whole group, create the token on the group page (Group access token) or use a personal token.',
          github:
            'This token sees no matching organization. A token only sees the organizations granted when it was created.',
        },
        pick: 'Pick this group',
        alreadyLinked: 'Linked',
        urlLabel: 'Group link',
        urlHint:
          'Open the group page in the browser and copy the address, e.g. {{example}}. Typing the group name works too.',
        urlError: {
          host_unknown: 'This link is not on {{host}} — the server of the token in use.',
          invalid_url: 'This is not a link to a group yet.',
        },
        continue: 'Continue',
        confirmTitle: {
          gitlab: 'Link the whole group {{path}} to the project?',
          github: 'Link the whole organization {{path}} to the project?',
        },
        rule: {
          direct:
            'Every repository directly inside the group is linked now. Repositories in subgroups are not included; archived repositories are skipped.',
          cap: 'At most {{max}} repositories are linked at a time. Larger groups: the rest become suggestions — link them from "Choose the project repositories".',
          later:
            'Repositories created later are not linked automatically: ChillPM tells you and waits for you to press Link or Skip.',
        },
        link: 'Link the whole group',
        linking: 'Linking…',
        pickOther: 'Pick another group',
        errorTitle: 'The group is not linked yet',
        result: {
          title: 'Linked the group {{path}}',
          linked_one: 'Linked {{count}} repository.',
          linked_other: 'Linked {{count}} repositories.',
          suggested_one:
            '{{count}} repository was not linked because it is over the limit of {{max}} per time — it is now a suggestion in "Choose the project repositories":',
          suggested_other:
            '{{count}} repositories were not linked because they are over the limit of {{max}} per time — they are now suggestions in "Choose the project repositories":',
          unreadable_one: '{{count}} repository cannot be read with this token, so it was not linked:',
          unreadable_other:
            '{{count}} repositories cannot be read with this token, so they were not linked:',
          alreadyLinked_one: '{{count}} repository was already linked — kept as is.',
          alreadyLinked_other: '{{count}} repositories were already linked — kept as is.',
          truncated:
            'The group has too many repositories for ChillPM to list them all — the rest will show up as suggestions over time.',
          importing:
            'ChillPM is loading the history of the new repositories for the first time — large ones can take tens of minutes.',
          done: 'Done',
        },
      },
      // APM-GIT-s-b — the project repository list.
      selection: {
        title: 'Choose the project repositories',
        hint: 'Tick the repositories ChillPM should follow, untick to unlink — then press "Save selection". New repositories in linked groups show up here as suggestions.',
        reload: 'Reload the list',
        groupKind: { gitlab: 'Group', github: 'Organization' },
        groupMeta: '{{host}} · token of {{account}} · linked since {{date}}',
        removeGroup: 'Unlink group',
        removeAsk_one: 'Also unlink the {{count}} repository linked through {{group}}?',
        removeAsk_other: 'Also unlink the {{count}} repositories linked through {{group}}?',
        removeAskNone: 'Unlink the group {{group}} from the project?',
        removeExplain:
          'Once the group is unlinked, ChillPM stops suggesting its new repositories. Linked repositories can stay (they move to "Linked by hand") or be unlinked too — unlinking stops pulling data, while old links to tasks stay and the repository can be linked again.',
        removeWithRepos_one: 'Unlink group and {{count}} repository',
        removeWithRepos_other: 'Unlink group and {{count}} repositories',
        removeKeepRepos: 'Unlink only the group, keep the repositories',
        groupRemoved: 'Group unlinked.',
        groupRemovedWithRepos_one: 'Unlinked the group and {{count}} repository.',
        groupRemovedWithRepos_other: 'Unlinked the group and {{count}} repositories.',
        groupError:
          'Could not ask the git server about this group: {{message}} Linked repositories are still listed below.',
        truncated:
          'The group has repositories not listed yet (too many) — they will show up over time.',
        groupEmpty: 'This group has no repositories yet.',
        manualTitle: 'Linked by hand',
        status: {
          linked: 'Linked',
          suggested: 'Suggested — new',
          skipped: 'Skipped',
          unlinked: 'Unlinked',
        },
        origin: {
          manual: 'Linked by hand',
          group: 'Linked through {{group}}',
          removedGroup: 'Linked through a group that was unlinked',
          createdAt: 'Created {{date}}',
        },
        change: {
          link: 'Will be linked when you press Save.',
          relink: 'Will be linked again when you press Save — old links stay intact.',
          unlink:
            'Will be unlinked when you press Save: ChillPM stops pulling new data; old links stay and it can be linked again.',
        },
        quickLink: 'Link',
        quickSkip: 'Skip',
        save: 'Save selection',
        saveCount_one: 'Save selection ({{count}} change)',
        saveCount_other: 'Save selection ({{count}} changes)',
        saving: 'Saving…',
        discard: 'Discard changes',
        unlinkConfirm_one:
          'Unlink {{count}} repository: {{paths}}? ChillPM stops pulling new data; old links to tasks stay and it can be linked again any time.',
        unlinkConfirm_other:
          'Unlink {{count}} repositories: {{paths}}? ChillPM stops pulling new data; old links to tasks stay and they can be linked again any time.',
        unlinkConfirmButton: 'Save and unlink',
        deferred_one:
          '{{count}} repository is not linked yet (at most {{max}} per save) — press "Save selection" again to continue.',
        deferred_other:
          '{{count}} repositories are not linked yet (at most {{max}} per save) — press "Save selection" again to continue.',
        unreadable_one:
          '{{count}} repository cannot be read with this token, so it was not linked: {{paths}}.',
        unreadable_other:
          '{{count}} repositories cannot be read with this token, so they were not linked: {{paths}}.',
        toast: {
          saved: 'Saved: {{parts}}.',
          nothing: 'Saved — no repository changed.',
          linked_one: 'linked {{count}} repository',
          linked_other: 'linked {{count}} repositories',
          unlinked_one: 'unlinked {{count}} repository',
          unlinked_other: 'unlinked {{count}} repositories',
          skipped_one: 'skipped {{count}} repository',
          skipped_other: 'skipped {{count}} repositories',
        },
      },
      // APM-GIT-r — the "Add repository" flow in the summary.
      addRepo: {
        title: 'Add repository',
        intro:
          'Link one more repository to the project. Linked repositories stay as they are; cancelling halfway changes nothing.',
        whichToken: 'Which token?',
        usingToken: 'Using the token of {{account}}.',
        notFoundHint:
          'Repository not in the list? This token may not have access to it, or it lives on another server — create a new token following the guide.',
        useOtherToken: 'Use another token',
        backToRepositories: 'Back to choosing a repository',
      },
      summary: {
        done: 'All connected. ChillPM now updates branches, merge requests and commits on its own — nothing else to do.',
        importing:
          'Loading the history for the first time — large repositories can take tens of minutes. You can close this page; the Git tab fills in gradually.',
        connectionsTitle: 'Tokens in use',
        host: 'Server',
        account: 'Account',
        access: 'Access',
        expires: 'Expires',
        status: 'Status',
        statusValue: {
          ok: 'Working',
          invalid: 'Rejected by the server — replace the token',
          expired: 'Expired — replace the token',
        },
        hostDisabled: 'Your organisation admin has turned this git server off — its repositories have paused updating.',
        replaceToken: 'Replace token',
        repositoriesTitle: 'Connected repositories',
        importingBadge: 'Loading history for the first time',
        tokenAccount: 'Token account',
        sync: 'Sync status',
        usedBy: 'Used for',
        unused: 'Not used by any repository',
        replaceImpact_one:
          'This token is used for {{count}} repository: {{paths}}. The new token must be able to see it, or it stops updating.',
        replaceImpact_other:
          'This token is used for {{count}} repositories: {{paths}}. The new token must see all of them — any it cannot see stops updating.',
        addRepository: 'Add repository',
        revoke: 'Revoke token',
        revokeConfirm:
          'Revoke the token of {{account}}? It is removed from ChillPM; revoke it on the git server yourself.',
        revoked: 'Token revoked.',
      },
      tabs: {
        label: 'Sections of Git repositories',
        repositories: 'Repositories ({{count}})',
        connections: 'Tokens ({{count}})',
        options: 'Options ({{count}})',
      },
      optional: {
        title: 'Extra options',
        hint: 'ChillPM already works with the part above. Everything below is optional — open what you need.',
        badge: 'Optional',
        branchFormat: {
          title: 'Branch name format',
          benefit:
            'Set one branch name pattern for the team, so "Copy branch name" gives the right name and branches link to items on their own.',
        },
        rules: {
          title: 'Automatic status changes',
          benefit:
            'For example: opening a merge request moves the item to "In review", merging moves it to "Done" — no more dragging cards by hand.',
        },
        webhook: {
          title: 'Instant updates (webhook)',
          benefit:
            'A webhook lets the git server "ping" ChillPM whenever the repository changes — data arrives within seconds instead of at the next sync.',
        },
        identities: {
          title: 'Git accounts ↔ members',
          benefit:
            'Tell ChillPM which git account is which project member, so numbers and notifications point at the right person.',
        },
        write: {
          title: 'Write back to git',
          benefit:
            'Leave a link to the ChillPM item on merge requests, or turn item codes (like GOLD-123) in commits into clickable links.',
        },
        aiReadDiff: {
          title: 'Let AI read the code',
          benefit:
            'Allow AI to read the changed code of a merge request for more accurate summaries and checks. Off by default.',
        },
      },
    },
    // APM-GIT-b — git servers (`/admin?section=git-hosts`), §3.1; owned by the organisation since APM-ORGINT-e.
    hosts: {
      title: 'Git servers',
      subtitle:
        'Your company GitLab, GitHub.com and GitHub Enterprise that projects can connect repositories from. Tokens belong to each project — only addresses live here.',
      intro:
        'A PM pastes the repository link and the project token under Project settings → Git repositories; the link is accepted only when its server is declared and turned on here.',
      empty: 'No server yet — no project can connect a repository.',
      add: 'Add git server',
      preset: {
        gitlab: 'GitLab',
        github_com: 'GitHub.com',
        ghes: 'GitHub Enterprise',
      },
      // APM-ORGINT-e — pick the kind with glass tabs in the add dialog.
      pickKind: 'Where does your organisation keep its code?',
      presetTab: {
        gitlab: 'Company GitLab',
        github_com: 'GitHub.com',
        ghes: 'GitHub Enterprise',
      },
      githubComTaken: 'Your organisation already has GitHub.com — one is enough.',
      addTitle: 'Add a git server',
      editTitle: 'Edit server {{name}}',
      presetLine: 'Server type: {{preset}}.',
      create: 'Add',
      edit: 'Edit',
      howToTitle: 'Set up {{preset}} step by step',
      // APM-ORGINT-e — numbered steps, no checkboxes (`gitHostGuideSteps`).
      guide: {
        copyAddress: {
          gitlab:
            'Open your company GitLab in the browser and copy the address bar up to the end of the server name — e.g. **https://git.company.com**. If GitLab lives under a sub-path, keep the whole path, e.g. **https://company.com/gitlab**.',
          ghes: 'Open your company GitHub Enterprise in the browser and copy the address bar up to the end of the server name — e.g. **https://github.company.com**.',
        },
        paste: {
          gitlab: 'Paste it into **Server address** below. Do not add /api/v4 or a project name after it.',
          ghes: 'Paste it into **Server address** below. Do not add /api/v3 or a repository name after it.',
        },
        internal:
          'Does the server only open on the company network or VPN (an internal address)? Then ask the system administrator to turn on "allow internal addresses" for your organisation first — otherwise ChillPM will refuse this address.',
        ca: 'Does the browser warn about an unknown certificate when you open the server? Ask IT for the company CA file (PEM, starting with -----BEGIN CERTIFICATE-----) and paste it into **Private CA**. Otherwise skip this step.',
        test: 'Press **Add**, then **Test** on the server card to make sure ChillPM can talk to it. PMs then connect repositories under Project settings → Git repositories.',
        githubComReady: 'GitHub.com has a fixed address — nothing to enter. Just press **Add**.',
        githubComToken:
          'Each project creates its own GitHub token under Project settings → Git repositories — there are step-by-step instructions there.',
      },
      name: 'Display name',
      namePlaceholder: {
        gitlab: 'Company GitLab',
        github_com: 'GitHub.com',
        ghes: 'Internal GitHub',
      },
      nameHint: 'The name a PM sees when connecting a repository.',
      apiBaseUrl: 'Server address',
      apiBaseUrlShort: 'API address',
      apiBaseUrlHint: {
        gitlab: 'The root of GitLab, e.g. https://git.company.com — without /api/v4.',
        ghes: 'The root of GitHub Enterprise Server, e.g. https://github.company.com — without /api/v3.',
      },
      httpsOnly: 'A production install accepts https:// only.',
      webBaseUrl: 'Web address (optional)',
      webBaseUrlShort: 'Web address',
      webBaseUrlHint:
        'The address people open in a browser, and what a pasted repository link is matched against. Leave empty if it is the same as the server address.',
      fixedAddress: 'The addresses of GitHub.com are fixed.',
      caPem: 'Private CA (optional)',
      caPemHint:
        'If the internal server uses a certificate from a private CA, paste the CA in PEM format. There is no option to skip certificate verification.',
      caShort: 'Certificate',
      caSet: 'Private CA set',
      minInterval: 'Minimum interval',
      minIntervalHint: 'Repositories on this server cannot pick a shorter pull interval.',
      minutes: '{{n}} min',
      enabledLabel: 'Server enabled',
      enabledHint: 'When off, every repository on this server stops pulling. Nothing is deleted.',
      on: 'On',
      off: 'Off',
      usage: 'In use',
      inUse: '{{connections}} project connections · {{identities}} linked accounts',
      notInUse: 'No project yet',
      test: 'Test',
      testing: 'Testing…',
      testOk:
        'Connected — the server answered in {{ms}} ms. Projects can now connect repositories under Project settings → Git repositories.',
      testFail: 'Not usable yet: {{message}}',
      remove: 'Delete',
      removeConfirm: 'Delete server "{{name}}"? No project uses it, so no data is lost.',
      removeBlocked:
        '{{connections}} project connections and {{identities}} linked accounts still point to this server, so it cannot be deleted — turn it off instead.',
      created: 'Server {{name}} added.',
      saved: 'Git server saved.',
      removed: 'Git server deleted.',
      toggledOn: 'Server {{name}} turned on.',
      toggledOff: 'Server {{name}} turned off — its repositories stop pulling.',
      errors: {
        nameRequired: 'Enter a server name.',
        nameTooLong: 'The name is at most 80 characters.',
        invalidUrl: 'Enter a full http(s) address, without a query, # or credentials.',
        httpsRequired: 'https:// is required on a production install.',
        invalidPem: 'The CA must be in PEM format (starting with -----BEGIN CERTIFICATE-----).',
        githubDotCom: 'This is the address of GitHub.com — pick the "GitHub.com" tab above.',
      },
      // Why the Test button failed — by `details.reason` of GIT_HOST_UNREACHABLE.
      unreachable: {
        timeout: 'The server did not answer within 20 seconds.',
        network: 'Could not connect to this address.',
        tls: 'The server certificate is not trusted — paste the internal CA into the server settings.',
        redirect:
          'The address redirects to {{host}} — not followed, because the project token would follow it. Enter the final address.',
        bad_response:
          'The address answers, but it is not a git server API (an HTML page or the wrong format).',
        unexpected_status:
          'The server returned {{status}} instead of the expected code for a call without a token — check the address and sub-path.',
      },
    },
    // APM-GIT-e — the project's Git tab and the "Development" block (§8.1–§8.3). One sub-block per view.
    views: {
      label: 'Git tab views',
      overview: 'Overview',
      changeRequests: 'Merge requests',
      branches: 'Branches',
      commits: 'Commits',
      releases: 'Releases',
    },
    common: {
      noAccess: 'You do not have access to the Git section of this project.',
      state: { open: 'Open', draft: 'Draft', merged: 'Merged', closed: 'Closed' },
      review: {
        none: 'Not reviewed',
        requested: 'Awaiting review',
        changes_requested: 'Changes requested',
        approved: 'Approved',
      },
      ci: {
        pending: 'CI pending',
        running: 'CI running',
        success: 'CI passed',
        failed: 'CI failed',
        canceled: 'CI canceled',
      },
      target: {
        closes: 'Closing link: merging completes the code for this item',
        mentions: 'Only mentions this item',
        manual: 'Linked by hand',
      },
      age: { minutes: '{{n}} min', hours: '{{n}} h', days: '{{n}} d' },
      viewingAsOf: 'Viewing git data as of {{at}}.',
      partial: 'There is no git data before {{date}} — what is shown here is incomplete.',
      openOnHost: 'Open on the git server',
      repositoryFilter: 'Repository',
      allRepositories: 'All repositories',
      setup: {
        title: 'No repository is connected to this project yet',
        step1:
          'Your organisation admin registers the git server (company GitLab, GitHub) under Admin → Git servers.',
        step2:
          'The PM creates a token on GitLab / GitHub and pastes it under Project settings → Git repositories — with a step-by-step guide.',
        step3:
          'The PM links a repository by pasting its link or picking from the repositories the token can see.',
        manual:
          'Without a repository the "Copy branch name" button on an item still works: a branch with that name links itself to the item as soon as the repository is connected.',
        openSettings: 'Open the Git setup guide',
        askManager: 'Ask the project PM to set up Git — it needs the Git management permission of the project.',
      },
    },
    overview: {
      defaultBranch: 'Default branch',
      lastSync: 'Last successful sync',
      nextSync: 'Next sync',
      running: 'Syncing…',
      historyFrom: 'Data since',
      syncNow: 'Sync now',
      syncing: 'Queuing…',
      syncQueued: 'A sync has been queued — new data arrives in a few minutes.',
      syncAlreadyQueued: 'A sync is already queued or running.',
      health: {
        ok: 'Syncing on schedule.',
        waiting: 'Just linked — waiting for the first sync.',
        importing: 'Initial import in progress: {{n}} items read.',
        failing: 'The latest sync failed ({{n}} in a row): {{reason}}',
        hostDisabled:
          'Your organisation admin has disabled this git server — syncing is paused, nothing is lost.',
        connection: {
          invalid:
            'The git server rejected the connection token — syncing is paused. The PM pastes a new token under Project settings → Git repositories.',
          expired:
            'The connection token has expired — syncing is paused. The PM pastes a new token under Project settings → Git repositories.',
        },
      },
    },
    changeRequests: {
      column: {
        title: 'Merge request',
        tasks: 'Items',
        author: 'Author',
        branches: 'Source → target branch',
        review: 'Review · CI',
        age: 'Age',
      },
      mergedOn: 'Merged {{date}}',
      closedOn: 'Closed {{date}}',
      noTask: 'No item linked',
      filter: {
        state: 'Filter by state',
        stateOption: { open: 'Open', merged: 'Merged', closed: 'Closed', all: 'All' },
        linked: 'Filter by linked item',
        linkedOption: { all: 'All MRs', linked: 'Linked', unlinked: 'Not linked' },
      },
      emptyTitle: 'No merge request matches the filters',
      emptyHint:
        'Change the state or repository filter. For a repository that was just linked, wait for the initial import — see its status on the Overview.',
      drawer: {
        label: 'Merge request {{ref}}',
        loading: 'Loading…',
        reviewers: 'Reviewers: {{names}}',
        size: '{{files}} files changed · +{{additions}} −{{deletions}} lines',
        description: 'Description',
        phases: 'Time per phase',
        phase: { coding: 'Coding', pickup: 'Pickup', review: 'Review' },
        phaseHint: {
          coding: 'From the first commit to ready for review',
          pickup: 'From ready for review to the first review',
          review: 'From the first review to merge',
        },
        milestone: {
          firstCommit: 'First commit',
          created: 'Opened',
          ready: 'Marked ready',
          firstReview: 'First review',
          approved: 'Approved',
          merged: 'Merged',
          closed: 'Closed',
        },
        commits: 'Commits',
        commitsCount: 'Commits ({{n}})',
        noCommits: 'No commits at the viewed point in time.',
      },
    },
    branches: {
      branchesTitle: 'Branches',
      commitsTitle: 'Commits on the default branch',
      noBranches: 'No branches at the viewed point in time.',
      noCommits: 'No commits on the default branch at the viewed point in time.',
      flag: {
        default: 'Default',
        protected: 'Protected',
        merged: 'Merged',
        deleted: 'Deleted',
      },
      column: { name: 'Branch', lastCommit: 'Last commit', changeRequest: 'Merge request' },
    },
    settings: {
      connections: {
        noExpiry: 'No expiry / unknown',
      },
      repositories: {
        interval: 'Sync every',
        minutes: '{{n}} min',
        backfill: 'Initial import',
        days: 'last {{n}} days',
        saved: 'Repository settings saved.',
        unlink: 'Unlink',
        unlinkConfirm:
          'Unlink {{path}} from the project? Syncing stops; the data already pulled and the links to items stay, and relinking does not import from scratch.',
        unlinked: 'Repository unlinked.',
      },
      branchFormat: {
        title: 'Branch name format',
        hint: 'The pattern used by the "Copy branch name" button on an item. A branch named this way links itself to the item.',
        format: 'Branch name pattern',
        placeholders:
          'Available: {type} (by item type), {key} (project key), {number} (item number), {slug} (item title without accents). {key}-{number} is required.',
        previewTask: 'Task GOLD-123',
        previewIssue: 'Issue GOLD-I12',
        sample: { task: 'Fix the sign-in error', issue: 'Report fails to load' },
        issue: {
          empty: 'Enter a branch name pattern.',
          too_long: 'The pattern is limited to 120 characters.',
          unknown_placeholder: 'Only {type}, {key}, {number} and {slug} are available.',
          missing_key_number:
            'The pattern must contain {key}-{number} together — without it a branch cannot link itself to the item.',
          invalid_characters:
            'The fixed text may only contain letters, digits and . _ / - ; it cannot start or end with / or a dot.',
        },
        typeMap: 'Item type ⇒ {type}',
        typeMapHint:
          'Item types missing from the table use "feature" (operational issues use "fix"). Type the item type name as it is shown.',
        typeName: 'Item type name',
        typeValue: '{type} value',
        addRow: 'Add row',
        removeRow: 'Remove row',
        typeRowInvalid:
          'Each row needs an item type name not used by another row and a {type} made of lowercase letters, digits and hyphens (20 characters at most).',
        reset: 'Reset to default',
        saved: 'Branch name format saved.',
      },
      write: {
        title: 'Write back',
        hint: 'ChillPM creates branches / merge requests from work items and posts back-link comments on MRs — under the token account, so the token needs write access.',
        backlinkTitle: 'Back-link comment on merge requests',
        backlinkHint:
          'Turned on for a repository ⇒ each new merge request mentioning a work item gets exactly one comment "ChillPM: GOLD-123 — <item title> <link>". Merge requests that existed before the repository was linked are never commented on. Off by default.',
        noRepository: 'No repository linked yet.',
        postsAs: 'Comments are posted as {{account}}.',
        postsAsUnknown: 'Comments are posted as the token account.',
        backlinkOn: 'Back-link comments turned on — they start with the next sync.',
        backlinkOff: 'Back-link comments turned off.',
        autolinkTitle: 'Back-links without a write token',
        autolinkHint:
          'Configure once on the git server: work item codes (GOLD-123) in every commit and merge request become links back to ChillPM. No token needed, nothing to turn on here.',
        copy: 'Copy',
        copied: 'Copied.',
        copyFailed: 'Could not copy — select and copy it by hand: {{value}}',
        guide: {
          gitlab: {
            title: 'GitLab — Custom issue tracker',
            steps:
              'In the project (or group): Settings → Integrations → Custom issue tracker → enable, then fill in the Issue URL below. Use the ChillPM address for Project URL and New issue URL too.',
            issueUrl: 'Issue URL',
          },
          github: {
            title: 'GitHub — Autolink references',
            steps:
              'In the repository: Settings → Autolink references → Add autolink reference, choose "Alphanumeric", then fill in the two fields below.',
            prefix: 'Reference prefix',
            urlTemplate: 'Target URL',
          },
        },
      },
    },
    development: {
      title: 'Development',
      copyBranch: 'Copy branch name',
      readOnlyNotice:
        'This project has no Git repository linked any more — showing the links recorded earlier, read only.',
      copied: 'Copied branch name {{name}}',
      copyFailed: 'Could not copy — select and copy it by hand: {{name}}',
      empty:
        'No branch, merge request or commit mentions this item yet. Name the branch as suggested above and it links itself.',
      changeRequests: 'Merge requests of this item',
      branches: 'Branches of this item',
      commitCount: '{{n}} commits',
      commitsCapped: 'Only the {{n}} most recent commits are shown.',
      hide: 'Hide',
      hideHint: 'Wrong link? Hide it from this item — later syncs will not bring it back.',
      hidden: 'Link hidden from this item.',
      removeManual: 'Remove',
      removeManualHint: 'Remove the link that was added by hand',
      overrideRemoved: 'Decision removed — the link is back to what was pulled from git.',
      hiddenTitle: 'Hidden links',
      hiddenItem: 'Hidden {{kind}} {{ref}}',
      unhide: 'Unhide',
      manual: {
        open: 'Link by hand',
        title: 'Link something in git to this item',
        kind: 'Kind',
        kindOption: { change_request: 'Merge request', branch: 'Branch', commit: 'Commit' },
        placeholder: {
          change_request: 'MR number or MR link (for example 45)',
          branch: 'Branch name',
          commit: 'Full commit hash (sha)',
        },
        invalid: {
          change_request: 'Enter the merge request number, or paste its link.',
          branch: 'Enter a branch name without spaces.',
          commit: 'Enter a commit hash (7–40 hex characters), or paste the commit link.',
        },
        submit: 'Link',
        added: 'Linked to this item.',
        noRepository: 'The project has no repository linked, so there is nothing to link yet.',
      },
    },
    // APM-GIT-k — write back (`components/git-write-*.tsx`), §8.4.
    write: {
      createBranch: 'Create branch',
      createChangeRequest: 'Create merge request',
      blocked: {
        host_disabled: 'The git server of this repository is turned off by your organisation admin, so branches / merge requests cannot be created.',
        connection_unusable:
          'The repository token was rejected by the git server or revoked — the PM pastes a new token in Project settings → Git repositories.',
        not_supported: 'This git server does not support creating branches / merge requests from ChillPM yet.',
        read_only: {
          gitlab:
            'The project token is read-only. To create branches / merge requests, the PM pastes a token with the api scope and Developer role or higher in the repository.',
          github:
            'The project token is read-only. To create branches / pull requests, the PM pastes a token with read and write access to Contents and Pull requests (or a classic PAT with the repo scope).',
        },
      },
      branchTitle: 'Create a branch for {{code}}',
      changeRequestTitle: 'Create a merge request for {{code}}',
      asAccount: 'Will be created as {{account}} on {{host}} — the project token account, not yours.',
      asAccountUnknown: 'Will be created as the project token account on {{host}}, not yours.',
      repository: 'Repository',
      branchName: 'Branch name',
      branchNameHint: 'Keep the work item code in the name so the branch links to this item.',
      baseBranch: 'Create from branch',
      sourceBranch: 'Source branch',
      sourceBranchHint: 'The branch with the changes — it must already exist in the repository.',
      targetBranch: 'Merge into branch',
      sameBranches: 'Source and target branches must differ.',
      title: 'Title',
      titleHint: 'Keep the work item code at the start so the merge request links to this item.',
      titleEmpty: 'Enter a title.',
      draft: 'Create as draft',
      draftHint: 'Nobody is asked to review a draft until you mark it ready on the git server.',
      afterCreate: 'Once created, the branch / merge request shows up in Development after the next sync (usually within a minute).',
      creating: 'Creating…',
      submitBranch: 'Create branch',
      submitChangeRequest: 'Create merge request',
      branchCreated: 'Branch {{name}} created.',
      changeRequestCreated: 'Merge request #{{number}} created.',
      changeRequestExisting: 'This branch already has open merge request #{{number}} — nothing new was created.',
      open: 'Open',
      nameIssue: {
        empty: 'Enter a branch name.',
        too_long: 'Branch names are at most 200 characters.',
        invalid: 'The branch name has characters git does not allow (spaces, ~ ^ : ? * [ \\, "..").',
      },
      forbidden: {
        gitlab: 'The project token cannot write to this repository — it needs the api scope and the Developer role or higher (protected branches may need more).',
        github:
          'The project token cannot write to this repository — it needs read and write access to Contents and Pull requests (or a classic PAT with the repo scope), plus push access to the repository.',
      },
      forbiddenRotate: 'If access was granted since, the PM pastes the token again in Project settings → Git repositories so ChillPM re-reads it.',
      branchExists: 'Branch "{{name}}" already exists in the repository. Pick another name, or create a merge request from that branch.',
    },
    // APM-GIT-k — deep link by code (`routes/_app/t.$code.tsx`).
    workItem: {
      opening: 'Opening {{code}}…',
      notFound: 'Could not find {{code}} — wrong code, deleted item, or you are not in that project.',
    },
    // APM-GIT-l — release notes (`components/release-notes/`), §8.5.
    releaseNotes: {
      title: 'Release notes',
      status: {
        ai_suggested: 'AI suggestion — awaiting review',
        approved: 'Approved',
        manual: 'Written by hand',
      },
      empty: 'This release has no release notes yet.',
      templateTitle: 'Template — built from the items in this release, grouped by type',
      useTemplate: 'Start from the template',
      template: {
        heading: 'Release {{tag}}',
        ungrouped: 'Other',
        truncated:
          'The commit range of this release was cut short — the item list may be incomplete.',
        noItems: 'No items are linked to this release yet.',
      },
      editorLabel: 'Release notes content (markdown)',
      placeholder: 'Write for people who do not read code: what this release brings, what it fixes…',
      save: 'Save',
      saved: 'Release notes saved.',
      edit: 'Edit',
      approve: 'Approve',
      approvedToast: 'Release notes approved.',
      approvedAt: 'Approved {{date}}',
      copy: 'Copy',
      copied: 'Notes copied — paste them into the release on the git server.',
      copyFailed: 'Could not copy — select and copy by hand.',
      readOnlyAsOf: 'Showing the notes as of {{date}} — read only.',
      notPublished:
        'Notes are not published to the git server automatically. Once approved, copy and paste them into the release.',
      aiButton: 'Draft with AI',
      aiLabel: 'Draft release notes for {{tag}}',
      aiPrompt: 'Draft release notes for release {{tag}}.',
      aiResult: '{{count}} suggested note lines',
      aiApply: 'Send for review',
      applied: 'Suggestion added — approve or edit it before use.',
      notApplied:
        'The notes are already approved or written by hand, so the AI suggestion did not replace them. Edit by hand to use it.',
    },
    // APM-GIT-m — MR summary (`git-cr-summary-slot`) and the "let AI read code" switch, §8.5, §8.6.
    crSummary: {
      title: 'Summary',
      run: 'Summarize with AI',
      rerun: 'Summarize again',
      trackLabel: 'Summarize MR !{{number}}',
      writeManual: 'Write manually',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      approve: 'Approve',
      approvedToast: 'Summary approved.',
      saved: 'Summary saved.',
      done: 'Summarized — waiting for approval.',
      empty: 'No summary yet for the latest version of this MR.',
      pastEmpty: 'There was no summary at that time.',
      outdated:
        'The MR has new commits since it was summarized (summary of {{sha}}). Summarize again to match the latest version.',
      placeholder: 'Three sentences: what changed, which part it touches, which work it relates to.',
      status: {
        ai_suggested: 'AI suggestion — pending approval',
        approved: 'Approved',
        manual: 'Written manually',
      },
      basis: {
        diff: 'Read the code',
        description: 'Based on the description, code not read',
      },
    },
    aiReadDiff: {
      title: 'Let AI read code changes',
      hint: 'By default AI only reads the title, description, commit messages and file paths of an MR. Turn it on per repository so summaries also use the changed code.',
      limits:
        'At most 256 KB per MR and 16 KB per file. The code only passes through the memory of one AI run — it is not stored. Sensitive files (.env, keys, certificates…) are sent as paths only.',
      label: 'Let AI read code changes of {{path}}',
      on: 'On',
      off: 'Off',
      confirm:
        'The changed code in merge requests of {{path}} will be sent to the AI provider configured for this project. Turn on?',
      confirmLabel: 'Turn on',
      enabled: 'AI can now read code changes of {{path}}.',
      disabled: 'Turned off — AI no longer reads code changes of {{path}}.',
      empty: 'The project has no repository linked yet.',
    },
    // APM-GIT-h — automatic status rules (`components/settings/git-rules-section.tsx`), §6.
    rules: {
      title: 'Automatic status rules',
      hint: 'When something happens in git — coding starts, a merge request is opened / approved / merged, a production deploy — move the linked item to the column you choose.',
      intro:
        'No rule is on by default. Rules only move items forward: never backwards, never out of the Done column, and if someone drags an item back by hand the same event does not push it again. A repository’s first import does not run rules; merge requests with [skip-pm] in the title or description are skipped.',
      empty: 'No rules yet — items change column only when someone moves them.',
      noStatuses: 'The project has no board columns to use as a target.',
      add: 'Add rule',
      remove: 'Remove rule',
      discard: 'Discard changes',
      saved: 'Automatic status rules saved.',
      cannotSave: 'Some rules are not valid yet (red bar) — fix them before saving.',
      ruleLabel: 'Rule {{n}}',
      enabled: 'Turn on rule {{n}}',
      on: 'On',
      off: 'Off',
      when: 'When',
      moveTo: 'Move the item to column',
      statusMissing: '(column was deleted — pick again)',
      statusInSprint: '{{status}} · sprint {{sprint}} only',
      scope: 'Target branch of the merge request',
      scopeKind: {
        default: 'Default branch',
        protected: 'Protected branches',
        pattern: 'By pattern',
      },
      scopeNotUsed: 'Not used — this event does not depend on a target branch.',
      pattern: 'Branch name pattern',
      patternMatches: 'matches {{n}} pulled branches',
      trigger: {
        work_started: 'Coding starts (first branch or commit)',
        cr_opened: 'Merge request opened (not draft)',
        cr_approved: 'Merge request approved',
        cr_merged: 'Merge request merged',
        deployed: 'Deployed to production',
      },
      error: {
        pattern_empty: 'Enter a branch name pattern, for example release/*.',
        status_not_in_workflow:
          'The target column is no longer on the board — this rule is not running. Pick a column again.',
        duplicate_rule: 'Another rule already covers the same event and branch scope.',
      },
      warning: {
        unprotected_branch:
          'Branch {{branches}} of {{repository}} is not protected: anyone who can push can complete their own items. Protect the branch on the git server, or choose “Protected branches”.',
        sprint_points:
          'The project uses sprints: this rule moves items into Done without anyone pressing a button — sprint points will count them.',
        sprint_scoped_status:
          'Column {{status}} belongs to sprint {{sprint}} only: items outside that sprint cannot be moved (logged as skipped).',
      },
      skip: {
        already_fired: 'this event already moved the item once',
        backward: 'the item is already at or past the target column — rules never move backwards',
        mentions_only: 'the merge request only mentions the item, it does not close it',
        pending_work: 'the item still has another open merge request',
        skipped_by_marker: 'the merge request carries [skip-pm]',
        status_missing: 'the target column was deleted',
        rule_inactive: 'the rule is off',
      },
      recent: {
        title: 'Recent rule runs',
        empty: 'No rule has run yet — the log starts with the first pull after a rule is turned on.',
        outcome: { applied: 'Moved', skipped: 'Skipped' },
        because: 'Reason: {{reason}}',
        deletedTarget: '(deleted item)',
        branch: 'branch {{name}}',
        commit: 'commit {{sha}}',
      },
    },
    // APM-GIT-g — "pull now" webhook (§5.2): status line on the repository card + settings section.
    // APM-GIT-i — Releases view, default-branch CI, chips on tasks, marks on Kanban cards (§8.1, §8.2).
    releases: {
      listTitle: 'Releases',
      empty:
        'No releases at the selected point in time. A release is a tag matching the repository tag pattern (v* by default).',
      prerelease: 'Pre-release',
      firstRelease: 'First release in the pulled data',
      fromTag: 'Since {{tag}}',
      cell: { releasedAt: 'Released', items: 'Items', commits: 'Commits' },
      range: { pending: 'Computing items', truncated: 'List may be incomplete' },
      back: 'All releases',
      detailLabel: 'Release {{tag}}',
      releasedAt: 'Released {{at}}',
      rangeLine: '{{from}} → {{to}} · {{n}} commits',
      rangeLineFirst: 'First release · {{n}} commits',
      pendingNotice:
        'The item list of this release is still being computed — the next pull will finish it. Use "Pull now" on the Overview if you need it sooner.',
      truncatedNotice:
        'The range {{from}} → {{to}} has more than {{max}} commits, so only {{max}} were read: the item list below may be incomplete.',
      asOfNotice:
        'The item list is the latest computation, not rebuilt for the selected point in time; item titles and statuses are current.',
      itemsTitle: 'Items in this release ({{n}})',
      noItems: 'No item is linked to a commit or merge request in this release range.',
      untyped: 'Uncategorised',
      via: 'via {{ref}}',
      direct: 'via a direct commit',
      issueState: {
        new: 'New',
        triaged: 'Triaged',
        in_progress: 'In progress',
        waiting_customer: 'Waiting for customer',
        waiting_third_party: 'Waiting for third party',
        resolved: 'Resolved',
        reopened: 'Reopened',
        closed: 'Closed',
      },
      deploymentsTitle: 'Deployments of this release',
      noDeployments: 'No deployment of this release at the selected point in time.',
    },
    deployments: {
      title: 'Deployments by environment',
      empty:
        'No deployments at the selected point in time. Repositories that do not use GitLab / GitHub deployments leave this empty.',
      production: 'Production',
      count: '{{n}} runs',
      more: 'and {{n}} older runs',
      status: { running: 'Running', success: 'Succeeded', failed: 'Failed', canceled: 'Canceled' },
      finishedAt: 'finished {{at}}',
      startedAt: 'started {{at}}',
    },
    ci: {
      line: 'CI on {{branch}}:',
      none: 'No CI run on the default branch in the pulled data yet.',
      status: {
        none: 'No CI',
        pending: 'Pending',
        running: 'Running',
        success: 'Passed',
        failed: 'Failed',
        canceled: 'Canceled',
      },
    },
    releaseChips: {
      inRelease: 'In {{tag}}',
      inReleaseTitle: 'Release {{tag}} ({{repository}}), released {{date}}, contains this item',
      deployed: 'In production {{date}}',
      estimate: 'estimate',
      deployedTitle:
        'Estimate: the first successful production deployment containing (or after) every merge request that closes this item. May be wrong when that deployment shipped an older commit (rollback).',
    },
    taskMark: {
      open: '{{n}} open merge requests',
    },
    webhook: {
      title: 'Webhook',
      hint: 'Lets the git server tell ChillPM right away about a new push, merge request or pipeline instead of waiting for the next scheduled pull. Without it the data is still complete — just a few minutes later.',
      mode: {
        none: 'Webhook: off — pulling on schedule',
        manual: 'Webhook: pasted by hand',
        auto: 'Webhook: registered automatically',
      },
      lastSeen: 'last received {{at}}',
      neverSeen: 'no delivery received yet',
      noRepository: 'No repository linked yet — link one in the Repositories section above first.',
      needsPublicUrl:
        'Webhooks need a public address — running on polling. ChillPM is running at an address the git server cannot reach; the repository is still pulled on schedule.',
      cannotAuto:
        'The connection token is not allowed to create webhooks on this repository (GitLab needs the Maintainer role, GitHub needs admin access to the repository). The repository owner can paste it by hand using the guide.',
      autoForbidden:
        'The git server refused: the connection token is not allowed to create webhooks on this repository. Nothing was changed — ask the repository owner to paste it by hand using the guide below.',
      leftover:
        'ChillPM could not remove the old webhook on the git server. Delete it by hand under Settings → Webhooks of the repository. Leaving it is harmless (every delivery it makes is rejected) but it stays in the webhook list.',
      action: {
        auto: 'Register automatically',
        manual: 'Paste by hand',
        regenerate: 'Generate a new secret',
        off: 'Turn webhook off',
      },
      regenerateConfirm:
        'Generate a new secret for {{path}}? The old secret stops working immediately: the webhook on the git server is rejected until the new secret is pasted in.',
      changed: {
        auto: 'Webhook registered on the git server.',
        manual: 'Secret created — paste the address and the secret into the git server.',
        none: 'Webhook turned off — the repository keeps being pulled on schedule.',
      },
      manual: {
        title: 'Paste into the git server by hand',
        where: {
          gitlab:
            'In GitLab: open the repository → Settings → Webhooks → Add new webhook. Paste the address into URL, the secret into "Secret token", then tick the boxes below.',
          github:
            'In GitHub: open the repository → Settings → Webhooks → Add webhook. Paste the address into "Payload URL", the secret into "Secret", choose "Let me select individual events" and tick the items below.',
        },
        url: 'Address (URL)',
        secret: 'Secret',
        secretOnce: 'The secret is shown this one time. Copy it now — ChillPM cannot show it again.',
        secretHidden:
          'The secret is shown once, when it is created. If it is lost, press "Generate a new secret" and paste the new one into the git server.',
        secretPending:
          'Press "Paste by hand" below to have ChillPM generate the secret — it is shown once.',
        copyUrl: 'Copy address',
        copySecret: 'Copy secret',
        copied: 'Copied.',
        copyFailed: 'Could not copy — select and copy it by hand.',
        events: 'Events to tick',
        traps: {
          title: 'Three pitfalls with self-hosted GitLab',
          group:
            'Group webhooks need the Premium tier. On CE the webhook must be added on each project — one webhook per repository linked here.',
          pushLimit:
            'A single push of more than 3 branches or tags makes GitLab send no push hook at all (push_event_hooks_limit). The scheduled pull still picks those branches up.',
          localNetwork:
            'If ChillPM sits on a local-network address as seen from GitLab, a GitLab admin must enable "Allow requests to the local network from webhooks and integrations".',
        },
      },
      // Nhãn ô tích — đúng chữ trên giao diện của máy chủ git, không dịch.
      event: {
        gitlab: {
          push_events: 'Push events',
          tag_push_events: 'Tag push events',
          merge_requests_events: 'Merge request events',
          note_events: 'Comments',
          pipeline_events: 'Pipeline events',
          deployment_events: 'Deployment events',
          releases_events: 'Releases events',
        },
        github: {
          push: 'Pushes',
          create: 'Branch or tag creation',
          delete: 'Branch or tag deletion',
          pull_request: 'Pull requests',
          pull_request_review: 'Pull request reviews',
          check_suite: 'Check suites',
          status: 'Statuses',
          workflow_run: 'Workflow runs',
          deployment_status: 'Deployment statuses',
          release: 'Releases',
        },
      },
    },
    coverage: {
      title: 'Check criteria against MRs',
      help: 'Puts the acceptance criteria in the task description next to what the linked MRs say they did, to show which criteria nobody has mentioned yet. A suggestion only — it does not edit the task, change its status or block a merge.',
      run: 'Check with AI',
      prompt: 'Check the criteria of this task against the linked MRs.',
      criteriaHeading: 'Task criteria',
      changeRequestsHeading: 'Linked MRs',
      noCriteriaManual: 'The task description has no list items to use as criteria.',
      noChangeRequests: 'No MR is linked to this task yet.',
      status: 'AI suggestion — not reviewed',
      basis: {
        diff: 'Read the code',
        description: 'Only checked the MR descriptions',
      },
      at: 'Checked at {{at}}',
      noCriteria: 'This task has no criteria to check against.',
      missingHeading: 'Not mentioned by any MR',
      seenHeading: 'Mentioned',
      evidence: '!{{number}}: “{{quote}}”',
      checked: 'Checked against: {{list}}',
      checkedNone: 'This run did not read any MR.',
      newSince: 'MRs linked after this check: {{list}} — check again to include them.',
    },
    // APM-GIT-j: flow metrics, "Needs attention", git account ↔ person (plan-git-integration §7, §4.1).
    insights: {
      title: 'Flow metrics',
      partial: 'No git data before {{date}} — weeks before that date are empty, not quiet.',
      emptyHelp: 'Merged MRs of the last 12 weeks appear here once the repository has been synced.',
      p85: 'p85 {{value}}',
      samples: '{{n}} MR',
      cycle: {
        title: 'MR cycle time, four phases',
        subtitle: 'Median per merge week · {{n}} MRs merged in 12 weeks',
        empty: 'No MR was merged in these 12 weeks',
      },
      phase: {
        coding: 'Coding',
        pickup: 'Waiting for review',
        review: 'Review',
        deploy: 'To production',
      },
      axis: {
        mergeWeek: 'Merge week (Monday)',
        week: 'Week (Monday)',
        medianHours: 'Hours (median)',
        medianDays: 'Days (median)',
        openCount: 'Open MRs',
        deploys: 'Deployments',
        releases: 'Releases (by tag)',
      },
      open: {
        title: 'Open MRs by age',
        subtitle: '{{n}} MRs open',
        empty: 'No MR is open',
      },
      age: {
        under1d: '< 1 day',
        d1to3: '1–3 days',
        d3to7: '3–7 days',
        over7d: '> 7 days',
      },
      deploys: {
        title: 'Deployment frequency',
        empty: 'No deployment or release in these 12 weeks',
        source: {
          deployments: 'Successful deployments to production per week',
          tags: 'Releases per week, by tag — the repository has no deployments',
          mixed: 'Deployments to production, plus releases by tag for repositories without deployments',
          none: 'No repository linked',
        },
      },
      leadTime: {
        title: 'Lead time for changes',
        subtitle: 'First commit → production (estimated), median per merge week',
        empty: 'No merged MR has reached production yet',
        whole: 'Whole 12 weeks',
      },
      unlinked: {
        title: 'Work without a task (last {{days}} days)',
        help: 'How to read work without a task',
        detail: '{{unlinked}} of {{commits}} commits on the default branch are not linked to any task',
      },
    },
    attention: {
      title: 'Needs attention',
      asOfNote: 'Task statuses are their current status, not the status at the viewed time.',
      empty: 'Nothing is out of step between tasks and code.',
      truncated: '({{shown}} of {{total}}, oldest first)',
      pipeline: 'pipeline at {{sha}}',
      codes: 'Codes not found: {{codes}}',
      skipped: 'Rule skipped: {{reason}}',
      ruleOff: 'The “MR merged” rule is off',
      kind: {
        done_with_open_cr: 'Task done, code not merged',
        merged_not_done: 'Code merged, task not done',
        review_waiting: 'MR waiting for a first review for more than {{hours}} hours',
        stale_change_request: 'MR not updated for more than {{days}} days',
        stale_branch: 'Branch of an in-progress task without commits for more than {{days}} days',
        unlinked_change_request: 'Open MR not linked to any task',
        unknown_code: 'Mentions a task code the project does not have',
        default_branch_red: 'Default branch is red',
      },
      since: {
        done_with_open_cr: 'MR opened {{at}}',
        merged_not_done: 'merged {{at}}',
        review_waiting: 'ready for review since {{at}}',
        stale_change_request: 'last updated {{at}}',
        stale_branch: 'last commit {{at}}',
        unlinked_change_request: 'opened {{at}}',
        unknown_code: 'since {{at}}',
        default_branch_red: 'red since {{at}}',
      },
      author: {
        manual: 'Author: {{name}} ({{username}})',
        email: 'Author: {{name}} ({{username}}, matched by email)',
        unknown: 'Author: {{username}}',
      },
    },
    identities: {
      title: 'Git accounts ↔ people',
      hint: 'Every account seen in the project repositories — merge request authors, reviewers, and commit authors (by email, since commits carry no username). Applies to the whole organisation. Accounts are matched by commit email automatically; choose a person to override. Used to say whose MR or commit it is — never to rank people.',
      empty: 'No git account seen yet — the repository is still importing, or has no merge requests / commits.',
      saved: 'Saved',
      choose: 'Person behind {{account}}',
      none: '— not linked —',
      byEmail: 'Matched by email: {{name}}',
      count: '{{total}} accounts · {{unmapped}} not linked',
      filter: 'Filter by account, email or name',
      noMatch: 'No account matches.',
      unmapped: 'Not linked',
      inProject: 'In this project',
      outsideProject: 'Outside this project',
      seenAs: {
        change_request_author: 'MR author',
        reviewer: 'Reviewer',
      },
      commits_one: '{{count}} commit',
      commits_other: '{{count}} commits',
    },
  },
  projectSync: {
    kind: {
      aiservice: 'aiservice',
      jira_cloud: 'Jira Cloud',
      redmine: 'Redmine',
      trello: 'Trello',
      lark_tasks: 'Lark Tasks',
      meegle: 'Lark Project (Meegle)',
    },
    // APM-SYNC2-a — credential field labels by kind (`SyncProviderCapabilities.credentialFields`).
    credentials: {
      aiservice: {
        apiKey: "The other project's API key",
        apiKeyPlaceholder: 'pmk_…',
        apiKeyHelp:
          'Find it under Project settings → API key in {{provider}}. Each key opens exactly one project, and is never shown again once saved.',
      },
      jira_cloud: {
        siteUrl: 'Jira site',
        siteUrlPlaceholder: 'https://company.atlassian.net',
        siteUrlHelp: 'The start of the address when you open Jira — https://<name>.atlassian.net.',
        email: 'Atlassian account email',
        emailHelp: 'Email of the account that created the API token. Work pushed to Jira is attributed to this person.',
        apiToken: 'API token',
        apiTokenHelp:
          'The token carries exactly that account\'s permissions and expires after at most 1 year — then use "Change credentials".',
      },
      redmine: {
        apiKey: 'Redmine API key',
        apiKeyHelp:
          'On the Redmine My account page, right column → API access key → Show. Work pushed out is attributed to this person.',
      },
      trello: {
        apiKey: 'API key (Power-Up)',
        apiKeyHelp: 'The key of a Power-Up you create at trello.com/power-ups/admin.',
        token: 'Token',
        tokenHelp:
          'Click the "Token" link next to the API key, then Allow. Key + token can read/write every board of that Trello account.',
      },
      meegle: {
        email: 'Meegle email',
        emailHelp:
          'Your Meegle account email — must match your ChillPM sign-in email. Everything pushed out is done as you and sees only what you can see.',
        emailPlaceholder: 'you@company.com',
      },
    },
    // APM-SYNC2-c — how to get credentials, by kind (plan-sync-providers §3–§7).
    howTo: {
      jira_cloud:
        '1. Open id.atlassian.com → Security → API tokens.\n2. Click "Create API token" (the unscoped kind), name it, pick an expiry.\n3. Paste the token here with the account email and the Jira site.',
      redmine:
        '1. Sign in to Redmine and open My account (top right).\n2. Right column: API access key → Show, copy the key.\n3. Not there? A Redmine admin must enable Administration → Settings → API → Enable REST web service.',
      trello:
        '1. Open trello.com/power-ups/admin and create a Power-Up (any name).\n2. In the Power-Up: API key → Generate a new API key.\n3. Click the "Token" link next to the key, choose Allow, copy the token.\nKey + token can act on every board of the account — use a dedicated sync account if you can.',
      lark_tasks:
        '1. Press "Connect with Lark Tasks", sign in to Lark as the owner of the task list and grant access.\n2. Pick the list: ChillPM adds its app to that list. If Lark refuses, use "Create a new list" — ChillPM creates the list and hands ownership to you.\n3. After that sync runs as the app and keeps nobody\'s token. Descriptions and comments in Lark are plain text (at most 3,000 characters).',
      meegle:
        '1. The ChillPM plugin must be installed in your Meegle space (Space settings → Plugin management).\n2. Enter your Meegle account email (the same as your ChillPM sign-in email) and click Check.\n3. Pick the space and work item type. Types using a node flow only pull status from Meegle; other fields sync both ways.',
    },
    // APM-SYNC2-c — connection steps by kind: check, pick project/board, status groups.
    connect: {
      howToTitle: 'Where do I get {{source}} credentials?',
      oauth: 'Connect with {{source}}',
      // APM-SYNC2-g — sign in at the source (Lark) in a popup.
      oauthAgain: 'Sign in to {{source}} again',
      oauthBlocked: 'The browser blocked the popup.',
      oauthOpen: 'Open the {{source}} sign-in page',
      oauthStatus: {
        waiting: 'Finish signing in in the {{source}} window — your lists will show up here (or press Check).',
        state: 'The sign-in has expired or is invalid — press "Connect with {{source}}" again.',
        denied: 'You did not grant access in {{source}}.',
        error: '{{source}} did not complete the sign-in — try again in a few minutes.',
      },
      oauthAppNote:
        'This source runs on the installation app and has no credentials of its own — Save to re-check the app access to the list.',
      newContainer: 'Or create a new list',
      newContainerHint:
        'ChillPM creates a list in {{source}} and hands ownership to you — use this when the app cannot be added to an existing list.',
      newContainerName: 'List name',
      create: 'Create',
      creating: 'Creating…',
      fieldError: {
        required: 'This field is required.',
        invalid: 'This value is not in the right format.',
      },
      check: 'Check',
      checking: 'Checking…',
      checkAgain: 'Check again',
      signedInAs: 'Signed in as {{name}}',
      containerTitle: 'Pick the external project / board',
      containerSearch: 'Search by name or key',
      search: 'Search',
      containerEmpty: 'This account cannot see any project / board yet.',
      containerNoMatch: 'No project / board matches "{{q}}".',
      loadMore: 'Load more',
      loading: 'Loading…',
      preview: 'Preview',
      tasksTitleUnknown: 'Tasks — count unknown',
      totalUnknown: 'This source cannot count its tasks up front — the real number shows in the progress view while importing.',
      scopesUncounted: {
        all: 'All',
        open: 'Only unfinished tasks',
      },
      entityNotInSource: '{{source}} does not have this information.',
      categoryHint:
        'This source does not group its statuses — pick a group for every column so reports, burndown and "unfinished tasks" count correctly.',
      categoryFor: 'Group of column {{name}}',
      categoryPlaceholder: '— pick a group —',
      needCategories: '{{n}} column(s) still need a group.',
    },
    // Why the Test button failed — by `details.reason` of SYNC_PROVIDER_UNREACHABLE.
    unreachable: {
      timeout: 'The source did not answer within 20 seconds.',
      network: 'Could not connect to this address.',
      redirect:
        'The address redirects to {{host}} — not followed, because the project key would follow it. Enter the final address.',
      bad_envelope:
        'The address answers, but it is not the source API (an HTML page or the wrong format).',
      unexpected_status:
        'The source returned {{status}} instead of the expected 401 for a call without a key.',
    },
    admin: {
      title: 'Sync sources',
      subtitle:
        'External work-management systems that projects can pull tasks from and push tasks to. Keys belong to each project — only addresses live here.',
      intro:
        'Project creators pick "Sync from a source" from the enabled sources listed here, then paste the API key of the project on the other side.',
      empty:
        'No sources yet — projects can only be created from scratch, not synced from anywhere.',
      add: 'Add source',
      addTitle: 'Add sync source',
      editTitle: 'Edit source {{name}}',
      create: 'Add',
      edit: 'Edit',
      kindLine: 'Source type: {{kind}}.',
      // APM-SYNC2-c — per-kind form (plan-sync-providers §2.2).
      kind: 'Source type',
      howToTitle: 'How do I set up {{kind}}?',
      howTo: {
        jira_cloud:
          'Jira Cloud has nothing to configure here: each project enters its own site (https://<name>.atlassian.net), email and API token.',
        trello:
          'Trello has a single API address — nothing to configure. Each project pastes its own API key + token.',
      },
      // APM-ORGINT-e — per-kind step-by-step guide (`providerSetupGuide`), no checkboxes.
      guideTitle: 'Create {{kind}} for your organisation — step by step',
      guide: {
        lark: {
          open: 'Open the Lark Developer Console with your company’s Lark admin account (same region as **Lark region** below).',
          openLink: 'Open Lark Developer Console',
          create:
            'Press **Create Custom App** and give it a recognisable name, e.g. "ChillPM". Each organisation creates its own app.',
          scopes: 'Open **Permissions & Scopes** and add all the scopes below:',
          redirect:
            'Open **Security Settings** → **Redirect URLs** and add exactly the address below — copy it whole, including https:// (one character off and PMs cannot sign in):',
          publish:
            'Open **Version Management & Release**, create a version and submit it for release. The app only works after your Lark admin approves that version.',
          credentials:
            'Open **Credentials & Basic Info**, copy the **App ID** and **App Secret**, paste them into the two fields at the end of this dialog and press **Add**.',
        },
        meegle: {
          open: 'Open the Meegle (Lark Project) **Developer Platform** with your company’s Meegle admin account.',
          create:
            'Create an **enterprise plugin** (your company’s own plugin) — not a marketplace plugin: those cannot look users up by email.',
          permissions:
            'In the plugin permissions, request: users, spaces, work items, configuration, comments.',
          publish:
            '**Publish** the plugin. If you change permissions later, publish again and update the installation in each space.',
          install:
            'Ask the admin of each Meegle space to install the plugin: **Space settings → Plugin management**. Projects cannot pick a space that has not installed it.',
          credentials: 'Copy the **Plugin ID** and **Plugin Secret** into the two fields at the end of this dialog.',
          domain:
            'Does your company use its own Meegle domain? Pick **Custom domain…** in **Meegle domain** and enter it. Otherwise keep the default.',
        },
        redmine: {
          api: 'Sign in to Redmine as an administrator, open **Administration → Settings → API**, tick **Enable REST web service** and **Save**.',
          format:
            'Check **Administration → Settings → General → Text formatting** and pick the same format in **Server text format** below — the wrong one breaks task descriptions on both sides.',
          address:
            'Open Redmine in the browser, copy the address up to the end of the server name (e.g. **https://redmine.company.com**) and paste it into **API address**.',
          internal:
            'Does Redmine only open on the company network or VPN (an internal address)? Ask the system administrator to turn on "allow internal addresses" for your organisation first — otherwise ChillPM refuses this address.',
          ca: 'Does the browser warn about an unknown certificate when opening Redmine? Ask IT for the company CA file (PEM) and paste it into **Private CA**. Otherwise skip this.',
          keys: 'No API key is needed here: each project pastes its own Redmine key when connecting.',
        },
        ownApp:
          'Did this source exist before, when sources were shared by the whole system? Then its App ID / Secret may belong to another organisation’s app that was copied over — create your organisation’s own app with the steps above and paste the new App Secret over it.',
      },
      sharedSecret:
        'This source existed before each organisation declared its own sources, so its App Secret may belong to another organisation’s app that was copied over. Create your organisation’s own app (see the guide above) and paste the new App ID and App Secret here.',
      apiBaseUrlHintKind: {
        redmine: 'Root of the Redmine server, e.g. https://redmine.company.com — without /projects.',
        meegle: 'Your own Meegle domain, e.g. https://project.company.com.',
      },
      apiPerConnection: 'Per connection',
      meegleDomain: 'Meegle domain',
      meegleCustom: 'Custom domain…',
      region: 'Lark region',
      regions: {
        larksuite: 'Lark international (open.larksuite.com)',
        feishu: 'Feishu (open.feishu.cn)',
      },
      textFormat: 'Server text format',
      textFormats: {
        common_mark: 'CommonMark Markdown (default since Redmine 5.1)',
        textile: 'Textile (older Redmine)',
        markdown: 'Markdown (Redcarpet, before Redmine 6.0)',
      },
      textFormatHint: 'Exactly as Administration → Settings → General → Text formatting on the server.',
      caCert: 'Private CA (optional)',
      caCertHint: 'If the internal server uses a certificate from a private CA, paste that CA as PEM.',
      app: {
        lark: { title: 'Lark app', appId: 'App ID', appSecret: 'App Secret' },
        meegle: { title: 'Meegle plugin', appId: 'Plugin ID', appSecret: 'Plugin Secret' },
      },
      appShort: 'App',
      appSaved: 'Saved: {{hint}}',
      appKeepHint: 'Leave both fields empty to keep the saved secret.',
      appWriteOnlyHint: 'Write-only — once saved, the secret is never shown again.',
      name: 'Display name',
      namePlaceholder: 'AI Service',
      nameHint: 'The name project creators see when choosing a source.',
      apiBaseUrl: 'API address',
      apiBaseUrlHint: 'The system root, e.g. https://pm.aiservice.vn — without /api/public/v1.',
      httpsOnly: 'A production install only accepts https://.',
      webBaseUrl: 'Web address (optional)',
      webBaseUrlShort: 'Web address',
      webBaseUrlHint:
        'Joined with each task path to build the "open in source" link. Leave empty to use the API address.',
      webSameAsApi: 'Same as API address',
      minInterval: 'Minimum interval',
      minIntervalHint:
        'Projects cannot pick an interval shorter than this. The other side allows 60 requests per minute per key.',
      duration: {
        minutes: '{{n}} min',
        hours: '{{n}} h',
        days: '{{n}} day(s)',
      },
      enabledLabel: 'Enable source',
      enabledHint:
        'When off, every project using this source stops syncing ("Source disabled by the organisation admin"); nothing is deleted.',
      on: 'On',
      off: 'Off',
      usage: 'In use',
      inUse: '{{n}} project(s)',
      notInUse: 'No projects',
      test: 'Test',
      testing: 'Testing…',
      testOk:
        'Connected — the source answered in {{ms}} ms. Project creators can now pick this source.',
      testFail: 'Not usable yet: {{message}}',
      remove: 'Delete',
      removeConfirm: 'Delete source "{{name}}"? No project uses it, so no data is lost.',
      removeBlocked:
        '{{n}} project(s) still use this source, so it cannot be deleted — turn it off instead.',
      created: 'Added source {{name}}.',
      saved: 'Sync source saved.',
      removed: 'Sync source deleted.',
      toggledOn: 'Turned on source {{name}}.',
      toggledOff: 'Turned off source {{name}} — projects using it stop syncing.',
      errors: {
        nameRequired: 'Enter a source name.',
        nameTooLong: 'Name is at most 80 characters.',
        invalidUrl: 'Must be a full http(s) address, without ?, # or login credentials.',
        httpsRequired:
          'Must use https:// — a production install does not send project keys over http.',
        invalidPem: 'The CA must be PEM (-----BEGIN CERTIFICATE-----).',
        appRequired: 'Fill in both fields.',
      },
    },
    // "Work source" step of the create-project dialog — APM-SYNC-d (plan-project-sync §7.1).
    create: {
      sourceLabel: 'Work source',
      source: {
        fresh: 'Start from scratch',
        sync: 'Sync from another source',
      },
      sourceHint: {
        fresh: 'An empty project; tasks are created right here in ChillPM.',
        sync: 'Pull tasks from another work management system and keep both sides in step.',
      },
      provider: 'Source',
      providerPlaceholder: '— choose a source —',
      singleProvider: 'Source: {{name}}',
      thisSource: 'the source',
      apiKey: "The other project's API key",
      apiKeyHint:
        'Find it under Project settings → API key in {{provider}}. Each key opens exactly one project, and is never shown again once saved.',
      preview: 'Check and preview',
      previewAgain: 'Preview again',
      previewing: 'Reading from the source…',
      previewTitle: 'Preview of what will be pulled',
      needPreview: 'Check the key and preview first.',
      externalProject: 'External project',
      keyName: 'Key: {{name}}',
      tasksTitle: 'Tasks — {{n}}',
      category: { todo: 'To do', in_progress: 'In progress', done: 'Done' },
      recentTitle: '{{n}} most recently updated tasks',
      noTasks: 'The external project has no tasks yet.',
      unassigned: 'Unassigned',
      statusesTitle: 'Statuses — become the project board columns, in this order',
      membersTitle: 'People — {{matched}} matched by email, {{unmatched}} unmatched',
      memberUnmatched: 'not matched — their tasks will arrive without an assignee',
      addMatched: 'Add the {{n}} matched people to the project',
      addMatchedHint:
        'People in your organization with the same email who are not yet in the project become members.',
      taskTypesTitle: 'Task types',
      sprintsTitle: 'Sprints',
      outcome: {
        matched: 'matched to {{name}}',
        default: 'uses the default type {{name}}',
        create: 'will be created',
        none: 'not matched',
      },
      sprintsNotSynced:
        'The source has {{n}} sprints, but a {{methodology}} project has no sprints — sprints will not be synced.',
      unsupportedTitle: 'Not synced',
      unsupportedTags: 'Tags: {{n}} — ChillPM has no task tags yet, so tags stay at the source.',
      unsupportedCustomFields:
        "Custom fields: {{n}} — the source's API is read-only for them, and the two sides define fields differently.",
      direction: 'How to sync',
      directions: {
        two_way: 'Two-way',
        pull: 'Pull only',
        push: 'Push only',
        once: 'One-time import',
      },
      directionHints: {
        two_way:
          'Changes on either side reach the other. When the same field changes on both sides, the source wins.',
        pull: 'The source is the master; edits in ChillPM are kept until the source changes that same field.',
        push: 'ChillPM is the master; tasks are pulled once to link both sides, then only changes are pushed out.',
        once: 'Pull once and stop. Links are kept, so turning sync on later creates no duplicates.',
      },
      scope: 'Which tasks to pull',
      scopes: {
        all: 'All — {{n}} tasks',
        open: 'Only unfinished — {{n}} tasks',
      },
      interval: 'Interval',
      sprintColumns: 'Columns of sprints created from the source',
      sprintColumnsOptions: { all: 'All project columns', used: 'Only columns in use' },
      sprintColumnsHints: {
        all: 'A new sprint gets every column of the project, including columns with no tasks yet.',
        used: 'A new sprint only gets the columns that have tasks. When a task moves to a status with no column, that column is added to the sprint in the project order.',
      },
      intervalManual: 'Only when "Sync now" is pressed',
      intervalHint:
        'The source allows 60 requests per minute per key; a run with nothing new costs 1.',
      entities: 'What to sync',
      entity: {
        title: 'Title',
        description: 'Description',
        status: 'Status',
        assignee: 'Assignee',
        priority: 'Priority',
        dates: 'Start / due dates',
        estimate: 'Estimated hours',
        sprint: 'Sprint',
        parent: 'Parent task',
        taskType: 'Task type',
        comments: 'Comments',
      },
      entityUnsupported: {
        tags: 'Tags',
        customFields: 'Custom fields',
      },
      entityUnsupportedWhy: {
        tags: 'ChillPM has no task tags yet.',
        customFields: 'Read-only at the source.',
      },
      submit: 'Create and start syncing',
      created: 'Project created — importing from the source.',
      createdPendingApproval: 'Sent. The project starts importing from the source once approved.',
    },
    // Display status of a connection (contracts `SyncConnectionStatus`) — APM-SYNC-i, shared with the Charter block.
    status: {
      active: 'Syncing',
      initial_import: 'Importing for the first time',
      imported: 'Import finished',
      paused: 'Paused',
      error: 'Last run failed',
      key_invalid: 'Key rejected',
      provider_disabled: 'Source disabled by the organisation admin',
      pending_approval: 'Waiting for project approval',
      disconnected: 'Disconnected',
    },
    // "Sync" block on the Charter tab — APM-SYNC-h (plan-project-sync §7.3).
    problem: {
      heading: 'Needs attention',
      noTaskType: {
        title: 'No task could be imported: this project has no task type.',
        hint: 'Every task needs a task type. Add at least one task type to the project, then press Retry — every task at the source will be imported.',
      },
      keyInvalid: {
        title: 'Sync has stopped: the source API key is no longer valid.',
        hint: 'Open the sync settings and paste a new key. Scheduled runs resume right after.',
      },
      error: {
        title: 'The latest sync run failed ({{n}} in a row): {{message}}',
        hint: 'The system retries on its own, backing off after each failure. You can retry now, or open the log to see where the run failed.',
      },
      skipped: {
        title: '{{n}} tasks at the source could not be imported ({{imported}} imported).',
        hint: 'Open the log to see each task and why. Once the cause is fixed, re-import from scratch to pull them in.',
        hintReason:
          'Most common reason: {{reason}}. Once fixed, re-import from scratch to pull these tasks in.',
      },
      reason: {
        NO_TASK_TYPE: 'the project has no task type to assign',
        EMPTY_TITLE: 'the task has no title at the source',
      },
      action: {
        retry: 'Retry now',
        reimport: 'Re-import from scratch',
        taskTypes: 'Add a task type',
        configure: 'Open sync settings',
        log: 'View log',
        map: 'Map now',
      },
      pending: {
        title: '{{n}} source values could not be mapped — {{tasks}} tasks are placed temporarily.',
        hint: 'The system does not guess a column or task type when unsure. Open the latest run result to pick the right target, then re-apply.',
      },
      reimportQueued: 'A full re-import has been queued.',
    },
    runs: {
      title: 'Sync runs',
      hint: 'Open a run to see its own progress, result and log.',
      back: 'Sync runs',
      trigger: { schedule: 'scheduled', manual: 'manual', change: 'on change' },
      outcome: {
        ok: 'Done',
        no_change: 'No change',
        partial: 'Partly done',
        error: 'Failed',
        running: 'Running',
      },
      count: {
        created: '{{n}} tasks created',
        updated: '{{n}} tasks updated',
        comments: '{{n}} comments',
        conflicts: '{{n}} conflicts',
        errors: '{{n}} errors',
        pending: '{{n}} to map',
      },
      nothing: 'Nothing changed.',
      stillRunning: 'Running…',
      failedHint: 'This run stopped on an error — open it to see why.',
      resultTitle: 'Result of this run',
      stat: {
        created: 'Created',
        updated: 'Updated',
        comments: 'Comments',
        conflicts: 'Conflicts',
        errors: 'Errors',
        pending: 'To map',
      },
      unit: {
        created: 'tasks',
        updated: 'tasks',
        comments: 'comments',
        conflicts: 'tasks',
        errors: 'items',
        pending: 'items',
      },
      pendingTitle: 'Needs mapping',
      pendingHint:
        'This run met source values it could not map with certainty, so it did not guess. Tasks carrying them were imported but placed temporarily. Pick the right target for each item, then re-apply.',
      pendingDone: 'Every item of this run has been mapped and applied.',
      pendingKind: { status: 'Source status', task_type: 'Source task type' },
      pendingWaiting: '{{n}} tasks placed temporarily',
      pendingNone: 'mapped, no task left waiting',
      pendingPick: 'Map “{{name}}” to',
      pendingChoose: { status: '— Pick a column —', task_type: '— Pick a task type —' },
      pendingIgnore: 'Do not map (keep the local value)',
      pendingReadOnly: 'Only project managers can map these items.',
      saveAndApply: 'Save {{n}} mappings and re-apply',
      applyAgain: 'Re-apply',
      applyNote:
        'Re-applying re-reads every task at the source; existing tasks are not duplicated.',
      applied: 'Mappings saved and a re-apply run queued.',
    },
    charter: {
      title: 'Sync',
      row: {
        health: 'Still running?',
        lastRun: 'Last update',
        lastPull: 'Last new data pulled',
        lastPush: 'Last push out',
        missing: 'Gone from source',
      },
      health: {
        active: 'Running',
        importing: 'Initial import in progress',
        importingProgress: 'Initial import {{scanned}}/{{total}}',
        imported: 'Import finished',
        importedAt: 'Import finished at {{at}}',
        paused: 'Paused',
        error: 'Error: {{message}} ({{n}} in a row)',
        key_invalid: 'Key no longer valid — paste a new key',
        provider_disabled: 'Source disabled by the organisation admin',
        pending_approval: 'Waiting for project approval',
        disconnected: 'Disconnected',
        lastRunError: 'Last run failed: {{message}} ({{n}} in a row)',
      },
      result: {
        noChange: 'nothing new',
        changes: '{{n}} changes',
        partial: '{{n}} changes, not finished — continues next run',
        error: 'error',
      },
      running: 'Running…',
      neverRun: 'No run yet',
      next: 'Next: {{at}}',
      nextManual: 'Next: only when "Sync now" is pressed',
      noPull: 'Nothing new pulled yet',
      noPush: 'Nothing pushed yet',
      missing: '{{n}} tasks no longer at the source',
      missingHint:
        'The weekly reconcile no longer finds these tasks at the source. ChillPM keeps them and never deletes on its own — check, then delete by hand if needed.',
      missingSince: 'Detected at {{at}}',
      parts: {
        created: '{{n}} new tasks',
        updated: '{{n}} tasks changed',
        conflicts: '{{n}} conflicts',
        comments: '{{n}} comments',
        errors: '{{n}} errors',
        pushed: 'pushed {{n}} tasks',
        pushFailed: '{{n}} tasks failed to push',
      },
      showItems: 'Show {{n}} tasks',
      showFailures: 'Show {{n}} tasks that failed to push',
      action: {
        created: 'created',
        updated: 'updated',
        conflict: 'conflict — source wins',
        pushed: 'pushed',
        push_failed: 'failed to push',
        delete_unsupported: 'deleted on the other side — kept here',
        missing: 'no longer at the source',
        restored: 'found at the source again',
        skipped: 'skipped',
      },
      field: {
        title: 'title',
        description: 'description',
        status: 'status',
        assignee: 'assignee',
        priority: 'priority',
        startDate: 'start date',
        dueDate: 'due date',
        estimate: 'estimate',
        sprint: 'sprint',
        parent: 'parent task',
        taskType: 'task type',
      },
      // Line in task detail (§7.4).
      taskLine: 'Synced with {{source}}',
      taskLineDisconnected: 'Was synced with {{source}} (disconnected)',
      openExternal: 'Open {{code}} in {{source}}',
    },
    // "Progress & log" dialog — APM-SYNC-k (plan-project-sync §5.7, §7.3).
    progress: {
      open: 'View progress & log',
      title: 'Progress & log — {{source}}',
      loading: 'Loading…',
      importing: 'First import in progress',
      imported: 'First import finished',
      running: 'Running',
      idle: 'Idle',
      barLabel: 'First import progress',
      percent: '{{percent}}% — {{processed}}/{{total}} tasks',
      fetched: 'Fetched {{n}} tasks (the source does not report a total)',
      phaseLabel: 'First import steps',
      phase: {
        lookups: 'Catalogs',
        tasks: 'Tasks',
        comments: 'Comments',
        done: 'Done',
      },
      stat: {
        processed: 'Processed',
        rate: 'Rate',
        eta: 'Time left',
        requests: 'Requests this run',
        next: 'Next run',
      },
      unit: {
        tasks: 'tasks',
        perMin: 'tasks/min',
        calls: 'calls',
      },
      ofTotal: '/ {{total}} tasks',
      eta: {
        seconds: '{{n}} s',
        minutes: '{{n}} min',
        hours: '{{h}} h {{m}} min',
      },
      duration: {
        seconds: '{{n}} s',
        minutes: '{{m}} min {{s}} s',
      },
      runningNow: 'running',
      nextBatch: 'next batch at {{at}}',
      nextNone: 'only when you press "Sync now"',
      runs: 'Runs',
      runsHint: 'Click a run to see only its log.',
      noRuns: 'No runs yet.',
      kind: {
        initial: 'First import',
        pull: 'Pull',
        push: 'Push',
        reconcile: 'Reconcile',
      },
      runFilter: 'Showing: {{kind}} at {{at}}',
      clearRun: 'Show all runs',
      log: 'Log',
      filterAction: 'Filter by action',
      filterDirection: 'Filter by direction',
      allActions: 'All actions',
      allDirections: 'Both directions',
      search: 'Search by code or name',
      direction: {
        pull: 'in',
        push: 'out',
        reconcile: 'reconcile',
      },
      entity: {
        task: 'task',
        comment: 'comment',
        sprint: 'sprint',
        member: 'person',
        status: 'status',
      },
      action: {
        created: 'created',
        updated: 'updated',
        conflict: 'conflict — source wins',
        skipped: 'skipped',
        error: 'error',
        pushed: 'pushed',
        push_failed: 'push failed',
        missing: 'gone from source',
        restored: 'back at source',
        delete_unsupported: 'deleted — kept here',
      },
      empty: 'No log lines yet.',
      emptyFiltered: 'No lines match the filters.',
      loadMore: 'Load more',
      newRows: '{{n}} new lines ↑',
      openTask: 'Open {{code}}',
    },
    // "Sync sources" section in Project settings — APM-SYNC-i (plan-project-sync §7.2).
    settings: {
      title: 'Sync sources',
      subtitle:
        'External systems this project pulls work from or pushes work to: mode, interval, fields, and people/column mapping.',
      intro:
        'Each source is a project in another system, opened with that project’s API key. Tasks created in ChillPM are pushed only to the primary source.',
      empty: 'This project is not synced with any source yet.',
      noProviders: 'Your organisation has no enabled sync source yet — ask your organisation admin to add one under Admin → Sync sources.',
      openAdmin: 'Open Admin → Sync sources',
      orgAdminHint: 'You are an organisation admin, so you can add one right away.',
      add: 'Add source',
      addTitle: 'Add a sync source',
      connect: 'Connect and start syncing',
      connected: 'Connected — importing from the source for the first time.',
      primary: 'Primary source',
      primaryLabel: 'Primary source',
      primaryHint:
        'Tasks created in ChillPM are pushed to the primary source. A project has only one.',
      primaryTaken: '{{name}} is the primary source — turn it off there first.',
      keyLabel: 'Credentials',
      direction: 'Mode',
      interval: 'Interval',
      intervalOnce: 'Does not run on a schedule',
      nextRun: 'Next run: {{at}}',
      nextRunNone: 'Next run: only when you press "Sync now"',
      lastRun: 'Last run: {{at}}',
      lastError: 'Last error: {{message}}',
      configure: 'Configure',
      collapse: 'Collapse',
      partSettings: 'Mode & interval',
      partPeople: 'People mapping',
      partMapping: 'Status / task type / sprint mapping',
      save: 'Save',
      saved: 'Sync settings saved.',
      // APM-SYNC2-f — priority / estimate from custom fields (Trello).
      fieldMap: {
        title: 'Priority & estimate from custom fields',
        hint: 'This source has no built-in priority or estimate — pick the custom fields that carry them. Leave empty and these two are not synced.',
        none: 'No custom fields seen at the source yet (the board needs Custom Fields enabled). The list refreshes after the next sync run.',
        priority: 'Priority from field',
        estimate: 'Estimate (hours) from field',
        estimateHint: 'A number field; the value is read as hours.',
        notUsed: 'Not used',
        noValue: '— not mapped —',
      },
      runNow: 'Sync now',
      runQueued: {
        initial: 'Queued the next import run.',
        pull: 'Queued a pull of changes.',
        push: 'Queued a push of changes.',
        reconcile: 'Queued a reconcile run.',
      },
      pause: 'Pause',
      resume: 'Resume',
      paused: 'Sync paused.',
      resumed: 'Sync resumed.',
      rotateKey: 'Change credentials',
      rotateKeyTitle: 'Change the credentials of {{name}}',
      rotateKeyHint:
        'The new credentials must open the same external project ({{key}}). The old ones stop being used as soon as you save.',
      rotateUnsupported:
        'This install no longer supports the source type of this connection — credentials cannot be changed.',
      keyRotated: 'Credentials changed — the next run uses them.',
      disconnect: 'Disconnect',
      disconnectConfirm:
        'Disconnect from {{name}}? Tasks already pulled stay in the project; syncing just stops. To reconnect, add the source again with a key of the same project — old tasks are not duplicated.',
      disconnected: 'Disconnected. Tasks already pulled stay in the project.',
      disconnectedNote:
        'Disconnected at {{at}}. Reconnect via "Add source" with a key of the same project.',
      disconnectedHeldNote:
        'The external project is still held for this project — it cannot be connected to another one yet.',
      release: 'Disconnect & release',
      releaseConfirm:
        'Disconnect {{name}} and release the external project? It can then be connected to another project. Tasks already pulled, links and logs stay in this project.',
      releaseOnly: 'Release external project',
      releaseOnlyConfirm:
        'Release {{name}}? The external project can then be connected to another project. This project can only reconnect while no other project has taken it.',
      released: 'Released — the external project can now be connected to another project.',
      releasedNote:
        'Disconnected at {{at}} and released: this external project can be connected to another project.',
      reimport: 'Re-sync everything',
      reimportConfirm:
        'Read every task of {{name}} again from scratch — including old tasks and ones that failed or were skipped? Tasks already pulled are updated, not duplicated. On a large project this run takes a while.',
      reimportQueued: 'A full re-sync has been queued.',
      mappingRefreshed: 'Source catalog read at {{at}}.',
      mappingEmpty: 'The source has nothing to map yet.',
      mappingSave: 'Save mapping ({{n}} changes)',
      mappingSaved: 'Mapping saved.',
      mappingNone: '— Do not map —',
      mappingAuto: '↺ Drop the manual mapping, use the automatic suggestion',
      mappingPendingAuto: 'suggestion recomputed on save',
      mappingKind: {
        status: 'Status',
        task_type: 'Task type',
        sprint: 'Sprint',
      },
      sprintNotSynced:
        '{{methodology}} projects have no sprints — the source’s sprints are not synced.',
      source: {
        auto: 'auto-matched',
        autoUser: 'matched by email',
        manual: 'manual',
        none: 'not mapped',
      },
      category: { todo: 'To do', in_progress: 'In progress', done: 'Done' },
      sprintState: { upcoming: 'upcoming', running: 'running', closed: 'closed' },
    },
    users: {
      title: 'User mapping',
      subtitle:
        'Who each person at a sync source is in ChillPM — tasks of unmapped people arrive without an assignee.',
      intro:
        'Sources with emails are mapped automatically to people of the same organization; for sources that hide emails, pick by name and ID. A manual mapping always wins over the automatic suggestion.',
      emptyTitle: 'No sync source yet',
      empty:
        'This project is not connected to any source, so there is nobody outside to map. Add a source under Sync sources first.',
      emptyAction: 'Go to Sync sources',
      viewLabel: 'View',
      viewExternal: 'By source people',
      viewMembers: 'By ChillPM members',
      sourceLabel: 'Source',
      summary: '{{people}} people unmapped — {{tasks}} of their tasks will have no assignee',
      allMapped: 'Everyone at the source is mapped or deliberately left unmapped.',
      onlyUnmapped: 'Only unmapped people',
      search: 'Search by name, email, ID',
      noMatch: 'Nobody matches the filter.',
      noPeople: 'The source has nobody to map yet.',
      noEmail: 'No email',
      shortId: 'ID {{id}}',
      tasks: 'Assigned {{n}} tasks at the source',
      localPersonFor: 'ChillPM person for {{name}}',
      groupMembers: 'Project members',
      groupOthers: 'Others in the organization',
      unmappedOption: '— Not mapped yet —',
      state: {
        auto: 'auto-mapped by email',
        manual: 'mapped manually',
        unmapped: 'not mapped yet',
        skipped: 'do not map',
      },
      notMember: '⚠ Not a project member',
      save: 'Save mapping ({{n}} changes)',
      saved: 'User mapping saved.',
      runHint:
        'New mappings apply to tasks on the next sync run. Sync now to apply them right away.',
      addMembers: 'Add {{n}} mapped people to the project',
      addingMembers: 'Adding…',
      addMembersHint: 'Save the mapping before adding people to the project.',
      membersAdded: 'Added {{n}} people to the project.',
      reverseIntro:
        'Which account each project member has at every source — tasks pushed out for someone without an account there will have no assignee.',
      reverseEmpty: 'The project has no members to compare yet.',
      noAccount: 'No account',
      notMemberRow: 'Mapped but not a project member',
      editorSummary: '{{mapped}}/{{total}} source people mapped.',
      editorLink: 'Edit under User mapping',
    },
  },
  telegramBot: {
    foreign:
      'The active bot belongs to {{origin}}, so this environment neither receives nor sends through it — two places sharing one bot steal each other’s messages. To use Telegram here, create a separate bot with @BotFather and save its token below.',
    title: 'Telegram bot',
    subtitle:
      'One bot for the whole install. People connect their own account in Settings, then get notifications and update progress inside Telegram.',
    none: 'Not configured. The Telegram section in everyone\u2019s Settings is switched off.',
    step1: 'Open Telegram, message @BotFather and send /newbot.',
    step2: 'Give the bot a name and a username (the username must end in "bot").',
    step3: 'BotFather replies with a line like 123456789:AAE… — paste that whole line below.',
    token: 'Bot token',
    tokenTail: 'Token ••••{{last4}}',
    modeLabel: 'How updates arrive',
    mode: {
      polling: 'Ask Telegram (polling)',
      webhook: 'Telegram calls in (webhook)',
    },
    modeHelp: {
      polling:
        'Works anywhere, including a machine on a private network. Prefer this until you are on a real server.',
      webhook:
        'Needs a public HTTPS address on port 443, 80, 88 or 8443. Slightly lighter and faster.',
    },
    httpsRequired:
      'APP_URL is not https, so the Mini App and the Telegram menu button cannot be enabled yet. Notifications and quick-action buttons still work.',
    lastUpdate: 'Last update received: {{at}}',
    noUpdateYet: 'No updates received yet.',
    lastError: 'Last error: {{message}}',
    save: 'Save bot',
    saving: 'Checking token…',
    saved: 'Connected @{{bot}}.',
    test: 'Test connection',
    testOk: 'Telegram answered normally (@{{bot}}).',
    remove: 'Remove bot',
    removeConfirm:
      'Removing the bot stops all Telegram notifications. Account links are kept — plug the same bot back in and they work again.',
    removed: 'Bot removed.',
  },
  googleOauth: {
    title: 'Sign in with Google',
    subtitle:
      'Let people who already have an account sign in with Google. New accounts are never created — unknown emails are refused.',
    none: 'Not configured. The Google button is hidden on the sign-in page.',
    current: 'Using Client ID {{clientId}}.',
    redirectUri: 'Authorized redirect URI — paste this into the Google Console',
    redirectHelp:
      'Google matches it character for character. Also add the origin ({{origin}}) under "Authorized JavaScript origins".',
    copy: 'Copy',
    copied: 'Copied.',
    copyFailed: 'Your browser blocked copying. Select the text and copy it by hand.',
    clientId: 'Client ID',
    clientSecret: 'Client secret',
    save: 'Save configuration',
    saving: 'Saving…',
    saved: 'Saved. Try the Google button on the sign-in page.',
  },
  demoAiKey: {
    needBaseUrl: 'Fill in the gateway address first — LiteLLM has no default.',
    refresh: 'Re-test and refresh models',
    refreshing: 'Testing…',
    refreshed: 'The gateway listed {{n}} models. Model chain updated for this key.',
    refreshHelp:
      'Use this when the key is fine but calls report an unknown model: it re-reads the gateway model list and sets the key\u2019s model chain. No tokens spent, no need to paste the key again.',
    modelHint:
      'A live key is not enough: calls carry the model names from System configuration (`ai.default_model`, `ai.model.strong`, `ai.model.small`). Your gateway must expose those exact names, or set a model chain on this key.',
    label: 'Demo key',
    field: 'AI key for the demo org',
    warning:
      'Anyone on the internet can log into the demo org, so every AI call there spends this key. Use a dedicated key (revocable without touching your real one) and set a cap under AI budget — with no cap there is no ceiling.',
    current: 'Using key •••• {{last4}}. Enter a new one to replace it.',
    none: 'No key yet. AI features are off inside the demo org.',
    save: 'Save and test',
    saving: 'Testing…',
    saved: 'Saved and activated for the demo org.',
    failed: 'That key could not connect — it was not activated.',
  },
  demoAdmin: {
    title: 'Demo organisation',
    subtitle:
      'The showcase dataset for visitors without an account. It resets on a schedule and generates its own activity so it looks alive.',
    state: 'State',
    on: 'Running',
    onIdle: 'Enabled, nothing to run against',
    off: 'Disabled',
    noOrg:
      'No demo organisation yet. Run `pnpm seed:demo-org` on the server to create one — the system deliberately will not create it by itself.',
    cadence: 'Simulation cadence',
    cadenceValue: '{{count}} actions every {{minutes}} minutes',
    lastRun: 'Last run',
    never: 'Never run',
    actionsSinceStart: 'Actions generated (since this restart)',
    lastActions: 'Last run did:',
    action: {
      start: 'Started a task',
      finish: 'Completed a task',
      comment: 'Commented',
      create: 'Created a task',
    },
    runNow: 'Generate activity now',
    resetNow: 'Reset demo data now',
    ranNow: 'Generated {{count}} actions',
    resetDone: 'Rebuilt {{count}} projects',
  },
  demoProjects: {
    help: 'The demo scenario lives in source code; here you layer on top of it. Overrides survive every rebuild, which makes them the only way a change in the demo organisation outlives half an hour.',
    changedCount: '{{n}} fields overridden',
    untouched: 'as scripted',
    editTitle: 'Edit demo project',
    scenarioKey:
      'Scenario key: {{key}} — the identity that never changes, even when you change the displayed key.',
    key: 'Project key',
    name: 'Name',
    description: 'Description',
    color: 'Colour',
    methodology: 'Methodology',
    maintain: 'Maintenance',
    startOffset: 'Start (days from seeding day)',
    endOffset: 'End (days from seeding day)',
    manMonths: 'Man-months',
    manMonthsHint: 'Drives how many tasks are generated',
    pm: 'Project manager',
    contributors: 'Members',
    hiddenTabs: 'Hidden tabs',
    hiddenTabsHelp:
      'Pinned tabs (Charter, People, Tasks, Reports) stay visible regardless — that gate lives in the tab bar itself.',
    reset: 'Back to the scenario',
    saved: 'Saved. Takes effect on the next rebuild.',
    cleared: 'Project restored to the original scenario.',
    applyHint:
      'Changes are stored as configuration and do not touch the running projects: hit "Reset demo data now" above to see them immediately.',
  },
  demoReset: {
    switchSaved: 'Switch saved.',
    simulate: 'Simulate activity in the demo organisation',
    simulateHelp:
      'Periodically creates tasks, moves them and posts comments so the showcase looks alive. Off means the data sits still between rebuilds.',
    autoReset: 'Rebuild on a schedule (every {{minutes}} minutes)',
    autoResetHelp:
      'Wipes the demo organisation’s projects and reseeds them from the scenario, including projects visitors created. No other organisation is touched.',
    frozenWarning:
      'The schedule is off: every change will stay, but the demo data ages. The scenario dates everything relative to "today" and only a rebuild re-anchors it to real time — leave it off for long and progress, burndown and Gantt all drift into the past.',
    manualHint:
      'This button works even with the schedule off: it is a deliberate action by a person, not a machine running in the background.',
    historyTitle: 'Recent rebuilds',
    noRuns: 'Nothing recorded yet. This log starts at the first rebuild after the feature landed.',
    colAt: 'When',
    colTrigger: 'Trigger',
    colProjects: 'Projects',
    colSwept: 'Changes swept',
    colDuration: 'Took',
    trigger: { cron: 'Scheduled', manual: 'Manual' },
    projectsValue: 'dropped {{dropped}} · seeded {{seeded}}',
    sweptHelp:
      '"Changes swept" counts what was written after the previous seed — including the activity simulator, which writes exactly like a real person. A "—" means there was no watermark to compare against, not zero.',
  },
  settings: {
    title: 'System configuration',
    subtitle: 'Feature flags and defaults. Changes take effect immediately, no restart needed.',
    saved: 'Configuration saved.',
    changed: 'changed',
    reset: 'Reset to default',
    group: {
      feature: 'Feature toggles',
      ai: 'AI defaults',
      standard: 'Work standards',
      notification: 'Notifications',
      ai_policy: 'AI policy',
    },
  },
  table: {
    sortAsc: 'Sort by {{label}}, ascending',
    sortDesc: 'Sort by {{label}}, descending',
  },
  /** Task create/edit form and "AI breakdown" — APM-1308b. */
  boardFilter: {
    modeSwitch: 'Narrow by',
    mode: { highlight: 'Highlight', filter: 'Filter' },
    assignee: 'Assignee',
    anyAssignee: 'Everyone',
    taskType: 'Task type',
    anyTaskType: 'All types',
    due: 'Deadline',
    anyDue: 'Any',
    dueBucket: {
      overdue: 'Overdue',
      today: 'Due today',
      upcoming: 'Still has time',
    },
    clear: 'Clear',
    criteria: 'Criteria',
  },
  taskStart: {
    TASK_START_NO_ACTIVE_SPRINT:
      'The project has no running sprint — start a sprint before starting this task.',
    TASK_START_SPRINT_NO_COLUMN:
      'The running sprint has no in-progress column — add one to the sprint and try again.',
    TASK_START_NO_ASSIGNEE: 'No assignee yet — assign someone before moving it to In progress.',
    TASK_START_NO_ESTIMATE:
      'No estimate yet — you can still start, but velocity loses this sample.',
    TASK_START_DEPENDENCY_UNFINISHED:
      'A task it depends on is not done: {{blockedBy}}. Finish that one first.',
  },
  checklist: {
    title: 'Task checklist',
    add: 'Add task checklist',
    remove: 'Remove checklist',
    addItem: 'Add item',
    itemLabel: 'Item {{number}} text',
    weightLabel: 'Item {{number}} weight (%)',
    removeItem: 'Remove item {{number}}',
    itemPlaceholder: 'Work to complete',
    auto: 'Auto',
    weightHelp:
      'Leave weights blank to share the remaining percentage equally. Total weight must be 100%.',
    invalid:
      'Every item needs text. Weights must be positive with at most 2 decimal places and total 100%, or leave a remainder for automatic shares.',
    parentHelp: 'Parent task progress comes from subtasks and cannot use a weighted checklist.',
    count: 'Checklist ({{done}}/{{total}})',
    expandItems: 'Show checklist items',
    collapseItems: 'Hide checklist items',
    doneHelp: 'Reopen the task before unchecking or adding incomplete items.',
    todoHelp: 'Move the task to In progress before ticking items.',
    backlogHelp: 'This task is in the backlog — add it to a sprint before ticking items.',
    summary: '{{done}}/{{total}} items · {{progress}}%',
    CHECKLIST_PROGRESS_LOCKED: 'Tasks with a checklist cannot have their progress edited directly.',
    CHECKLIST_INCOMPLETE: 'Complete every checklist item before moving this task to done.',
    CHECKLIST_PARENT_CONFLICT: 'A task cannot have both a weighted checklist and subtasks.',
    CHECKLIST_STALE:
      'The checklist has changed. Close and reopen the form to load the latest version.',
    CHECKLIST_REOPEN_REQUIRED: 'Reopen the task before making its checklist incomplete.',
  },
  richText: {
    headingLevel: 'Heading level',
    align: { left: 'Align left', center: 'Center', right: 'Align right', justify: 'Justify' },
    paragraph: 'Normal text',
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    heading4: 'Heading 4',
    blockquote: 'Quote',
    codeBlock: 'Code block',
    separator: 'Separator (Ctrl+Enter)',
    link: 'Insert link',
    linkUrl: 'Address',
    linkText: 'Text to show',
    linkInvalid: 'Only http://, https:// or mailto: links are allowed.',
    applyLink: 'Insert',
    removeLink: 'Remove link',
    fontFamily: 'Font',
    defaultFont: 'Default font',
    fontGroups: {
      sans: 'Sans serif',
      serif: 'Serif',
      mono: 'Monospace',
      handwriting: 'Handwriting',
      display: 'Display',
      added: 'Added to this document',
      document: 'In the text',
    },
    addGoogleFont: 'Add Google font',
    googleFontName: 'Font name on Google Fonts',
    add: 'Add',
    fontNameInvalid: 'Font names contain only letters, digits and spaces.',
    fontNotFound: 'This font was not found on Google Fonts.',
    fontOffline: 'Could not reach Google Fonts — try again later.',
    textColor: 'Text colour',
    backgroundColor: 'Highlight colour',
    otherColor: 'Other colour',
    removeColor: 'Remove colour',
    formatting: 'Description formatting',
    bold: 'Bold',
    italic: 'Italic',
    strike: 'Strikethrough',
    underline: 'Underline',
    smaller: 'Decrease font size',
    larger: 'Increase font size',
    fontSize: 'Font size',
    bulletList: 'Bullet list',
    orderedList: 'Numbered list',
    taskList: 'Checklist (links to tasks)',
    indent: 'Indent list',
    outdent: 'Outdent list',
    undo: 'Undo',
    redo: 'Redo',
    listHelp: 'In a list: Tab to indent, Shift + Tab to outdent.',
    insertImage: 'Insert image',
    imageType: '“{{name}}” is not a PNG, JPEG, GIF or WebP image.',
    imageTooLarge:
      '“{{name}}” is still larger than {{mb}} MB after compression — insert it as a file instead.',
    pastedImagesDropped_one:
      'Could not get {{count}} image from the pasted content — insert it with the Insert image button.',
    pastedImagesDropped_other:
      'Could not get {{count}} images from the pasted content — insert them with the Insert image button.',
    documentTooLarge: 'The document would exceed {{mb}} MB — remove some images and try again.',
    uploadingImages_one: 'Uploading {{count}} image…',
    uploadingImages_other: 'Uploading {{count}} images…',
    imagesDisabled: 'Images cannot be inserted yet: {{reason}}',
  },
  dependency: {
    title: 'Must finish first',
    help: 'This item can only start once the items below are done. Adding or removing takes effect immediately — no need to press Save.',
    none: 'No dependencies yet.',
    pick: 'Pick an item that must finish first',
    add: 'Add',
    pickPlaceholder: '— pick an item —',
    removeOne: 'Remove dependency on {{key}}',
  },
  triage: {
    duplicates: 'Similar tasks already here',
    done: '(done)',
    storyPoints:
      'Similar finished work is usually {{value}} points (range {{low}}–{{high}}, {{count}} tasks).',
    askAi: 'Ask AI for more',
    asking: 'Asking…',
    applyPriority: 'Set priority: {{value}}',
    applyAssignee: 'Assign to the suggested person',
    skipped: {
      no_key: 'AI is not configured, so only the computed part is shown.',
      failed: 'AI could not answer right now; the computed part is still above.',
    },
  },
  bulkEdit: {
    proposed: '{{count}} changes proposed',
    openResult: 'View result',
    minimise: 'Minimise',
    restore: 'Reopen',
    open: 'Quick edit',
    title: 'Bulk edit by sentence',
    prompt: 'What do you want to change?',
    placeholder: 'For example: move everything of Minh next week to Ha, except items in review',
    help: 'AI only drafts a preview. Nothing changes until you approve.',
    preview: 'Preview',
    running: 'Drafting the preview…',
    remaining: 'Will apply {{count}} changes',
    apply: 'Approve and apply',
    applying: 'Applying…',
    applied: 'Applied {{count}} changes',
  },
  taskDelete: {
    button: 'Delete task',
    title: 'Delete {{key}}?',
    body: 'The task disappears from every list, board and chart. You can bring it back from the project’s Changes tab.',
    withChildren: '{{count}} subtasks are deleted with it, and restored with it.',
    confirm: 'Delete',
    deleted: 'Deleted {{key}} — restore it from the Changes tab.',
    restore: 'Restore',
    restored: 'Restored {{key}}.',
    errors: {
      TASK_DELETE_FORBIDDEN:
        'Only the project manager or the task’s creator can delete or restore this task.',
      TASK_RESTORE_PARENT_DELETED: 'The parent task is deleted — restore the parent first.',
    },
  },
  taskForm: {
    selfAssigned: 'You — tasks you create are assigned to you',
    assigneeByPm: 'The project PM can change the assignee if needed.',
    newTask: 'New task',
    createTitle: 'New task',
    editTitle: 'Edit task',
    edit: 'Edit',
    title: 'Title',
    titlePlaceholder: 'What needs doing?',
    description: 'Description',
    descriptionPlaceholder: 'What is it for, and what does done look like? (optional)',
    type: 'Type',
    kindMilestone: 'Milestone',
    milestoneOwner: 'Accountable',
    assignee: 'Assignee',
    unassigned: '— unassigned —',
    assigneeSearch: 'Type a name to search…',
    assigneeNone: 'No one matches.',
    scheduleLocked:
      'You can’t change this task’s start and due dates — the PM can allow it in Project settings › Task date permissions. Only the PM changes priority.',
    priorityLocked: 'Only the project manager can change priority.',
    ai: {
      toggle: 'Use AI to assign',
      storyPoints: 'Story points',
      estimate: 'Estimate Storypoint',
      autoAssign: 'Auto Assign',
      working: 'Working…',
      why: 'Why this many points',
      noEstimate: 'No similar finished task to compare with yet. Enter the story points yourself.',
      needPoints: 'Story points first — click Estimate Storypoint or type the points.',
      assignedTo: 'Suggested assignee: {{name}}.',
      pace: {
        person_one: 'Hours use {{hours}} h/point — from {{count}} finished task by this person.',
        person_other: 'Hours use {{hours}} h/point — from {{count}} finished tasks by this person.',
        project_one:
          'Hours use {{hours}} h/point — the project median ({{count}} task), as this person has too few finished tasks.',
        project_other:
          'Hours use {{hours}} h/point — the project median ({{count}} tasks), as this person has too few finished tasks.',
      },
      noPace:
        'The project has too few finished tasks with story points to convert into hours — fill in Time to do, then press Auto Assign again to get a due date.',
      typedHours:
        'The project has no story-point pace yet, so the schedule uses the Time to do you entered ({{hours}} h).',
      noRoom:
        'The next 90 days do not have enough free time for these hours — set the Due date yourself.',
    },
    assigned: 'Assignee changed.',
    priority: 'Priority',
    priorityName: { low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' },
    estimate: 'Time to do (hours)',
    milestoneDate: 'Milestone date',
    startDate: 'Start',
    dueDate: 'Due',
    save: 'Save',
    created: 'Task created.',
    updated: 'Task updated.',
    hours: '{{hours}}h',
    breakdown: 'AI breakdown',
    breakdownAsking: 'AI is splitting…',
    breakdownPrompt: 'Split this task into workable subtasks with hour estimates.',
    breakdownTitle: 'AI suggests splitting into:',
    breakdownFound: 'AI suggests {{count}} subtasks',
    breakdownEmpty: 'AI suggested no subtasks.',
    breakdownApply: 'Create {{count}} selected subtasks',
    breakdownAccepted: '{{count}} subtasks created.',
    breakdownDiscard: 'Discard this suggestion',
    needApprove: 'You cannot approve AI suggestions.',
  },
  /** AI reschedule — APM-1313. */
  reschedule: {
    ask: 'Schedule with AI',
    asking: 'AI is working…',
    prompt: 'Reschedule the tasks that lost their assignee, keeping the end date if possible.',
    movesFound: 'AI proposes {{count}} changes',
    noPlan: 'AI found no workable plan with the current team.',
    empty: 'Could not read the result.',
    toPerson: '→ assign to {{name}}',
    toDates: '→ {{from}} to {{to}}',
    apply: 'Apply {{count}} changes',
    applied: 'Plan applied. Schedule and critical path recalculated.',
    discard: 'Discard this plan',
    needApprove: 'You cannot approve AI suggestions.',
    shortfallTitle: 'Not solvable with the current team — needs:',
    needPeople: '{{people}} more {{skill}} for {{days}} days',
    extendDays: 'or extend the deadline by {{days}} days',
  },
  /** Schedule impact on the plan — APM-1312. */
  impact: {
    reason: {
      assignee_absent: '{{name}} is away during this task',
      assignee_leaving: '{{name}} left before this task ends',
      commitment_shortfall: '{{name}} has too few committed hours for this task',
    },
    needsReassign: 'needs reassigning to someone else',
    needsMoreDays: 'needs about {{days}} more working days',
    stillFits: '{{hours}}h left, still fits',
    omitted: '{{count}} more affected tasks not shown — see the signals table in RAID.',
    slip: '{{days}} days late — projected finish {{date}}',
  },
  /** Offboarding — APM-1311b. */
  offboarding: {
    label: 'Last working day:',
    none: 'still working',
    scheduled: 'Leaving after {{date}} — plans count 0 hours from the next day.',
    done: 'Left after {{date}}. Account locked, all data kept.',
    cancel: 'Cancel offboarding',
    saved: 'Last working day updated.',
    // Offboarding plan — APM-OFFB-b.
    rowLabel: 'Offboarding',
    planAction: 'Offboarding plan…',
    editPlan: 'Edit plan…',
    planTitle: 'Offboarding plan — {{name}}',
    dayLabel: 'Last working day',
    planHelp:
      'The account works normally until the end of this day, then locks itself. From the next day, project plans count them as 0 hours.',
    impactTitle: 'Impact',
    noProjects: 'Not in any running project.',
    openTasks: '{{count}} open tasks',
    lastPm: 'last PM',
    usesAiKey: 'runs AI on their key',
    directReports: '{{count}} people report to them — someone must take over.',
    myFiles: '{{count}} personal files ({{size}}) — someone must take over.',
    pendingApprovals: '{{count}} projects are waiting for their approval.',
    notifyLeader:
      'Saving notifies {{pms}} PMs and their leader {{leader}} to arrange the handover.',
    notifyAdmins:
      'Saving notifies {{pms}} PMs and the organization admins (no leader) to arrange the handover.',
    savePlan: 'Save plan',
    planSaved: 'Offboarding plan for {{name}} saved.',
    handoverLink: 'Handover plan →',
    guestHelp:
      'This is not the home organization of {{name}}, so a last working day cannot be set here. You can only lock their access now.',
  },
  /** Remove from organization — APM-ORGRM-a. */
  memberRemoval: {
    action: 'Remove from organization',
    hint: 'Someone who joined by mistake and has no tasks is removed outright. Someone with tasks goes through offboarding.',
    confirmRemove:
      'Remove {{name}} from the organization? They lose access to it and to {{projects}} projects, and their sessions here are revoked. Their account stays.',
    confirmRemoveAction: 'Remove',
    removed: '{{name}} was removed from the organization.',
    hasWork:
      '{{name}} has {{count}} tasks in this organization ({{open}} open), so they cannot be removed outright — history and reports still need them. Offboard them with today as the last working day? The account locks after today; remember to reassign open tasks.',
    hasWorkAction: 'Offboard from today',
    hasWorkGuest:
      '{{name}} has {{count}} tasks in this organization ({{open}} open), so they cannot be removed outright. This is not their home organization, so a last working day cannot be set — lock them in this organization now?',
    hasWorkGuestAction: 'Lock access now',
    hasWorkIntro:
      '{{name}} has {{count}} tasks in this organization ({{open}} open), so they cannot be removed outright — history and reports still need them. Plan their offboarding instead:',
    alreadyOffboarded:
      '{{name}} has tasks so cannot be removed outright, and already has a last working day.',
    alreadyLocked: '{{name}} has tasks so cannot be removed outright, and is already locked here.',
    blocker: {
      self: 'You cannot remove yourself from the organization.',
      super_admin: 'The system administrator account cannot be removed.',
      last_org_admin: 'This is the last organization admin. Make someone else an admin first.',
      last_pm: 'This person is the last PM of project "{{project}}". Assign another PM first.',
    },
  },
  /** Handover screen for a leaver — APM-OFFB-f. */
  offboardingHandover: {
    title: 'Take over from {{name}}',
    lastDay: 'Last working day: {{date}}',
    daysLeft: '{{count}} days left',
    locked: 'has left, account locked',
    helpLeader:
      'You are their leader. Take over yourself or hand it to someone you manage directly. Project tasks are reassigned by PMs, not here.',
    helpAdmin:
      'You are acting as organization admin — you can pick anyone active. Project tasks are reassigned by PMs, not here.',
    category: { my_files: 'My files', direct_reports: 'Direct reports' },
    status: {
      unplanned: 'Not planned',
      planned: 'Moves when the account locks',
      done: 'Moved',
      failed: 'Could not move',
    },
    nothing: 'Nothing to hand over.',
    filesSummary: '{{count}} personal files ({{size}}), with their share links and grants.',
    reportsSummary: '{{count}} people report to them: {{names}}.',
    targetLabel: 'Who takes over',
    targetFor: 'Who takes over: {{group}}',
    choose: 'Choose a receiver…',
    self: 'I take over',
    none: 'Do not hand over',
    plannedBy: 'Plan saved by {{name}} at {{at}}.',
    doneMovedFiles: 'Moved {{count}} files to {{name}} at {{at}}.',
    doneMovedReports: 'Moved {{count}} people to manager {{name}} at {{at}}.',
    doneSkipped: 'Decided not to hand over (at {{at}}).',
    failedTarget:
      'The receiver no longer qualifies (left, locked or not a manager) — pick someone else.',
    failedError: 'Moving failed — pick the receiver again and save to retry.',
    save: 'Save plan',
    saved: 'Handover plan saved.',
    execute: 'Run now',
    executeConfirm:
      'Move everything from {{name}} now? They lose access to their files from now on, even before their last day.',
    executed: 'Handover plan ran.',
    whenLater: 'The plan runs by itself when the account locks (after the last working day).',
    whenLocked: 'The account is locked — saving the plan moves things right away.',
    forbiddenTitle: 'You cannot plan the handover for this person',
    forbiddenHelp: 'Only their leader or an organization admin can do this.',
    notPlannedTitle: 'This person has no offboarding plan',
    notPlannedHelp:
      'An admin sets the last working day in the org chart or the Users section first.',
    mineTitle: 'Handovers to plan ({{count}})',
    mineRow: 'leaves after {{date}} · {{count}} groups not planned',
  },
  /** Leave — APM-1311. */
  absence: {
    dayCount: '{{count}} days',
    partial: 'partly off',
    working: 'working',
    shifts: 'Shifts off',
    pickShift: 'Pick at least one shift.',
    noWorkingDay: 'No working days in this range.',
    request: 'Request leave',
    requested: 'Leave request sent.',
    submit: 'Send request',
    filterUser: 'Filter by person',
    filterAll: 'Everyone',
    endBeforeStart: 'The end date must be on or after the start date.',
    colPerson: 'Person',
    colRole: 'Role',
    colProjects: 'Projects',
    colFrom: 'Dates',
    colHours: 'Hours',
    colApproval: 'Approval',
    colTiming: 'Status',
    allDay: 'All day',
    approve: 'Approve',
    approval: {
      pending: 'Pending',
      approved: 'Approved',
    },
    timing: {
      past: 'Done',
      current: 'Away now',
      upcoming: 'Upcoming',
    },
    noneOrg: 'No absences yet',
    noneOrgHelp: 'Absences appear here once someone declares one.',
    title: 'Time off',
    subtitle:
      'Declare the days you are not working. It takes effect immediately, no approval step — project plans subtract it.',
    adminTitle: 'Absences',
    pageSubtitle:
      'Your requests, your team\u2019s, or the whole organisation — depending on your role.',
    pickUser: 'Pick a person',
    pickUserNone: 'Nobody selected',
    adminSubtitle: 'Declare time off on their behalf. Same effect as if they did it.',
    from: 'From',
    to: 'To',
    kind: 'Kind',
    kindName: { leave: 'Leave', sick: 'Sick', other: 'Other' },
    byHour: 'Partial day',
    start: 'From time',
    end: 'To time',
    note: 'Note',
    notePlaceholder: 'Note (optional)',
    declare: 'Declare',
    declared: 'Time off declared. Plans now subtract those days.',
    retract: 'Retract this time off',
    retracted: 'Time off retracted.',
    empty: 'No time off declared.',
    hours: '{{hours}}h',
    preview: 'This takes {{hours}}h off your schedule.',
    previewNoWorkday: 'This range has no working days of yours — nothing is subtracted.',
    hourSingleDayOnly:
      'Partial days can only be declared one day at a time. For several mornings off, declare each day.',
  },

  /** Organisation profile — APM-ORGP-b. */
  orgProfile: {
    title: 'Organisation profile',
    subtitle: 'Name, description and greeting that invitees see before joining.',
    hint: 'This shows on the invite page, which anyone holding the link can open. Empty fields are left out.',
    field: {
      name: 'Organisation name',
      industry: 'Industry',
      website: 'Website',
      logoUrl: 'Logo (image URL)',
      description: 'Description',
      welcomeMessage: 'Welcome message',
    },
    industryPlaceholder: 'Software, retail, education…',
    descriptionPlaceholder: 'What the organisation does, who the team is.',
    welcomePlaceholder: 'A greeting for people who accept the invite.',
    invalidName: 'The name needs at least 2 characters.',
    invalidUrl: 'Website and logo must start with http:// or https://.',
    previewTitle: 'What invitees will see',
    previewHelp:
      "Each link's own message shows right under the inviter's name. The member count shows on the real invite page.",
    saved: 'Organisation profile saved',
    savedRenamed: 'Organisation profile saved. The new name shows in the top bar after a reload.',
  },

  /** Organisation holidays — APM-1311. */
  orgWorkSchedule: {
    title: 'Default working hours',
    subtitle:
      "The schedule for everyone without a personal one, including people who join later. Anyone with their own schedule keeps it; you can still edit each person's under Users.",
  },
  orgTimezone: {
    title: 'Time zone',
    subtitle: 'Work schedule hours and the "today" boundary are read in this time zone.',
    hint: 'Changing the time zone shifts every work shift in the organization: the timer, overtime and the weekly timesheet are all recalculated.',
    label: 'Organization time zone',
    nowThere: 'Current time there: {{at}}',
    saved: 'Time zone saved',
  },
  timerReminder: {
    title: 'Timer reminders',
    subtitle: 'Telegram reminder when a shift starts without a running timer, with quick leave buttons.',
    hint: 'Anyone who reaches the start of a shift without starting the timer gets a Telegram message with two buttons: take leave for that shift or for the whole day. Only people with a linked Telegram receive it; days off, holidays and declared leave are skipped.',
    enabled: 'Enable timer reminders',
    roles: 'Roles that get reminded',
    rolesHint: 'Organization roles, not project roles.',
    delay: 'Remind after shift start (minutes)',
    delayHint: '0 to 60 minutes. The message may arrive up to 5 minutes later.',
    excluded: 'People not reminded',
    excludedHint: 'People who do not track standard working hours.',
    noneExcluded: 'Nobody excluded.',
    addExcluded: '+ Add a person to exclude…',
    removeExcluded: 'Remove {{name}} from the list',
    unknownUser: 'Former member',
    saved: 'Timer reminder settings saved',
  },
  holiday: {
    title: 'Holidays',
    subtitle: "Days the whole organisation is off. Everyone's capacity subtracts them.",
    date: 'Date',
    name: 'Holiday name',
    namePlaceholder: 'National day, New Year…',
    add: 'Add',
    remove: 'Remove this holiday',
    empty: 'No holidays yet.',
  },
  /** Per-project commitment — APM-1310b. */
  commitment: {
    label: 'Committed to this project:',
    unit: 'Unit',
    unitName: { hours: 'hours/day', percent: '% of schedule' },
    value: { hours: '{{value}} hours/day', percent: '{{value}}% of schedule' },
    notSet: 'not set',
    save: 'Save',
    saved: 'Commitment updated.',
    alsoIn: 'Also committed on {{count}} other projects.',
    overflow:
      'Committed {{committed}}/{{capacity}} hours on {{weekday}}, over their work schedule — including {{count}} other projects.',
    willOverflow:
      'This would make it {{committed}}/{{capacity}} hours on {{weekday}}, over their work schedule — including {{count}} other projects.',
    hiddenProjects: '+{{count}} projects you cannot see',
    baselineDefault: '· no schedule declared, using the Mon–Fri 8h default',
    weekday: {
      0: 'Sunday',
      1: 'Monday',
      2: 'Tuesday',
      3: 'Wednesday',
      4: 'Thursday',
      5: 'Friday',
      6: 'Saturday',
    },
  },
  /** Project invite link — APM-1317b. */
  invite: {
    apply: 'Apply to join',
    applied:
      'Application sent. The project manager will settle the role and allocation, then reply.',
    wantRole: 'Role you want',
    pmDecides:
      'This is a request. The project manager may change the role, dates and allocation before accepting.',
    projectPeriod: 'Project runs {{from}} → {{to}}',
    openEnded: 'no end date',
    sectionTitle: 'Invite people to the project',
    sectionSubtitle:
      'Anyone in the organisation can open the link, sign in and apply with the role, dates and allocation they want. Applications land under Applications below for you to settle. The link never creates accounts.',
    create: 'Create invite link',
    recreate: 'Create new link',
    revoke: 'Revoke',
    revoked: 'Invite link revoked.',
    copy: 'Copy',
    copied: 'Copied',
    copyFailed: 'Could not copy automatically — select the link and copy it by hand.',
    none: 'No link is currently usable.',
    expiresAt: 'Expires {{date}}.',
    onceWarning:
      'This string is shown ONCE — only its hash is stored. Copy it now; creating a new link revokes this one.',
    openProject: 'Open project',
    backToProjects: 'Back to projects',
    acceptInvitation: 'Accept',
    declineAtHome: 'To decline, use My invitations on the home page — you can give a reason there.',
    status: {
      pending_request: 'You have applied — waiting for approval',
      invited: 'The project manager has invited you',
      ok: "You're invited to this project",
      already_member: "You're already on this project",
      expired: 'This invite link has expired',
      revoked: 'This invite link was revoked',
      not_found: 'This invite link cannot be used',
    },
    help: {
      pending_request:
        'The project manager will settle the role and allocation, then reply. No need to resend.',
      invited:
        'The invitation is waiting for your answer. Accept it and you join the project right away, with the role the project manager chose.',
      ok: 'Pick the role and allocation you want, then send your application.',
      already_member: 'Nothing more to do.',
      expired: 'Links last 7 days. Ask the project manager for a new one.',
      revoked: 'The project manager revoked this link. Ask them for a new one.',
      not_found: 'The link is wrong, or belongs to another organisation. Check it with the sender.',
    },
  },
  approval: {
    title: 'Waiting for your approval ({{count}})',
    subtitle: 'Someone you manage opened a new project. Approving lets it start.',
    noDescription: 'The creator has not written a description.',
    approve: 'Approve',
    reject: 'Reject',
    confirmReject: 'Confirm rejection',
    notePlaceholder: 'Why not? The creator will read this.',
    needNote:
      'Give a reason first — a silent rejection just gets a near-identical project reopened.',
    approved: 'Approved. The project can start.',
    rejected: 'Rejected, with a reason.',
    titleOrg: 'Projects awaiting approval ({{count}})',
    subtitleOrg:
      'Projects waiting on you come first. The rest are waiting on a line manager — you can decide on their behalf when they are away.',
    requestedBy: 'Opened by {{name}}',
    onBehalf: 'Waiting on {{name}} — you are deciding on their behalf',
  },
  handOverPm: {
    title: 'Hand over PM role',
    subtitle: 'Pass the PM role to another member of this project.',
    help: 'Pass your PM role to another member. You become a Contributor and they become PM — both change at once, so the project is never left without a PM.',
    field: 'Hand over to',
    placeholder: '— pick a member —',
    action: 'Hand over',
    sending: 'Handing over…',
    done: "Handed over. You are no longer this project's PM.",
    noCandidates:
      'Nobody to hand over to yet. Add members to the project first — handover only moves the role between people already in it.',
  },
  stopProject: {
    title: 'Stop maintenance project',
    subtitle: 'Close a maintenance project and record its end date.',
    help: "A maintenance project has no end date — it runs until you stop it. The date you stop it becomes the project's end date.",
    action: 'Stop project',
    confirm:
      "Stopping marks the project completed and removes it from everyone's list of running projects. You can reopen it from the status field, but in the meantime the team will have seen it disappear.",
    confirmAction: 'Stop anyway',
    stopping: 'Stopping…',
    done: "Project stopped. Today's date was recorded as its end date.",
  },
  archiveProject: {
    title: 'Archive project',
    subtitle: 'Put away a project created by mistake, stopped midway or cancelled.',
    help: 'For projects that will not reach completion: created by mistake, stopped midway or cancelled. The project leaves the running list; tasks, sprints and documents are kept.',
    action: 'Archive project',
    reasonLabel: 'Reason for archiving',
    reason: {
      created_by_mistake: 'Created by mistake',
      stopped: 'Stopped midway',
      cancelled: 'Cancelled',
    },
    notePlaceholder: 'Add a note (optional)',
    confirm:
      "Archiving removes the project from everyone's running list and recurring tasks stop generating new work. You can reopen it here at any time.",
    confirmAction: 'Archive',
    disconnect: {
      title: 'Disconnect from the outside',
      help: 'Archiving does not disconnect any of these by itself. They stay as they are by default — restore the project and they keep working; anything disconnected has to be set up again.',
      sync: 'Sync sources: {{sources}}',
      syncLabel: 'What to do with sync sources',
      syncChoice: {
        keep: 'Keep',
        disconnect: 'Disconnect',
        release: 'Disconnect & release',
      },
      syncHelp: {
        keep: 'Syncing stops while the project is archived and resumes when it is restored. The external project stays held for this project.',
        disconnect:
          'Stops syncing for good. The external project stays held for this project — it cannot be connected to another one yet.',
        release:
          'Stops syncing and releases the external project: it can be connected to another project. Tasks already pulled and their links stay.',
      },
      shareLinks: 'Revoke {{count}} public share link(s) of Decision sheets',
      inviteLinks: 'Revoke {{count}} project invite link(s)',
      guests: 'Remove {{count}} guest(s) from the project',
      guestsKept:
        '{{count}} guest(s) with unfinished work will not be removed — reassign their tasks first.',
    },
    archiving: 'Archiving…',
    done: 'Project archived.',
    archivedTitle: 'Archived project',
    archivedHelp:
      'This project is put away. Reopening returns it to the status it had before archiving (On hold if unknown).',
    restore: 'Reopen project',
    restoring: 'Reopening…',
    restored: 'Project reopened.',
  },
  createProject: {
    settingUp: 'Setting up the project…',
    title: 'New project',
    key: 'Key',
    name: 'Project name',
    namePlaceholder: 'Online store',
    description: 'Problem & objectives',
    descriptionPlaceholder:
      'What does this project solve, and what does done look like? Your approver reads this.',
    methodology: 'Methodology',
    manager: 'Your line manager',
    managerPlaceholder: '— pick an approver —',
    managerHelp:
      'You have not set a line manager yet. Whoever you pick approves this project, and is saved to your profile.',
    willBeApprovedBy: '{{name}} will approve this project before it starts.',
    submit: 'Create project',
    needKeyAndName: 'A key and a name are required.',
    needManager: 'Pick an approver first.',
    created: 'Project created.',
    sentForApproval: 'Sent. The project starts once approved.',
  },
  leaveProject: {
    title: 'Leave project',
    subtitle: 'You stay with the company — you just stop working on this project.',
    action: 'Leave project',
    confirmTitle: 'Leave this project?',
    warning:
      'You still have {{count}} unfinished tasks. Leaving returns them to unassigned and the project manager has to find someone else.',
    noTasks: 'You have no unfinished tasks in this project.',
    more: '…and {{count}} more.',
    due: 'due {{date}}',
    lastPm:
      'You are the last project manager. Name another one before leaving — otherwise nobody can assign work or add people to what is left.',
    confirm: 'Leave anyway',
    left: 'You left the project.',
    leftWithTasks:
      'You left the project. {{count}} tasks went back to unassigned and the project manager was notified.',
  },
  retro: {
    title: 'Review & Retrospective',
    help: 'A sprint that closes without notes is a sprint the team repeats.',
    goalOutcome: 'Sprint goal:',
    summaryPlaceholder: 'What shipped, and what is worth saying…',
    addPlaceholder: 'Add a line, then Enter…',
    toTask: 'Turn into a task',
    markDone: 'Mark done',
    reopen: 'Reopen',
    drop: 'Decided not to',
    remove: 'Delete line',
    outcome: { met: 'Met', partial: 'Partly met', missed: 'Missed' },
    kind: { keep: 'Keep', drop: 'Drop', try_it: 'Try' },
    kindHelp: {
      keep: 'This worked — do not lose it.',
      drop: 'This hurt — stop doing it.',
      try_it: 'What the team decided to change next sprint — this line can become a task.',
    },
  },
  taskStatusRules: {
    title: 'Status change permissions',
    subtitle: 'Who can move tasks between columns, and who can move them to done.',
    statusChangeLabel: 'Who can change a task’s status',
    statusChange: {
      members: 'Every member (except the Viewer role)',
      assignee_or_creator: 'Only the task’s assignee or creator',
      roles: 'Only the selected roles',
    },
    doneLabel: 'Who can move a task to done',
    done: {
      pm: 'Only the PM',
      roles_and_pm: 'The selected roles and the PM',
      assignee_or_creator_and_pm: 'The task’s assignee or creator, and the PM',
      not_assignee_and_pm: 'Anyone other than the assignee (second-person sign-off), and the PM',
      anyone: 'Anyone who can change that task’s status',
    },
    pmNote:
      'The PM and org admins can always do it. Applies to status changes only — editing a task keeps its own permission.',
    doneNote:
      'Also applies to reopening a done task. Moving to done also requires passing the rule above.',
    rolesLabel: 'Allowed roles',
    noRoles: 'No role selected — only the PM can do it.',
    save: 'Save',
    saved: 'Status change permissions saved.',
  },
  // Minimum time before completion — APM-TMIN-b.
  storyPointAnchor: {
    title: 'Story point anchor',
    subtitle: 'How big one story point is — so everyone picks points on the same scale.',
    hint: 'A story point is still the size of the work; the anchor only says how big one point is. When estimating, the dialog lists anchor tasks first: a 1 SP task by the person doing it, otherwise a 1 SP task or one finished by someone at this level within that many hours (±25%), of the same task type. Hours per point in velocity and performance are still measured from real data — changing the anchor does not rewrite past numbers.',
    level: 'Level',
    hours: 'Hours',
    preview: '1 story point ≈ the work a {{level}} does in {{hours}}h.',
    noLevelNote: 'People without a level never count as a level anchor — set levels under Users.',
    save: 'Save',
    saved: 'Story point anchor saved.',
  },
  // Progress updates ↔ commits — APM-PCOM, docs/plan-progress-commits.md.
  progressCommits: {
    headingChecklist: 'Checked: {{text}}',
    headingCompleted: 'Completed: {{title}}',
    reported_one: 'Linked {{count}} commit',
    reported_other: 'Linked {{count}} commits',
    reportFailed: 'The update was saved but the commits were not linked: {{reason}}',
    dialog: {
      title: 'Pick commits for this update',
      help: 'Pick a branch first, then tick the commits behind the work you just did. No commits (meetings, docs…)? Choose "No commits".',
      branchStep: '1. Branch',
      commitStep: '2. Commits',
      lastCommitAt: 'Last commit: {{at}}',
      pickedOnBranch: '{{count}} picked',
      noBranch: 'No branches.',
      matchesTask: "this task's branch",
      defaultBranch: 'default',
      pickBranch: 'Pick a branch to see its commits.',
      loadingCommits: 'Reading commits from the git server…',
      showOthers: "Show other people's commits",
      noCommits: 'This branch has no commits yet.',
      noneMine: 'No commits of yours on this branch yet.',
      mapEmailHint: 'Committing with a different git email? Ask the PM to map it under Git settings › Options › Git accounts, or turn on "Show other people\'s commits".',
      alreadyLinked: 'linked',
      skip: 'No commits',
      submit_one: 'Link {{count}} commit',
      submit_other: 'Link {{count}} commits',
    },
    settings: {
      title: 'Tie progress updates to commits',
      howItWorks: 'When someone ticks a checklist item, closes a task or moves it to Done (including dragging progress to 100%), a branch → commit picker opens so they can link their commits. "No commits" skips it. Dragging progress below 100% does not ask.',
      noRepository: 'Link a git repository and paste an access key before turning this on.',
      connectionUnusable: 'The tokens of the linked repositories no longer work — replace the token first.',
      openGit: 'Git settings',
      managerOnly: "Only the project's Git manager (PM) can change this.",
      saved: 'Commit linking setting saved',
    },
    block: {
      title: 'Reported commits',
      checklist: 'Checked "{{text}}"',
      completed: 'Completed',
      skipped: 'Reported without commits',
      emptied: 'All commits removed',
      remove: 'Remove commit {{sha}}',
      removed: 'Commit removed',
    },
    chip_one: '{{count}} commit',
    chip_other: '{{count}} commits',
    review: {
      label: 'AI review:',
      verdict: {
        related: 'Related',
        partial: 'Partly related',
        unrelated: 'Unrelated',
      },
      status: {
        pending: 'Reviewing…',
        failed: 'Review failed',
        skipped: 'Skipped (AI off or budget reached)',
      },
      basedOn: {
        diff: 'Based on the code changes',
        message: 'Based on commit messages only, no code read',
      },
      readStats: 'read {{read}}/{{files}} files, {{kb}} KB',
      mapOnly: 'the list of {{files}} changed files was enough, no code read',
      suggestionOnly: 'AI suggestion — for reference, the task is not changed',
      commitTitle: 'commit title',
      acknowledge: 'Seen',
      acknowledged: '{{name}} marked as seen at {{at}}',
      retry: 'Review again',
      badge: {
        short: 'Commit?',
        title: 'AI review of reported commits: {{verdict}}',
      },
      indicator: {
        title_one: '{{count}} AI commit review running',
        title_other: '{{count}} AI commit reviews running',
        hint: 'AI is reading the commits you just reported, in the background — you do not need to wait.',
        item: 'Checked "{{item}}"',
        completed: 'Completed the task',
        since_one: '{{count}} minute ago',
        since_other: '{{count}} minutes ago',
      },
      toast: {
        related: 'AI finished reviewing {{task}}: commits are related',
        partial: 'AI finished reviewing {{task}}: commits are only partly related — the PM will look',
        unrelated: 'AI finished reviewing {{task}}: commits look unrelated — the PM will look',
        notReviewed: 'Could not review the commits of {{task}} — open the task to retry',
      },
      settings: {
        title: 'AI checks whether the commits match the work',
        howItWorks:
          'After each commit report, AI reads the changes (only in repositories with "AI reads code" on) and compares them with the item / task. Low matches get a small label on the card and a note to the PM; the task is not changed. Each report is one AI run, charged to the reporter\'s budget.',
        needsCommits: 'Turn on "Tie progress updates to commits" first.',
        needsAi: 'The organization has not set up the AI assistant.',
      },
    },
  },
  taskPlanGate: {
    settings: {
      title: 'Require a due date and estimate before work starts',
      subtitle:
        'Tasks without a due date or an hour estimate cannot move to In progress or Done.',
      enable: 'On',
      howItWorks:
        'Anyone who tries gets a dialog to fill in what is missing; the hour estimate is based on a past task that was actually done. Milestones and system changes (sync, git rules) are not blocked.',
      save: 'Save',
      saved: 'Saved',
    },
    bulkHeld_one: '{{count}} task has no due date or estimate yet and was not moved — open it to fill them in.',
    bulkHeld_other: '{{count}} tasks have no due date or estimate yet and were not moved — open each to fill them in.',
    dialog: {
      title: 'Not enough information to start',
      body: '"{{title}}" needs a due date and an hour estimate before it can move to In progress or Done. Fill in what is missing below.',
      dueLabel: 'Due date',
      dueAskPm: 'You cannot set the due date of this task — ask the PM to set it, then move it again.',
      estimateLabel: 'Hour estimate',
      loading: 'Looking for similar finished tasks…',
      noReference: 'No similar finished task to compare with — enter an hour estimate.',
      manualHours: 'Hours',
      manualPoints: 'Story points',
      optional: '(optional)',
      chooseManual: 'Enter SP myself',
      chooseManualHint: 'Do not compare with any item above — type the story points and hours.',
      chooseManualHintGate: 'Do not compare with any item above — type the hours, plus story points if you have them.',
      chosenReference: 'Chosen reference',
      repick_one: 'Choose again',
      repick_other: 'Choose again ({{count}} items)',
      pickOne: 'The most similar finished task — read it again and compare.',
      pickMany: 'Pick the most similar finished task — read its description and checklist to compare.',
      pickRecent:
        'No similar finished task yet — these are recently finished tasks. Pick one as a yardstick and rate how much more or less this task takes.',
      tierAssignee: 'Tasks the assignee has finished in this project.',
      tierProject: 'The assignee has no finished task in this project yet — these are tasks by others in the project.',
      tierOrg: 'This project has no finished task to compare with yet — these are from other projects.',
      ratioLabel: 'Compared with the reference, this task takes:',
      less: 'Less',
      more: 'More',
      actual: 'actual {{hours}}h',
      spShort_one: '{{count}} SP',
      spShort_other: '{{count}} SP',
      noSpShort: 'no SP yet',
      resultLabel: 'Story points for this task',
      resultEdit: 'Edit',
      resultUseComputed: 'Use the computed number',
      resultHours: 'Estimated hours',
      hoursAuto: 'Hours follow the assignee’s {{level}} speed — you can edit them.',
      hoursAutoNoLevel: 'Hours follow the assignee’s speed — you can edit them.',
      hoursAfterAssign: 'Not assigned yet — hours will be filled from the assignee’s level once assigned.',
      hoursNeedAssign: 'Not assigned yet — enter hours to start, or assign the task first.',
      spConverted: '≈{{value}} SP (converted)',
      spConvertedHint: 'This task has no SP. Converted from actual hours using the anchor 1 SP ≈ a {{level}} working {{hours}}h; done by: {{doer}}.',
      doerUnleveled: 'no level set (treated as standard)',
      anchorLine: 'Your organization\'s anchor: 1 story point ≈ the work a {{level}} does in {{hours}}h. Anchor tasks are listed first.',
      anchorOwn: 'Anchor · a 1 SP task by the assignee',
      anchorOnePoint: 'Anchor · a 1 SP task',
      anchorLevelHours: 'Anchor · done by a {{level}} in {{hours}}h (≈1 SP)',
      calibration_one:
        'Your last estimate by reference: you picked {{chosen}} on average, it actually took {{actual}}.',
      calibration_other:
        'Your last {{count}} estimates by reference: you picked {{chosen}} on average, they actually took {{actual}}.',
      moreItems: '… and {{count}} more',
      later: 'Later',
      save: 'Save and continue',
      step: 'Step {{current}}/{{total}} · {{label}}',
      next: 'Next',
      back: 'Back',
      estimateTitle: 'Estimate story points',
      estimateBody:
        'Pick a finished item to compare "{{title}}" with, then slide to say how much more or less work it is.',
      ownSectionShort: 'The assignee’s own ≈1 SP items',
      ownEmpty: 'No such finished item in this project yet — compare with a similar item below.',
      ownNearHours: '≈1 SP at {{level}} speed ({{hours}}h)',
      levelStandard: 'standard level',
      similarSection: 'Similar items',
      similarEmpty: 'No other similar item to compare with.',
      saveEstimate: 'Save estimate',
    },
  },
  taskMinDuration: {
    unit: {
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      hours_one: '{{count}} hour',
      hours_other: '{{count}} hours',
      minutes_one: '{{count}} minute',
      minutes_other: '{{count}} minutes',
    },
    dialog: {
      title: "This task can't be completed yet",
      body: 'This project requires a task to be worked on for at least {{min}} after it starts. “{{title}}” started at {{start}}, so it can be completed from {{earliest}}.',
      declareLabel: 'Started earlier? Declare the actual start time',
      declareHelp: 'A declared start is recorded with your name and marked "declared" on the task.',
      future: 'The start time cannot be in the future.',
      tooOld: 'The start time can go back at most {{days}} days.',
      stillTooSoon: 'With this time, the task can only be completed from {{earliest}}.',
      later: 'Later',
      declare: 'Declare and complete',
    },
    settings: {
      title: 'Minimum time before completion',
      subtitle: "Don't allow moving a task to Done before it has been worked on long enough since it started.",
      enable: 'Enable minimum time',
      amountLabel: 'Minimum',
      unitLabel: 'Unit',
      unitMinutes: 'Minutes',
      unitHours: 'Hours',
      invalid: 'Enter a positive whole number, at most {{max}} days.',
      howItWorks: 'Counted from the actual start (first move to "In progress"), or from creation if never started. Applies to everyone including the PM — on the board, list, Quick update and the Telegram Mini App. Anyone who forgot to move a task to "In progress" can declare the start time when completing. Milestones are exempt.',
      save: 'Save',
      saved: 'Minimum time saved',
    },
    bulkHeld_one: '{{count}} task has not reached the minimum time and was not moved — open it to declare the start time.',
    bulkHeld_other: '{{count}} tasks have not reached the minimum time and were not moved — open each to declare the start time.',
    declared: 'declared',
    declaredHint: 'Start time declared by the assignee when completing, not recorded by the system.',
  },
  taskScheduleRules: {
    title: 'Task date permissions',
    subtitle: 'Who can change a task’s start and due dates (including dragging on the Gantt).',
    label: 'Who can change start and due dates',
    option: {
      pm: 'Project manager only',
      assignee_or_creator: 'The assignee or creator of the task, and the PM',
      roles: 'Selected roles and the PM',
    },
    pmNote:
      'The PM and organization admins can always change dates. Dates only — priority stays PM-only; members still edit only their own tasks.',
    rolesLabel: 'Allowed roles',
    noRoles: 'No role selected — only the PM can change dates.',
    save: 'Save',
    saved: 'Task date permissions saved.',
  },
  standards: {
    title: 'Standards for this project',
    subtitle: 'How long a work item may run, and how many days ahead due dates are flagged.',
    help: 'These two inherit in different ways — read the hint under each field.',
    wbsMaxDays: 'Max item length (days)',
    wbsMaxDaysHint:
      'Copied from the org default when the project was created. Changing the default later does not touch this project.',
    dueSoonDays: 'Due-soon warning (days)',
    dueSoonInherited: 'Inheriting the org default ({{days}} days). Enter a number to override.',
    dueSoonOwn: 'This project decides. Clear the field to go back to the org default.',
  },
  sprintEdit: {
    title: 'Edit {{name}}',
    closed: 'This sprint is closed — editing history makes later velocity read wrong.',
    name: 'Sprint name',
    goal: 'Description',
    goalPlaceholder: 'What is this sprint for, and why?',
    startDate: 'Starts',
    endDate: 'Ends',
    color: 'Colour',
    colorAuto: 'Automatic',
    badRange: 'The end date must be after the start date.',
    saved: 'Sprint saved',
  },
  sprintCreate: {
    open: 'New sprint',
    title: 'Create a sprint',
    help: 'Set up the timebox first; fill it with work afterwards, on the Work tab.',
    namePlaceholder: 'For example: Sprint 1',
    planningNote:
      'The sprint starts out in planning. The board shows the running sprint by default, so pick this one in the selector above the Work tab to see it.',
    submit: 'Create sprint',
    created: '{{name}} created',
  },
  sprintLifecycle: {
    start: 'Start sprint',
    startHelp: 'Only one sprint runs at a time — close the running one first if there is one.',
    started: '{{name}} started',
    close: 'Close sprint',
    closed: '{{name}} closed — {{completed}} done, {{carried}} unfinished moved out.',
    alreadyClosed: 'This sprint is closed.',
    carryOver: 'Where does unfinished work go?',
    carry: {
      backlog: 'Back to the backlog',
      next_sprint: 'Into the next sprint',
    },
  },
  sprintStats: {
    tasks: '{{done}}/{{total}} tasks done',
    hours: '{{hours}} hours logged on the sprint tasks (subtasks included)',
    members: '{{n}} people involved — assigned a sprint task or logged time on one',
  },
  sprintGraph: {
    title: 'Sprint flow',
    help: 'Time runs top to bottom. Arrows are ordering constraints — two linked sprints must run in order; unlinked ones are free.',
    helpEdit:
      'Time runs top to bottom. Drag from the dot under a sprint onto another to force them to run in order; click an arrow to drop the constraint.',
    link: 'Link {{name}} to another sprint',
    unlink: 'Remove this ordering constraint',
    openTasks: 'View work in {{name}}',
    editSprint: 'Edit {{name}}',
    drag: 'Drag {{name}} elsewhere on the diagram',
    overlapTag: 'overlapping',
    overlapWarning_one:
      '{{count}} sprint overlaps another in time. The Scrum Guide (2020) has sprints run back to back — a new one starts right after the previous ends — and this system only runs one sprint at a time, so the later one will wait. You can still keep it if that is deliberate.',
    overlapWarning_other:
      '{{count}} sprints overlap in time. The Scrum Guide (2020) has sprints run back to back — a new one starts right after the previous ends — and this system only runs one sprint at a time, so the later one will wait. You can still keep it if that is deliberate.',
    empty: 'No sprints yet',
    emptyHelp:
      'An Agile project needs at least one sprint before work can leave the queue. Create the first one right here.',
    emptyHelpReadonly: 'The project manager will create the first sprint.',
    noEstimate: 'not estimated',
  },
  projectColor: {
    red: 'Red',
    orange: 'Orange',
    amber: 'Amber',
    gold: 'Gold',
    lime: 'Lime',
    green: 'Green',
    emerald: 'Emerald',
    teal: 'Teal',
    cyan: 'Cyan',
    sky: 'Sky',
    blue: 'Blue',
    indigo: 'Indigo',
    violet: 'Violet',
    purple: 'Purple',
    magenta: 'Magenta',
    pink: 'Pink',
    rose: 'Rose',
    stone: 'Stone',
    slate: 'Slate',
    graphite: 'Graphite',
    blush: 'Blush',
    peach: 'Peach',
    yellow: 'Yellow',
    olive: 'Olive',
    mint: 'Mint',
    aqua: 'Aqua',
    ice: 'Ice',
    azure: 'Azure',
    lavender: 'Lavender',
    candy: 'Candy',
  },
  projectPriority: {
    label: 'Priority',
    badge: 'Priority: {{level}}',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    urgent: 'Urgent',
  },
  /** Change project type from the edit dialog — APM-PMETH. */
  methodologyChange: {
    label: 'Project type',
    help: 'Changing the type changes the project’s tabs, board and reports. Pick another type to preview what is affected.',
    checking: 'Checking…',
    blockedTitle: 'Can’t change yet:',
    lossesTitle: 'After the change, the project will lose or stop showing:',
    noLosses: 'No data will be lost.',
    acknowledge: 'I have read this and agree',
    submit: 'Change to {{name}}',
    done: 'Project changed to {{name}}',
    blocker: {
      SAME_METHODOLOGY: 'The project is already this type.',
      SPRINT_ACTIVE: 'Sprint "{{name}}" is running — close it before changing.',
      PROJECT_CLOSED: 'The project is closed or stopped — reopen it first.',
    },
    loss: {
      SPRINTS_HIDDEN_one:
        'The Sprint tab, velocity, reviews and retros will be hidden ({{count}} sprint, {{tasks}} tasks in sprints). The data is kept — switch back to Agile to see it again.',
      SPRINTS_HIDDEN_other:
        'The Sprint tab, velocity, reviews and retros will be hidden ({{count}} sprints, {{tasks}} tasks in sprints). The data is kept — switch back to Agile to see it again.',
      END_DATE_CLEARED:
        'The end date {{date}} will be removed — maintenance projects run until you stop them.',
      SCHEDULE_REPORTS_HIDDEN:
        'Schedule reports (burndown, EVM, baseline, critical path) turn off, as there is no end date.',
      RECURRENCES_PAUSED_one:
        '{{count}} recurring schedule will be paused. Tasks already created stay as they are.',
      RECURRENCES_PAUSED_other:
        '{{count}} recurring schedules will be paused. Tasks already created stay as they are.',
      FLOW_REPORTS_HIDDEN: 'Maintenance reports (throughput, backlog) and period filters turn off.',
    },
  },
  overview: {
    startDate: 'Start date',
    endDate: 'End date',
    endDateMaintain: 'Maintenance projects have no end date — they run until you stop them.',
    editTitle: 'Edit project details — {{name}}',
    dateOrder: 'The end date must come after the start date.',
    color: 'Project colour',
    colorHelp: 'Tints the tab label and the sidebar row so projects are easy to tell apart.',
    colorNone: 'No colour',
    sprintMilestones: 'Sprint milestones ({{count}})',
    noSprintMilestones:
      'No milestones in this sprint yet. Set a task’s kind to “Milestone” within the sprint.',
    scopeCommitted: 'Committed',
    scopeAdded: 'Added mid-sprint',
    scopeCompleted: 'Done',
    scopeCarried: 'Carried over',
    scopeUnknown: 'not measured',
    sprints: 'Sprints ({{count}})',
    sprintsHelp: 'Open a sprint to see its burndown, delivered work and review notes.',
    sprintRowGoals: 'Goals',
    sprintRowMilestones: 'Milestones',
    sprintSummary: 'Sprints ({{count}})',
    sprintRunning: 'Active: {{name}}',
    sprintNoneRunning: 'No sprint is running.',
    sprintSummaryHelp: 'Each sprint in detail lives on the Sprint tab.',
    sprintDependencies: 'Dependencies',
    sprintDependsOn: 'Must finish before this sprint: {{names}}',
    sprintBlocks: 'This sprint must finish before: {{names}}',
    sprintNoDependency: 'Not linked to any sprint. Drag an arrow on “Flow” to enforce an order.',
    sprintPoints: '{{done}}/{{total}} pts',
    sprintPointsShort: '{{points}} pts',
    sprintNoPoints: 'not estimated',
    sprintNoGoal: 'This sprint has no description yet.',
    sprintDelivered: 'Delivered ({{count}})',
    sprintCarried: 'Not finished ({{count}})',
    sprintNoTasks: 'No tasks.',
    documents: 'Documents ({{count}})',
    noDocuments: 'No documents yet.',
    uploadDocuments: 'Upload documents',
    newDocument: 'New document',
    uploadingDocuments: 'Uploading {{count}} file(s)…',
    uploadedDocuments: 'Added {{count}} document(s) to the project.',
    uploadDocumentFailed: 'Couldn’t upload “{{name}}”: {{reason}}',
    documentLibrary: 'Search, filter and review in the document library',
    documentVersion: 'v{{version}}',
    saved: 'Saved',
    descriptionLabel: 'Project description',
    descriptionPlaceholder: 'What does this project do, for whom, and what does success mean?',
    noDescription:
      'No description yet. Add one under Settings so newcomers know what this project is for.',
    milestones: 'Milestones',
    milestonesHelp:
      'Tasks of kind Milestone in this project — no duration, just a date to hit. Kept in sync with the project’s milestones.',
    noMilestones:
      'No milestones yet. Set a task’s kind to “Milestone” when creating or editing it.',
    milestoneNoDate: 'no date',
    milestoneOverdue: 'overdue {{date}}',
  },
  invitations: {
    applicationsTitle: 'Applications via link ({{count}})',
    applicationsSubtitle:
      'Applicants state what they want; you settle role, dates and allocation before accepting.',
    applicationAccept: 'Accept on these terms',
    applicationAccepted: 'Accepted. They are now a project member.',
    noSkills: 'No skills declared yet.',
    wanted: 'Asked for: {{role}} · {{period}} · {{commitment}}',
    title: 'Project invitations ({{count}})',
    subtitle: 'Open one to see the project goal and the terms.',
    openDetail: 'view details →',
    noDescription: 'This project has no goal description yet.',
    role: 'Role:',
    period: 'Period:',
    periodOpen: 'not set',
    commitment: 'Allocation:',
    commitmentOpen: 'not set',
    hoursPerDay: '{{value}}h/day',
    accept: 'Accept',
    decline: 'Decline',
    confirmDecline: 'Confirm decline',
    notePlaceholder: 'Why not join? The inviter will read this.',
    needNote:
      'Give a reason first — a silent decline leaves the inviter waiting instead of looking elsewhere.',
    accepted: 'Accepted. You are now a member.',
    declined: 'Declined, with a reason.',
    inviteTitle: 'Invite someone',
    inviteSubtitle:
      'An invitation carries a role, a date range and an allocation. It only counts toward team capacity once accepted.',
    invitePending: 'awaiting reply',
    inviteSend: 'Send invitation',
    inviteSent: 'Invitation sent.',
    inviteFrom: 'From',
    inviteTo: 'To',
    invitePercent: 'Allocation (%)',
    // Invitations page — where an invitation notification leads (APM-INVOPEN).
    page: {
      title: 'Project invitations',
      subtitle: 'Invitations sent to you. Accept to join the project with the role and allocation below.',
      othersTitle: 'Other invitations waiting for you',
      emptyTitle: 'No invitations waiting for you',
      emptyDescription: 'When someone invites you to a project, the invitation shows up here and in the notification bell.',
      notFoundTitle: 'Invitation not found',
      notFoundDescription:
        'It may have been sent to another account, or belong to another organization — switch organization in the top bar and open the notification again.',
      reason: 'Reason: {{note}}',
      openProject: 'Open project',
      outcome: {
        alreadyMember: 'You are already in this project — nothing left to answer.',
        awaitingPm: 'Your application is waiting for the project manager.',
        withdrawn: 'The inviter withdrew this invitation.',
        invite: {
          accepted: 'You accepted this invitation on {{at}}.',
          declined: 'You declined this invitation on {{at}}.',
        },
        application: {
          accepted: 'The project manager accepted your application on {{at}}.',
          declined: 'The project manager declined your application on {{at}}.',
        },
      },
    },
  },
  projectRoles: {
    title: 'Roles & permissions',
    rulesSaved: 'Status and date permissions saved.',
    subtitle:
      'Three built-in roles, plus any you define. Custom roles exist only inside this project.',
    systemBadge: 'built-in',
    systemLocked:
      'Built-in roles cannot be edited: other parts of the system rely on them to recognise who the PM is.',
    systemExtrasHint:
      'Built-in permissions of this role cannot be removed. Tick more to grant them to this role — in this project only.',
    builtIn: 'Built into the role',
    extraLine: 'Added in this project: {{list}}',
    memberCount: '{{count}} people',
    memberSplit: '{{staff}} staff · {{guests}} guests',
    guestOwnOnly: 'This role lacks “View project”: guests holding it see only their own tasks.',
    guestForbidden:
      'A guest holds this role — project administration and approve / commit permissions cannot be turned on.',
    noPermission: 'no permissions selected',
    add: '+ Create a role',
    name: 'Role name',
    namePlaceholder: 'Document approver, QA lead…',
    delete: 'Delete',
    deleted: 'Role deleted.',
    saved: 'Role saved.',
    inUse: '{{count}} people still hold this role. Move them to another role first.',
    needName: 'A role needs a name.',
    needPermission: 'Pick at least one permission — a role with none allows nothing.',
    broaderWarning:
      '⚠️ This allows editing EVERY task in the project, not just your own — broader than the default Contributor role.',
    group: {
      view: 'View',
      work: 'Do the work',
      approve: 'Documents & approvals',
      design: 'Design',
      git: 'Git',
      track: 'Tracking & configuration',
    },
    memberHint:
      'Want a role like the default Contributor? Tick: View project · Create tasks · Edit tasks assigned to them · View project reports.',
    permission: {
      'project:read': 'View the project, board, Gantt, reports',
      'project:update': 'Edit project info, board columns, members',
      'workflow:manage': 'Add, edit, re-group and delete board columns',
      'task:create': 'Create tasks',
      'task:update@own': 'Edit tasks assigned to them',
      // Guest collaborator — APM-GUEST-c. Only matters when the role lacks 'View project'.
      'task:read@own': 'See only their own tasks (no board)',
      'task:update': 'Edit every task in the project',
      'task:delete': 'Delete tasks',
      'task:assign': 'Assign work to others',
      'document:read': 'Read project documents',
      'document:write': 'Upload & edit documents',
      'document:approve': 'Approve documents',
      'raid:manage': 'Manage risks, issues, decisions',
      'ai_suggestion:approve': 'Approve AI suggestions',
      'change_request:approve': 'Approve change requests',
      'report:read_project': 'View project reports',
      'design:read': 'View design screens and comparisons',
      'design:write': 'Link Figma files, capture versions, approve descriptions',
      // Git tab — APM-GIT-b. Outside collaborators and clients never get these, whatever their role.
      'git:read': 'View the Git tab and the Development block on tasks',
      'git:act': 'Create branches and merge requests, add or hide links',
      'git:manage': 'Paste the token, connect repositories, set auto-transition rules',
    },
  },
  projectList: {
    edit: 'All projects',
    title: 'Arrange the sidebar project list',
    help: 'Drag the handle at the start of a row onto the sidebar to add it, or use the bin in the sidebar to remove it. New projects always appear on their own.',
    finish: 'Done',
    saved: 'List saved.',
    helpCompact:
      'Turn the switch on to add a project to Quick access; reorder them on the Quick access tab. New projects always appear on their own.',
    tabAll: 'All',
    pinLabel: 'Show {{name}} in Quick access',
    openHint: 'Open {{name}}',
    dragHint: 'Drag onto the sidebar',
    reorderHint: 'Drag to reorder',
    onSidebar: 'On the sidebar',
    notOnSidebar: 'Hidden',
    acceptFirst: 'Accept the invitation first',
    removeFromSidebar: 'Remove {{name}} from the sidebar (does not delete the project)',
    /** Projects I joined as an outside collaborator (`GUEST`). */
    collab: {
      badge: 'Collaborator',
      badgeHint: 'You take part in this project as a collaborator from outside the organization',
      title: 'Collaborating projects',
      help: 'Projects in other organizations where you are a collaborator. Click to switch to that organization and open the project.',
      open: 'Switch to {{org}} and open {{name}}',
      switchFailed: 'Could not switch to that organization. Please try again.',
    },
    group: {
      invited: 'Invitations',
      notStarted: 'Not started',
      running: 'In progress',
      done: 'Completed',
      archived: 'Archived',
      rejected: 'Rejected',
    },
    rejected: 'Rejected',
    rejectedNote: 'Reason: {{note}}',
    rejectedNoNote: 'The approver gave no reason.',
    col: {
      key: 'Key',
      name: 'Project',
      creator: 'Created by',
      role: 'Your role',
      status: 'Status',
      period: 'Dates',
      people: 'People',
      tasks: 'Tasks',
      onSidebar: 'Sidebar',
    },
    status: {
      pending_approval: 'Awaiting approval',
      planning: 'Planning',
      active: 'In progress',
      on_hold: 'On hold',
      completed: 'Completed',
      archived: 'Archived',
    },
  },
  projectSetup: {
    title: '{{count}} things left to set up',
    badgeTitle: '{{count}} things to set up in this project',
    item: {
      taskTypes: 'Choose work types for this project',
      aiKey: 'Attach an AI key to this project',
      members: 'Add people to this project',
      rates: 'Set hourly hire rates',
    },
    why: {
      taskTypes:
        'The project has no usable work type yet, so no task can be created — every task needs a type.',
      aiKey: 'Without a key the project’s AI features stay off — everything else works as normal.',
      members: 'The project has only one person, so there is nobody to assign work to.',
      rates:
        'Without rates the Budget tab cannot work out labour cost. Auto-fill does not guess a rate for people without a level — give them a level or enter a custom rate.',
    },
  },
  projectSettings: {
    nav: {
      back: 'All settings',
      group: {
        team: 'Members & permissions',
        work: 'Work',
        integrations: 'Connections & AI',
        lifecycle: 'Project lifecycle',
      },
      search: {
        label: 'Search settings',
        placeholder: 'Search settings — e.g. permissions, holidays, token…',
        empty: 'No setting matches “{{query}}”. Try another word, or clear the search to see all.',
        enterHint: 'Press Enter to open this one.',
      },
      sidebarLabel: 'Settings sections',
      jumpLabel: 'Jump to another setting',
      keywords: {
        members: 'members, team, people, staff, add people, remove people, open work',
        invites: 'invite, invitation, invite link, add people, email',
        roles:
          'permissions, access, roles, PM, viewer, who can do what, status, move column, kanban, done, acceptance, status permission, start date, due date, deadline, calendar, Gantt, date permission',
        syncUsers: 'map people, match accounts, sync, Jira, Trello, email',
        staffingRates: 'rate, cost, salary, pay, speed factor, budget',
        sync: 'sync, Jira, Trello, import, connect, integration, external source',
        aiKey: 'token, key, API, Claude, AI',
        taskTypes: 'task type, category, label, bug, feature',
        issueConfig: 'issue, problem, incident, ticket, issue type, support',
        definitionOfDone: 'DoD, done, done criteria, acceptance, checklist',
        // General settings — standards, minimum time, required due date + estimate (2026-10-15).
        general:
          'general settings, parameters, standards, WBS, work package, deadline warning, reminder, minimum time, too soon, declare start time, due date, deadline, estimate, hours, required, block',
        tabLayout: 'tab bar, tab order, hide tab, layout',
        handOver: 'hand over, change PM, transfer management',
        stop: 'stop, pause, cancel, close project',
        archive: 'archive, put away, cancel, created by mistake',
        aiMemory: 'AI memory, remember, notes, context, AI',
        leave: 'leave project, exit, quit',
        git: 'git, gitlab, github, token, repository, repo, merge request, MR, pull request, webhook, branch, commit',
      },
    },
    general: {
      title: 'General settings',
      subtitle:
        'Small project parameters: require a due date and estimate before work starts, minimum task time, work package length, due-date reminders.',
      workRules: 'Starting and finishing tasks',
      reminders: 'Due-date reminders',
      remindersHelp: 'Turn each kind on or off separately. Both go to the task assignee.',
      dueSoonEnable: 'Warn before the due date',
      timeLowEnable: 'Remind when an in-progress task is running out of time',
      timeLowPct: 'Time left (%)',
      timeLowHint:
        'Measured from when the task moved to "In progress" to the end of the working day on its due date. Default 10%: started 9:00, due today ending 18:00 ⇒ reminder at 17:06.',
      invalidNumber: 'Enter a whole number from {{min}} to {{max}}.',
      save: 'Save',
      saved: 'General settings saved',
    },
    membersTitle: 'Project members',
    membersSubtitle: 'Who is on the project, in what role, and how much open work they carry.',
    role: 'Role',
    roleName: { PM: 'Project manager', CONTRIBUTOR: 'Contributor', VIEWER: 'Viewer' },
    openTasks: '{{count}} tasks · {{hours}}h',
    noOpenTasks: 'nothing open',
    remove: 'Remove',
    cannotRemove: {
      has_open_tasks: 'Open tasks still assigned to this person. Reassign them first.',
      last_pm: 'A project needs at least one manager. Appoint someone else first.',
      ok: '',
    },
    searchPlaceholder: 'Search by name, email, department…',
    noMatch: 'Nobody matches “{{query}}”.',
    addMember: '+ Add someone to the project',
    add: 'Add',
    everyoneAdded: 'Everyone in the organisation is already on this project.',
    guestRoleHint:
      'Guests cannot hold a role with project administration or approve / commit permissions.',
    guestRoleBlocked: '{{role}} — too wide for a guest',
    pending: {
      title: 'Waiting for approval ({{count}})',
      subtitle:
        'Colleagues who applied through the project link and guests who opened the invite link. Pick a role, then approve.',
      defaultGuestRole: 'Outside collaborator (created on approval)',
      guestMeta: '{{email}} · sent {{at}}',
    },
    saved: 'Team updated.',
  },
  mySettings: {
    nav: {
      back: 'All settings',
      group: {
        workplace: 'Organization and skills',
        time: 'Working time',
        assistants: 'Connections and AI assistant',
        display: 'Display',
      },
    },
  },
  /** Glass bottom bar on phones — APM-MBAR-a. */
  dock: {
    account: 'Account',
    aiAssistant: 'AI assistant',
    row: {
      main: 'Main row',
      features: 'Features {{n}}/{{total}}',
      account: 'Account',
    },
    short: {
      velocity: 'Estimates',
      aiConsole: 'AI console',
      teamReports: 'Team reports',
      settings: 'Settings',
      tour: 'Guide',
    },
  },
  navLayout: {
    title: 'Navigation bar',
    subtitle:
      'Choose which items show on the top bar, which go under "Features", and which are hidden.',
    help: 'Drag and drop to rearrange. The top bar holds at most {{max}} items; hiding an item only removes it from the bar, the page still opens by its link. Each organization has its own setup. On a phone, the first {{mobile}} items of the bar make up the main row of the bottom bar; swipe the bar vertically for the rest.',
    zone: { bar: 'On the bar', menu: 'Under "Features"', hidden: 'Hidden' },
    moveTo: { bar: 'Move to the bar', menu: 'Move under "Features"', hidden: 'Hide' },
    moveMenu: 'Move “{{name}}” to…',
    drag: 'Drag to move “{{name}}”',
    empty: 'Drop items here',
    barFull: 'The bar already has {{max}} items — move one out before adding another.',
    reset: 'Restore default',
    saved: 'Navigation bar saved',
    resetDone: 'Back to the default layout',
  },
  appearance: {
    title: 'Light/dark mode',
    subtitle: 'Follow the system, always light or always dark.',
    option: { system: 'System', light: 'Light', dark: 'Dark' },
    help: 'Applies immediately and only to this browser on this device — other devices keep their own choice. "System" switches automatically when your device does.',
  },
  mySkills: {
    title: 'My skills',
    subtitle:
      'What you can work on. Used to suggest the right person when assigning work and when AI reschedules.',
    level: 'Level',
    add: '+ Add a skill',
    addButton: 'Add',
    remove: 'Remove this skill',
    empty: 'No skills declared yet.',
    saved: 'Skills updated.',
    help: 'This is a self-declared profile, not an assessment. Real throughput is measured from task data, not from the numbers here.',
  },
  workSchedule: {
    title: 'Working hours',
    subtitle:
      'Which weekdays you work and the shifts on each. A lunch break is the gap between two shifts. Planning and workload charts use this to know how many hours you have left.',
    adminSubtitle:
      "Fill this in for people who haven't set it themselves. It sets their capacity ceiling across every project.",
    usingDefault:
      "No personal schedule yet — using the organization's default. Edit and save here to have your own.",
    resetToOrg: "Use the organization's default",
    resetConfirm:
      "Drop the personal schedule and use the organization's default? The current personal schedule will be gone.",
    resetDone: "Switched to the organization's default schedule.",
    orgUsingSystemDefault:
      'The organization has not set one yet — using Monday–Friday, 8 hours a day.',
    weekday: {
      0: 'Sunday',
      1: 'Monday',
      2: 'Tuesday',
      3: 'Wednesday',
      4: 'Thursday',
      5: 'Friday',
      6: 'Saturday',
    },
    start: 'From',
    end: 'To',
    addShift: 'Add another shift to this day',
    removeShift: 'Remove this shift',
    dayOff: 'Not working',
    hours: '{{count}}h',
    hoursUnit: 'hours',
    weeklyTotal: 'Weekly total:',
    invalidShift: 'A shift ends before or exactly when it starts.',
    overlappingShift: 'Two shifts on the same day overlap.',
    needOneDay: 'At least one working day is required. Record extended absence as leave, not here.',
    saved: 'Working hours saved.',
  },
  wbs: {
    title: 'WBS template library',
    subtitle:
      'A WBS (Work Breakdown Structure) splits a project into progressively smaller items, ' +
      'down to something one person can own. It says WHAT the project has to do, not yet ' +
      "when — scheduling is the Gantt chart's job. Each template here is a ready-made " +
      'breakdown per architecture, so planners and AI start from one instead of a blank page.',
    architecture: {
      web_fullstack: 'Web full-stack',
      backend_only: 'Backend / API',
      frontend_only: 'Frontend',
      mobile: 'Mobile app',
      microservices: 'Microservices',
      other: 'Other',
    },
    empty: 'No templates yet',
    emptyHelp: 'Create your first template, or clone a built-in one.',
    builtIn: 'Built-in template',
    builtInHint: 'Built-in templates are read-only. Click “Clone” to make a copy you can edit.',
    clone: 'Clone',
    cloned: 'Copy created. Editing the copy does not touch the original.',
    newTemplate: 'New template…',
    newName: 'New template',
    newItem: 'New item title',
    addItem: 'Add',
    removeItem: 'Remove item {{title}}',
    delete: 'Delete template {{name}}',
    moveUp: 'Move up',
    moveDown: 'Move down',
    showInactive: 'Show disabled templates',
    inactive: 'disabled',
    itemCount: '{{count}} items',
    totalHours: 'about {{hours}} hours',
    hours: '{{hours}}h',
    after: 'after: {{name}}',
  },
  chartHelp: {
    overtime: {
      what: 'Hours you clocked **outside your own shifts** over the last 90 days. Off-shift covers before start, after end, the lunch gap, and Saturdays, Sundays and holidays.',
      read: '⚠️ This is **not** a performance dimension. It answers "how much is this person carrying", not "how well are they doing" — which is why it is absent from the team comparison table. High overtime is almost always a sign of bad estimation or overload: something to fix in the plan, not something to praise in a person.',
      watch:
        'Look at the **number of days**, not just the total: 10 hours across 8 days is a working rhythm, 10 hours in one night is a missed deadline. Off-shift timers stop themselves after 2 hours, so press **Continue** if you are still working — anything after that is not recorded otherwise.',
    },
    okrStart: {
      what: 'Where this person stood **when the target was set**.',
      read: 'Captured **once** and then kept: by the nightly job for an auto-measured goal, by the first report for a free-form one. A baseline that follows the present pins progress at 0% forever.',
      watch:
        'With no starting point the progress reads **"never measured"**, not 0% — a new joiner, or a period without data.',
    },
    okrNumbers: {
      what: 'The three numbers behind an OKR. **Starting point** is where the person stood when the target was set — captured **once**, then kept. **Target** is the number committed to for the end of the period, and for an auto-measured goal it is the **only** number a human decides. **Measured** is where they are now.',
      read: 'The percentage measures **the distance between starting point and target**, not "measured divided by target". Going from 75% toward a 78% target while sitting at 80% is **167%**: three points committed, five delivered. It has to work this way because lower-is-better metrics (reopens: 8 → target 3) would be scored backwards by a plain division. Who writes the number: an auto-measured goal is computed by the nightly job from completed work and has no input field; a free-form key result is reported by the manager, and the first report becomes the starting point.',
      watch:
        'Over **100%** means the target was set too low — worth recording at period close, because next period should aim further. **0%** with a starting point present means going backwards, not "nothing done yet". And **"never measured"** means there is no starting point at all — a new joiner, or a period without data — which is a different thing from 0%.',
    },
    /** (i) next to each auto-measured OKR metric — requested 2026-09-30. */
    okrMetric: {
      on_time_ratio: {
        what: 'Of the **dated** tasks you finished this period, the share finished **on or before the due date**.',
        read: 'The nightly job counts tasks you completed in the period. Tasks without a due date are left out. Nothing to enter.',
        watch:
          'Under **3 dated tasks** the number swings hard — one late task costs a dozen points.',
      },
      rework_ratio: {
        what: 'The share of finished tasks that were **never reopened** — done means done.',
        read: 'Counts tasks completed in the period; a task reopened even once after closing counts as rework. Higher is better.',
        watch:
          'Below **90%** means more than one in ten tasks came back — usually rushed closing or unclear requirements.',
      },
      sla_met_ratio: {
        what: 'Of the service issues (incidents, requests…) you closed this period, the share resolved **within the committed SLA**.',
        read: 'Only issues with a resolution deadline count; priorities without a commitment are left out.',
        watch:
          'A period with no closed service issues **cannot be measured** — pick it only if you do service work.',
      },
      reopen_count: {
        what: 'How many times service issues you closed this period were **reopened**.',
        read: 'An issue reopened twice counts 2. **Lower is better** — the target sits below the starting point.',
        watch:
          'Rising together with the number of closed issues: closing fast without fixing for good.',
      },
      speed_index: {
        what: 'How fast you work **against the estimate**, adjusted for how each project estimates. **100** = the project’s pace.',
        read: 'Actual vs estimated hours of finished tasks, divided by the whole project’s level, then weighted by the hours you spent in each project. 120 ≈ 20% faster, 50 = twice as slow. Capped at 150.',
        watch:
          'Must be paired with a **quality** metric in the same period — speed from sloppy work does not count. Hitting **150** usually means a bad estimate, not talent.',
      },
      service_hours: {
        what: '**Attributed hours** you put into service issues closed this period.',
        read: 'Taken from the time issues spent in progress; handling n issues at once gives each 1/n. Counted in hours, not tickets — splitting tickets does not raise it.',
        watch:
          'A **volume** metric, so it must be paired with a quality metric in the same period (e.g. SLA met ratio).',
      },
    },
    timesheetWeek: {
      what: 'Hours you actually **clocked** this week, by day. This is not company attendance — only the stretches you started and stopped.',
      read: 'A `–` means **nothing clocked** that day, which is different from working zero hours. Greyed cells are days with no shift (weekend, holiday, leave). Avg/day divides the week total by the number of **days with a shift**, so a week with three days of leave still reads correctly.',
      watch:
        'Avg/day below half your scheduled hours two weeks running — usually forgotten timers rather than less work, and everything built on those numbers is then short. A week total above your scheduled hours is worth a look too: either steady overtime, or a timer left running.',
    },
    testProgress: {
      what: 'How many cells each round has run, and with what result. One cell is one test case on one environment.',
      read: 'The full column height is the total number of cells to run, so the grey part is what has **not** been run. Compare columns to see which rounds ran fully and which were cut short.',
      watch:
        'A large grey part on a round that is already closed — that round drew conclusions from part of the case set. And a lot of amber (blocked): that is not a product defect, it is a broken test environment.',
    },
    testFixYield: {
      what: 'Per round, how many bugs the developers marked fixed and how many the testers confirmed.',
      read: '100% means every bug dev marked done really was done. 60% means for every 10 bugs marked fixed, 4 were still broken on retest. The two lines are two sides of one number, so they add to roughly 100%.',
      watch:
        'Below 70%, or falling round over round. Both say bugs are being marked done before they are done, and every later round will cost one extra pass. A round with no point is a round where nobody fixed anything — quite different from 0%.',
    },
    testConvergence: {
      what: 'Bugs still to fix at the end of each round, and bugs newly found during that round.',
      read: 'Both lines falling means you are converging — nearly done. Read the **new bugs** line first: it tells you whether the product is still producing defects.',
      watch:
        'Bugs remaining falling while new bugs stay flat: you are chasing your own tail, and that is what you need to see **before** committing to a release date. New bugs spiking in a late round usually means a fix broke something else.',
    },
    burndown: {
      what: 'Remaining sprint work falling day by day, against the ideal line.',
      read: 'Above the ideal line means behind the pace; below means ahead. Slope matters more than position: a steady slope means work finishes evenly, while a flat line that drops at the end means everything landed on the last days.',
      watch:
        "A flat stretch lasting more than a couple of days, or a line that stays level until the very end. Either way the final number can't be predicted from mid-sprint.",
    },
    perfSpeed: {
      what: 'How fast you finish work compared to **that project’s own pace**.',
      read: '100 means on the project’s pace, 50 means twice as slow as that pace. Normalised per project rather than compared in absolute terms, because a PM who estimates generously makes the whole team look fast — comparing that way measures the PM’s estimating habits, not you. The index is capped at 150.',
      watch:
        'Hitting the 150 cap is **not** good news: running half again faster than the project pace almost always means work was estimated too generously, not that someone is that much quicker. Sustained below 70, look at how work is being broken down before concluding anything about speed.',
    },
    perfOnTime: {
      what: 'Of the work that **has a due date**, what share you finished in time.',
      read: 'The denominator counts only dated work — undated work enters neither side of the ratio. So someone doing mostly undated work shows “not measured yet”, not 0%.',
      watch:
        'Below 70% is usually a problem with **how dates get set**, not with the work: deadlines set by someone else without checking current load never improve by trying harder.',
    },
    perfRework: {
      what: 'What share of the work you finished **was never reopened**.',
      read: 'Higher is better — same direction as every other figure here, so no cell has to be read backwards. This is speed’s counterweight: read it **alongside** “Speed”, because the quickest way to make one look good is to damage the other.',
      watch:
        'Below 85% deserves a closer look. And any time Speed rises while this falls — that is the clearest sign of going faster by cutting corners.',
    },
    perfService: {
      what: 'Operational work: how much met its service commitment, how many **weighted hours** it took, and how many issues came back.',
      read: 'Measured in **hours**, not ticket count. Counting tickets rewards picking easy ones and splitting work up — splitting a two-hour job into four tickets is still two hours. The commitment ratio’s denominator counts **only issues that carry a commitment**, so no commitment is not the same as a missed one.',
      watch:
        'Reopens rising while weighted hours also rise: more is being handled, but less is being finished. An empty block means you do no operational work — different from doing it and closing nothing yet.',
    },
    perfLoad: {
      what: 'How much of your working capacity is already committed for the **next** eight weeks.',
      read: '⚠️ This is the only figure looking **forward** — every other one recounts the past. Computed from dated assigned work against the hours left after subtracting booked time off.',
      watch:
        'Above 110% the schedule has promised more than the hours that actually exist, and something will slip — raising this number early costs far less than reporting the delay later.',
    },
    velocity: {
      what: 'Points completed per sprint, plus the average of recent sprints.',
      read: "Look at the **spread** before the value: steady velocity supports forecasting, but velocity that doubles then halves has an average you can't promise on.",
      watch: 'Two consecutive sprints falling sharply, or a spread wider than the average itself.',
    },
    issueArrival: {
      what: 'How many issues arrive each week, and how many get closed.',
      read: 'Two lines close together means the team is **keeping up**. The gap between them is the backlog added that week.',
      watch:
        'The arrivals line above the closures line for **three weeks running** — the backlog can then only grow, and it will not clear itself. This is the earliest warning in the group.',
    },
    issueCycle: {
      what: 'How long an issue takes from being recorded to being closed.',
      read: 'Read the **85%** column, not an average: this distribution has a very heavy tail, so an average sits where no issue is and hides exactly the ones that make customers call. "85% done within 3 days" is a promise you can make.',
      watch:
        'The 85% column climbing across periods, or drifting far from the median column — the tail is lengthening: most are still fast, but a few are forgotten for longer and longer.',
    },
    issueSla: {
      what: 'What share of issues met the response and resolution commitments.',
      read: 'The denominator counts **only issues that have a commitment**. P4 usually has no resolution target, so that cell is a dash rather than 0% — no commitment is not the same as a missed one.',
      watch:
        'The response column dropping before the resolution column: the team is overloaded at intake, not at fixing. Those two causes need different responses.',
    },
    issueAging: {
      what: 'How long the currently **open** issues have been open, and how many of them are already overdue.',
      read: 'This is the only chart in the group that looks **forward** — the other four recount the past. The red bar is what already breached; the rest of each bar is still in time.',
      watch:
        'The "older" bucket swelling between visits: work nobody refuses and nobody does. And any bucket where red dominates.',
    },
    issueMaintenance: {
      what: 'Which kind of maintenance the effort goes into: corrective, adaptive, perfective or preventive.',
      read: 'Measured in **attributed hours**, not issue count: ten small requests and one architectural fix are eleven items but the effort is the other way round. This answers "where does the maintenance budget go".',
      watch:
        'Corrective dominating for several periods — the product is decaying and the team only has time to firefight. A large "unclassified" slice means this chart cannot be read at all yet.',
    },
    unplanned: {
      what: "What share of each week's work nobody had time to plan — created right up against the day it had to start.",
      read: 'It measures **predictability**, not productivity. A high ratio means work arrives unannounced, and every plan for anything else is fragile. A **break in the line** is a week with nothing measurable — not a 0% week.',
      watch:
        'A ratio climbing over several weeks: the team is being pulled into firefighting. And a single week near 100% is usually one incident worth naming.',
    },
    throughput: {
      what: 'How many items finish each week.',
      read: 'It counts **items**, not size — so it answers "how fast is the team closing work", not "how much got done". Weekly because daily counts for a small team are just noise.',
      watch:
        'A downward trend over several weeks, or a week at zero while work is still in progress — usually something is stuck in one column.',
    },
    flow: {
      what: 'Cumulative flow diagram: each band is the number of items in one column, stacked over time.',
      read: 'Read band **thickness**, not the edges: a band growing means more work enters that column than leaves it. Vertical distance between lines is work in progress; horizontal distance is how long an item takes to cross.',
      watch:
        'A band swelling steadily — that is the bottleneck. And a flat top band means the team takes on new work faster than it finishes.',
    },
    cycle: {
      what: 'Each dot is a finished item: how many days it took from start to done.',
      read: 'The percentile line matters more than any dot: p85 means 85% of work finishes within that many days — that is the number to promise on, not the average. The lower chart shows whether the process is stable (moving average ± 2σ).',
      watch:
        'Dots outside the ±2σ band are genuinely unusual and worth asking about. And a p85 climbing week over week means every deadline promise is drifting wrong.',
    },
    aging: {
      what: 'How many days each **unfinished** item has been sitting in progress.',
      read: 'Unlike cycle time this counts work that is **not** done — so it describes now, not history. The "aged" threshold is this project\'s own p85, so a slower or faster team moves the threshold with it.',
      watch:
        "Anything past the threshold: it is unusual against the team's own history, not against some general number. Click a row to see what it is waiting on.",
    },
    evm: {
      what: 'Three earned-value lines, measured in **hours** rather than money: PV = what should be done by now, EV = what is actually done, AC = what has been spent.',
      read: 'EV below PV means behind schedule; AC above EV means it costs more effort than planned. The two figures in the subtitle are ratios of those lines: SPI = EV/PV (schedule), CPI = EV/AC (efficiency). 1.0 means on plan.',
      watch:
        'SPI or CPI below 0.9. And gaps that **widen over time** matter more than a large but constant gap.',
    },
    estimate: {
      what: 'Each dot is one item: original estimate (horizontal) against actual time (vertical).',
      read: 'The diagonal is "estimated correctly". Dots **above** it took longer than planned, **below** took less. Hollow dots were blocked at some point — slow because of waiting, not because of capability.',
      watch:
        'Dots clustering on one side of the diagonal: the team estimates with a systematic bias, and that number feeds every other forecast.',
    },
    slippage: {
      what: 'How far actual completion dates drift from the agreed baseline plan.',
      read: 'Positive bars are later than baseline, negative are earlier. It is measured against the **baseline**, so it only means anything once the original plan has been locked.',
      watch:
        'Several positive bars in a row — the plan is drifting steadily, not just one item slipping.',
    },
    riskMatrix: {
      what: 'Risks placed by likelihood (horizontal) and impact (vertical), each on a 1–5 scale.',
      read: 'Position matters more than count: one cell in the top-right outweighs five in the bottom-left. Score = likelihood × impact, up to 25.',
      watch:
        'Red cells (score 15 or more) need a named owner and a response plan — that is the same threshold the system uses to raise alerts.',
    },
    riskBurndown: {
      what: 'Total risk score for the project falling over time.',
      read: 'It measures **total exposure**, not risk count: closing one large risk pulls the line down further than closing three small ones.',
      watch:
        'A line that stays flat or rises while the project runs: new risks appear faster than old ones get handled.',
    },
    crTrend: {
      what: 'Change requests over time — raised and approved.',
      read: 'The **raised** bars are the ones to watch: how often changes get proposed tells you how hard scope is being stretched, even when most are rejected.',
      watch:
        'A rising rate of proposals in the second half of a project — an early sign of scope creep.',
    },
    documents: {
      what: "Which state the project's documents are in: draft, in review, approved.",
      read: 'The list below is more useful than the three counts: it names **which** documents are waiting and for how long.',
      watch:
        'A growing review pile: approval is the bottleneck, and anything depending on those documents is standing still.',
    },
    periodComparison: {
      what: "This period's five numbers, next to the same numbers for the period before.",
      read: 'Read the **first two rows together**: how much came in, how much went out. Consistently closing less than you receive means the backlog only grows. Arrows show direction; colour shows whether that direction is good.',
      watch:
        '⚠️ Unplanned share up **and** backlog up: the team is firefighting, not merely busy. Those two need very different responses.',
    },
    gates: {
      what: 'The state of each phase gate in a Waterfall project.',
      read: 'Each gate\'s entry conditions are read from the project\'s own **dependency graph**, not from a checkbox someone ticks. So "not ready" always comes with a specific reason.',
      watch:
        'An overdue gate that is still not ready: every phase after it is running on an unconfirmed assumption.',
    },
    resource: {
      what: "Staffing demand over time, against the team's real capacity.",
      read: 'Bars above the capacity line mean **the plan asks for more hours than the team has** in that period — already net of weekends, holidays and booked leave.',
      watch:
        'Any bar over the line: that schedule cannot run as drawn, even if every single item looks reasonable on its own.',
    },
    objectives: {
      what: "The project's objectives and the work serving each one.",
      read: 'Read it for two questions: which objective has **no work** attached, and which has a lot in flight.',
      watch:
        'An objective with nothing attached: it is being committed to while nobody is working on it.',
    },
    teamPerf: {
      what: 'Throughput per person, split by work type and technology.',
      read: 'Compare a person against **their own previous period**, not against other people: different work types and difficulty make the numbers non-comparable.',
      watch:
        '⚠️ This is individual performance data. Every time it is opened is recorded, and the people shown can read that trail.',
    },
    health: {
      what: "Every project's health on one board, three signals each: schedule, risk, quality.",
      read: 'Each cell carries a **number**, not just a colour — so you can see why it is red without opening each project.',
      watch:
        'Two or more red signals on one row: the problems are compounding rather than staying independent.',
    },
    projectHealth: {
      what: 'Three health signals for this project: schedule, risk, quality.',
      read: 'Same calculation as the leadership board — deliberately so, that PM and leadership see the same colour for the same project.',
      watch:
        'A signal changing colour while its number has barely moved: it just crossed the threshold, which is the cheapest moment to act.',
    },
    capacity: {
      what: 'Forecast team capacity for the coming weeks, against committed work.',
      read: 'Capacity is net of weekends, holidays and booked leave — so it is the hours actually available, not the hours on paper.',
      watch:
        'Any week where commitment exceeds capacity: work needs spreading or scope trimming, and knowing early is cheaper than finding out that week.',
    },
    storage: {
      what: 'Storage used by **available** files in each project on your sidebar — task attachments and document versions. Deleted files are not counted.',
      read: 'The longest bar is the heaviest project; the others are relative to it. The number on the right is the real answer, with the file count. Pick a date in "view as of" to see usage at that point.',
      watch:
        "One project taking **more than half** of the total, or **doubling** between two dates a week apart: usually builds, videos or archives uploaded in the wrong place — open the project's Files page to review and delete.",
    },
    roadmap: {
      what: 'Projects laid out on a shared timeline.',
      read: 'Use it to spot **overlap**: two large projects running the same stretch means they compete for the same people.',
      watch:
        'Several projects ending close together — handover work bunches up, and that is when quality usually drops.',
    },
    activity: {
      what: 'How often each person updates their work, day by day.',
      read: 'It measures the **habit of updating**, not productivity: a pale cell means nothing was recorded that day, not that nothing was done.',
      watch:
        '⚠️ This is individual performance data and every view is recorded. A long pale stretch is worth asking about because the reporting data is unreliable, not to judge the person.',
    },
    teamPerf2Cols: {
      tasksDone: {
        what: 'Tasks this person finished in the period (still done).',
        read: 'Credited to whoever did the task when velocity was measured, otherwise the current assignee. Tasks reopened and not done again are not counted. Arrow: vs the previous period.',
        watch: 'A sharp drop vs the previous period without leave — ask whether a big task is stuck.',
      },
      checklistDone: {
        what: 'Checklist items this person ticked off in the period, across all tasks.',
        read: 'Only the final state in the period counts: ticked then unticked is not counted, tick–untick–tick counts once. System ticks are not credited to anyone.',
        watch: 'Many checklist items but few tasks done: tasks are big, or poorly split.',
      },
      spDeclared: {
        what: 'Story points set on tasks done in the period.',
        read: 'Tasks without points add nothing here — see SP estimated.',
        watch: 'Zero for the whole team means nobody points tasks — use SP estimated instead.',
      },
      spEstimated: {
        what: 'Estimated story points for tasks done in the period that had no points.',
        read: "Uses the AI suggestion when there is one; otherwise estimated hours ÷ the organization's hours per point (median, needs ≥ 5 tasks with both), rounded up to the Fibonacci scale. Hover the number for the AI / formula / not estimated split.",
        watch: 'Much larger than SP set: the number leans on estimates and is less reliable.',
      },
      onTimePct: {
        what: 'Share of tasks done on or before their due date, among tasks done that had one.',
        read: 'Compared with the due date in force when the task was done — moving it later does not change the number. Tasks without a due date are left out; "—" when none had one.',
        watch: 'Below 80% is worth a look; below 60% is usually the plan, not the person.',
      },
      lateDone: {
        what: 'Tasks done after the due date in force when they were done.',
        read: 'Same sample as % on time: on time + done late = tasks done with a due date.',
        watch: 'Rising two periods in a row.',
      },
      dueExtensions: {
        what: 'Total times the due date was pushed later, across tasks done in the period — counted until each task was done. Hover the cell to see how many tasks they fall on.',
        read: 'One extension = an existing due date changed to a later day, by anyone (applying a reschedule suggestion included). Setting a first due date, pulling it earlier or clearing it does not count; nor does moving it after the task is done.',
        watch: '% on time compares against the due date at completion, so a high % on time alongside a high count here means on time thanks to extensions. A task extended many times usually had a wrong initial estimate — ask before concluding.',
      },
      overdueOpen: {
        what: 'Tasks assigned to this person, not done by the end of the period, due before its last day.',
        read: 'A period ending today means literally "overdue now". Past periods use the current assignee and due date, so tasks reassigned or rescheduled afterwards may be off.',
        watch: 'Three or more, or rising two periods in a row.',
      },
      reopenPct: {
        what: 'Share of tasks done in the period that had been reopened after being done.',
        read: 'Reopened = moved from a done column back to another. Each task counts once however many times.',
        watch: 'Above 15%: "done" is declared early, or the definition of done is unclear.',
      },
      cycleDays: {
        what: 'Calendar days from starting work to done — median over tasks done in the period.',
        read: 'Start = first time the task entered "in progress"; tasks moved straight to done have no start and are left out. A median, so one long task does not skew it.',
        watch: 'Creeping up period after period: work is getting bigger, or waiting more.',
      },
      wip: {
        what: 'Tasks of this person currently in "in progress", at the time of viewing.',
        read: 'A current number, not per period, so no comparison. Many things in progress at once usually stretches cycle time.',
        watch: 'More than 3 things in progress at once.',
      },
      hours: {
        what: 'Hours this person tracked with the timer in the period.',
        read: 'Only the part inside the period; a running timer counts up to now. Hours typed on a task are not included.',
        watch: 'Unusually low usually means a forgotten timer, not idle time — ask before concluding.',
      },
      estimateRatio: {
        what: 'Actual hours ÷ estimated hours, summed over tasks done that have both.',
        read: '1 means on target; 1.3 means 30% over the estimate; 0.8 means faster. Actual = hours entered on the task, otherwise timer hours on that task.',
        watch: 'Above 1.5 or below 0.7: estimates are systematically off.',
      },
      capacityThisWeek: {
        what: 'Load this week (Monday → Sunday): assigned task hours ÷ available hours.',
        read: 'Task hours = estimates of assigned tasks spread over working days from start to due date, including tasks done since Monday. Available hours = work schedule minus leave and holidays. Above 100% red (overloaded), from 85% amber.',
        watch: 'Above 100% (red) is overloaded; from 85% (amber) is near the limit.',
      },
      capacityAhead: {
        what: 'Load for the next N days from tomorrow, N being the length of the selected period.',
        read: 'Same calculation as Capacity this week but open tasks only. Always looks ahead from today, even when the selected period is in the past.',
        watch: 'Above 100% means more work accepted than hours available in the next N days.',
      },
    },
    myPerf2Overview: {
      what: 'Eight numbers about you in the selected period, each with an arrow against the previous period — the same calculation as the team overview.',
      read: "Green means better, red means worse, grey means unchanged or a metric with no better direction. These equal your row in your manager's table. Tap (i) on a tile to see how it is measured.",
      watch:
        'On-time below 80% or overdue rising two periods in a row is the moment to check By project to see which project drives it.',
    },
    myPerf2Projects: {
      what: 'The same metrics as the overview, split by each project you have numbers in this or the previous period, or open work in.',
      read: 'Under each number is the change against the previous period: green is better, red is worse. "—" means not measurable, not 0. Click a project name to open it.',
      watch: 'A project with logged hours but no finished tasks for several periods usually means stuck or oversized work — split it or ask the PM.',
    },
    myActivity: {
      what: 'How many updates you made in each project, per day of the period (per week when the period is longer than 62 days).',
      read: 'Counts everything you did yourself: moving cards, editing tasks, ticking checklists, commenting… The last row is the total across projects. It measures **update habits**, not productivity.',
      watch: 'A project blank for many days while you are still working on it means its reports do not reflect your work.',
    },
    teamActivity: {
      what: 'How many updates each team member made, per day of the period (per week when longer than 62 days), across all projects.',
      read: 'Everyone in the team has a row, including people with no updates — the blank rows are the point. It measures **update habits**, not productivity: a pale cell means nothing was recorded that day, not that nothing was done.',
      watch:
        "⚠️ Personal performance data — every view is logged in each person's access log. Long pale stretches are worth asking about because the reporting data is unreliable, not to judge people.",
    },
    myPerf2: {
      overdue: {
        what: 'Your assigned tasks still open at the end of the period with a due date before the last day.',
        read: 'Period ending today: literally "overdue now". Past periods use the current assignee and due date.',
        watch: 'Rising two periods in a row — move dates with a reason or tell the PM early.',
      },
      utilization: {
        what: 'Hours you logged divided by your scheduled working hours in the period.',
        read: 'Only counts timer hours — forgetting to log time makes it low without meaning you were idle.',
        watch: 'Below 50% usually means missing time logs; above 100% for long means too much overtime.',
      },
      working: {
        what: "Your tasks currently in progress. Subtitle: this week's load.",
        read: 'Load = assigned hours ÷ available hours this week, same as the Capacity column of the team table. No previous period — this is the current number.',
        watch: 'Many tasks in progress at once usually slows them all; load above 100% means you took on more than your hours.',
      },
    },
    teamPerf2Overview: {
      what: 'Eight team-wide numbers for the selected period, each with an arrow against the previous period of the same length (month vs month, week vs the same weekdays last week).',
      read: "Green arrows mean better, red worse, grey unchanged or a metric with no good direction (hours tracked, utilization). Colour is a hint, not a verdict — a period of easy tasks also turns tasks done green. Team rates are summed from everyone's counts, not averaged from individual rates. Click (i) on each tile for how it is measured.",
      watch:
        'On time below 80%, overdue rising two periods in a row, or utilization below 50% / above 100% is when to open By person and see who is moving the number.',
    },
    teamPerf2People: {
      what: 'Countable metrics per person for the period: output, deadlines, quality, flow and effort. Under each number is the change against the previous period.',
      read: 'Under each number: green is better than the previous period, red is worse (for Overdue, Done late, Extensions, % reopened and Cycle time lower is better; Actual/estimate is best near 1), grey is unchanged. Sorted by name by default — click a column header to sort; sorting does not reload data. "—" means not measurable (no qualifying task), not zero. Click a name to open the full profile and set goals.',
      watch:
        "This is not a ranking: there is no overall score. Someone finishing few tasks may be carrying big ones — read story points and cycle time together, and ask before concluding. Every view is recorded in each person's access log.",
    },
    teamPerf2: {
      throughput: {
        what: 'Tasks done in the period (still done), credited to whoever did them. Subtitle: checklist items people ticked off themselves in the period.',
        read: 'Equals the "Tasks done" column of People × projects for the same period. Tasks reopened and not done again are not counted.',
        watch: 'Down more than 30% while active people stay the same — check for a big task that is stuck.',
      },
      storyPoints: {
        what: 'Story points of tasks done: points set on the task, plus an estimate for tasks without points.',
        read: 'The estimate uses an AI suggestion when there is one, otherwise converts estimated hours with the organization\'s hours-per-point ratio (median, needs at least 5 tasks with both). Hover "SP estimated" in the table for the AI / formula split.',
        watch: 'Estimated larger than set means the team rarely points tasks — the number is less reliable then.',
      },
      onTime: {
        what: 'Share of tasks done on or before their due date, among tasks done that had one. Subtitle: tasks done late.',
        read: "Compared with the due date in force when the task was done, not today's — moving the date later does not change it. Tasks without a due date are left out.",
        watch: 'Below 80% is worth a look; below 60% is usually the plan, not the people.',
      },
      overdue: {
        what: 'Assigned tasks not done by the end of the period with a due date before its last day. Subtitle: people with at least one overdue task.',
        read: 'Period ending today: literally "overdue now". Past periods use the current assignee and due date, so tasks reassigned or rescheduled afterwards will be off.',
        watch: 'Rising two periods in a row, or piling up on one or two people.',
      },
      cycle: {
        what: 'Calendar days from actually starting to done — median over all tasks the team finished.',
        read: 'A median, so one month-long task does not skew it. Tasks that never went to "in progress" have no start and are left out.',
        watch: 'Creeping up period after period: work is getting bigger, or waiting more.',
      },
      reopen: {
        what: 'Share of tasks done in the period that had been reopened after being done.',
        read: 'Reopened = moved from a "done" column back to another. Counted once per task however many times.',
        watch: 'Above 15% suggests "done" is declared early or the definition of done is unclear.',
      },
      utilization: {
        what: 'Hours tracked by the team divided by scheduled working hours in the period.',
        read: 'Only timer hours count. A team that rarely uses the timer will look idle when it is not.',
        watch: 'Below 50% is usually missing timer data; above 100% is sustained overtime.',
      },
      active: {
        what: 'People with a task done, tracked hours or a checklist item in the period, out of everyone in the team. Subtitle: people above 100% load this week.',
        read: 'Load = assigned hours ÷ available hours this week, the same as the Capacity column of People × projects.',
        watch: 'Someone inactive in the period may be on leave — check before asking.',
      },
    },
    teamProjects: {
      what: 'Which projects each team member worked on in the period: hours tracked, tasks done, tasks assigned, % of time committed and actual, story points done, staff level, plus capacity this week and for the next N days.',
      read: '% committed is the commitment the PM set for that person on the project (against their schedule); % actual is the share of their tracked hours spent on it; the Allocation tab lays it out as a matrix (people as columns, projects as rows, each column adds up to 100%). Gaps of 20 points or more are highlighted. Capacity = assigned hours ÷ available hours — above 100% is overloaded, from 85% is near the limit.',
      watch:
        'Hours come only from the timer. This week\'s capacity includes work done since Monday. The "Next N days" column looks ahead from tomorrow, N being the length of the chosen period.',
    },
    teamDaily: {
      what: 'For each member on the chosen day: which item the timer is running on, total working time and how much of it was OT (outside their scheduled shifts), how many tasks and checklist items were completed.',
      read: 'Click a row to see each item: hours that day, progress from start of day → now (the number in brackets is the increase), and the checklist items that person ticked. Use < > to see other days.',
      watch:
        'Hours come only from the timer — someone who works without it shows 0h. A done task is credited to whoever did it when velocity was measured, otherwise to the assignee.',
    },
    allocation: {
      what: 'Which projects each person is allocated to, and at what percentage.',
      read: "Sum across a row for someone's total load: over 100% means they have been promised to more places than they have hours for.",
      watch:
        '⚠️ Reading this table leaves a trail: each view writes a log entry for every person shown, and they can read it.',
    },
    // APM-GIT-j — Git tab flow metrics (plan-git-integration §7.1).
    gitCycleTime: {
      what: 'How long a merge request takes, split into four phases: Coding (first commit → ready for review; never a draft ⇒ MR creation), Waiting for review (ready → first review), Review (first review → merge), To production (merge → first successful production deployment, estimated).',
      read: 'Each bar is the median of MRs merged that week, stacked by phase; the boxes below give the median and p85 of the whole 12 weeks. An MR without a review or not yet deployed adds no sample to that phase rather than a 0.',
      watch:
        'Waiting for review with a median above 24 hours means MRs sit idle — agree on a review slot. Review above 2 days or a p85 more than 3× the median: MRs are too large, split them.',
    },
    gitOpenAge: {
      what: 'Merge requests open at the viewed time, grouped by age since creation: under 1 day, 1–3, 3–7 and over 7 days.',
      read: 'Most MRs should sit in the first two bars. The right-hand number on each bar is the count.',
      watch: 'More than 3 MRs older than 7 days, or the > 7 days bar longer than the < 1 day bar: work is piling up unmerged — close or finish the old ones first.',
    },
    gitDeployFrequency: {
      what: 'How many successful deployments reached the production environment each week. A repository with no deployments is counted by release tag instead (the subtitle says so).',
      read: 'Look at the trend rather than one week; the environment name comes from the repository settings.',
      watch: 'Fewer than 1 deployment a week for 4 weeks in a row means changes wait in batches — smaller, more frequent releases are safer.',
    },
    gitLeadTime: {
      what: 'Lead time for changes: from the first commit of an MR to its estimated arrival in production (the first successful production deployment of the merge commit, otherwise the first one after the merge on the same branch).',
      read: 'Bars are the weekly median in days; the box below is the median and p85 of the 12 weeks. Only MRs already in production count.',
      watch: 'A median above 7 days, or rising 3 weeks in a row: check which of the four phases grew in the cycle time chart.',
    },
    gitUnlinkedWork: {
      what: 'Share of commits on the default branch in the last 30 days that are not linked to any task (no task code in the branch, MR or message, and no manual link).',
      read: 'Work that happens outside the plan: hotfixes, refactoring, small chores. A number to discuss with the team, not to judge anyone.',
      watch: 'Above 30%: a large part of the work does not show up in the plan or in velocity — agree to put the task code in branch names.',
    },
  },
  chart: {
    asOf: 'Viewing as of {{at}}',
    helpLabel: 'How to read this chart',
    auditedLabel: 'Views are logged',
    help: {
      what: 'What it shows',
      read: 'How to read it',
      watch: 'When to worry',
    },
    metricHelp: {
      what: 'What it is',
      read: 'How it is measured',
      watch: 'Keep in mind',
    },
    itemCount: '{{count}} tasks',
    closeDrillDown: 'Close list',
    deletedTask: 'deleted',
    drillDownEmpty: 'No tasks in this slice.',
    drillDownTruncated: '{{count}} more tasks — the list was cut short.',
    drillDown: {
      flow_status: '{{category}} tasks on {{date}}',
      completed_on: 'Tasks completed on {{date}}',
      created_on: 'Tasks created on {{date}}',
    },
    category: { todo: 'To do', in_progress: 'In progress', done: 'Done' },
  },
  storageByProject: {
    title: 'File storage by project',
    subtitle: '{{total}} in total · available files only, deleted files excluded',
    files_one: '· {{count}} file',
    files_other: '· {{count}} files',
    summary: 'File storage: {{total}} across {{n}} projects',
    expand: 'Show by project',
    collapse: 'Collapse',
  },
  myPerf2: {
    overviewTitle: 'My overview',
    projectsTitle: 'By project',
    empty: 'No numbers in this period yet',
    emptyHelp: 'Finish a task, log time or tick a checklist in a project and the numbers show up here.',
    overview: {
      overdueSub: 'my tasks due before the period end',
      utilizationSub: '{{hours}}h logged / {{scheduled}}h scheduled',
      working: 'In progress',
      workingSub: 'load this week {{pct}}%',
      workingSubOff: 'off all this week',
    },
    cols: { project: 'Project' },
  },
  periodActivity: {
    axisPeople: 'People',
    axisProjects: 'Projects',
    axisDay: 'Day',
    axisWeek: 'Week',
    teamTitle: 'Team update activity',
    mineTitle: 'My update activity',
    subtitleDay: 'updates per day',
    subtitleWeek: 'updates per week — period longer than 62 days',
    total: 'Total',
    rowTotal: 'Sum',
    cellTitle: '{{label}} · {{column}}: {{count}} updates',
    weekOf: 'week from {{date}}',
    teamEmpty: 'Nobody in the team to show',
    mineEmpty: 'You made no updates in this period',
    emptyHelp: 'Counts actions people take — moving cards, editing tasks, ticking checklists, commenting.',
  },
  teamPerf2: {
    title: 'Team performance',
    overviewTitle: 'Team overview',
    compareTo: 'vs {{range}}',
    truncated:
      'More than 5,000 tasks were done in this period — on-time and reopen rates only cover the latest 5,000.',
    views: { people: 'By person', projects: 'People × projects' },
    now: 'now',
    previousValue: 'Previous period: {{value}}',
    unchanged: 'unchanged',
    days: '{{value}} days',
    helpLabel: 'About: {{name}}',
    sortBy: 'Sort by',
    openProfile: "Open {{name}}'s profile",
    aiEstimate: 'Estimate SP with AI ({{n}} tasks)',
    aiHint:
      'AI reads the title, description and checklist of done tasks without points and suggests story points. Suggestions are not written to the task — they only show in "SP estimated". Runs with your permissions and AI budget, up to 20 tasks at a time.',
    aiQueued:
      'Queued {{n}} tasks for AI estimation ({{skipped}} not detailed enough, using the formula). Reload in a few minutes to see the new numbers.',
    aiNothing:
      'No tasks left that are detailed enough for AI ({{skipped}} not detailed enough, using the formula).',
    dueExtensionsTitle: '{{n}} extensions across {{tasks}} tasks',
    spEstimatedTitle:
      'AI: {{ai}} pts · formula: {{formula}} pts · could not estimate: {{none}} tasks',
    overview: {
      throughput: 'Tasks done',
      throughputSub: '{{n}} checklist items done',
      storyPoints: 'Story points done',
      storyPointsSub: '{{declared}} set · {{estimated}} estimated',
      onTime: 'On time',
      onTimeSub: '{{n}} tasks done late',
      overdue: 'Overdue now',
      overdueSub: '{{n}} people with overdue tasks',
      cycle: 'Cycle time',
      cycleSub: 'median of {{n}} tasks',
      reopen: 'Reopened',
      reopenSub: '{{n}} tasks reopened',
      utilization: 'Utilization',
      utilizationSub: '{{hours}}h tracked / {{scheduled}}h scheduled',
      active: 'Active people',
      activeSub: '{{n}} overloaded this week',
    },
    groups: {
      output: 'Output',
      deadline: 'Deadlines',
      quality: 'Quality',
      flow: 'Flow',
      effort: 'Effort',
    },
    cols: {
      person: 'Person',
      tasksDone: 'Tasks done',
      checklistDone: 'Checklist items',
      spDeclared: 'SP set',
      spEstimated: 'SP estimated',
      onTimePct: '% on time',
      lateDone: 'Done late',
      dueExtensions: 'Extensions',
      overdueOpen: 'Overdue',
      reopenPct: '% reopened',
      cycleDays: 'Cycle time',
      wip: 'In progress',
      hours: 'Hours tracked',
      estimateRatio: 'Actual/estimate',
    },
  },
  teamProjects: {
    title: 'People × projects',
    presets: {
      thisWeek: 'This week',
      thisMonth: 'This month',
      lastMonth: 'Last month',
      last90: '90 days',
    },
    from: 'From',
    to: 'To',
    totals: {
      hours: 'Hours tracked',
      tasksDone: 'Tasks done',
      storyPoints: 'Story points done',
      projects: 'Projects',
      people: 'People',
    },
    grouping: 'View',
    by: { people: 'By person', projects: 'By project', matrix: 'People × project allocation' },
    matrixCorner: 'Projects ↓ · People →',
    matrixHours: '{{hours}} h',
    matrixCellHours: '~{{hours}} h',
    matrixCellTitle: '{{name}} spends {{pct}}% of their time on {{project}} — ~{{hours}} h',
    matrixNote:
      'Each cell is the share of the column person’s time spent on the row project — every column adds up to 100%. Column and row headers show total tracked hours in the period.',
    matrixEmpty: 'Nobody tracked time on any project in this period.',
    person: 'Person',
    project: 'Project',
    memberProjectCount: 'Projects joined',
    memberProjectCountHint:
      'Projects this person is a member of that are planning / active / on hold — whether or not they had work in the period.',
    projectCount: 'Projects with work in period',
    projectCountHint:
      'Projects where this person tracked time, finished or holds tasks, or has a commitment in the chosen period.',
    peopleCount: 'People',
    tasksDone: 'Tasks done',
    storyPoints: 'Story points',
    level: 'Level',
    noLevel: 'No level',
    tasksOpen: 'Assigned',
    hours: 'Hours',
    thisWeek: 'Capacity this week',
    ahead: 'Next {{count}} day(s)',
    teamPct: "% of team's hours",
    doneEstimate: 'Estimate of done work',
    registeredPct: '% committed',
    actualPct: '% actual',
    expand: 'Show details for {{name}}',
    loadTitle: '{{committed}} assigned hours / {{capacity}} available hours',
    noCells: 'No project with hours, tasks or commitment in this period.',
    empty: 'Nobody in your team yet',
    emptyHelp:
      'This table lists everyone in your reporting line. An org admin assigns managers on the People page.',
  },
  teamDaily: {
    title: "Team's activity today",
    scopeOrg: 'Whole organization · {{count}} people',
    scopeDirect: 'Direct reports · {{count}} people',
    prev: 'Previous day',
    next: 'Next day',
    today: 'today',
    backToToday: 'Today',
    audited:
      'Views are written to each person\'s "Who viewed my performance profile" log (at most one entry per 30 minutes) — no notification or Telegram message is sent.',
    filter: 'Filter by name',
    member: 'Member',
    state: 'Status',
    current: 'Working on',
    minutes: '{{count}} min',
    hours: 'Hours',
    totalHours: 'Total working time',
    overtime: 'OT',
    overtimeHelp:
      "Overtime — tracked time outside that person's shifts in their work schedule (days off and holidays count entirely).",
    tasksDone: 'Tasks done',
    checklistDone: 'Checklist done',
    totalHoursShort: 'Hours',
    checklistDoneShort: 'Checklist',
    expand: "Show {{name}}'s items",
    states: { running: 'Working', paused: 'Paused', idle: 'No timer' },
    since: 'since {{time}}',
    pausedAt: 'paused at {{time}}',
    mostTime: 'Most time',
    mostProgress: 'Most progress',
    noItems: 'No item with tracked time, progress or checklist on this day.',
    item: 'Item',
    project: 'Project',
    progress: 'Progress during the day',
    checklist: 'Checklist items ticked',
    done: 'Done',
    empty: 'Nobody in your team yet',
    emptyHelp:
      'This table lists the people you manage directly. An org admin assigns managers on the People page.',
  },
  dashboard: {
    myProjectsTitle: 'Projects I lead ({{count}})',
    myProjectsHelp:
      'The same three signals as the leadership board — schedule, risk, progress — for every project you manage.',
    myProjectsMore: '{{count}} more projects — see the Projects page.',
    orgProjectsTitle: 'Projects on my sidebar ({{count}})',
    orgProjectsHelp:
      'Every project on your sidebar, in sidebar order — including projects you do not manage directly.',
    sidebarAllHidden: 'Every project here is hidden from the sidebar',
    sidebarAllHiddenHelp:
      'These projects are hidden from the sidebar. Open “All projects” to show them again.',
    myObjectivesTitle: 'S.M.A.R.T. objectives across my projects',
    myObjectivesHelp: 'Each row is a number to reach. Report values on each project’s Charter tab.',
    objectiveColumn: 'Objective',
    valueColumn: 'Current / Target',
    progressColumn: 'Progress',
    noValueYet: 'no value yet',
    tabMe: 'Mine',
    tabProject: 'Project',
    tabOrg: 'Organization',
    openCount: 'Open work',
    doneThisWeek: 'Done in 7 days',
    committed: 'Committed / capacity',
    allClear: 'Nothing needs attention right now',
    allClearHelp: 'Nothing blocked, overdue, or due today.',
    blocked: 'Blocked',
    blockedHelp: 'The action here is to unblock, not to go faster.',
    overdue: 'Overdue',
    dueToday: 'Due today',
    upcoming: 'Upcoming',
    healthTitle: 'Project health board',
    healthSubtitle: '{{count}} projects',
    healthEmpty: 'No projects yet',
    healthEmptyHelp: 'Create the first project so this board has something to show.',
    project: 'Project',
    overall: 'Overall',
    signalSchedule: 'Schedule',
    signalRisk: 'Risk',
    signalProgress: 'Progress',
    nextMilestone: 'Next milestone',
    lateUnit: 'late',
    scoreUnit: 'score',
    gapUnit: 'pp behind',
    projectHealthTitle: 'Project health',
    taskUnit: 'tasks done',
    lateTasks: '{{count}} tasks late vs baseline',
    openRisks: '{{count}} open risks',
    noMilestone: 'No milestone ahead',
    roadmapTitle: 'Portfolio roadmap',
    roadmapSubtitle: '{{from}} → {{to}}',
    roadmapOpenEnded: 'from {{start}} · no end date',
    roadmapEmpty: 'No project has start and end dates yet',
    roadmapEmptyHelp: 'The roadmap draws project dates — set them in project settings.',
    milestone: 'Milestone',
    phaseGate: 'Phase gate',
    passed: 'Passed',
    unscheduled: '{{count}} project(s) without dates, not drawn on the axis',
    attention: {
      title: 'Needs your attention',
      hint: 'click a tile to filter the portfolio or jump to its section',
      allClear: 'Nothing needs your attention right now',
      atRisk: 'projects at high risk',
      milestoneOverdue: 'milestones overdue',
      late: 'tasks late against baseline',
      riskRed: 'projects with red risk',
      objectivesLow: 'projects with objectives below 50%',
      overloadedWeeks: 'weeks where demand exceeds capacity',
      fromWeek: 'from week of {{date}}',
      unclocked: 'people not clocked in today',
      doneToday: 'tasks done today',
      idle: 'people with spare capacity (under 30%)',
      approvals: 'projects awaiting your approval',
    },
    ended: 'Ended',
    endedOn: 'Ended · {{date}}',
    pastEndBy: '{{n}} days past end date',
    timelineWindow: '90 days',
    projectEnd: 'Project end',
    noMilestonesYet: 'This project has no dated milestones yet.',
    todayMark: 'Today',
    milestoneReached: 'Reached',
    overdueBy: '{{n}} days overdue',
    endedAgo: 'Ended {{n}} days ago',
    inDays: 'In {{n}} days',
    portfolioTitle: 'Project portfolio',
    portfolioMatching: '{{shown}}/{{count}} projects match the filter',
    portfolioSort: 'Sort portfolio',
    portfolioSortBy: { risk: 'Risk first', due: 'Next milestone', sidebar: 'Sidebar order' },
    clearFilter: 'Clear filter',
    doneColumn: 'Done',
    timelineColumn: 'Timeline',
    milestoneOverdue: 'overdue',
    expandProject: 'Show objectives of {{name}}',
    objectivesNowOnly: 'Objectives only have current numbers — hidden when viewing a past date.',
    noObjectives: 'This project has no project-level objectives yet.',
    openProject: 'Open project',
    openProjectNamed: 'Open project {{name}}',
    capacityTitle: 'Capacity vs demand',
    capacityOk: '{{headcount}} people, no week over capacity',
    capacityOverloaded: '{{count}} week(s) with demand over capacity',
    capacityEmpty: 'No demand has been planned yet',
    capacityEmptyHelp: 'Needs tasks with an assignee, dates and hour estimates.',
    demand: 'Demand',
    capacity: 'Capacity',
    forwardLookingNote:
      'Capacity and allocation always show the current plan — they do not change with the date picked above.',
    allocationTitle: 'Cross-project allocation',
    allocationSubtitle: '{{count}} people over 100%',
    allocationEmpty: 'Nobody is in the range you may view',
    allocationEmptyHelp: 'You can view allocation for your direct reports.',
    allocationAudited:
      'Every view of this table is recorded, and the people in it can see that trail.',
    allocationIdleGroup: 'Spare capacity (under {{pct}}%)',
    allocationShowAll: 'Show all {{n}} people',
    allocationShowLess: 'Collapse',
    person: 'Person',
    projectCount: 'Projects',
    hours: 'Hours',
    allocationPct: 'Allocation',
    activityTitle: 'Team update activity',
    activitySubtitle: 'updates per day, last 4 weeks',
    activityEmpty: 'Nobody updated anything in this window',
    activityEmptyHelp:
      'The chart counts user-made events — moving cards, editing tasks, commenting.',
  },
  reports: {
    loadingOnScroll: 'This chart loads when you scroll to it',
    flowTitle: 'Work flow',
    flowSubtitle: 'built from {{count}} events',
    emptyTitle: 'No data in this range',
    emptyHelp: 'Create tasks and move cards across columns — the chart is built from that history.',
    throughputTitle: 'Throughput',
    throughputSubtitle: 'tasks completed per week',
    completedCount: 'Tasks done',
    noCompleted: 'Nothing completed in this range',
    noCompletedHelp: 'Move a card to the Done column — the chart reads that history.',
    drillBand: 'Drill into',
    cycleTitle: 'Cycle time & control chart',
    cycleSubtitle: 'p50 {{p50}} days · p85 {{p85}} days',
    cycleNoData: 'not enough data for percentiles',
    cycleLegend: 'Cycle time',
    leadLegend: 'Lead time',
    p85Legend: 'p85 line',
    controlAverage: 'Rolling average',
    controlUpper: 'Upper bound (2σ)',
    controlValue: 'Per task',
    agingTitle: 'Aging WIP',
    agingThreshold: 'aging threshold: {{days}} days (project p85)',
    agingNoThreshold: 'nothing completed yet, so no threshold',
    agingEmpty: 'Nothing in progress',
    agingEmptyHelp: 'Everything is done, or nothing has started yet.',
    agingMore: '{{count}} more tasks',
    ageDays: '{{days}} days',
    overThreshold: 'Over threshold',
    unassigned: 'Unassigned',
    burndownTitle: 'Burndown & scope line',
    burndownRange: 'over the selected range',
    remaining: 'Remaining',
    scope: 'Total scope',
    doneLine: 'Done',
    ideal: 'Ideal',
    pendingTitle: 'Report not ready yet',
    pending: {
      needSprint:
        'Needs at least one sprint. Velocity measures points completed **per sprint**, so a Waterfall or maintenance project will never have numbers here.',
      needPhaseGate:
        'Needs at least one task of item type **Phase gate**. Set it when creating or editing a task — any project can use it, not just Waterfall.',
      needBaseline:
        'Needs a baseline: open the Tasks tab → Gantt → **Set baseline**. Without an original plan there is nothing to measure progress against.',
    },
    velocityTitle: 'Velocity',
    asOfLabel: 'View by date',
    backToNow: 'Back to today',
    sprintFilter: 'Showing:',
    sprintFilterAll: 'Whole project',
    notBySprint: 'This group does not change with {{name}}',
    notBySprintHelp:
      'Health, open risks, the objective tree and aging work are project-level questions, or questions about right now — there is no “for one sprint” version. Pick “Whole project”, or switch to the Flow group.',
    sprintFilterHidden:
      'Flow, cycle time and aging WIP are hidden — they describe the whole project or the present, and do not split per sprint.',
    velocityAverage: 'average {{points}} points per closed sprint',
    velocityNoAverage: 'needs at least two closed sprints for an average',
    velocityEmpty: 'No sprints in this project',
    velocityEmptyHelp: 'Velocity only makes sense for Agile projects running sprints.',
    scopePoints: 'Points in sprint (now)',
    sayDoRatio: 'done / committed: {{ratio}}',
    donePoints: 'Points done',
    committedPoints: 'Points committed at start',
    evmTitle: 'S-curve & Earned Value',
    evmSubtitle: 'SPI {{spi}} · CPI {{cpi}} — unit: estimated hours',
    evmNoIndex: 'not enough data for SPI/CPI',
    evmUnitNote:
      'AC is derived from time spent in the in-progress column, not from the hours clocked in Timesheet.',
    evmUnestimated: '{{count}} task(s) without an estimate, excluded from the maths',
    evmEmpty: 'Cannot draw the S-curve yet',
    evmEmptyHelp:
      'EVM needs both estimated hours and planned dates on tasks — set them in Gantt or on the task form.',
    pv: 'Planned (PV)',
    ev: 'Earned (EV)',
    ac: 'Actual (AC)',
    slippageTitle: 'Slippage against baseline',
    slippageSubtitle: 'baseline set {{at}}',
    slippageNoBaseline: 'no baseline yet — nothing to compare against',
    slippageEmpty: 'Nothing is late against the baseline',
    slippageEmptyHelp: 'Set a baseline and this chart will track cumulative delay over time.',
    slippageDays: 'Cumulative days late',
    lateTasks: 'Late tasks',
    slippageWorst: 'Worst offenders',
    slippageStillOpen: 'still open',
    periodComparisonTitle: 'This period vs last',
    periodComparisonSubtitle: 'Compared with {{previous}}',
    periodMetric: 'Metric',
    periodCurrent: 'This period',
    periodPrevious: 'Last period',
    periodDelta: 'Change',
    periodMetricName: {
      created: 'New work received',
      completed: 'Work closed',
      openAtEnd: 'Open at period end',
      unplannedPct: 'Unplanned share',
      avgCycleDays: 'Avg. cycle time (days)',
    },
    gatesTitle: 'Phase gate status',
    gatesSubtitle: '{{passed}}/{{total}} gates passed',
    gatesEmpty: 'This project has no phase gates',
    gatesEmptyHelp: 'Change a milestone kind to "phase gate" in Gantt to track it here.',
    gatePassed: 'Passed',
    gateWaiting: 'Not passed',
    gateBlockers: '{{done}}/{{total}} conditions met',
    gateLate: '{{days}} days late',
    gateEarly: '{{days}} days early',
    riskMatrixTitle: 'Risk matrix 5×5',
    riskMatrixSubtitle: '{{count}} open risks · total score {{exposure}}',
    riskUnscored: '{{count}} open risk(s) without a score, not on the matrix',
    riskProbability: 'Probability →',
    riskImpact: 'Impact →',
    riskMatrixEmpty: 'No open risks',
    riskMatrixEmptyHelp: 'Log risks in the RAID log — each scored risk shows up here.',
    riskBurndownTitle: 'Risk burndown',
    riskBurndownSubtitle: 'total open risk score over time',
    riskExposure: 'Open exposure',
    riskRetired: 'Retired',
    estimateTitle: 'Estimate accuracy',
    estimateSubtitle: 'median {{ratio}}× the estimate',
    estimateNoData: 'no completed task with an estimate yet',
    estimateEmpty: 'No estimate-versus-actual data yet',
    estimateEmptyHelp:
      'Needs tasks that are done AND estimated — the chart measures exactly those two numbers.',
    estimateHours: 'Estimated hours',
    actualHours: 'Actual hours',
    estimatePerfect: 'On estimate',
    estimateBlocked: 'Was blocked',
    estimateBlockedNote: 'Hollow dots were blocked at some point — lateness there may be external.',
    estimateByAssignee: 'By person',
    estimateByType: 'By task type',
    estimateMedian: '{{ratio}}× ({{count}} tasks)',
    objectivesTitle: 'Which tasks serve which objective',
    objectivesSubtitle: '{{orphans}} objective(s) with no task serving them',
    objectivesAllServed: 'every objective has at least one task',
    objectivesEmpty: 'This project has no objectives',
    objectivesEmptyHelp: 'Set objectives and link tasks to see what serves what.',
    objectiveOrphan: 'No tasks yet',
    objectivesUnlinked: '{{count}} task(s) serve no objective',
    crTrendTitle: 'Change request trend',
    crTrendSubtitle: '{{count}} CRs in the window',
    crTrendEmpty: 'No change requests in this window',
    crTrendEmptyHelp:
      'A rising CR count is an early scope-creep signal — this chart is how you see it early.',
    crCreated: 'Requested',
    crApproved: 'Approved',
    crRejected: 'Rejected',
    crApplied: 'Applied',
    documentsTitle: 'Document status',
    documentsSubtitle: '{{count}} documents',
    documentsEmpty: 'This project has no documents',
    documentsEmptyHelp: 'Create one from a template on the Documents screen.',
    docDraft: 'Not submitted',
    docReview: 'In review',
    docApproved: 'Approved',
    docStale: 'Waiting over {{days}} days',
    docWaiting: '{{days}} days',
    resourceTitle: 'Resource allocation per week',
    resourceSubtitle: 'committed hours versus capacity — across all projects',
    resourceEmpty: 'Nobody is assigned dated work yet',
    resourceEmptyHelp: 'Needs tasks with an assignee, start date, due date and hour estimate.',
    resourceCommitted: 'Committed',
    resourceCapacity: 'Capacity',
    resourceOverloaded: 'Over capacity',
    resourceNote:
      'This chart looks forward from the current plan, so it does not follow the as-of cursor.',
    tabMaintenance: 'Operations & maintenance',
    tabIssues: 'Issues',
    unplannedTitle: 'Unplanned work ratio',
    unplannedSubtitle: 'Work created close to when it must start (within {{days}} day(s))',
    unplannedTooltip: '{{percent}}% — {{unplanned}}/{{counted}} items',
    unplannedNoData: 'No measurable work this week',
    unplannedNote:
      'Only counts work that has a start date or a due date. Without either there is no way to tell how much notice there was, so it stays out of both the numerator and the denominator.',
    unplannedEmpty: 'Nothing to measure yet',
    unplannedEmptyHelp:
      'Lead time needs a start or due date. Add dates to a few items and come back.',
    tabSummary: 'Project health',
    tabFlow: 'Flow',
    tabSchedule: 'Plan & schedule',
    tabGovernance: 'Risk & governance',
    summaryHelp:
      'Numbers that answer project-level questions. Charts that only mean something over a short window live in the other groups.',
    groupEmpty: 'Nothing to show in this group yet',
    groupEmptyShort: 'This project has no data for that group',
    groupEmptyReason: {
      no_issues:
        'No user-reported issues yet. That is good news — this group fills in as soon as the first issue is recorded.',
      no_end_date:
        'A maintenance project has no end date, so charts measuring progress against one (EVM, slippage, phase gates) do not apply. See the "Operations & maintenance" group.',
      not_maintenance: 'This group is only for maintenance projects.',
      no_tasks:
        'The project has no tasks yet, so there is no flow to measure. Create a few on the Board tab and come back.',
      no_baseline:
        'No task has a baseline and there are no phase gates. Set a baseline on the Coordination tab to measure plan adherence.',
      no_governance_data:
        'No risks, objectives, change requests or documents yet. For a small project that may be perfectly normal.',
    },
    asOfHint:
      "Pick a past date to see the data as it looked back then — this doesn't change current data. Leave it empty to return to today.",
  },
  // Change milestone / change deadline buttons on the Changes tab — APM-DL-b.
  deadlineChange: {
    milestoneButton: 'Move milestone',
    deadlineButton: 'Change deadline',
    milestoneTitle: 'Move a milestone',
    deadlineTitle: 'Change the project or sprint deadline',
    notifyHint: 'Once saved, every project member is notified of the new date.',
    milestone: 'Milestone',
    target: 'Deadline of',
    projectTarget: 'Project «{{name}}»',
    sprintTarget: 'Sprint «{{name}}»',
    currentDate: 'Current deadline: {{date}}',
    noDate: 'not set',
    newDate: 'New date',
    noMilestones: 'This project has no milestones yet',
    noMilestonesHelp:
      'Turn a task into a milestone in the task form or on the Gantt, then come back.',
    noTargets: 'No deadline to change yet',
    noTargetsHelp: 'Maintenance projects have no deadline, and this one has no open sprint.',
    milestoneSaved: 'Milestone «{{name}}» moved to {{date}}',
    deadlineSaved: 'Deadline of {{name}} changed to {{date}}',
    rebalanceHint:
      'The team has been notified. Review the tasks to rebalance them for the new date.',
    rebalance: 'Rebalance tasks',
  },
  changes: {
    absenceTitle: 'Absences clashing with work',
    absenceHelp:
      'Project members are away while work is assigned to them. The plan is about to slip without anyone changing it.',
    title: 'Change requests',
    new: 'New request',
    create: 'Create request',
    createHint: 'Both the content and the reason are required — the approver needs both.',
    crTitle: 'Title',
    titlePlaceholder: 'What changes, in one line',
    kindLabel: 'Change type',
    description: 'What changes',
    descriptionPlaceholder: 'Specifically: scope, dates, people, budget…',
    reason: 'Reason',
    reasonPlaceholder: 'Why is this needed? In six months this is the only answer left.',
    historyTitle: 'Change history',
    historyHelp:
      'Every recorded change in this project, newest first — including changes that did not go through a change request.',
    affectedTasks: 'Related tasks (hold Ctrl to pick several)',
    empty: 'No change requests yet',
    emptyHelp: 'Once the baseline is set, plan changes should go through here.',
    impact: 'Impact',
    impactTotals: '{{count}} tasks · {{hours}} hours · {{people}} people',
    impactTruncated: 'More tasks are affected — the list was cut short.',
    noTasksLinked: 'This request has no linked tasks, so there is nothing to trace yet.',
    direct: 'Named',
    downstream: 'knock-on ({{distance}} steps away)',
    critical: 'Critical path',
    analyse: 'Ask AI to review',
    analysePrompt: 'Review the impact of this change request for me.',
    note: 'Note',
    notePlaceholder: 'Note for the approver, or reason for rejecting…',
    decisionNote: 'Decision note: {{note}}',
    versionLabel: 'Version label',
    labelPlaceholder: 'Label for the new version, e.g. "UAT moved"',
    submit: 'Submit for approval',
    approve: 'Approve',
    reject: 'Reject',
    apply: 'Apply & re-baseline',
    applied: 'Applied — the plan was re-baselined as v{{no}}',
    appliedAs: 'Applied — created v{{no}}',
    versions: 'Plan versions',
    originalPlan: 'Original plan',
    compare: 'Compare',
    compareFrom: 'Older version',
    compareTo: 'Newer version',
    compareOrder: 'Pick the older version first.',
    diffHeader: 'Differences between v{{from}} and v{{to}} — built from {{events}} events',
    diffEmpty: 'These two versions are identical.',
    diffTruncated: 'More changes exist — the list was cut short.',
    kind: {
      scope: 'Scope',
      schedule: 'Schedule',
      resource: 'People',
      budget: 'Budget',
      other: 'Other',
    },
    status: {
      draft: 'Draft',
      submitted: 'Awaiting approval',
      approved: 'Approved',
      rejected: 'Rejected',
      applied: 'Applied',
    },
    change: { added: 'added', removed: 'removed', changed: 'changed' },
    dependency: { added: 'dependency added', removed: 'dependency removed' },
    field: {
      title: 'Title',
      startDate: 'Start',
      dueDate: 'Due',
      assignee: 'Assignee',
      estimateHours: 'Estimate',
      status: 'Status',
    },
  },
  documents: {
    new: 'New document',
    backToList: 'Document list',
    reorder: 'Reorder',
    reorderLabel: 'Reorder documents',
    reorderTitle: 'Drag the ⠿ handle to change the order, then click Save order.',
    reorderFiltered: 'Clear the search and filters to reorder the whole list',
    reorderSave: 'Save order',
    reorderSaved: 'Document order saved',
    dragHandle: 'Drag to move “{{title}}”',
    formatContent: 'Content written in the app (Markdown)',
    create: 'Create document',
    createFromFile: 'Create from uploaded file',
    createHint: 'Pick a template, or type the content.',
    title: 'Title',
    titlePlaceholder: 'Document name, e.g. "Payment requirements"',
    template: 'Template',
    kindSelect: 'Document type',
    unclassified: 'Unclassified',
    useTemplateNamed: 'Prefill from the “{{name}}” template',
    useTemplate: 'Prefill from a template',
    pickTemplate: 'Pick a template…',
    content: 'Content',
    contentPlaceholder: 'Markdown content. Leave empty when starting from a template.',
    search: 'Search documents',
    searchPlaceholder: 'Search… (accents optional)',
    filterKind: 'Filter by type',
    filterStatus: 'Filter by status',
    allKinds: 'All types',
    allStatuses: 'All statuses',
    empty: 'No documents yet',
    emptyHelp: 'Create the first one from a built-in template — SRS, design, test plan.',
    noMatch: 'No documents match “{{query}}”',
    noMatchHelp: 'Try fewer words. Search matches with or without Vietnamese accents.',
    versionCount: '{{count}} versions',
    matchedIn: 'matched in v{{no}}',
    approvedVersion: 'approved v{{no}}',
    approvedHint: 'Approved. Adding a new version sends the document back to draft.',
    history: 'Version history',
    noChangeNote: '(no change note)',
    unknownAuthor: 'Unknown',
    fileOnly: 'This version is an uploaded file, with no in-app content.',
    extraction: {
      pending: 'reading content…',
      skipped: 'content not read',
      failed: 'could not read content',
    },
    newVersion: 'New version',
    newVersionHelp:
      'Open in editor to keep writing the latest version in the app — every Save is a version carrying the note above. Or upload a file as the new version.',
    newVersionUploadOnly:
      'This format can’t be edited in the app — upload the edited file as the new version.',
    versionStatus: { superseded: 'Superseded' },
    versionAdded: 'Saved as v{{no}}',
    changeNote: 'What changed since the previous version?',
    changeNotePlaceholder: 'What changed since the last version?',
    uploadVersion: 'Upload new version',
    uploading: 'Uploading…',
    note: 'Review note',
    notePlaceholder: 'Note for the reviewer, or reason for sending it back…',
    submit: 'Submit for review',
    approve: 'Approve',
    reject: 'Send back to draft',
    delete: 'Delete document',
    deleteTitle: 'Delete “{{title}}”?',
    deleteBody:
      'The document and all its versions leave the library. You can bring it back from the project’s Changes tab.',
    deleteConfirm: 'Delete',
    deleted: 'Deleted “{{title}}” — restore it from the Changes tab.',
    // Rename a document — APM-DREN.
    rename: 'Rename',
    renameTitle: 'Rename “{{title}}”',
    renameLabel: 'New name',
    renameHint: 'Only the name shown in the project changes — uploaded files keep theirs.',
    renameEmpty: 'The name can’t be empty.',
    renameTooLong: 'The name can be at most {{max}} characters.',
    renamed: 'Renamed to “{{title}}”',
    quickApprove: 'Quick approve “{{title}}”',
    quickApproved: 'Approved “{{title}}”.',
    restore: 'Restore',
    restored: 'Restored “{{title}}”.',
    openEditor: 'Open in editor',
    preview: {
      open: 'Preview “{{title}}”',
      edit: 'Edit',
      editUnsupported: 'This format cannot be edited in the app yet — download it to edit.',
      viewInFiles: 'View in Files',
      viewInFilesOthers: 'Someone else uploaded this file, so it is not in your Files.',
      maximize: 'Maximize',
      restore: 'Restore size',
      download: 'Download',
      details: 'Versions & review',
      back: 'Preview',
      empty: 'This document has no content yet.',
    },
    share: {
      button: 'Share outside — not shared',
      buttonShared: 'Share outside — shared with: {{audience}}',
      badgeTitle: 'Shared with: {{audience}} · {{version}}',
      title: 'Share document',
      viewOnly: 'Recipients can only view, not edit. Comments in the document are never shared.',
      notShared: 'Not shared outside the project',
      managersOnly: 'Only people who can approve documents can change sharing.',
      audienceLabel: 'Who can view',
      audience: {
        org: 'Within the organization',
        chillpm: 'Anyone with a ChillPM account',
        public: 'Anyone with the link',
      },
      audienceShort: { org: 'Organization', chillpm: 'Has account', public: 'Anyone' },
      audienceHelp: {
        org: 'Everyone in your organization, including outside the project — sign-in required.',
        chillpm: 'People outside your organization can view too, as long as they sign in with ChillPM.',
        public: 'No sign-in — anyone holding the link can view.',
      },
      versionLabel: 'Shared version',
      versionMode: {
        version: 'A specific version',
        latest_approved: 'Latest approved version',
        latest: 'Latest version',
        all: 'All versions',
      },
      versionModeHelp: {
        version: 'Recipients always see this version, even when newer ones exist.',
        latest_approved: 'Drafts stay hidden until approved.',
        latest: 'Including drafts — recipients see every save right away.',
        all: 'Recipients can browse every version via Versions & approval.',
      },
      noApprovedYet: 'No version has been approved yet — recipients will see nothing.',
      versionPinned: 'Version v{{no}}',
      pickVersion: 'Pick a version',
      versionOption: 'v{{no}} · {{date}}',
      link: 'Share link',
      copy: 'Copy',
      copied: 'Share link copied',
      copyFailed: 'Could not copy — select the link and copy it manually.',
      opens: '{{count}} opens',
      enable: 'Share',
      save: 'Save changes',
      saved: 'Sharing updated',
      revoke: 'Stop sharing',
      revokeConfirm: 'Stop sharing? The current link will stop working; sharing again creates a new link.',
      revoked: 'Sharing stopped',
      pending: '{{count}} versions are waiting for a shareable copy (without comments).',
      preparing: 'Preparing {{count}} versions for recipients…',
      preparingFailed: 'Could not prepare {{count}} versions — recipients see "being prepared".',
      retry: 'Retry',
    },
    status: { draft: 'Not submitted', review: 'In review', approved: 'Approved' },
    kind: {
      srs: 'Requirements',
      design: 'Design',
      test_plan: 'Test plan',
      meeting_minutes: 'Meeting minutes',
      contract: 'Contract',
      other: 'Other',
    },
  },
  issueTracking: {
    add: 'Report an issue',
    created: 'Issue recorded',
    overridden: '(priority adjusted)',
    noCommitment: 'No commitment',
    unassigned: 'Unassigned',
    kind: { incident: 'Incident', request: 'Service request', problem: 'Problem' },
    state: {
      new: 'New',
      triaged: 'Triaged',
      in_progress: 'In progress',
      waiting_customer: 'Waiting on reporter',
      waiting_third_party: 'Waiting on third party',
      resolved: 'Resolved',
      reopened: 'Reopened',
      closed: 'Closed',
    },
    priority: { 1: 'P1 · Critical', 2: 'P2 · High', 3: 'P3 · Medium', 4: 'P4 · Low' },
    channel: {
      portal: 'Customer portal',
      email: 'Email',
      phone: 'Phone',
      chat: 'Chat',
      monitoring: 'Monitoring',
      internal: 'Recorded internally',
    },
    impactScale: { 1: 'One user', 2: 'A team or department', 3: 'All users' },
    urgencyScale: {
      1: 'Can wait',
      2: 'Blocking, workaround exists',
      3: 'Work has stopped',
    },
    waitReason: {
      need_info: 'Waiting for information',
      need_approval: 'Waiting for approval',
      customer_testing: 'Reporter is verifying',
      vendor_fix: 'Waiting on vendor',
      scheduled_window: 'Waiting for maintenance window',
      other: 'Other reason',
    },
    maintenanceCategory: {
      corrective: 'Corrective',
      adaptive: 'Adaptive',
      perfective: 'Perfective',
      preventive: 'Preventive',
    },
    reports: {
      hourSuffix: 'h',
      noData: 'No issues recorded in this period',
      noClosed: 'No issues closed in this period',
      noOpen: 'No issues are currently open',
      noHours: 'No handling time has been computed yet',
      arrival: {
        title: 'Arrivals vs closures',
        subtitle: 'How many issues arrive each week and how many get closed',
        created: 'Recorded',
        closed: 'Closed',
      },
      cycle: {
        title: 'Resolution time by priority',
        subtitle: 'Percentiles, not averages',
        samples: 'Samples',
        p50: 'Half done within',
        p85: '85% done within',
      },
      sla: {
        title: 'Commitment attainment',
        subtitle: 'Counts only issues that **have** a commitment',
        response: 'Response on time',
        resolution: 'Resolution on time',
      },
      aging: {
        title: 'Age of open issues',
        subtitle: 'As of {{at}}',
        upTo: 'up to {{days}} days',
        older: 'older',
        count: 'Open',
        overdue: 'Overdue',
      },
      maintenance: {
        title: 'Where maintenance effort goes',
        subtitle: 'By attributed hours, not by issue count',
        category: 'Category',
        count: 'Issues',
        hours: 'Hours',
        unclassified: 'Unclassified',
      },
    },
    filter: { label: 'Filter issues', open: 'Open', all: 'All' },
    column: {
      code: 'ID',
      title: 'Summary',
      priority: 'Priority',
      state: 'Status',
      due: 'Resolution due',
      assignee: 'Assignee',
    },
    empty: {
      title: 'No issues recorded yet',
      description:
        'Each incident or request you record here gets a resolution target based on its priority, and feeds the project operations report.',
      action: 'Report the first issue',
    },
    form: {
      kind: 'Type',
      title: 'What happened?',
      description: 'Description',
      steps: 'Steps to reproduce',
      stepsHint: 'What triggers it? Nobody can fix what they cannot reproduce.',
      impact: 'How many people are affected?',
      impactHint: 'Count the people affected, not how annoying it is.',
      urgency: 'How urgent is it?',
      urgencyHint: 'Is there a workaround that keeps people working?',
      resulting: 'Priority:',
      resultingHint: 'Derived from the two answers above.',
      channel: 'Reported via',
      reportedBy: 'Reported by',
      reportedByHint: 'Name of the person who hit the problem',
      submit: 'Record',
    },
    // Issue detail popup in the Issues tab — APM-ISC-d.
    detail: {
      label: 'Issue details',
      open: 'Open details of {{code}}',
      reporter: 'Reporter',
      internalNote: 'Internal note',
      none: 'None yet.',
      threads: 'Conversation threads',
      tabPublic: 'With the reporter',
      tabInternal: 'Internal',
    },
    // Conversation threads — APM-ISC-d/-e. Append-only: sent messages cannot be edited or deleted.
    thread: {
      empty: 'No messages yet.',
      emptyInternal: 'No internal notes yet.',
      internalBanner: 'Visible to the handling team only — the reporter cannot read this thread.',
      labelPublic: 'Reply to the reporter',
      labelInternal: 'Note for the team',
      placeholderPublic: 'Reply to the reporter… (cannot be edited once sent)',
      placeholderInternal: 'Note for the team… (hidden from the reporter)',
      labelReporter: 'Message the handling team',
      placeholderReporter: 'Message the handling team… (cannot be edited once sent)',
      send: 'Send reply',
      sendInternal: 'Send internally',
      closedHint: 'This issue is closed — reopen it to continue the conversation.',
    },
  },
  raid: {
    all: 'All',
    add: 'Add item',
    typeLabel: 'Kind',
    addRisk: 'Add the first risk',
    save: 'Save',
    empty: 'The RAID log is empty',
    emptyHelp:
      'Track risks, actions, issues and decisions here. When a risk actually happens, escalate it into an issue in one click.',
    created: 'Added {{code}}',
    escalate: 'Escalate',
    escalateHint: 'The risk has materialised — create an issue linked back to it',
    escalated: '{{risk}} escalated into {{issue}}',
    fromRisk: 'from {{code}}',
    becameIssue: '→ {{code}}',
    frozenHint: 'An escalated risk is kept as history. Edit the issue it became instead.',
    col: {
      no: 'ID',
      category: 'Category',
      description: 'Description',
      priority: 'Priority',
      nextActions: 'Next actions',
      owner: 'Owner',
      lastUpdate: 'Updated',
    },
    /** Bốn mức của cột "Mức độ" trên bảng RAID log — khác `severity` (chuỗi
     *  mô tả mức nghiêm trọng 1–5 của một hạng mục đơn lẻ). */
    severityLevel: {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      none: '—',
    },
    linksTitle: 'Related work',
    noLinks: 'Not linked to any task yet.',
    linkTask: 'Pick a task',
    linkTaskPlaceholder: '— pick a task —',
    linkSearch: 'Search tasks',
    linkNoMatch: 'No task matches.',
    linkMore: '+{{count}} more — type to filter',
    wholeProject: 'Whole project',
    relationLabel: 'Relation',
    addLink: 'Link',
    linkAdded: 'Linked to task',
    unlink: 'Unlink {{key}}',
    relation: {
      affects: 'affects',
      blocks: 'blocks',
      mitigates: 'handled by',
    },
    highRisk: '{{count}} high risks',
    score: 'Score {{score}}',
    severity: 'Severity {{value}}/5',
    noOwner: 'Unassigned',
    chooseOwner: 'Pick someone…',
    remove: 'Delete {{code}}',
    detailTitle: '{{code}} details',
    toggleDetail: 'Open details of {{code}}',
    formHint: 'Fill in the required fields to save.',
    mitigationPlaceholder: 'Mitigation plan / how it is being handled…',
    rationalePlaceholder: 'Reasoning behind the decision…',
    signalsTitle: 'Risk signals',
    signalsHelp:
      'Detected automatically from the schedule, workload, estimates and task descriptions.',
    signalCount: '{{count}} signals',
    noSignals: 'No signals detected yet. Either there is little data, or things look fine.',
    fromSignal: 'Create risk',
    scan: 'Ask AI to analyse',
    scanPrompt: 'Scan this project for risks based on the signals the system detected.',
    scanApply: 'Open the suggestion list to review',
    scanDone: 'AI suggested {{count}} risks, waiting for your review',
    scanEmpty: 'AI found no new risks worth logging',
    unavailableSources: 'Not detected yet: {{list}}',
    pendingSuggestions: '{{count}} suggestions to review',
    suggestionTitle: 'AI suggested {{count}} risks',
    suggestionOutdated: 'This suggestion came from an older version — run the scan again.',
    toolsUsed: 'read: {{list}}',
    evidence: 'Evidence: {{text}}',
    selectRisk: 'Select risk {{title}}',
    acceptSelected: 'Approve {{count}} selected risks',
    accepted: 'Added {{codes}} to the RAID log',
    dismiss: 'Dismiss',
    dismissReason: 'Reason for dismissing',
    dismissReasonPlaceholder: 'Why is this suggestion not usable? (required)',
    signalSource: {
      critical_path_no_buffer: 'Critical path',
      dependency_pileup: 'Dependency pile-up',
      workload_overload: 'Overload',
      single_point_of_failure: 'Single owner',
      estimate_below_history: 'Low estimate',
      vague_description: 'Vague description',
      cr_frequency: 'Change frequency',
      cross_project_memory: 'Other projects',
      assignee_unavailable: 'Assignee away',
    },
    signal: {
      critical_path_no_buffer:
        '{{taskKey}} is on the critical path with only {{floatDays}} days of float',
      dependency_pileup: '{{predecessorCount}} tasks must finish before {{taskKey}}',
      workload_overload:
        '{{subject}} is over capacity on {{overloadedDays}} days in the next {{horizonDays}}, peaking at {{peakHours}} hours/day',
      single_point_of_failure: 'Only 1 of {{memberCount}} members claims {{subject}} skills',
      estimate_below_history:
        '{{taskKey}} is estimated at {{estimateHours}} hours, while similar past work had a median of {{medianHours}} hours ({{samples}} samples)',
      vague_description: '{{taskKey}} has an unsettled description or an external dependency',
      cr_frequency: 'Many change requests in a short period',
      cross_project_memory: 'Similar work caused problems in another project',
      assignee_unavailable:
        '{{subject}} is away {{lostHours}}h during {{taskKey}}; {{availableHours}}h left, needs {{extraDays}} more days (float {{totalFloat}} days)',
    },
    type: {
      RISK: 'Risk',
      ASSUMPTION: 'Assumption',
      ACTION: 'Action',
      ISSUE: 'Issue',
      DECISION: 'Decision',
    },
    titlePlaceholder: {
      RISK: 'What could go wrong?',
      ASSUMPTION: 'What are we taking as true?',
      ACTION: 'What needs doing, by whom?',
      ISSUE: 'What has gone wrong?',
      DECISION: 'What was decided?',
    },
    status: {
      open: 'Open',
      in_progress: 'In progress',
      mitigated: 'Mitigated',
      resolved: 'Resolved',
      escalated: 'Escalated',
      recorded: 'Recorded',
      closed: 'Closed',
    },
    scale: {
      1: 'Very low',
      2: 'Low',
      3: 'Medium',
      4: 'High',
      5: 'Very high',
    },
    field: {
      title: 'Content',
      status: 'Status',
      owner: 'Owner',
      decider: 'Decided by',
      probability: 'Probability',
      impact: 'Impact',
      severity: 'Severity',
      dueDate: 'Due date',
      reviewDate: 'Review date',
      decidedAt: 'Decision date',
      mitigation: 'Mitigation plan',
      rationale: 'Reasoning',
      affectedScope: 'Affected scope',
    },
  },
  objectives: {
    sprintTitle: 'Sprint objectives (S.M.A.R.T.)',
    title: 'Project objectives (S.M.A.R.T.)',
    new: 'New objective',
    create: 'Create',
    objectiveTitle: 'Objective title',
    titlePlaceholder: 'Objective name, e.g. "More dealers ordering through the app"',
    empty: 'No objectives set yet',
    emptyHelp:
      'Each objective is a name and a number to reach. The PM reports the number regularly so the team can see how far off the goal is.',
    targetValue: 'Target value',
    newValue: 'New value',
    checkIn: 'Report',
    checkInNote: 'Reason for the change',
    checkInNotePlaceholder: 'Why did the number move? (someone will ask in three months)',
    history: 'History',
    noHistory: 'No check-ins yet.',
    overdue: 'Overdue',
    targetPlaceholder: 'Target, e.g. 50 dealers, 95%',
    targetInvalid: 'The target must start with a number, e.g. "50 dealers".',
    target: 'Target',
    current: 'Current',
    noValueYet: 'no value yet',
    noTarget: 'This objective has no target value yet.',
    reportTitle: 'Report: {{name}}',
    firstReportHelp:
      'This is the first report: this number becomes the starting point, so progress is measured from here.',
    reportSaved: 'Value reported',
    remove: 'Delete objective {{name}}',
    progressLabel: 'Progress: {{name}}',
  },
  plan: {
    chatTitle: 'Discuss the plan',
    chatHelp:
      'AI reads milestones, schedule and project documents first — it will say so if a milestone slips.',
    chatEmpty: 'Nothing asked yet. Try: “Can we push everything to next month?”',
    chatPlaceholder: 'Ask, or request a change to the plan…',
    chatLocked: 'This draft is already settled and can no longer be changed.',
    chatSend: 'Send',
    pickDocuments: 'Pick documents for AI to read',
    pickedDocuments: '{{count}} documents picked',
    startOffset: 'Starts after (days)',
    durationDays: 'Duration (days)',
    dialogTitle: 'Draft plans',
    dialogHelp:
      'AI proposes, you approve line by line. An unapproved draft touches nothing in the real plan.',
    openDrafts: 'Auto-plan',
    newDraft: 'New draft',
    backToList: 'Back to drafts',
    draftSummary: '{{count}} tasks · {{hours}}h',
    status: {
      clarifying: 'Waiting for your answers',
      pending: 'Pending review',
      applied: 'Applied',
      discarded: 'Discarded',
    },
    step: {
      describe: 'Describe',
      context: 'Context',
      clarify: 'Clarify',
      review: 'Review',
    },
    next: 'Continue',
    back: 'Back',
    optional: 'Optional',
    deadline: 'Must finish by',
    constraints: 'Constraints',
    constraintsPlaceholder:
      'e.g. only 2 backend engineers, no downtime allowed, must reuse the old payment system…',
    contextHint: 'You can leave these empty — the AI will ask about what it needs.',
    assign: {
      title: 'Pick who does what',
      help: 'The AI reads declared skills, the hours each person can still give this project (after leave and their committed percentage) and their open work, then proposes who takes which item.',
      run: 'Suggest assignees',
      noteLabel: 'Extra constraints (optional)',
      notePlaceholder: 'e.g. skip Minh, he hands over next week…',
      defaultPrompt: 'No extra constraints — go by skills and remaining hours.',
      unassigned: 'Could not assign',
      noneAssigned: 'Nothing could be assigned — see the reasons below.',
      apply_one: 'Apply {{count}} suggestion to the draft',
      apply_other: 'Apply {{count}} suggestions to the draft',
      applied: 'Suggested assignees written to the draft.',
    },
    clarify: {
      title: 'The AI needs {{count}} things clarified',
      help: 'Each question comes pre-filled with a recommended answer. Edit what looks wrong, or clear it to let the AI state its own assumption.',
      why: 'Why it asks: {{text}}',
      recommended: 'Recommended: {{text}}',
      useRecommended: 'Use the recommendation',
      useAll: 'Use all recommendations',
      recommendedBadge: 'Recommended',
      other: 'Something else…',
      optionsFor: 'Options for: {{question}}',
      answerPlaceholder: 'Your answer…',
      skip: 'Skip this one',
      skipped: 'Left empty — the AI will pick an assumption and spell it out in its rationale.',
      submit: 'Continue generating',
      submitting: 'Generating…',
      progress: '{{answered}}/{{total}} answered',
    },
    generateTitle: 'Generate a plan from a description',
    description: 'Project description',
    descriptionPlaceholder: 'Describe the project: goals, scope, time constraints…',
    needLongerDescription: 'Description needs at least 20 characters.',
    template: 'Template',
    noTemplate: 'No template',
    generate: 'Generate plan',
    noDraft: 'No draft plans yet',
    noDraftHelp:
      'Describe the project above, walk through “Describe” and “Context”, or create tasks in the Backlog tab.',
    draftTitle: 'Draft plan — {{count}} items',
    totalHours: '{{hours}} hours total',
    chooseDraft: 'Choose draft',
    itemTitle: 'Item title',
    newItemTitle: 'New item',
    addItem: 'Add item',
    adjustHours: 'Adjust estimate by {{step}} hours',
    hours: '{{hours}}h',
    assignee: 'Assignee',
    noAssignee: 'Unassigned',
    exclude: 'Exclude from apply',
    restore: 'Put back in the plan',
    split: 'Split {{title}}',
    remove: 'Remove {{title}}',
    expand: 'Show subtasks',
    collapse: 'Hide subtasks',
    noRationale: 'No estimate rationale',
    after: 'after: {{names}}',
    previewTitle: 'Gantt preview',
    refine: 'Refine',
    refining: 'Refining…',
    draftReady: '{{count}} items, waiting for your review',
    clarifyPending: 'The AI needs a few things clarified first',
    refineLabel: 'Refine the plan',
    refinePlaceholder: 'e.g. split the first item, or drop the reporting part',
    startDate: 'Start date',
    apply: 'Apply to project',
    applied: 'Created {{count}} tasks from the plan.',
    discard: 'Discard draft',
  },
  replay: {
    noHistory: 'This project has no events to replay yet.',
    playOn: 'Play mode',
    playOff: 'Leave play mode',
    play: 'Play',
    pause: 'Pause',
    stop: 'Back to start',
    stepForward: 'Step forward',
    stepBack: 'Step back',
    nextMarker: 'Next key event',
    prevMarker: 'Previous key event',
    speed: 'Speed',
    speedValue: '1 second = {{value}}',
    speedMinutes: '{{minutes}} min',
    speedHours: '{{hours}} hours',
    speedDays: '{{days}} days',
    mode: { time: 'By time', event: 'By event' },
    skipDeadTime: 'Skip dead time',
    stopAtMarkers: 'Stop at key events',
    loop: 'Loop',
    stoppedAtMarker: 'Stopped at a key event',
    filterAssignee: 'Person',
    everyone: 'Whole team',
    filterSprint: 'Sprint',
    allSprints: 'All sprints',
    burndownTitle: 'Remaining hours, drawn as the cursor moves',
    chartCaption: '{{hours}} hours left at this moment',
    chartLabel: 'Remaining hours chart, {{hours}} hours left',
    chartTooShort: 'This segment is too short to draw a line.',
    feedTitle: 'Live events',
    feedEmpty: 'No events at this moment yet. Press Play to start.',
    systemActor: 'System',
    feed: {
      taskCreated: 'created “{{title}}”',
      statusChanged: 'moved “{{title}}” to {{status}}',
      progressUpdated: 'set “{{title}}” progress to {{value}}%',
      assigned: 'changed the assignee of “{{title}}”',
      datesChanged: 'rescheduled “{{title}}”',
      estimateChanged: 'changed the estimate of “{{title}}”',
      storyPointsChanged: 'changed the story points of “{{title}}”',
      priorityChanged: 'changed the priority of “{{title}}”',
      blocked: 'blocked “{{title}}”',
      unblocked: 'unblocked “{{title}}”',
      taskDeleted: 'deleted “{{title}}”',
      sprintStarted: 'started a sprint',
      sprintClosed: 'closed a sprint',
      scopeChanged: 'changed sprint scope',
      raidCreated: 'added RAID “{{title}}”',
      raidStatusChanged: 'moved a RAID item to {{status}}',
      documentCreated: 'added document “{{title}}”',
      documentStatusChanged: 'moved a document to {{status}}',
      crApproved: 'approved a CR',
      crCreated: 'created CR “{{title}}”',
      baselineSet: 'set the baseline',
      phaseGate: 'passed the {{phase}} gate',
      dependencyAdded: 'added a dependency',
      dependencyRemoved: 'removed a dependency',
      taskUpdated: 'edited “{{title}}”',
      becameMilestone: 'made “{{title}}” a milestone',
      becameTask: 'turned “{{title}}” back into a task',
      statusCreated: 'created column “{{name}}”',
      memberAdded: 'added a project member',
      objectiveCreated: 'created objective “{{title}}”',
      keyResultUpdated: 'checked in a key result: {{value}}',
    },
    narrationTitle: 'Narration',
    narrate: 'Ask AI to narrate',
    narrationLoading: 'Loading the narration…',
    narrationEmpty: 'No narration yet. The event list next to this still reads fine.',
    narrationGap: 'No narration for this moment.',
    narrationApply: 'Show subtitles on the scrubber',
    narrationDone: 'Narration ready. Replaying costs nothing more.',
    narrationCost: 'Generated {{at}} · ${{cost}} · replaying costs nothing more',
    inflectionAt: 'Turning point {{at}}:',
    quickTour: 'Moments worth revisiting',
    scrubberTitle: 'Timeline',
    eventCount: '{{count}} events',
    bucket: { day: 'by day', week: 'by week' },
    densityLabel: 'Event density over time, busiest bucket {{peak}} events',
    jumpTo: 'Jump to {{label}}',
    marker: {
      sprint_started: 'Sprint started',
      sprint_closed: 'Sprint closed',
      cr_approved: 'CR approved',
      risk_high: 'High risk',
      milestone_set: 'Milestone',
      baseline: 'Baseline',
      phase_gate: 'Phase gate',
      due_pushed: 'First due-date slip',
    },
    view: {
      board: 'Board',
      gantt: 'Gantt',
      workload: 'Workload',
      progress: 'Progress',
      raid: 'RAID',
      objectives: 'Objectives',
      documents: 'Documents',
    },
    viewingPast: 'Viewing the state at {{at}}',
    readOnly: 'Read-only',
    compareOn: 'Compare two moments',
    compareOff: 'Stop comparing',
    compareLeft: 'Moment A',
    compareRight: 'Moment B',
    pickCompareAt: 'Pick the moment to compare',
    share: 'Copy link',
    linkCopied: 'Link copied, with the timestamp.',
    linkCopyFailed: 'Could not copy automatically — copy this link by hand:',
    foldedFromStart: 'Folded from the first event, {{count}} events',
    foldedFromSnapshot: 'Folded from the {{snapshot}} snapshot + {{count}} events',
    tookMs: '{{ms}}ms',
    partial: 'History cannot reconstruct: {{fields}}',
    progressPct: '{{value}}%',
    hours: '{{hours}}h',
    blocked: 'Blocked',
    emptyBoard: 'The project had no columns or cards at this moment.',
    emptyColumn: 'No cards',
    undated: '{{count}} items had no dates, so they cannot be drawn.',
    member: 'Member',
    unassigned: 'Unassigned',
    openTasks: 'Open',
    openHours: 'Open hours',
    doneTasks: 'Done',
    blockedTasks: 'Blocked',
    emptyWorkload: 'No members at this moment.',
    progressLabel: 'Progress',
    doneOfTotal: 'Done / total',
    inProgress: 'In progress',
    hoursDoneOfTotal: 'Hours done / total',
    raidSummary: '{{open}} items open · total risk score {{exposure}}',
    raidType: 'Type',
    raidTitle: 'Item',
    raidStatus: 'Status',
    raidScore: 'Score',
    emptyRaid: 'RAID log was empty at this moment.',
    emptyObjectives: 'No objectives at this moment.',
    linkedTasks: '{{count}} linked tasks',
    docTitle: 'Document',
    docType: 'Type',
    docStatus: 'Status',
    docVersion: 'Version',
    emptyDocuments: 'No documents at this moment.',
  },
  rebalance: {
    title: 'Rebalance suggestions',
    considered: 'looked at {{count}} upcoming items',
    none: 'No suggestions. Speeds are not far enough apart to justify moving work.',
    save: 'Save as suggestion',
    saving: 'Saving…',
    saved: 'Saved. Use “Apply” on a row to carry it out.',
    nothingToSave: 'Nothing to save yet.',
    saveFirst: 'Save as a suggestion before applying.',
    apply: 'Apply',
    applied: 'Work moved.',
    saves: 'saves an estimated {{hours}}h',
    rescheduleFor: 'Reschedule for {{name}}',
    rescheduleHint:
      'Nobody better to move it to. Rescheduling is your call — the system will not change dates by itself.',
    evidenceFrom: '{{name}}: {{ratio}}× estimate ({{samples}} samples, {{confidence}})',
    evidenceTo: '{{name}}: {{ratio}}× ({{samples}} samples, {{confidence}})',
    overloaded: 'over capacity on {{count}} days in the next 4 weeks',
  },
  teamPerf: {
    service: 'Service',
    noCommitment: 'no commitment',
    showFull: 'Show all columns',
    showCompact: 'Compact',
    sortBy: 'Sort by',
    sortAsc: 'Ascending — tap to reverse',
    sortDesc: 'Descending — tap to reverse',
    title: 'Team performance',
    period: '{{from}} – {{to}}',
    groupPerformance: 'In period',
    groupLoad: 'Upcoming load',
    audited: 'Every personal-performance view is logged',
    truncated:
      'This period hit the sample ceiling, so early-period data was cut. Narrow the range to see it all.',
    empty: 'Nobody on your list yet',
    emptyHelp:
      'You can see performance for people who report to you directly. Nobody has you set as their line manager yet.',
    person: 'Person',
    department: 'Department',
    noDepartment: 'Unassigned',
    samples: 'Samples',
    environment: 'Excluded (environment)',
    windowProjects: 'Projects in period',
    windowHours: 'Hours in period',
    committed: 'Committed',
    capacity: 'Capacity',
    allocation: 'Allocation',
    activeProjects: 'Active projects',
    sampleCount: 'n={{count}}',
    lowMark: '· few samples',
    reviewReason:
      'Not measured — there is no code review integration yet. The column stays, empty, because one signal from the spec is still unmeasured.',
    profileTitle: 'Full profile',
    windowLabel: 'Period',
    windowDays_one: 'Last {{count}} day',
    windowDays_other: 'Last {{count}} days',
    openHint: 'Opening this table records an access trace for everyone in it.',
  },
  performance: {
    loadTitle: 'From {{from}} to {{to}}',
    loadValue: 'Committed at {{pct}}% of capacity — {{count}} projects',
    loadRoomy: 'room for more work',
    previousValue: 'Previous period: {{value}}',
    unchanged: 'unchanged',
    tabs: {
      me: 'My performance',
      team: "My team's performance",
    },
    sectionLoad: 'Upcoming capacity',
    sectionProfile: 'Performance profile',
    tabTitle: 'Performance',
    teamAuditNotice:
      "Every look at someone else's performance or hours is written to their own access log.",
    pageTitle: 'My performance',
    noProfileYet:
      'Nothing finished yet to measure. Your profile appears once you close your first few items.',
    speed: 'Speed',
    onTime: 'On time',
    rework: 'No rework',
    review: 'Review',
    notMeasured: 'not measured yet',
    projectWork: 'Project work',
    onTimeHint: 'dated work, finished in time',
    reworkHint: 'finished work, never reopened',
    speedHint: '100 = on the project’s pace',
    serviceTitle: 'Operational work',
    slaMet: 'Met commitment',
    slaSamples: 'of {{count}} issues with a commitment',
    serviceHours: 'Weighted hours',
    serviceHoursHint: 'splitting tickets does not raise this',
    closed: 'Closed',
    closedHint: 'including uncommitted ones',
    reopened: 'Reopened',
    reopenedHint: 'the counterweight to the two on the left',
    indexValue: '{{value}}/100',
    samplesShort: '{{count}} samples · {{confidence}}',
    sampleSummary: '{{count}} samples over 12 months',
    environment: '{{count}} excluded as environment',
    noData:
      'Not enough data yet. Samples come from finished tasks with an estimate and an assignee.',
    project: 'Project',
    hours: 'Hours',
    weight: 'Weight',
    baseline: 'Project pace',
    normalised: 'vs pace',
  },
  tour: {
    drag: 'Drag to move this card out of the way',
    title: 'User guide',
    next: 'Next',
    back: 'Back',
    done: 'Done',
    skip: 'Skip the tour',
    reopen: 'User guide',
    progress: 'Step {{current}}/{{total}}',
    away: 'You have left the screen for this step.',
    goThere: 'Back to this screen',
    step: {
      projectList: {
        title: 'All projects',
        body: 'Start here: the "All projects" button at the top of the sidebar opens a table of all your projects, grouped (invitations, not started, in progress, done) — key, name, your role, status, period, people and tasks. Click a name to open the project; drag the handle at the start of a row onto the sidebar to pin it, or use the bin in the sidebar to remove it. With no projects yet, the sidebar only has the create button.',
      },
      createProject: {
        title: 'Create a new project',
        body: 'The "New project" button creates a project — fill in the key, name, goals, methodology and work types. It lives on the "All projects" screen: in the button row above the table and at the top of the sidebar. With no projects yet, it sits right in the sidebar.',
      },
      aiAssistant: {
        title: 'AI assistant',
        body: 'The ✨ button next to the notification bell opens this panel: whatever the AI is working on shows here step by step, along with questions waiting for your answer and the history of past runs. The "AI tour" button in the panel walks through every AI feature. The AI only suggests — you approve.',
      },
      editProject: {
        title: 'Edit the project in the Charter tab',
        body: 'Name, description, start and end dates and colour are edited with the "Edit" button here. Members, work types and standards live in the Settings tab.',
      },
      okr: {
        title: 'Register and manage OKRs',
        body: 'Each period, everyone registers objectives and key results; managers approve, lock the period and track progress here. Report numbers regularly so progress stays accurate.',
      },
      newTask: {
        title: 'Create a task',
        body: 'The "New task" button in the Tasks tab: title, work type, assignee, estimate and due date. If the assignee is overloaded, the form warns you right away.',
      },
      newIssue: {
        title: 'Create an issue',
        body: 'Issues are operational problems reported by users or clients. Click the add button to record one, set severity and owner, and follow it until closed.',
      },
      designFile: {
        title: 'Add a design file',
        body: 'Connect a Figma file to the project: every screen is captured each time the design changes, and you can compare two versions side by side.',
      },
      testRound: {
        title: 'Add a test round',
        body: 'Open a new round when a fix is ready: every case runs again, and each round keeps its own results to compare with the previous one. Only one round is open at a time.',
      },
      reports: {
        title: 'Project reports',
        body: 'Burndown, CFD, cycle time, EVM — built from the project event stream, so they always match the board and can be viewed as of any date.',
      },
      orgChart: {
        title: 'Organisation chart',
        body: 'Who reports to whom, by direct line management. Click a person to see their profile; admins edit position and line manager right on the chart.',
      },
      absences: {
        title: 'Leave',
        body: 'Request leave, approve it and see who is off next week. Leave is subtracted from capacity, so schedules and overload warnings recalculate themselves.',
      },
      mySettings: {
        title: 'My settings',
        body: 'Your language, theme, notifications, Telegram and personal AI key are all here.',
      },
      replay: {
        title: 'Replay',
        body: 'Drag the timeline to see what the project looked like on any day — board, Gantt and figures rebuilt from the event history.',
      },
      performance: {
        title: 'People performance',
        body: 'Velocity, estimate accuracy and workload per person, compared with themselves over time. Every view of someone else’s performance is recorded in the access log.',
      },
    },
    fallback:
      "This step's button isn't shown here: open a project to see it, or check that AI is enabled and you have permission to use it. The highlighted area is the closest place.",
    ai: {
      title: 'AI features tour',
      open: 'AI tour',
      step: {
        assistant: {
          title: 'AI assistant panel',
          body: 'Every AI run shows its steps here, along with the history of past runs. AI only suggests, you approve — and without an AI key everything in this tour can still be done by hand.',
        },
        plan: {
          title: 'Automatic planning',
          body: 'The "Quick edit" menu → automatic planning: describe the project in words and AI builds a work breakdown with estimates, dependencies and suggested assignees. Without AI, create tasks yourself or start from a WBS template.',
        },
        taskForm: {
          title: 'Suggestions when creating and splitting tasks',
          body: 'In the "New task" dialog AI suggests type, priority and assignee from the title you type, and its breakdown proposes subtasks with hours. Without AI, fill in the form and add subtasks as usual.',
        },
        dependencies: {
          title: 'Find missing dependencies',
          body: 'The "Quick edit" menu → find missing dependencies: AI reads the tasks and proposes undeclared dependencies, quoting its evidence. By hand: turn on chart editing and drag between two Gantt bars.',
        },
        budget: {
          title: 'Budget estimate',
          body: 'The automatic budget estimate button on the Budget tab: AI explains where the money sits, what is still a guess and how far it is from the approved amount. Without AI, the rate table and expense lines still add up by themselves.',
        },
        adjust: {
          title: 'Late tasks, rescheduling, bulk edits',
          body: 'The "Quick edit" menu handles late tasks and bulk edits written in plain words; the Gantt view offers AI rescheduling when something needs moving. By hand: drag Gantt bars, or select several tasks and change them at once.',
        },
        changes: {
          title: 'Scope negotiation and change impact',
          body: 'On the Changes tab, enter a new constraint and AI proposes cut-or-defer options with a simulated end date; open a change request and AI points out what may break if approved. Without AI, read the precomputed impact table.',
        },
        raid: {
          title: 'Risk suggestions and past decisions',
          body: '"Risk signals" lets AI propose new RAID entries from the critical path, workload and estimates; the decision tool rebuilds the context of an old decision. By hand: add risks from the signal table and filter RAID by Decision.',
        },
        delayRisk: {
          title: 'Delay risk explained',
          body: 'The automatic risk analysis button at the top of the Reports outline: AI puts the delay risk score into words — what the project is facing and what to do this week. Without AI, read the indicator table and score.',
        },
        replay: {
          title: 'Replay narration and "what if"',
          body: '"What if" rewinds to a point, changes a few decisions and compares against actual progress; in playback mode AI narration writes captions that follow the timeline. Without AI, use the event table and slider markers.',
        },
        teamReport: {
          title: 'Team work summary',
          body: 'Open a report and ask AI to write the summary from the captured figures — it never ranks people. Without AI, the top section already holds a draft built from the figures; just edit the wording.',
        },
        okr: {
          title: 'OKR suggestions',
          body: 'The OKR suggestion button reads the latest period results and proposes measurable Objectives and Key Results with the current baseline. Without AI, set OKRs yourself or browse the template library.',
        },
        testing: {
          title: 'Generate test cases',
          body: 'The AI case generator on the Testing tab reads the selected tasks and attached documents and proposes a set of test cases. Without AI, add cases by hand right in this tab.',
        },
        design: {
          title: 'Describe and narrate designs',
          body: 'Open a linked Figma file: AI writes the purpose and main parts of each screen, and when comparing two snapshots explains what each change means. Without AI, write descriptions yourself and review before/after images.',
        },
        aiKey: {
          title: 'Enter your AI key',
          body: 'Start here: every button in this tour needs your own AI key — enter it here, choose models and set a cost limit. Without one the buttons still show and point you here when clicked; the system still works fully, everything is simply done by hand.',
        },
      },
    },
  },
  goalMetric: {
    on_time_ratio: 'On-time rate',
    rework_ratio: 'No-rework rate',
    sla_met_ratio: 'Commitment met rate',
    reopen_count: 'Reopened issues',
    speed_index: 'Speed index',
    service_hours: 'Operational weighted hours',
  },
  goal: {
    edit: 'Edit',
    delete: 'Delete',
    updated: 'Changes saved',
    deleted: 'Goal deleted',
    period: 'Period',
    periodEnd: 'Ends',
    periodKind: {
      month: 'This month',
      quarter: 'This quarter',
      year: 'This year',
    },
    suggestionsTitle: 'AI suggestions',
    ask: 'Suggest OKRs',
    asking: 'Asking…',
    suggestionsReady: '{{count}} suggestions to review',
    noSuggestions: 'Nothing waiting for review.',
    measuredBy: 'Measured from: {{by}}',
    ambition: { committed: 'committed', aspirational: 'aspirational' },
    suggestionEmpty: 'AI does not have enough data to suggest goals for this person.',
    acceptCount_zero: 'Pick items to accept',
    acceptCount_other: 'Accept {{count}} goals',
    accepted: '{{count}} goals created',
    dismiss: 'Dismiss',
    dismissReason: 'Why dismiss it?',
    confirmDismiss: 'Confirm dismiss',
    dismissed: 'Suggestion dismissed',
    sectionTitle: 'Goals this quarter',
    add: 'Add key result',
    cancel: 'Cancel',
    save: 'Save',
    close: 'Close',
    metric: 'Metric',
    targetLabel: 'Target {{unit}}',
    target: 'target {{value}}',
    current: 'now {{value}}',
    start: 'from {{value}}',
    progressExplain:
      '{{pct}}% of the way from {{start}} to the {{target}} target (now at {{current}}).',
    startTip: {
      auto: 'Starting point: where this person stood when the goal was set. The nightly job measures it once from completed work and then keeps it — a baseline that follows the present pins progress at 0% forever.',
      manual:
        'Starting point: the number from the first report, kept from then on. Nothing measures a free-form key result for you.',
    },
    targetTip:
      'Target: the number committed to by the end of the period. For an auto-measured goal this is the ONLY number a human decides. For a lower-is-better metric the target sits BELOW the starting point.',
    currentTip: {
      auto: 'Measured: computed by the nightly job from work completed in the period. There is no input for this number.',
      manual:
        'Measured: the latest number reported by the manager. The nightly job never touches a free-form key result.',
    },

    progressNoStart: 'No baseline was captured, so the distance covered cannot be computed.',
    startHelpLabel: 'What the starting point is',
    numbersHelpLabel: 'What these three numbers are and how they are measured',
    notMeasuredYet: 'Not measured yet — the number appears after tonight’s run.',
    created: 'Goal set',
    archived: 'Goal closed',
    emptyForManager: 'No goals set for this person yet.',
    pairingHint:
      'A speed or volume goal must come with a quality goal in the same period — otherwise the easiest way to hit the target is to cut corners.',

    historyLink: 'See every period →',
    historyTitle: 'Goals by period',
    historyOf: 'Goals of {{name}}',
    historyMine: 'My goals',
    historySubtitle:
      'Every goal ever set, most recent period first. Each verdict carries the numbers it rests on — there is no free-text field to type an assessment into.',
    historyEmpty: 'No goals have been set for this person yet.',
    historyForbidden:
      'You cannot set goals for this person, so you cannot see their goals either. Only their direct manager sees this.',
    backToPerformance: '← Back to Performance',
    currentPeriod: 'Current period',
    manageHere: 'Add, edit and delete goals right below.',

    outcome: {
      in_progress: 'In progress',
      not_measured: 'Not measured',
      achieved: 'Achieved',
      missed: 'Missed',
      closed_early: 'Closed early',
    },
    reason: {
      in_progress:
        'The period is still running. As of the last measurement, {{metric}} sits at {{current}} against a target of {{target}}.',
      not_measured:
        'No samples in the period to measure {{metric}}, so there is no verdict. This is not 0% — no measurement happened at all.',
      achieved: 'At period end, {{metric}} was {{current}} — the target of {{target}} was reached.',
      missed: 'At period end, {{metric}} stopped at {{current}}, short of the {{target}} target.',
      closed_early:
        'A manager closed this goal before the period ended, so it is scored neither achieved nor missed.',
    },

    basisTitle: 'What this rests on',
    basisStart: 'Starting point',
    basisCurrent: 'Measured',
    basisTarget: 'Target',
    basisSamples: 'Samples',
    basisSource: 'Data source',
    sourceManual: 'reported by a manager',
    basisMeasuredAt: 'Last measured',
    basisNever: 'never measured',
    basisDirection: {
      up: 'higher is better for this metric',
      down: 'lower is better for this metric',
    },
    lowSamples: 'Few samples (under {{min}}) — read this as a signal, not a verdict.',
  },
  okrReview: {
    title: 'Period review',
    by: 'Closed by {{name}} on {{date}}',
    comment: 'Overall comment for the period',
    commentHint: 'How the period went, why, and what should change next time',
    verdict: 'Verdict',
    verdictValue: { met: 'Met', missed: 'Missed', exceeded: 'Exceeded' },
    exceededPct: 'Exceeded by how many percent',
    exceededBy: 'Exceeded by {{pct}}%',
    noteAdd: 'Comment on this goal',
    noteEdit: 'Edit comment',
    noteFor: 'Comment on "{{name}}"',
    noteSave: 'Save note',
    needSomething: 'Write at least one comment before saving.',
    needPct: 'Picking "exceeded" requires the percentage.',
    needGoalPct: 'A goal is marked "exceeded" but has no percentage.',
    perGoalHint: 'Per-goal notes — optional.',
    perGoalPlaceholder: 'Note for this goal',
    perGoalVerdict: 'Verdict for "{{name}}"',
    perGoalNoVerdict: 'No separate verdict',
    save: 'Save review',
    edit: 'Edit review',
    saved: 'Period review saved',
    goalGone: 'Goal deleted',
  },
  okr: {
    lateBadge: 'Current period has no goals — the {{date}} deadline has passed',
    warnBadge: 'Next period has no goals — locks {{date}}',
    scopeMine: 'My OKRs',
    scopeTeam: "My team's OKRs",
    pickMember: 'Person',
    selfAddOnly:
      'You can edit and delete what you entered yourself while the period is open. Goals your manager set are theirs to change.',
    myTitle: 'My OKRs',
    summaryTitle: 'OKRs in progress',
    summaryLink: 'Open the OKR page →',
    subtitle:
      'Goals by month, quarter and year. Catalogue metrics are measured nightly by the system; free-form key results are reported by the direct manager.',
    tabsLabel: 'OKR tabs',
    tab: {
      overview: 'Overview',
      month: 'Monthly',
      quarter: 'Quarterly',
      year: 'Yearly',
    },
    tabShort: {
      overview: 'Overview',
      month: 'Month',
      quarter: 'Quarter',
      year: 'Year',
    },

    previousPeriod: 'Previous period',
    nextPeriod: 'Next period',
    currentPeriod: 'Back to current period',
    noFurtherPeriod:
      'Goals can only be set for the next period, so there is nowhere further to go.',
    periodLabel: {
      previous: 'Previous period',
      current: 'Current period',
      next: 'Next period',
      other: 'Past period',
    },
    openUntil: 'Open for changes until {{at}}',
    lockedPeriod: 'Locked — view only, and report values on free-form key results',
    periodStrip: {
      label: 'Periods at a glance',
      month: 'M{{month}}',
      quarter: 'Q{{quarter}}',
      met: 'Met',
      missed: 'Missed',
      exceeded: 'Exceeded {{pct}}%',
      unreviewed: 'To review',
      running: 'In progress',
      next: 'Next',
      none: 'Empty',
      notSet: 'Not set yet',
      krCount_one: '{{count}} KR',
      krCount_other: '{{count}} KRs',
      krReached: '{{reached}}/{{count}} KRs reached',
      open: 'Open period {{label}} — {{status}}',
    },
    periodUnlock: {
      chip: 'Unlocked — editable until {{at}}',
      open: 'Unlock for editing',
      extend: 'Extend',
      relock: 'Lock again',
      dialogTitle: 'Unlock the current period for {{name}}',
      dialogBody:
        'While unlocked, {{name}} can add, edit and delete OKRs for this period (still only the items they created). It locks again automatically, and never beyond the last day of the period.',
      daysLabel: 'Unlock for',
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      confirm: 'Unlock',
      opened: 'Unlocked until {{at}}',
      relocked: 'This period is locked again',
      ownerNote: '{{name}} unlocked this period so you can edit your OKRs, until {{at}}.',
      thisEmployee: 'this employee',
    },
    warnNextEmpty: 'The next period has no goals yet and its deadline is near',

    noneThisPeriod: 'No goals for this period.',
    limitTitle: 'Recommended maximum objectives',
    limitHint:
      'A quarter has 12 weeks: each goal needs enough time to move something. The first three count objectives in a period; the last counts key results within one objective. Going over only warns — it never blocks, because most goals should come from the person doing the work.',
    limitKrs: 'Key results per objective',
    limitSave: 'Save recommendation',
    reportReminder: {
      title: 'Reminders to report free-form key results',
      hint: 'From the moment OKRs are locked, every this-many percent of the period, people whose free-form key results got no value in the last stretch are reminded (red dot on OKR in the top bar, a notification and Telegram). The last working day of the period always reminds once more.',
      label: 'Remind every (% of the period)',
      preview: 'This month: around {{days}} and the last working day',
      save: 'Save reminder rhythm',
      saved: 'Reminder rhythm saved',
    },
    limitSaved: 'Recommended OKR count saved',
    status: { reviewed: 'Reviewed', pending: 'Not reviewed', noGoals: 'No goals' },
    pendingCount: '{{count}} people still need a review for last period',
    pendingNone: 'Everyone has been reviewed for last period',
    draftNone: 'Next month: not started',
    draftSome: 'Next month: {{objectives}} O · {{krs}} KR',
    draftingCount: '{{count}}/{{total}} people have started next month’s OKRs',
    pickMemberAria: 'Choose a person — viewing {{name}}',
    reviewColumn: 'Last period',
    draftColumn: 'Next month',
    statusShort: { reviewed: 'Reviewed', pending: 'Not reviewed', noGoals: 'No goals' },
    reviewSignal: 'Last period: {{status}}',
    draftShortNone: 'Not started',
    draftShortKr: '{{krs}} KR',
    draftShortBoth: '{{objectives}} O · {{krs}} KR',
    nextMonthTitle: 'Next month’s OKRs · drafting',
    nextMonthOpen: 'Open next month',
    nextMonthEmpty: 'No OKRs drafted for next month yet. Locks {{date}}.',
    nextMonthLock: 'locks {{date}}',
    tooManyObjectives:
      'This period already has {{count}} objectives. We recommend at most {{limit}} — too many targets at once and none gets enough time to move.',
    addObjective: 'Add objective',
    objectiveTitle: 'Objective',
    objectivePlaceholder: 'Ship on time without trading away quality',
    objectiveHint:
      'A sentence stating intent, with no number in it. The numbers belong to the key results underneath — with numbers in both places, nobody knows which one to grade at the end.',
    objectiveHasNumber:
      'This sentence carries a number. Move it down into a key result, and rewrite the line above as the intent behind that number.',
    objectiveAmbition: 'Kind of objective',
    ambitionHint: {
      committed: '— expected to land at 100%',
      aspirational: '— aim far; 60–70% already counts as success',
    },
    objectiveEmpty:
      'This objective has no key results, so there is nothing to grade at the end of the period. Add 2-4 measures that would prove it landed.',
    objectiveCreated: 'Objective added',
    objectiveDeleted: 'Objective deleted. Its key results are still there.',
    objectiveDelete: 'Delete objective "{{name}}"',
    editKr: 'Edit key result',
    editObjective: 'Edit objective',
    moreActions: 'Actions for "{{name}}"',
    confirmDeleteKr: 'Delete key result "{{name}}"?',
    confirmDeleteObjective: 'Delete objective "{{name}}"? Its key results are kept.',
    objectiveUpdated: 'Objective saved',
    details: 'Details',
    hideDetails: 'Collapse',
    krCount_zero: 'No key results yet',
    krCount_one: '{{count}} key result',
    krCount_other: '{{count}} key results',
    avgProgress: 'Average progress of the key results',
    setByManager: 'Set by manager',
    metricHelpLabel: 'What "{{name}}" is and how it is measured',
    metricLocked: 'The metric cannot become a manual one — delete and add again if needed.',
    autoKrTitle: 'Auto-measured key result',
    autoKrHint: 'Pick a built-in metric — measured every night, nobody enters numbers.',
    freeKrTitle: 'Free-form key result',
    freeKrHint: 'Name, unit and target of your own — you report the number through the period.',
    autoKrHintUsed: '{{count}} metrics left this period — each metric can be set once per period.',
    allMetricsUsed:
      'Every auto-measured metric is already used this period. Edit or delete an existing key result, or add a free-form one.',
    krHelpLabel: 'What "{{name}}" means',
    krHelpProgress:
      'Started at {{start}} → target {{target}}, now {{current}}: **{{pct}}%** of the way there.',
    krHelpNoStart: 'Target {{target}}, now {{current}}. No starting point yet, so no % yet.',
    krHelpBehind:
      'Started at {{start}} → target {{target}}, now {{current}}: **behind** where it started.',
    krHelpNotMeasured: 'Target {{target}}. No number yet — {{when}}.',
    krHelpWhenAuto: 'it appears after tonight’s measurement',
    krHelpWhenManual: 'press "Report" to record the first one',
    krHelpUp: 'Higher is better',
    krHelpDown: 'Lower is better',
    krHelpAuto: 'measured automatically every night, nothing to enter.',
    krHelpManual: 'reported by hand.',
    noObjective: 'Not under any objective',
    pickObjective: 'Which objective? (optional)',
    noObjectiveOption: '— Not under any objective —',
    emptyEditable: 'No goals for this period yet — add one with the button above.',
    emptyLocked: 'This period has no goals, and it is locked so none can be added now.',

    source: { auto: 'Auto-measured', manual: 'Reported by hand' },
    reportValue: 'Report value',
    reportDue: {
      cta: 'Value due',
      badge: '{{count}} key results need a value',
    },
    valueLabel: 'Value reached {{unit}}',
    valueReported: 'Value saved',

    valueForm: {
      titlePlaceholder: 'e.g. Sign new customers',
      kindLabel: 'Value type',
      kind: { integer: 'Number', decimal: 'Decimal', percent: 'Percent', text: 'Text' },
      kindHint: {
        integer: 'Countable in whole numbers: customers, articles, bugs… Progress % is computed.',
        decimal: 'Numbers with a fraction: review score, revenue, seconds… Progress % is computed.',
        percent: 'A rate, usually from 0 to 100%. Progress % is computed.',
        text: 'Not countable: each report says where things stand, plus an estimated ~ %.',
      },
      start: 'Start',
      target: 'Target',
      textStartDefault: 'Not started',
      textTargetDefault: 'Done',
      textHint: 'Progress comes from the "~ %" you estimate on each report, from 0% to 100%.',
      direction: { up: 'Higher is better', down: 'Lower is better' },
      example: 'Example: reporting {{value}} gives {{pct}}% progress.',
    },
    reportForm: {
      valueLabel: 'Current value',
      notReportedYet: 'No value reported yet — use "Report value".',
      decrease: 'Decrease by 1',
      increase: 'Increase by 1',
      integerOnly: 'This key result only takes whole numbers.',
      percentLabel: 'Current percent',
      textLabel: 'Where does it stand?',
      textPlaceholder: 'e.g. Draft done, waiting for review',
      approxLabel: 'Estimated done',
      approx: '~ {{pct}}%',
      textTarget: '100% means: {{target}}',
      preview: '{{pct}}% of the way',
      previewReached: '{{pct}}% — target reached',
      previewBehind: '0% — behind where it started',
      firstIsStart:
        'This key result has no starting point yet, so the first reported value becomes it and shows 0%. Use Edit to set a different start.',
      farBeyond: 'More than twice the distance to the target — check the number and the unit.',
    },

    presetsTitle: 'Metrics the system measures for you',
    presetsHint:
      'Pick one of these and nobody has to type a number: the nightly job measures it from work already done. The target is a suggestion — edit it before saving.',
    baselineNow: 'currently {{value}}',
    noBaseline: 'not measurable for this person yet',
    addPreset: 'Add',

    freeFormShow: 'Or set a free-form key result →',
    freeFormHide: 'Hide free-form key result',
    freeFormWarning:
      'Nobody measures a free-form key result for you: the direct manager has to report its value throughout the period.',
    freeTitle: 'Key result name',
    freeUnit: 'Unit',
    freeUnitPlaceholder: 'customers, articles…',
    freeDirection: 'Good direction',

    lockTitle: 'OKR deadline',
    lockSubtitle:
      "The last day OKRs of a period can be added or edited, counted in days from the period's first day. Positive: a few extra days into the period. Negative: must be finalised before the period starts. The deadline lasts until the end of that day in the organisation's time zone.",
    lockMonth: 'Monthly OKRs (days)',
    lockQuarter: 'Quarterly OKRs (days)',
    lockYear: 'Yearly OKRs (days)',
    lockSaved: 'OKR deadline saved',
    lockHint:
      'For a month starting 01/11: +3 ⇒ editable until the end of 04/11; 0 ⇒ until the end of 01/11; -7 ⇒ until the end of 25/10.',
    lockPreview: 'Period {{period}}: editable until the end of {{at}}',
  },
  goalSource: {
    on_time_ratio: 'tasks completed in the period',
    rework_ratio: 'tasks completed in the period',
    speed_index: 'tasks completed in the period',
    sla_met_ratio: 'service issues closed in the period',
    reopen_count: 'service issues closed in the period',
    service_hours: 'service issues closed in the period',
  },
  /** Performance anomaly signals — APM-ACHEAT-g. */
  anomalies: {
    title: 'Signals worth a second look',
    subtitle:
      'Behaviour patterns that repeated often enough in the last {{days}} days. Every one of them has an innocent explanation — this is a list of things to ask about, not a verdict. Each view is logged in the sensitive-access log.',
    empty: 'No signals in this window.',
    truncated: 'Too many events in this window — results are partial. Narrow the date range.',
    person: 'Person',
    pattern: 'Pattern',
    count: 'Times',
    threshold: 'threshold ≥ {{value}}',
    tasks: 'Related tasks',
    lastAt: 'Most recent',
    kind: {
      rapid_completions: 'Several tasks completed within 10 minutes',
      repeated_reschedule: 'Due date pushed later repeatedly on one task',
      self_block: 'Flagged own task as blocked',
      estimate_bump_before_done: 'Raised the estimate, then closed within 24 hours',
      timesheet_at_cap: 'Timer stopped by the job at a cap',
    },
    kindHelp: {
      rapid_completions:
        'Could be tidying the board after a day of not moving cards — or bulk-closing to look good.',
      repeated_reschedule:
        'Could be slipping dependencies — or moving the date so nothing is ever "late".',
      self_block:
        'Self-blocked time no longer counts as waiting, but the habit is still worth asking about.',
      estimate_bump_before_done:
        'An estimate edited right before completion is no longer an estimate.',
      timesheet_at_cap: 'Forgot to stop the timer repeatedly, or left it running.',
    },
  },
  velocity: {
    restricted:
      'This page is for planners — team leads, directors and org admins. Your own numbers are on the Performance page.',
    title: 'Estimation calibration',
    subtitle:
      'How long this kind of work, on this stack, takes each person — measured from real task data, used to size sprints. Your own performance lives on the Performance page. Every time you read someone else’s numbers it is logged.',
    mineTitle: 'Mine',
    mineHint: 'Only you and people with performance access can see this.',
    orgTitle: 'Whole organisation',
    orgForbidden:
      'You do not have access to other people’s velocity. The “Mine” section above is complete.',
    audited: 'Every view is written to the audit log',
    byTechStack: 'Split by tech stack',
    sync: 'Rebuild samples',
    syncing: 'Rebuilding…',
    synced: 'Built {{count}} new samples.',
    window: 'Window {{from}} – {{to}} · {{count}} samples',
    truncated: 'hit the cap, this table covers only part of the data',
    empty:
      'No samples yet. Samples come from finished tasks that have an estimate and an assignee.',
    person: 'Person',
    taskType: 'Task type',
    techStack: 'Tech stack',
    anyStack: 'unknown',
    ratio: 'Ratio',
    ratioValue: '{{value}}×',
    spread: 'Middle range',
    samples: 'Samples',
    confidence: 'Confidence',
    confidenceValue: { low: 'Not enough', medium: 'Some', high: 'Solid' },
    waiting: 'Waiting',
    waitingValue: '{{count}} samples · {{hours}}h',
    waitingHint: 'Samples excluded because most of the time was waiting, {{hours}} hours total',
    samplesTitle: 'Evidence: {{name}} · {{type}}',
    close: 'Close',
    completedAt: 'Finished',
    estimate: 'Estimate',
    worked: 'Actual',
    blocked: 'Waiting',
    counted: 'Counted',
    isCounted: 'Yes',
    notCounted: 'No (environment)',
  },
  demo: {
    badge: 'DEMO',
    badgeHint: 'Sample project — excluded from org reports and performance profiles.',
  },
  gantt: {
    pageRows: 'Rows {{from}}–{{to}} / {{total}} · Critical path covers the full scope',
    offPageLinks: '{{count}} links are outside this page',
    allRows: 'View full diagram',
    pagedRows: 'Show 200 rows per page',
    dependencyType: {
      finish_to_start: 'Finish to start',
      start_to_start: 'Start to start',
      finish_to_finish: 'Finish to finish',
      start_to_finish: 'Start to finish',
    },
    zoom: { day: 'Day', week: 'Week', month: 'Month' },
    chartLabel: 'Gantt chart',
    noDates: 'No task has dates yet. Set dates to see bars.',
    emptyTitle: 'No tasks yet',
    emptyDescription: 'Create tasks and set dates to build a schedule.',
    criticalOnly: 'Critical path only',
    colTask: 'Task',
    colAssignee: 'Assignee',
    colStatus: 'Status',
    statusTodo: 'Not started',
    statusDone: 'Done',
    colDays: 'Days',
    colDates: 'From – to',
    resizeList: 'Drag to resize the task list panel',
    noAssignee: 'unassigned',
    noDatesShort: 'no dates',
    linkHint: 'Drag onto another task to create a dependency',
    edit: 'Edit chart',
    editDone: 'Done',
    moveMilestoneTitle: 'Move this milestone?',
    moveMilestoneFrom: 'Currently',
    moveMilestoneTo: 'New date',
    moveMilestoneConfirm: 'Move milestone',
    unlinkHint: 'Click to remove the link',
    linked: 'Dependency created',
    unlinked: 'Link removed',
    deadlineLabel: 'Project deadline',
    roundMarker: 'Round {{number}}',
    roundMarkerOpen: 'Test round {{number}} — open. Click to open the Testing tab.',
    roundMarkerClosed: 'Test round {{number}} — closed {{date}}. Click to open the Testing tab.',
    kind: { milestone: 'Milestone', phase_gate: 'Phase gate', task: 'Task' },
    selectedCount: '{{count}} tasks ticked',
    clearSelection: 'Clear ticks',
    groupSelected: 'Group into a new group',
    moveToExistingGroup: 'Move into an existing group',
    createGroup: 'Create group',
    groupName: 'Group name',
    groupNamePlaceholder: 'e.g. Phase 1 — Discovery',
    noGroupYet: 'No group in this project yet',
    removeFromGroup: 'Remove from group',
    ungrouped: 'Not in a group',
    toggleGroup: 'Collapse/expand group {{name}}',
    renameGroup: 'Rename group {{name}}',
    deleteGroup: 'Delete group {{name}}',
    selectTask: 'Tick {{name}}',
    groupCreateDone: 'Group created',
    groupRenameDone: 'Group renamed',
    groupDeleteDone: 'Group deleted — its tasks are still there',
    groupAssignDone: 'Group updated',
    criticalTitle: 'On the critical path',
    criticalLength: 'Critical path: {{days}} days',
    onCriticalPath: 'On the critical path — one day late is one day late for the project',
    floatDays: '{{days}} days of float left',
    barTitle: '{{float}} days of float',
    holidayTitle: 'Holiday: {{name}}',
    violationTitle: 'Starts {{days}} days earlier than dependencies allow',
    violationToast: 'Saved, but this starts {{days}} days before its predecessor finishes.',
    baselineTitle: 'Baseline',
    setBaseline: 'Set baseline',
    rebaseline: 'Re-baseline',
    baselineSaved: 'Baseline saved',
    slippage: '{{days}} days behind baseline',
    legendOnTrack: 'On track',
    legendBehind: 'Behind expected progress',
    legendOverdue: 'Overdue',
    legendDone: 'Done',
    legendDoneLate: 'Done late',
    doneLateTitle: 'Completed on {{done}}, after the {{due}} deadline',
    legendViolation: 'Invalid schedule: starts before its predecessor ends',
    legendToggle: 'Colour legend',
    offscreenHint: 'The bar for "{{name}}" is off-screen — tap to scroll to it',
    moreActions: 'More actions',
    makeMilestone: 'Make milestone',
    makeTask: 'Make task',
    passGate: 'Pass gate',
    gatePassed: 'Phase gate recorded',
    selectHint: 'Click a bar for details. Drag the body to move it, drag the edges to resize.',
  },
  recurrence: {
    intro:
      'Task templates that repeat on a schedule. Each time one is due, a real task lands on the board.',
    new: 'New recurring task',
    newTitle: 'New recurring task',
    editTitle: 'Edit recurring task',
    create: 'Create template',
    created: 'Recurring task created',
    updated: 'Template saved — changes apply to future occurrences',
    deleted: 'Template deleted. Tasks already generated are kept',
    confirmDelete: 'Delete template "{{title}}"? Tasks already generated are kept.',
    emptyTitle: 'No recurring tasks yet',
    emptyHelp:
      'Create a template (e.g. "Weekly service review") and a task will be generated each time it is due.',
    emptyHelpReadOnly: 'The project PM sets up recurring tasks here.',
    /** Period/sprint pickers are locked (not hidden) while the Recurring view is open. */
    pickersLocked:
      'Recurring templates belong to no period or sprint — the pickers apply to Board, Gantt and List.',
    paused: 'Paused',
    pause: 'Pause',
    resume: 'Resume',
    noOccurrences: 'Nothing generated yet.',
    occurrenceOn: 'Occurrence {{date}}',
    dueOn: 'due {{date}}',
    titleLabel: 'Task name',
    titlePlaceholder: 'E.g. Review service logs and disk usage',
    scheduleLabel: 'Schedule',
    freqLabel: 'Repeats',
    freq: {
      daily: 'Every working day',
      weekly: 'Weekly',
      monthly: 'Monthly',
      interval: 'Every N days',
    },
    weekdaysLabel: 'Days of the week',
    weekdayShort: {
      '0': 'Sun',
      '1': 'Mon',
      '2': 'Tue',
      '3': 'Wed',
      '4': 'Thu',
      '5': 'Fri',
      '6': 'Sat',
    },
    monthDayLabel: 'Day of month',
    lastDay: 'Last day of month',
    intervalLabel: 'Days between occurrences',
    startLabel: 'Starts',
    endLabel: 'Ends (optional)',
    leadLabel: 'Show ahead (days)',
    leadHelp: 'The task appears on the board this many days before it is due. 0 = on the due date.',
    previewLabel: 'Next 3:',
    previewNone: 'No more occurrences in this schedule.',
    previewInvalid: 'Schedule is not valid yet',
    previewHelp:
      'An occurrence on a day off moves to the next working day ("every working day" skips days off).',
    addChecklist: 'Add checklist',
    editHelp: 'Edits apply to future occurrences only; generated tasks are kept as they are.',
    col: {
      title: 'Task',
      rule: 'Repeats',
      assignee: 'Assignee',
      last: 'Last',
      next: 'Next',
      pending: 'Open',
    },
    summary: {
      daily: 'Every working day',
      weekly: 'Weekly: {{days}}',
      monthly: 'Day {{day}} of each month',
      monthlyLast: 'Last day of each month',
      interval_one: 'Every {{count}} day',
      interval_other: 'Every {{count}} days',
    },
    mark: 'Recurring task',
    markHelp:
      'Recurring task — generated from a template. Edits here change this occurrence only; edit the template in the "Recurring" view to change future ones.',
  },
  tasks: {
    view: {
      board: 'Board',
      gantt: 'Gantt',
      list: 'List',
      recurring: 'Recurring',
    },
    pickSprintTitle: 'Pick a sprint to see the work',
    pickSprintHelp:
      'No sprint is running here. Each sprint has its own columns, so merging them all gives a board nobody can read.',
  },
  scheduleDrawer: {
    title: 'Unscheduled work',
    hint: 'Drag a row onto the Gantt chart to put it in the sprint and set dates at once.',
    empty: 'Nothing left outside the sprint.',
    noEstimate: 'no estimate',
    hours_one: '{{count}} hour',
    hours_other: '{{count}} hours',
    sendToSprint: 'Into {{name}}',
    scheduled: 'Scheduled {{key}}: {{from}} → {{to}}',
    scopeCreep:
      'Added {{key}} to a **running** sprint — scope goes up while the start-of-sprint commitment does not.',
  },
  actualTime: {
    title: 'Baseline, plan and actual',
    baseline: 'Baseline',
    baselineHelp:
      'The original plan: start and due dates captured when the baseline was set. Moving the deadline does not change it — it can only be re-set through an approved change request.',
    dueMoved: 'Deadline moved away from the baseline',
    planned: 'Current',
    actual: 'Actual',
    start: 'Start',
    end: 'End',
    hours: 'Hours',
    hoursValue: '{{count}}h',
    elapsed: '{{count}}h elapsed from start to finish (incl. nights and days off).',
    actualHoursHelp: 'Counted from the timer — not editable.',
    estimateTitle: 'Estimate',
    spValue: '{{value}} SP',
    spNone: 'No SP',
    estimateRun: 'Estimate SP',
    estimateRerun: 'Re-estimate',
    openReference: 'Open the reference in a new tab',
    badgeTitle: 'Story points — click to see where they came from and re-estimate',
    badgeNone: '– SP',
    summary: {
      sp: 'Estimated SP',
      hours: 'Estimated time',
      method: 'How it was estimated',
      methodReference: 'Compared with a past task',
      methodEdited: 'Compared with a past task, then SP edited by hand',
      methodDialog: 'SP typed in by hand',
      methodForm: 'Typed in the task form',
      methodNone: 'Not estimated yet',
      refTask: 'Compared with',
      refPoints: 'Its SP',
      ratio: 'Difference',
      ratioMore: '{{pct}} (this one takes more)',
      ratioLess: '{{pct}} (this one takes less)',
      ratioSame: '0% (about the same)',
      none: 'None',
    },
    hiddenReference: 'in a project you cannot see',
  },
  taskList: {
    searchPlaceholder: 'Search by key or title',
    filterAssignee: 'Filter by assignee',
    filterStatus: 'Filter by column',
    everyone: 'Everyone',
    unassigned: 'Unassigned',
    allStatuses: 'All columns',
    overdueOnly: 'Overdue only',
    overdue: 'overdue',
    doneLate: 'done late',
    doneLateTitle: 'Completed on {{done}}, after the {{due}} deadline',
    count: '{{shown}}/{{total}} items',
    sortBy: 'Sort by {{column}}',
    emptyTitle: 'No items',
    emptyFiltered: 'Nothing matches the current filters. Try removing a condition.',
    open: 'Open {{key}}',
    pick: 'Select {{key}}',
    pickAll: 'Select all',
    picked: '{{count}} selected',
    clearPick: 'Clear',
    changeStatus: 'Change column',
    groupBy: 'Group by',
    groupCount: '{{count}} items',
    groupEmpty: 'No items in this group yet.',
    allRows: 'All items',
    drag: 'Drag {{key}} to another column',
    group: {
      status: 'Column',
      assignee: 'Assignee',
      none: 'No grouping',
    },
    changePriority: 'Change priority',
    changeDue: 'Change due date of {{key}}',
    changeTimeline: 'Change timeline of {{key}}',
    changeProgress: 'Change progress of {{key}}',
    changeAssignee: 'Change assignee of {{key}}',
    startDate: 'Start',
    clearDate: 'Clear due date',
    addTask: 'Add item',
    addTaskPlaceholder: 'Item name, then Enter',
    pickStatus: '— pick a column —',
    column: {
      key: 'Key',
      title: 'Item',
      status: 'Column',
      assignee: 'Assignee',
      due: 'Due',
      timeline: 'Timeline',
      progress: 'Progress',
      priority: 'Priority',
      points: 'Points',
    },
  },
  projectBudget: {
    estimateTitle: 'Cost estimate',
    approvedTitle: 'Approved budget',
    ratesTitle: 'Hourly rates',
    itemsTitle: 'Cost items',
    newItem: 'Add cost item',
    aiSpent:
      'Spent {{value}} USD on AI across {{count}} calls (not added to the total above — different currency).',
    loadFailed: 'Could not load budget data.',
    topTasks: 'Most expensive',
    taskHours: '{{hours}}h',
    taskGone: '(task deleted)',
    assumptionsTitle: 'Assumptions from the AI estimate',
    assumptionsHint:
      'These are assumptions from one run, not data yet. Save one as a real rate and it applies to every later report.',
    saveAssumption: 'Save as a rate',
    assumptionSaved: 'Saved as a rate, effective today.',
    trendTitle: 'Estimated cost over time',
    trendSubtitle: 'Last 90 days',
    trendTotal: 'Estimated total',
    trendApproved: 'Approved',
    trendEmpty: 'Nothing to plot yet',
    trendEmptyHelp: 'Needs at least one task with estimated hours and a rate that applies to it.',
    labour: 'Labour',
    items: 'Cost items',
    contingency: 'Contingency',
    asOf: 'as of {{asOf}}',
    covered: '{{covered}} of {{total}} tasks costed',
    missing: 'Missing: {{hours}} task(s) without an estimate, {{rates}} without a rate.',
    vsApproved: 'Against the approved budget: {{delta}}',
    confidence: { low: 'low confidence', medium: 'medium confidence', high: 'high confidence' },
    approvedLine: 'Approved {{amount}} (version {{version}}, {{contingency}}% contingency)',
    noApproval: 'No budget approved yet',
    noApprovalHint:
      'Approve an amount to compare the estimate against a commitment. Each approval keeps the previous one.',
    approve: 'Approve this amount',
    approveAmount: 'Amount',
    approveContingency: 'Contingency (%)',
    approved: 'New approval recorded.',
    ratesAggregated:
      'Showing rates aggregated by role and skill. Per-person rates are pay data and need a separate permission.',
    ratesFull:
      'Showing everything, including per-person rates. Each view is written to the access log.',
    noRates: 'No rates declared yet',
    noRatesHint:
      'Without rates the labour part cannot be costed. Declare them under Administration.',
    rateSubject: 'Applies to',
    rateHourly: 'Hourly rate',
    rateFrom: 'Effective from',
    subjectType: { user: 'A person', project_role: 'Project role', skill: 'Skill' },
    noItems: 'No cost items yet',
    noItemsHint: 'Equipment, licences, infrastructure, vendors — anything not billed by the hour.',
    itemName: 'Item name',
    itemAmount: 'Amount',
    itemKind: 'Kind',
    itemRecurrence: 'Recurrence',
    addItem: 'Add',
    itemAdded: 'Cost item added.',
    removeItem: 'Remove cost item {{name}}',
    kind: {
      equipment: 'Equipment',
      license: 'Licence',
      infra: 'Infrastructure',
      vendor: 'Vendor',
      travel: 'Travel',
      other: 'Other',
    },
    recurrence: {
      once: 'One-off',
      daily: 'Daily',
      weekly: 'Weekly',
      monthly: 'Monthly',
      yearly: 'Yearly',
    },
  },
  drift: {
    title_one: '{{count}} task is past its deadline',
    title_other: '{{count}} tasks are past their deadline',
    show: 'See list',
    hide: 'Collapse',
    due: 'Due {{date}}',
    changeDue: 'Change deadline of {{key}}',
    dueSaved: 'Deadline updated',
    cause: { late: '{{days}} working days late', early: '{{days}} working days early' },
  },
  board: {
    moreTabs: '{{n}} more tabs',
    tab: {
      sprints: 'Sprints',
      overview: 'Charter',
      tasks: 'Work',
      raid: 'RAID',
      documents: 'Documents',
      changes: 'Changes',
      objectives: 'Objectives',
      reports: 'Reports',
      budget: 'Budget',
      testing: 'Testing',
      design: 'Design',
      git: 'Git',
      issues: 'Issues',
      coordination: 'People',
      replay: 'Replay',
      settings: 'Settings',
    },
    status: { planning: 'Planning', active: 'Active', completed: 'Closed' },
    chooseProject: 'Choose project',
    methodologyName: { AGILE: 'Agile', WATERFALL: 'Waterfall', MAINTAIN: 'Maintenance' },
    methodology: 'Methodology: {{value}}',
    noProject: 'No projects yet',
    noProjectHelp: 'Create your first project to start planning.',
    emptyTitle: 'The board is empty',
    emptyDescription: 'No tasks in this sprint yet. Pull tasks from the backlog, or create one.',
    emptyDescriptionPeriod: 'No tasks in this period. Pick another period, or create one.',
    emptyDescriptionAll: 'No tasks yet. Create the first one.',
    emptyColumn: 'Drop cards here',
    wipTitle: '{{count}} cards, limit {{limit}}',
    startTask: 'Start work — move to {{status}}',
    taskStarted: 'Work started on this item.',
    closeTask: 'Complete',
    closeTaskTitle: 'Move to {{status}}',
    taskClosed: 'Task completed',
    progressReadOnly: 'Progress: {{value}}%',
    progressRollup: 'Progress rolls up from subtasks',
    progressNotStarted: 'Move to In progress to update',
    progressInBacklog: 'This task is in the backlog — add it to a sprint to update progress',
    progressNotInProgress: 'Only tasks in progress can have their progress updated',
    setProgress: 'Set progress to {{value}}%',
    progressDown: 'Decrease by {{step}}%',
    progressUp: 'Increase by {{step}}%',
    progressSlider: 'Progress slider',
    progressSaved: 'Progress updated',
    backlog: 'Backlog',
    backlogEmpty: 'Backlog is empty',
    backlogEmptyHelp: 'Every task is already in a sprint.',
    sprints: 'Sprints',
    addColumn: 'Add column',
    columnName: 'Column name',
    columnNamePlaceholder: 'e.g. In review, Waiting for QA…',
    columnColor: 'Column colour',
    colorAuto: 'Auto, from group',
    columnCategory: 'Belongs to',
    categoryHint:
      'The group decides how burndown, cycle time and CFD count this column — not how it looks on the board.',
    category: { todo: 'Not started', in_progress: 'In progress', done: 'Done' },
    columnMenu: 'Options for column {{name}}',
    renameColumn: 'Rename column',
    setWipLimit: 'Set WIP limit',
    changeWipLimit: 'WIP limit: {{limit}}',
    moveColumnLeft: 'Move left',
    moveColumnRight: 'Move right',
    deleteColumn: 'Delete column',
    deleteColumnBlocked: '{{count}} cards left — cannot delete',
    deleteColumnDefault: 'Default column — cannot delete',
    selectedCount: '{{count}} selected',
    moveSelectedToSprint: 'Move to sprint',
    clearSelection: 'Clear selection',
    newSprintName: 'New sprint name',
    createAndMove: 'Create and move',
    noOpenSprint: 'No open sprint yet',
    selectTask: 'Select {{key}}',
    backlogHint: 'Select several items and move the whole batch into a sprint.',
    sprintPickerNone: '— pick a sprint —',
    sprintPicker: 'Sprint:',
    sprintPickerAll: 'Whole project',
    sprintRunning: 'running',
    sprintFrame: 'Columns of {{name}}',
    sprintFrameAny: 'Sprint columns',
    sprintFrameEmpty: 'Pick a sprint to see its own columns.',
    sprintFrameNoColumns:
      'This sprint has no columns of its own — its work sits in the shared columns below.',
    columnScope: 'This column belongs to',
    scopeThisSprint: '{{name}} only',
    scopeEverySprint: 'Every sprint',
    outsideZone: 'Columns outside the sprint',
    moveColumnIntoSprint: 'Move into the sprint frame',
    moveColumnOutside: 'Move outside the frame',
    columnMoved: 'Column moved',
    columnMovedDisplaced_one: 'Column moved — {{count}} item had to change column',
    columnMovedDisplaced_other: 'Column moved — {{count}} items had to change column',
    confirmMoveColumn:
      'Column "{{name}}" holds {{count}} items. Any item not in the column\u2019s new sprint will be moved to another column. Continue?',
    needSprintFirst: 'No active sprint — create one before moving work into it.',
    sprintIsClosed:
      '{{name}} is closed — no more work can go into it. Pick a running sprint or create a new one.',
    sprintClosedGeneric: 'That sprint is closed — no more work can go into it.',
    sprintClosedTask: 'That sprint is closed — its tasks are history and can no longer be edited.',
    taskCompletedLocked:
      'This task is done — reopen it before changing the estimate or story points.',
    statusDragOnly:
      'You can change a task’s column. Reordering is for the project manager; moving tasks in or out of a sprint is for the project manager or the task’s creator.',
    movedToSprint: 'Moved {{count}} items into the sprint',
    movedToBacklog: 'Moved {{count}} items back to the backlog',
    sprintStatus: { planning: 'not started', active: 'active', completed: 'closed' },
    sprintView: { flow: 'Flow', list: 'Sprint list' },
    createdBy: 'Created by',
    createdByOther: 'Off-plan',
    createdByOtherTitle: '{{name}} added this task; it is not part of the plan the PM laid out.',
    createdByOtherUnknown: 'This task was not created by the project manager.',
    dragCard: 'Drag {{key}} to another column',
    milestones: 'Milestones',
    milestoneDone: 'Milestone reached',
    milestoneLate: 'Milestone past its date and not reached',
    milestoneOpen: 'Upcoming milestone — click for details',
    cardKey: 'Task key',
    cardType: 'Task type',
    cardPriority: 'Priority: {{value}}',
    cardPriorityPick: 'Priority: {{value}} — click to change',
    cardAssigneePick: 'Assignee of {{key}}: {{name}} — click to reassign',
    cardAssigneeEmpty: '{{key}} is unassigned — click to assign',
    cardAssign: 'Assign',
    cardDue: 'Due: {{value}}',
    cardDueLate: 'Due: {{value}} — overdue',
    cardDueDoneLate: 'Due: {{value}} — completed late, on {{done}}',
    cardDuePick: 'Due: {{value}} — click to change',
    dueChanged: 'Due date changed',
    cardBlocked: 'Blocked: {{reason}}',
    cardBlockedNoReason: 'Blocked',
    priorityChanged: 'Priority updated',
    sprintGroup: {
      active: 'Active ({{count}})',
      planning: 'Not started ({{count}})',
      completed: 'Closed ({{count}})',
    },
    finishPrompt: '“{{title}}” is at 100%. Move it to the done column?',
    finishConfirm: 'Move to {{column}}',
    newSprint: 'New sprint',
    noSprintYet: 'No sprints yet.',
    noSprint: 'No sprint',
    chooseSprint: 'Move to sprint',
    moveToSprint: 'Move {{key}} to a sprint',
    startSprint: 'Start',
    closeSprint: 'Close sprint',
    sprintClosed: 'Sprint closed. {{carried}} unfinished tasks returned to the backlog.',
    quickUpdate: 'Quick update',
    quickUpdateDragHint: 'Quick update — drag to move the button',
    nothingInProgress: 'You have nothing in progress.',
  },
  ai: {
    running: 'Working…',
    goManual: 'Do it manually',
    keyRequired: {
      title: 'AI key needed',
      body: '“{{feature}}” runs on AI, so it needs an AI key first. Add one on the Settings page — it takes a minute.',
      manual: 'Or do it by hand:',
      openSettings: 'Add an AI key',
    },
    confirm: {
      run: 'Run',
      genericDescription:
        'The AI assistant will read the relevant project data and propose a result for this.',
      whatHappens: 'When you press Run:',
      readsScope: 'The AI only reads data you are allowed to see.',
      suggestOnly:
        'The result is only a suggestion — nothing in the project changes until you review and approve it.',
      costAndTime:
        'Each run costs AI credit and takes from a few seconds to about a minute; each step shows in the right-hand panel.',
    },
    failure: {
      title: 'The assistant could not complete this',
    },
    estimate: {
      value: '{{value}} {{unit}}',
      range: '({{low}}–{{high}})',
      primary: '· primary',
      unit: {
        sp: 'pts',
        hours: 'hours',
        days: 'days',
        usd: 'USD',
        pct: '%',
      },
      confidence: {
        low: 'low confidence',
        medium: 'medium confidence',
        high: 'high confidence',
      },
      evidenceKind: {
        similar_task: 'Similar task:',
        velocity: 'Team velocity:',
        calendar: 'Working calendar:',
        dependency: 'Dependency:',
        assumption: 'Assumption:',
        knowledge: 'Knowledge base:',
      },
    },
    activity: {
      title: 'What the AI is doing',
      close: 'Close activity panel',
      toggle: 'AI assistant panel',
      browseTitle: 'AI assistant',
      browseHelp: 'What is waiting for you, and what has run.',
      openHistory: 'Show past runs',
      backToRun: 'Back to the current run',
      historyBack: 'Back to the list',
      historyEmpty: 'You have not asked the assistant for anything yet.',
      awaitingBadge: 'Needs your answer',
      resume: 'Carry on',
      resumeHint: 'Keeps what the assistant already read; only the unfinished part runs again.',
      resumedFrom: 'Continues after turn {{turn}} — everything above is from the earlier run.',
      controlPhase: {
        evidence: 'Read required evidence',
        synthesis: 'Ready to synthesize the result',
        normalized: 'Normalized the draft format',
        repair: 'Keep the draft and repair invalid parts',
      },
      totalTime: '{{value}} seconds inside the harness. Five slowest phases:',
      contextMemory_one: 'Memory ({{count}} item)',
      contextMemory_other: 'Memory ({{count}} items)',
      contextCache: 'Cache breakpoint — everything above is reused on the next turn',
      contextDropped: '⚠ Dropped for length: {{items}}',
      hiddenTools: 'Tools hidden because you cannot read them: {{tools}}',
      contextLayer: {
        harness_rules: 'Assistant rules',
        skill_prompt: 'Feature instructions',
        org_knowledge: 'Org knowledge',
        project_profile: 'Project profile',
        user_request: 'Your request',
      },
      noTrace: 'No trace kept for this run — only the questions remain.',
      awaitingQuestions_one: '{{count}} question',
      awaitingQuestions_other: '{{count}} questions',
      historyNoSteps: 'No steps were kept for this run.',
      waiting: 'Waiting for the assistant to start…',
      idlePhrases: {
        resolving: 'Resolving data',
        analyzing: 'Analysing',
        thinking: 'Thinking',
        drafting: 'Drafting an interim result',
        synthesizing: 'Synthesising',
      },
      applied: 'Applied to the main view.',
      applyHint: 'Nothing reaches the project until you click — the AI never writes on its own.',
      recommended: 'Recommended: {{value}}',
      cost: '{{cost}} USD · {{turns}} model calls',
      partial: 'Partial result — the assistant stopped because it {{reason}}.',
      partialManual: 'Finish the rest by hand: {{label}}',
      unverified_one: 'One point could not be verified — the system lacked data to judge it:',
      unverified_other:
        '{{count}} points could not be verified — the system lacked data to judge them:',
      stopReason: {
        turn_capped: 'hit the turn limit',
        tool_calls_capped: 'hit the per-turn tool limit',
        token_capped: 'hit the token limit',
        cost_capped: 'hit the cost limit',
        loop_capped: 'called one tool too many times',
      },
      status: {
        running: 'Running',
        clarifying: 'Waiting for your answer',
        done: 'Done',
        error: 'Stopped on an error',
      },
      kind: {
        thinking: 'Thinking',
        reasoning: 'Reasoning (thinking)',
        scratchpad: 'Plan (scratchpad — model has no thinking)',
        toolCall: 'Using a tool',
        toolResult: 'Result from {{tool}} ({{ms}}ms)',
        critique: 'Critic (round {{round}}/{{rounds}})',
        critiqueOk: 'No objections — result accepted.',
        critiqueOtherFamily: 'other family',
        critiqueStopped: {
          rounds: 'Critic rounds exhausted — result accepted, remaining notes go to the reviewer.',
          budget: 'Critic budget for this run exhausted — result accepted.',
          no_progress: 'Same objections as the previous round — stopped, sent to the reviewer.',
          infra: 'The critic did not answer — skipped, not blocking.',
        },
        question: 'The AI asked back',
        answer: 'You answered',
        resume: 'Continued from the previous run',
        context: 'What the assistant read',
        policy: 'Permissions and tools',
        timing: 'Where the time went',
        done: 'Result',
        error: 'Error',
      },
    },
  },
  aiKey: {
    demoBlocked: 'Demo accounts cannot add their own key',
    demoBlockedHelp:
      'The demo organisation shares one key entered by an administrator. If each visitor activated their own, the shared key would be deactivated and AI would go dark for everyone after them. Sign in with a real account to use your own key.',
    chainsTitle: 'Models used per tier',
    chainsSubtitle: 'order matters — the first one is tried first',
    chainsHelp:
      'The system tries model 1; on quota limits, gateway overload, a removed model, or a model without a pricing row it falls through to the next. A bad key or an exceeded budget does NOT fall through — another model hits the same error.',
    chainEmpty: 'Not set — uses the organization default.',
    useSuggestion: 'Use suggestion',
    addModel: '+ Add model…',
    saveModels: 'Save selection',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove',
    notPriced: 'no pricing',
    notPricedHelp:
      'No pricing row, so this model is skipped. Ask an admin to add it to the pricing table.',
    notUsable: 'not a text model',
    notUsableHelp:
      'This model cannot generate text (tts, audio, image, embedding) or is a gateway routing wildcard.',
    modelsSaved: 'Model selection saved.',
    tier: { strong: 'Hard work', standard: 'Everyday work', small: 'Easy work' },
    // Reasoning per kind of work and model lifecycle — APM-MDL-c.
    effortLabel: 'Reasoning',
    effort: {
      default: 'Default',
      off: 'Off',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      adaptive: 'Adaptive',
    },
    lifecycle: {
      title: 'Model lifecycle and recommendations',
      badge_one: '{{count}} item to review',
      badge_other: '{{count}} items to review',
      dot: 'new model or model recommendation',
      menuBadge: 'New model',
      scannedAt: 'Gateway last scanned: {{value}}',
      neverScanned: 'Gateway not scanned yet — the job runs daily at 04:00.',
      modeLabel: 'How the key follows new models',
      mode: { latest: 'Always use the latest version', pinned: 'Pin versions' },
      modeHint: {
        latest:
          'When the gateway has a newer version of the same family (e.g. Opus 5 → Opus 5.5) with a price, it is used automatically; the old one stays as fallback.',
        pinned: 'Calls exactly the saved chain. New versions are reported for you to decide.',
      },
      scanError: 'Last scan failed: {{error}}',
      newer: 'New model versions: {{models}}',
      addNewer: 'Add to chain',
      dismiss: 'Dismiss',
      recommendTitle: 'Recommendation per kind of work (accuracy ÷ price)',
      noRecommendation:
        'No recommendation yet — needs public benchmarks (Harness page) or internal measurements (harness:bench).',
      basis: {
        internal: 'internal golden {{score}} %',
        public: 'public index {{score}}',
      },
      price: '${{value}}/1M tokens',
      apply: 'Apply',
      inUse: 'In use',
      attribution: 'Public index source: Artificial Analysis (artificialanalysis.ai).',
      unmatched: 'No benchmark yet for: {{models}}',
      askAi: 'Ask AI for advice',
      usingLatest: 'Using the latest version: {{model}}',
    },
    tierHint: {
      strong: 'WBS generation, change-impact analysis',
      standard: 'risk signal wording, task breakdown',
      small: 'classification and extraction — runs often, must be cheap',
    },
    modelsTitle: 'Gateway returned {{count}} models',
    modelsTruncated: '{{count}} more models — trimmed for readability.',
    modelsHelp:
      'Copy a model name into the three tiers under Admin → System settings. Any model you actually use needs a pricing row, or calls are refused.',
    provider: 'Provider',
    providerAnthropic: 'Anthropic (api.anthropic.com)',
    providerLitellm: 'LiteLLM (self-hosted gateway)',
    providerOpenai: 'OpenAI (api.openai.com)',
    providerGemini: 'Google Gemini (AI Studio)',
    directProviderHelp:
      'After adding, press "Test" to load the models this key can use, then pick model chains for the three tiers. Models without a price row are skipped when calling; see which ones are priced under AI console → Models.',
    baseUrl: 'Gateway URL',
    baseUrlHelp: 'No /v1 suffix — the system adds it. Only LiteLLM needs this field.',
    labelPlaceholder: 'Personal',
    title: 'AI key',
    subtitle: 'Keys are encrypted at rest. The API never returns the real key.',
    none: 'No keys yet',
    noneHelp:
      'Add your key below, test the connection, then activate it. Without a key every AI feature falls back to the manual path.',
    label: 'Label',
    apiKey: 'API key',
    add: 'Add key',
    test: 'Test',
    activate: 'Activate',
    active: 'In use',
    verified: 'Verified',
    unverified: 'Not verified',
    created: 'Key added. Test the connection next.',
    activated: 'Key activated.',
    testPassed: 'Key works — {{count}} models visible.',
    testFailed: 'Test failed.',
    mustTestFirst: 'The connection test must pass before activating.',
    testBeforeActivate: 'New keys start inactive. Test the connection first.',
  },
  budget: {
    title: 'AI spend limits',
    subtitle:
      'Without limits a single runaway loop produces a real invoice. The hard limit blocks new calls.',
    none: 'No limits set — AI calls are not blocked.',
    periodLabel: 'Period',
    soft: 'Warning threshold (USD)',
    hard: 'Blocking threshold (USD)',
    saved: 'Limits saved.',
    perOrg: 'organisation-wide',
    perUser: 'per user',
    softMustBeLower: 'The warning threshold must be below the blocking one, or it never fires.',
    help: 'Crossing the warning threshold still runs, it only warns. Crossing the blocking one rejects new calls.',
    period: { day: 'Daily', month: 'Monthly' },
  },
  files: { upload: 'Upload' },
  org: {
    demoAccount: 'A demo account cannot open a new organization. Sign in with your own account.',
    title: 'My organisations',
    subtitle: 'Where you work, your role in each, and where to start a new one.',
    current: 'current',
    createLabel: 'Start a new organisation',
    createPlaceholder: 'Organisation name',
    create: 'Create',
    createHelp: 'You become its admin, and the screen switches there right away.',
    nameTooShort: 'The name needs at least 2 characters.',
    loadFailed: 'Could not load your organisations.',
    switchLabel: 'Switch organisation',
    /** Name and tag of the personal space in the organisation switcher — APM-PSPACE-f. */
    personalName: 'Personal space',
    personalBadge: 'Personal',
    /** Tag of a `GUEST` membership in the organisation switcher — APM-PSPACE-j. */
    guestBadge: 'Guest',
  },
  orgInvite: {
    demoAccount:
      'A demo account cannot accept an invitation to a real organization. Sign out, sign in with your own account, then open the link again.',
    title: 'Organisation invite link',
    subtitle:
      'Several links at once, each with its own role. Stopped links stay listed if anyone joined through them.',
    role: 'Role on joining',
    create: 'Create link',
    revoke: 'Revoke',
    colRole: 'Role',
    colCreated: 'Created',
    colExpires: 'Expires',
    colStatus: 'Status',
    colUsed: 'Joined',
    colMessage: 'Message',
    message: 'Message (optional)',
    messagePlaceholder: 'Hi Minh, this is the link to our team workspace.',
    statusActive: 'Active',
    statusStopped: 'Stopped',
    onceOnly: 'The link is shown once. Copy it now — you cannot read it back.',
    copy: 'Copy link',
    copyManual: 'Select and copy the link above.',
    copied: 'Link copied.',
    copyFailed: 'Could not copy. Select and copy it manually.',
    // Approval links — APM-ORGJR-b.
    requiresApproval: 'Requires admin approval',
    requiresApprovalOn:
      'People who open the link send a request; they join once an admin approves.',
    requiresApprovalOff:
      'Anyone holding the link joins directly — only for links sent privately to trusted people.',
    colApproval: 'Joining',
    needsApproval: 'Needs approval',
    direct: 'Direct',
  },
  joinOrg: {
    title: 'Join organisation',
    help: 'Accept the invitation. The screen then switches to this organisation.',
    accept: 'Join',
    members: '{{count}} members',
    invitedBy: 'invites you to join as {{role}}',
    invitedAs: 'You are invited to join as {{role}}',
    invalidTitle: 'This invite link is no longer valid',
    invalidHelp: 'It may have expired or been revoked. Ask for a new one.',
    // Approval links — APM-ORGJR-b.
    helpApproval:
      'This organisation approves each person. Send a request; you can join once an admin approves.',
    request: 'Request to join',
    pendingTitle: 'Request sent',
    pendingHelp:
      'An admin of {{org}} will review your request. You will be notified of the result.',
    failedTitle: 'Could not join',
  },
  /** Pending join requests — APM-ORGJR-b. */
  orgJoinRequests: {
    // The two tabs of the invite-link section — APM-ORGJR-c.
    tabLinks: 'Invite links',
    tabApprovals: 'Approvals',
    emptyTitle: 'Nobody is waiting for approval',
    emptyHelp:
      'People who open a "needs approval" link and send a request appear here, and org admins get a notification.',
    linkCreated: 'Link created {{date}}',
    colName: 'Requester',
    colRole: 'Role',
    colLink: 'Link',
    colSentAt: 'Sent',
    noAccount: 'no account yet — approving creates a Google sign-in account',
    accept: 'Approve',
    decline: 'Decline',
    accepted: '{{name}} was approved into the organisation.',
    acceptedNoAccount: '{{name}} was approved. They sign in with Google to join.',
    declined: 'Declined the request from {{name}}.',
    declineTitle: 'Decline the request from {{name}}',
    note: 'Reason (visible to the requester, optional)',
    decided: 'This request has already been handled.',
  },
  // Organisation merge / split — APM-ORGMS-f (docs/plan-org-restructure.md §3.7, §3.8).
  orgRestructure: {
    menu: {
      button: 'Merge / split organisation',
      merge: 'Propose a merge',
      split: 'Propose a split',
    },
    kind: {
      merge: 'Organisation merge proposal',
      split: 'Organisation split proposal',
    },
    status: {
      awaiting_partner: 'Waiting for the other side to accept',
      awaiting_approval: 'Awaiting approval',
      approved: 'Fully approved — about to run',
      executing: 'Running',
      done: 'Done',
      cancelled: 'Cancelled',
      expired: 'Expired',
      failed: 'Did not finish',
    },
    direction: {
      into_initiator: 'Merge the other organisation into this one',
      into_initiatorHelp:
        'Your organisation is kept; the other side’s projects, members and catalogues move here, then it closes.',
      into_partner: 'Merge this organisation into the other one',
      into_partnerHelp:
        'The other organisation is kept; everything of yours moves there, then yours closes.',
      merge: '“{{source}}” merges into “{{target}}”',
      otherOrg: 'the other organisation',
      yourOrg: 'your organisation',
    },
    banner: {
      partner: 'with “{{name}}”',
      noPartner: 'no one has accepted the invitation yet',
      newOrg: 'splitting off “{{name}}”',
      approved: '{{done}}/{{total}} approved',
      open: 'View proposal',
    },
    propose: {
      title: 'Propose an organisation merge',
      direction: 'Direction',
      help: 'Emails are optional: once created you get a link to send to the other side’s Admin. The configuration (Admins, Directors after the merge, project keys) can be edited later, before everyone approves.',
      submit: 'Create proposal',
      created:
        'Proposal created. Send the link below to the other organisation’s Admin — it can be used once.',
      open: 'Open proposal',
    },
    invite: {
      link: 'Invitation link',
      copy: 'Copy link',
      copied: 'Link copied',
      copyFailed: 'Could not copy — select and copy it manually',
      linkGone:
        'The link is no longer usable (already accepted, expired, or the proposal is closed).',
      expires: 'Expires {{date}}',
      emails: 'Other side’s Admin emails (several, separated by commas or new lines)',
      emailsPlaceholder: 'admin@other-company.com',
      emailsHelp:
        'Invitations arrive as in-app notifications and on Telegram (if linked), only for emails that are an organisation Admin. The system never tells which emails have an account.',
      invalidEmails: 'Not valid emails: {{emails}}',
      send: 'Send invitation ({{count}})',
      sent: 'Invitation sent to the emails that are an organisation Admin',
    },
    panel: {
      label: 'Organisation merge / split proposal',
      meta: 'Created by {{by}} on {{created}} · expires {{expires}}',
      cancel: 'Cancel proposal',
      cancelConfirm: 'Cancel this proposal? Starting again needs a new proposal.',
      cancelled: 'Proposal cancelled',
    },
    tab: {
      config: 'Configuration',
      preflight: 'Pre-check',
      approvals: 'Approvals',
      progress: 'Progress',
      report: 'Report',
    },
    config: {
      invite: 'Invite the other side',
      direction: 'Direction',
      roles: 'Admins and Directors after the merge',
      rolesHelp:
        'Pick from members of both sides — at least one Admin. People in both keep their role in the kept organisation unless picked here.',
      admin: 'Admin',
      director: 'Director',
      asAdmin: 'Make {{name}} an Admin',
      asDirector: 'Make {{name}} a Director',
      admins: 'Admins',
      directors: 'Directors',
      needAdmin: 'Pick at least one Admin.',
      projectKeys: 'Clashing project keys',
      projectKeysHelp:
        'Projects of the merged-in organisation whose key clashes with the kept one get a new key. Leave blank to use the suggestion.',
      project: 'Project',
      oldKey: 'Current key',
      newKey: 'Key after merge',
      newKeyFor: 'Key after merge for {{name}}',
      badKey:
        'A project key is UPPERCASE letters and digits, starts with a letter, at least 2 characters.',
      save: 'Save configuration',
      saved: 'Saved configuration version {{version}} — everyone needs to approve again',
      saveHelp: 'Saving makes a new version: every earlier approval stops counting.',
      version: 'Configuration version {{version}}',
      stale:
        'Someone else just edited the configuration — reloaded the new version, please review it.',
    },
    preflight: {
      blockers: 'Blockers ({{count}}) — cannot run until all are cleared',
      noBlockers: 'No blockers.',
      warnings: 'Warnings ({{count}})',
      moving: 'Moving from “{{source}}” to “{{target}}”',
      projects: 'Projects',
      members: 'Members',
      newMembers: 'Of which new to the kept organisation',
      files: 'Files',
      storage: 'Storage after the merge: {{after}} / quota {{quota}}',
      projectKeys: 'Project keys to change ({{count}})',
      catalogRenames: 'Clashing catalogue entries to rename ({{count}})',
      peopleInBoth: 'People in both ({{count}})',
      personalConflicts: 'Clashing personal data',
      handling: {
        report:
          '{{model}}: {{count}} rows — the kept organisation’s copy wins, the other goes to the report',
        merge_folder: '{{model}}: {{count}} folders — files move into the same-named folder',
        list: '{{model}}: {{count}} rows — both kept',
      },
      dropped: 'Merged-in configuration to be dropped (the kept organisation’s applies)',
      rows: '{{model}}: {{count}} rows',
      levelRateLock:
        '{{members}} members on {{projects}} projects are costed by level — their old rate is locked until the merge date.',
      computedAt: 'Checked at {{at}} on configuration version {{version}}',
    },
    blocker: {
      PARTNER_MISSING: 'No organisation has accepted the invitation yet.',
      ORG_DEMO: 'One side is a demo organisation — demo organisations cannot merge.',
      ORG_PERSONAL: 'One side is a personal space — personal spaces cannot merge.',
      ORG_DELETED: 'One side has been deleted.',
      ORG_EXECUTING: 'One side is part of another running merge / split.',
      DUPLICATE_EMAIL:
        '{{count}} emails have two different accounts across the sides — resolve first.',
      NO_ADMIN: 'No Admin in the configuration is still an active member.',
      ROLE_HOLDER_NOT_MEMBER:
        '{{count}} people among the Admins / Directors are no longer members.',
      PROJECT_KEY_CONFLICT: '{{count}} project keys still clash after renaming.',
      PROJECT_KEY_RENAME_STALE:
        '{{count}} edited project keys target projects no longer in the merged-in organisation (e.g. after changing direction).',
    },
    warning: {
      STORAGE_QUOTA:
        'Both sides’ files together exceed the kept organisation’s quota — old files stay readable, new uploads are blocked until the quota is raised.',
      LEVEL_RATE_LOCK:
        'The merged-in organisation’s level rates will be locked as per-member rates until the merge date.',
      ORG_AUDIENCE_SHARE_LINKS:
        '{{count}} "organisation" share links will open to every member of the kept organisation.',
      FIGMA_CONNECTION_REVOKED:
        'Both sides have a Figma connection — the merged-in one will be revoked.',
      GIT_HOSTS_MERGED:
        '{{count}} git servers of the merged-in organisation share an address with the kept organisation’s — connections, repositories and git accounts will use the kept server, the duplicate is deleted.',
    },
    catalog: {
      department: 'Department',
      tech_stack: 'Tech stack',
      task_type: 'Task type',
      wbs_template: 'WBS template',
      document_template: 'Document template',
    },
    more: '… and {{count}} more',
    approvals: {
      summary: '{{done}}/{{total}} approved on configuration version {{version}}',
      state: {
        current: 'Approved version {{version}}',
        old: 'Approved an old version ({{version}})',
        rejected: 'Rejected',
        pending: 'Not yet',
      },
      reason: 'Reason: {{reason}}',
      waitPartner: 'Approval opens once the other side accepts the invitation.',
      irreversible:
        'When the last person approves, the two organisations merge right away and close for a few minutes. This cannot be undone.',
      approve: 'Approve',
      confirm: 'Approve configuration version {{version}}? This cannot be undone.',
      reject: 'Reject',
      reasonLabel: 'Reason for rejecting',
      rejectHelp: 'One rejection cancels the proposal.',
      rejectSubmit: 'Reject proposal',
      recorded: 'Your approval is recorded',
      allApproved: 'Everyone approved — the merge is starting',
      allApprovedSplit: 'Everyone approved — the split is starting',
      irreversibleSplit:
        'When the last person approves, the new organisation is created right away; this organisation and the new one are closed for a few minutes. This cannot be undone.',
      blocked:
        'Approval recorded, but the proposal still has blockers so it has not run — see Pre-check.',
    },
    progress: {
      steps: 'Steps',
      state: {
        done: 'done',
        current: 'running',
        pending: 'not yet',
      },
      queued: 'Fully approved — the run is about to start.',
      notStarted: 'Not run yet.',
      frozen: 'Both organisations closed since {{at}}.',
      projects: 'Moved {{moved}}/{{total}} projects',
      failedFrozen:
        'The run stopped midway — both organisations remain closed. The system operator will resume it.',
      failedOpen: 'The run stopped before moving any data — both organisations are open again.',
      errorCode: 'Error code: {{code}}',
    },
    step: {
      freeze: 'Close both organisations',
      preflight: 'Re-check',
      catalogs: 'Catalogues',
      projects: 'Projects',
      personal: 'Personal data',
      members: 'Members and roles',
      infra: 'Sessions and invitations',
      finish: 'Finish',
    },
    report: {
      none: 'No report yet.',
      executedAt: '“{{source}}” merged into “{{target}}” at {{at}}',
      joined: 'Members moved over',
      merged: 'Members in both (merged into one)',
      roleChanges: 'Role changes',
      projectKeys: 'Project keys changed',
      catalogRenames: 'Catalogue entries renamed',
      levelRateLocks: 'Members with locked old rate',
      folderMerges: 'Folders merged',
      figmaRevoked: 'Figma connections revoked',
      sessions: 'Sessions revoked',
      invites: 'Invitations revoked',
      leftBehind: 'Not moved (the kept organisation’s applies)',
      kept: '{{model}}: {{count}} clashing rows, the kept organisation’s copy wins',
      dropped: '{{model}}: {{count}} configuration rows dropped',
      setting: 'Setting “{{column}}” follows the kept organisation',
    },
    split: {
      title: 'Propose an organisation split',
      intro:
        'Pick people on the chart to the left (clicking a manager offers the whole branch), pick the projects that move, then name the new organisation and choose its Admins / Directors. Every Admin and Director of this organisation, plus the people named Admin / Director of the new one, must approve.',
      direction: 'Split “{{name}}” off “{{source}}”',
      treeHint: 'Picking people for the new organisation — {{count}} picked',
      selectPerson: 'Move {{name}} to the new organisation',
      modeFor: 'How {{name}} joins the new organisation',
      mode: {
        move: 'Move',
        both: 'Both',
      },
      modeHelp: {
        move: 'Leaves this organisation; personal data moves to the new one.',
        both: 'Stays here and also becomes a member of the new organisation.',
      },
      branchTitle: 'Pick the whole branch?',
      branchBody: '“{{name}}” manages {{count}} people. Pick them as well?',
      branchOnly: 'Only “{{name}}”',
      branchAll: 'Whole branch ({{count}} people)',
      name: 'New organisation name',
      slug: 'New organisation slug (address)',
      slugTaken: 'Another organisation already uses this slug — pick another one.',
      slugBad: 'Use lowercase letters, digits and hyphens, at least 2 characters.',
      timezone: 'Time zone',
      projects: 'Projects that move ({{count}})',
      projectsHelp:
        'Selected projects move to the new organisation. Every project member is added to the people list as “Both”.',
      projectCount: '{{count}} projects',
      missingTitle: '{{count}} members of selected projects are not in the people list',
      missingRow: '{{name}} — project {{project}}',
      missingHelp:
        'When a project moves, the people working on it must move too. Pick them again, or remove them from the project first. Until then the proposal cannot be created / saved.',
      missingFix: 'Pick again (both)',
      people: 'People joining the new organisation ({{count}})',
      peopleHelp:
        'The new organisation’s Admins (at least one) and Directors are chosen among them.',
      noPeople: 'Nobody picked yet — tick the box on each person’s card in the chart.',
      remove: 'Remove {{name}} from the list',
      problem: {
        name: 'The new organisation has no name yet.',
        slug: 'The slug is not valid.',
        people: 'Nobody is picked yet.',
        admin: 'Pick at least one Admin for the new organisation.',
      },
      created: 'Split proposal created — waiting for approvals',
      editHelp:
        'Change people on the chart to the left or in the list below. Saving makes a new version: every earlier approval stops counting.',
    },
    splitBlocker: {
      ORG_DEMO: 'Demo organisations cannot be split.',
      ORG_PERSONAL: 'Personal spaces cannot be split.',
      ORG_DELETED: 'The organisation has been deleted.',
      ORG_EXECUTING: 'The organisation is in another merge / split run.',
      SLUG_TAKEN: 'Another organisation already uses the new organisation’s slug.',
      NO_ADMIN: 'None of the new organisation’s Admins is still an active member.',
      PERSON_NOT_MEMBER: '{{count}} picked people are no longer active members.',
      PROJECT_NOT_IN_ORG: '{{count}} picked projects no longer belong to this organisation.',
      PROJECT_MEMBER_NOT_SELECTED:
        '{{count}} members of selected projects are not in the people list — pick them, or remove them from the project first.',
      LAST_ROLE_HOLDER_LEAVING:
        'Every Admin (or every Director) of this organisation is set to “Move” — nobody would hold that role here.',
    },
    splitWarning: {
      CROSS_LINKS: 'Links between moving and staying projects will be cut.',
      LINE_MANAGER_RESET:
        'People set to “Move” manage people who stay — those people’s line manager becomes empty.',
      PROJECT_AI_KEY_STAYS:
        'Moving projects use the AI key of someone who stays — the new organisation will spend on that key.',
      FIGMA_NOT_COPIED:
        'Moving projects have Figma files — the Figma connection is not copied, the new Admin sets it up.',
      STORAGE_QUOTA: 'Files of the moving projects exceed the new organisation’s storage quota.',
      INTEGRATION_SECRETS_COPIED:
        'Sync sources of the moving projects carry an app secret (Lark / Meegle) — the copy in the new organisation shares this organisation’s app. The new Admin should create their own app and paste it over.',
    },
    splitStep: {
      freeze: 'Close the organisation temporarily',
      preflight: 'Check again',
      catalogs: 'Create the new organisation, copy settings and catalogues',
      projects: 'Move projects',
      personal: 'Personal data',
      members: 'Members and roles',
      infra: 'Sessions and pending jobs',
      finish: 'Finish',
    },
    splitPreflight: {
      moving: 'Will split “{{name}}” off “{{source}}”',
      newOrg: 'Slug {{slug}} · time zone {{timezone}}',
      movePeople: 'People moving',
      bothPeople: 'People in both',
      catalogCopies: 'Catalogues copied to the new organisation',
      configCopies: 'Settings copied to the new organisation',
      crossLinks: 'Cross-boundary links that will be cut',
      link: '{{kind}}: {{count}}',
    },
    splitLink: {
      task_dependency: 'Task dependencies',
      sprint_dependency: 'Sprint dependencies',
      raid_task_link: 'RAID – task links',
      change_request_task: 'Change request tasks',
      objective_task: 'Objective tasks',
      bug_task: 'Bug – task links',
      issue_task: 'Issue – task links',
    },
    splitModel: {
      Department: 'Departments',
      TechStack: 'Tech stacks',
      TaskType: 'Task types',
      WbsTemplate: 'WBS templates',
      DocumentTemplate: 'Document templates',
      OrgKnowledge: 'Organisation knowledge',
      AiMemoryNote: 'AI memory notes',
      OrgWorkScheduleShift: 'Work shifts',
      OrgHoliday: 'Holidays',
      LevelRate: 'Level rates',
      AiBudget: 'AI cost budgets',
      CostRate: 'Personal cost rates',
      UserSkill: 'Skills',
      SyncProvider: 'Sync sources',
      GitHost: 'Git servers',
    },
    splitReport: {
      executedAt: '“{{name}}” split off “{{source}}” at {{at}}',
      projects: 'Projects moved',
      moved: 'People moved',
      both: 'People in both',
      lineManagersReset: 'Line managers cleared',
      links: 'Links cut',
      copies: 'Copied to the new organisation',
    },
    accept: {
      subtitle: 'Organisation merge invitation',
      title: '“{{org}}” invites you to merge',
      direction: {
        into_initiator: '“{{org}}” proposes merging “{{mine}}” into “{{org}}”.',
        into_partner: '“{{org}}” proposes merging into “{{mine}}”.',
      },
      size: '{{members}} members · {{projects}} projects',
      inviter: 'Invited by {{name}}.',
      expires: 'Link expires {{date}}.',
      pickOrg: 'Accept for organisation',
      forOrg: 'Accept for “{{name}}”.',
      noEligible: 'You are not an Admin of any organisation that can accept this invitation.',
      help: 'Accepting merges nothing yet: next, Admins and Directors of both sides review the configuration and approve together.',
      submit: 'Accept invitation',
      joined: 'Invitation accepted — opening the proposal',
      back: 'Back to the org chart',
      refusal: {
        invalid: 'This merge link is no longer valid',
        not_admin: 'Only an organisation Admin can accept a merge invitation',
        demo: 'Demo organisations cannot merge',
      },
      refusalHelp: {
        invalid:
          'The link expired, was already accepted, or the proposal is closed. Ask for a new link.',
        not_admin:
          'Ask your organisation’s Admin to open this link, or sign in with an Admin account.',
        demo: 'Demo accounts are never part of a merge. Sign in with a real account to accept.',
      },
    },
    frozen: {
      title: 'The organisation is being merged / split',
      body: 'This usually takes a few minutes. The page reopens by itself when done — no need to reload.',
      switchHint: 'Member of another organisation? Switch in the account menu.',
    },
  },
  orgChart: {
    title: 'Org chart',
    subtitle: 'Who reports to whom — built from line managers, not departments.',
    total: '{{count}} people',
    cycleWarning: '{{count}} people left out because their reporting line forms a loop',
    lineManager: 'Line manager',
    lineManagerHelp:
      'They approve projects this employee opens. Only team leaders and above can be chosen.',
    tier: {
      lead: 'Leadership',
      manage: 'Leaders & admins',
      staff: 'Staff',
    },
    expand: 'Expand',
    collapse: 'Collapse',
    reportCount: '{{count}} people in this branch',
    inactive: 'deactivated',
    empty: 'No chart yet',
    emptyHelp: 'Nobody has a line manager set. Assign one under Users in the admin area.',
    invite: 'Invite to a project',
    inviteLabel: 'Invite {{name}} to a project',
    inviteTitle: 'Invite {{name}} to a project',
    inviteHelp:
      'Only projects where you are the PM are listed. The invitee is notified and decides whether to accept.',
    inviteProject: 'Project',
    inviteProjectPick: '— pick a project —',
    failed: 'Could not load the org chart.',
  },
  orgAdminRole: {
    adminNote:
      'This person is an organisation admin. The position below is held alongside it — their access is admin rights plus the rights of the position.',
    selfLocked: 'You cannot change your own position. Ask another admin to do it.',
    operatorLocked: 'The system operator account has no position in the organisation to change.',
    matrixNote:
      'Organisation admin is added on top: an admin has this column plus the column of the position they hold (Director, Leader or Employee).',
  },
  projectFiles: {
    title: 'Files',
    link: 'Files · {{size}}',
    back: 'Back to Overview',
    usage_one: 'used · {{count}} file',
    usage_other: 'used · {{count}} files',
    empty: 'This project has no files yet',
    emptyHelp: 'Task attachments and uploaded documents will show up here.',
    name: 'File name',
    location: 'Located in',
    uploadedAt: 'Uploaded',
    size: 'Size',
    actions: 'Actions',
    statusActive: 'Available',
    statusDeleted: 'Deleted',
    deletedBy: 'Deleted by {{name}} at {{at}}',
    download: 'Download {{name}}',
    delete: 'Delete {{name}}',
    confirmDelete: 'Delete “{{name}}”? It is removed from storage and can no longer be downloaded.',
    deleted: 'File deleted.',
    cannotDeleteDocument: 'This file is a document version — manage it under Documents.',
    cannotDeleteOthers: 'Only the project manager or the uploader can delete this file.',
    inTask: 'Task',
    inDocument: 'Document “{{title}}” · v{{version}}',
    taskGone: 'The task was deleted',
    inBug: 'Bug #{{code}} {{title}}',
    unattached: 'Not attached',
  },
  docFormat: {
    normalize: 'Normalize formatting',
    ai: 'Format with AI',
    title: 'Format document',
    before: 'Before',
    after: 'After',
    apply: 'Apply',
    discard: 'Discard',
    promptLabel: 'Extra request for AI (optional)',
    promptPlaceholder: 'E.g. more formal, Georgia for headings',
    running: 'AI is answering…',
    run: 'Run AI',
    willAsk: 'AI will decide {{count}} uncertain points and the overall plan.',
    bodyFont: 'Body font',
    bodySize: 'Base font size (px)',
    asPlan: 'As planned ({{value}})',
    appDefault: 'App default',
    bodyHint:
      'Applies to plain text only — headings, highlighted text and code are kept separately.',
    aiDone: 'AI answered {{count}} points.',
    aiBadge: 'AI',
    aiHelp:
      'AI takes the plan measured on your device as a hint and decides fonts, sizes, heading levels, indents and images for the whole document. No text is edited; preview before applying.',
    manualHelp:
      'Deterministic plan, no AI needed: majority font and size, numbered lines become headings at the right level, indents unified per class, standalone images and formulas centered. Tables, code and text colors are kept.',
    resetFonts: 'Reset to the app default font and size',
    steps: 'Done automatically',
    nothingAuto: 'Nothing to change automatically.',
    step: {
      font: 'Font and size: {{count}} blocks set to {{font}} (code keeps its font)',
      defaultFont: 'the default font',
      headings: 'Headings: {{count}} numbered lines re-leveled',
      lists: 'Lists: {{count}} lines became bullets / numbered items',
      indent: 'Indents: {{count}} paragraphs unified per class',
      images: 'Images: {{count}} blocks repositioned',
      formulas: 'Formulas: {{count}} re-aligned',
    },
    questions: 'Needs a decision ({{count}})',
    noQuestions: 'Nothing needs AI — the deterministic plan is enough.',
    truncated: '{{count}} more points took the default.',
    notes: 'AI notes',
    q: {
      heading: 'Is this line a heading?',
      fontRole: 'Which font is the body font?',
      indentParagraph: 'Paragraph indent',
      indentList: 'List item indent',
      image: 'How to place the image?',
      level: 'Pasted heading group',
      imageMove: 'Image after its lead-in',
    },
    opt: {
      paragraph: 'Plain paragraph',
      bullet: 'First item of the list below',
      ordered: 'First item of the numbered list below',
      heading: 'Heading level {{level}}',
      noIndent: 'No indent',
      firstLine: 'First line {{first}} mm, left {{left}} mm',
      hanging: 'Hanging {{hang}} mm, left {{left}} mm',
      square: 'Text wraps around, {{pct}} %',
      topBottom: 'Own line, {{pct}} %',
      sibling: 'Sibling of the section above',
      child: 'Child section',
      move: 'Move after the lead-in',
      stay: 'Keep in place',
    },
    noChange: 'The document is already tidy — nothing to change.',
    partial:
      'AI did not read the whole document (hit its limit) — the plan only covers what it read.',
    tooLong: 'The document has more than {{max}} blocks — AI only reads the first {{max}}.',
    applied: 'Formatting applied — Ctrl+Z to undo.',
  },
  docCellFormat: {
    button: 'Data format',
    title: 'Cell data format',
    kinds: {
      general: 'Automatic',
      text: 'Text',
      number: 'Number',
      currency: 'Currency (₫)',
      percent: 'Percent',
      date: 'Date & time',
    },
    hints: {
      general: 'Shown exactly as typed',
      text: 'Never calculated, even =…',
    },
    decimals: 'Decimal places',
    decimalsCount_one: '{{count}} decimal place',
    decimalsCount_other: '{{count}} decimal places',
    fewerDecimals: 'Decrease decimal places',
    moreDecimals: 'Increase decimal places',
    datePattern: 'Display as',
    appliesTo_one: 'Applies to the selected cell',
    appliesTo_other: 'Applies to {{count}} selected cells',
    dialog: {
      open: 'Format cells…',
      title: 'Format cells',
      category: 'Category',
      type: 'Type',
      preview: 'Preview',
      previewCell: 'Preview with the selected cell’s value',
      previewSample: 'Preview with a sample value (the selected cell is empty)',
      apply: 'Apply format',
      kinds: {
        general: 'Automatic',
        number: 'Number',
        currency: 'Currency',
        accounting: 'Accounting',
        date: 'Date',
        time: 'Time',
        datetime: 'Date & time',
        percent: 'Percentage',
        fraction: 'Fraction',
        scientific: 'Scientific',
        text: 'Text',
        custom: 'Custom',
      },
      hints: {
        general: 'Shown exactly as typed, no specific format.',
        number: 'Numbers with a thousands separator, decimal places and a negative style.',
        currency: 'Numbers with a currency symbol; pick the symbol, its position and the negative style.',
        accounting: 'As in Excel: symbol and minus sign on the left edge, number on the right, zero as "-".',
        date: 'Cells holding a date (dd/MM/yyyy) show in the chosen style, with a calendar button.',
        time: 'Cells holding a time (HH:mm) or a fraction of a day (0.5 = 12:00).',
        datetime: 'Cells holding a date and time (dd/MM/yyyy HH:mm) show in the chosen style.',
        percent: 'Multiplied by 100 with a % sign.',
        fraction: 'Shows the decimal part as a fraction.',
        scientific: 'Powers of ten, like 1.23E+05.',
        text: 'The cell is text and never calculated — even =… and numbers.',
        custom: 'Type an Excel format code, e.g. #,##0.00 ₫;[Red]-#,##0.00 ₫ or 0.0%.',
      },
      thousands: 'Use a thousands separator',
      symbol: 'Symbol',
      symbolPosition: 'Position',
      symbolAfter: 'After the number',
      symbolBefore: 'Before the number',
      negative: 'Negative numbers',
      negatives: {
        minus: 'Minus sign',
        red: 'Minus sign, red',
        parens: 'In parentheses',
        redParens: 'In parentheses, red',
      },
      code: 'Format code',
      invalidCode: 'Invalid format code — check brackets and quotes.',
    },
    picker: {
      open: 'Pick a date',
      title: 'Pick date & time',
      day: 'Day',
      month: 'Month',
      year: 'Year',
      hour: 'Hour',
      minute: 'Minute',
      monthShort: 'M{{month}}',
      today: 'Today',
      clear: 'Clear',
      done: 'Done',
      keyboardHint: 'Type digits into each box; a full box moves to the next one. Enter to finish.',
    },
  },
  /** Suggestion mode / track changes in `.chilldoc` — APM-CD2-h, plan-chilldoc-v2 §7.1. */
  chilldocReview: {
    title: 'Review',
    track: 'Track changes',
    panel: 'Review pane',
    trackOn: 'Tracking: typed and deleted text become suggestions for others to accept or reject.',
    trackOff: 'Off: edits go straight into the document.',
    structureBlocked: 'Turn off tracking to change structure (tables, images, charts, equations…)',
    empty: 'No suggestions yet.',
    count: '{{count}} suggestions',
    pending_one: '{{count}} suggestion awaiting review',
    pending_other: '{{count}} suggestions awaiting review',
    pendingHint: 'Showing the unreviewed text: suggested insertions are left out and suggested deletions are kept. Editors review them in the editor.',
    kind: { insert: 'Insert', delete: 'Delete' },
    accept: 'Accept',
    reject: 'Reject',
    acceptAll: 'Accept all',
    rejectAll: 'Reject all',
    jump: 'Go to suggestion',
    close: 'Close',
    unknownAuthor: 'Unknown author',
  },
  docComments: {
    add: 'Comment',
    selectFirst: 'Select some text or an object before commenting',
    tooMany: 'This document has too many comments (max 500) — delete some resolved ones',
    placeholder: 'Write a comment…',
    submitHint: 'Ctrl+Enter to send',
    cancel: 'Cancel',
    save: 'Save',
    send: 'Send',
    close: 'Close',
    edited: 'edited',
    edit: 'Edit',
    delete: 'Delete',
    deleteThread: 'Delete comment',
    confirmDelete: 'Delete this comment?',
    open: 'Open',
    resolve: 'Resolve',
    reopen: 'Reopen',
    resolvedBy: 'Resolved by {{name}} · {{at}}',
    reply: 'Reply',
    replyPlaceholder: 'Reply…',
    new: 'New comment',
    cardLabel: 'Comments',
    title_one: '{{count}} comment',
    title_other: '{{count}} comments',
    bubble_one: '{{count}} comment — click to view',
    bubble_other: '{{count}} comments — click to view',
    jump_one: '{{count}} open comment in this section — click to jump',
    jump_other: '{{count}} open comments in this section — click to jump, click again for the next',
    summary: 'Comments: {{open}} open · {{resolved}} resolved',
    showResolved: 'Show resolved comments',
    dragHint: 'Drag to move the comment box',
    shareStale:
      'People with full-document links and invitees are not seeing the latest version (comments removed) yet.',
    shareUpdate: 'Update shared copy',
    shareUpdated: 'Shared copy updated',
    shareRefreshFailed: 'Could not update the shared copy — recipients still see the previous save',
    retry: 'Retry',
  },
  docTableSort: {
    open: 'Sort and filter column {{column}}',
    title: 'Column {{column}}',
    sortAsc: 'Sort A → Z',
    sortDesc: 'Sort Z → A',
    blocked: {
      noHeader: 'The table has no header row yet.',
      merged: 'Cannot sort: the data range has vertically merged cells. Split them first.',
      noData: 'Sorting needs at least two data rows.',
      readOnly: 'The document is read-only — filtering only.',
    },
    warning:
      "The table has formulas pointing at other rows. When sorting, a reference to a single cell moves with the formula's row (like copying the formula), while a multi-row range (B2:B9, for example) stays put — those formulas may point at a different cell or give a different result.",
    fixedRows: 'Row {{rows}} totals other rows — it stays in place and is not sorted.',
    confirmSort: 'Sort anyway',
    cancel: 'Cancel',
    filter: 'Filter',
    filterNote: 'Filtering only hides rows while viewing; the file does not change.',
    search: 'Search values',
    selectAll: 'Select all',
    noMatch: 'No matching values.',
    clearFilter: 'Clear this column filter',
    clearAll: 'Clear all filters of the table',
    blank: '(Blank)',
    chip: 'Filtered: {{filter}}',
    chipValues: '{{column}} = {{values}}',
    chipNone: '{{column}}: all values hidden',
    chipCount: '{{column}}: {{count}}/{{total}} values',
    clearColumn: 'Clear filter: {{filter}}',
    repeatHeader: 'Repeat header rows',
    repeatHeaderHint: 'At the top of every A4 page, sticky while scrolling long tables, and in Word files.',
  },
  docTable: {
    insertButton: 'Insert table',
    gridPicker: 'Choose table size',
    size: '{{rows}} rows × {{cols}} columns',
    pickHint: 'Hover to choose a size, or type rows and columns.',
    rows: 'Rows',
    cols: 'Columns',
    insert: 'Insert',
    toolbar: 'Table toolbar',
    addRowAbove: 'Insert row above',
    addRowBelow: 'Insert row below',
    addColumnLeft: 'Insert column left',
    addColumnRight: 'Insert column right',
    deleteRow: 'Delete row',
    deleteColumn: 'Delete column',
    deleteTable: 'Delete table',
    mergeCells: 'Merge cells (drag to select several cells first)',
    splitCell: 'Split merged cell',
    widthMode: 'Table width',
    widthModes: { fit: 'Anchor to left & right margins', fixed: 'Fixed width' },
    autoFit: 'Auto-fit column widths to content',
    resizeColumn: 'Drag to resize column {{column}} (left/right arrows)',
    tidyTable: 'Tidy table automatically',
    resizeRow: 'Drag to resize row {{row}} (up/down arrows; double-click: fit content)',
    selectColumn: 'Select column {{column}} (while typing a formula: insert its range)',
    selectRow: 'Select row {{row}} (while typing a formula: insert its range)',
    selectTable: 'Select the whole table (while typing a formula: insert its range)',
    fillHandle:
      'Drag to copy the formula to adjacent cells (double-click: fill down to the last row)',
    headerRows: 'Header rows',
    headerRowsValue: 'Header rows: {{count}}',
    rowsGroup: 'Rows',
    columnsGroup: 'Columns',
    cellsGroup: 'Cells',
    dataGroup: 'Data',
    moveToolbar: 'Drag to move the toolbar (double-click: back to place)',
    collapseToolbar: 'Collapse toolbar — group buttons',
    expandToolbar: 'Expand toolbar — show every button',
    noRoomToExpand: 'Not enough room for every button — buttons are grouped',
    cellColor: 'Cell colour',
    alignGroup: 'Horizontal alignment',
    align: { left: 'Align left', center: 'Align centre', right: 'Align right' },
    valignGroup: 'Vertical alignment',
    valign: { top: 'Align top', middle: 'Align middle', bottom: 'Align bottom' },
    cellAddress: 'Selected cell',
    formulaBar: 'Cell formula or value',
    formulaPlaceholder: 'Type a value, or =SUM(B2:B5)',
    functions: 'Functions…',
    fnGroup: {
      math: 'Math',
      count: 'Count & conditions',
      logic: 'Logic',
      text: 'Text',
      date: 'Date',
    },
  },
  formula: {
    fn: {
      SUM: 'Adds numbers.',
      AVERAGE: 'Arithmetic mean.',
      MIN: 'Smallest number.',
      MAX: 'Largest number.',
      ROUND: 'Rounds to a number of digits (half up).',
      ROUNDUP: 'Rounds away from zero.',
      ROUNDDOWN: 'Rounds toward zero.',
      ABS: 'Absolute value.',
      SQRT: 'Square root.',
      MOD: 'Remainder (sign of the divisor).',
      POWER: 'Raises to a power.',
      COUNT: 'Counts numeric cells.',
      COUNTA: 'Counts non-empty cells.',
      COUNTIF: 'Counts cells matching a condition, e.g. ">10".',
      SUMIF: 'Adds cells matching a condition.',
      AVERAGEIF: 'Averages cells matching a condition.',
      IF: 'Picks a value by condition.',
      IFERROR: 'Replaces a value when the formula errors.',
      AND: 'True when all conditions are true.',
      OR: 'True when any condition is true.',
      NOT: 'Inverts true/false.',
      CONCAT: 'Joins text.',
      LEN: 'Number of characters.',
      UPPER: 'Upper case.',
      LOWER: 'Lower case.',
      TRIM: 'Removes extra spaces.',
      TODAY: "Today's date.",
    },
  },
  /** Compare two `.chilldoc` versions — APM-CD2-j. */
  chilldocCompare: {
    title: 'Compare two versions',
    heading: 'Compare {{left}} → {{right}}',
    open: 'the open document',
    openVersion: 'Version {{n}} (open)',
    openUnsaved: 'the open document (with unsaved changes)',
    loading: 'Loading both versions…',
    loadFailed: 'Could not load the versions to compare.',
    added_one: '+{{count}} added',
    added_other: '+{{count}} added',
    removed_one: '−{{count}} removed',
    removed_other: '−{{count}} removed',
    modified_one: '{{count}} changed',
    modified_other: '{{count}} changed',
    countsLabel: '{{added}} blocks added, {{removed}} removed, {{modified}} changed',
    position: '{{k}}/{{n}}',
    previous: 'Previous change',
    next: 'Next change',
    identical: 'The two versions are identical.',
    unchanged_one: '⋯ {{count}} unchanged block',
    unchanged_other: '⋯ {{count}} unchanged blocks',
    formatOnly: 'Formatting changed',
    empty: '(empty paragraph)',
    before: 'Before',
    after: 'After',
    restore: 'Restore this block',
    restored: 'Block restored from the older version — undo with Ctrl+Z',
    restoreFailed: 'This block cannot be restored there',
    pickTwo: 'Compare two',
    pickFirst: 'Pick two versions to compare',
    pickSecond: 'Pick one more version',
    compareHint: 'Or compare one version with the open document',
    pickVersion: 'Pick version {{n}} to compare',
    compareWithOpen: 'Compare version {{n}} with the open document',
    op: {
      added: 'Added block:',
      removed: 'Removed block:',
      modified: 'Changed block:',
    },
    object: {
      image: 'Image',
      chart: 'Chart',
      drawing: 'Drawing',
      slideBreak: 'Slide',
      blockMath: 'Equation',
      horizontalRule: 'Horizontal line',
      other: 'Object',
    },
  },
  myFiles: {
    nav: 'Files',
    title: 'Files',
    folderLabel: 'Folder',
    renameFile: {
      menu: 'Rename',
      title: 'Rename “{{name}}”',
      label: 'New name',
      versionsHint: 'All {{count}} versions are renamed too.',
      renamed: 'Renamed to “{{name}}”',
    },
    convert: {
      menu: 'Convert to .chilldoc',
      title: 'Convert to .chilldoc',
      loading: 'Reading the file…',
      building: 'Converting…',
      preview: '.chilldoc preview',
      previewEmpty: 'Pick at least one item to preview.',
      cancelled: 'Conversion cancelled.',
      retry: 'Convert again',
      lossyTitle: 'The new file cannot keep:',
      name: 'New file name',
      sourceKept: 'The original file stays as it is — the result is a new file.',
      create: 'Create file',
      openEditor: 'Open to edit',
      created: 'Created {{name}}',
      openCreated: 'Open',
      pdf: { page: 'Page {{page}}' },
      /** `.pptx` → slides (APM-CVT-d). */
      deck: {
        slide: 'Slide {{n}}',
        empty: 'The file has no slides — the new file only has a title.',
        lost: {
          images: 'EMF / WMF / TIFF pictures could not be embedded and were left out: {{count}}.',
          background:
            'Backgrounds and decorations of the slide master are not kept ({{count}} slides).',
        },
        imagesDropped: 'Pictures too large or unreadable, left out of the slides: {{count}}.',
        sourceKept:
          'Animations, transitions and speaker notes are not part of a .chilldoc. The original .pptx is untouched — it still opens and presents in the PowerPoint editor.',
      },
      /** Spreadsheet → chilldoc — APM-CVT-b. */
      sheet: {
        size: '{{rows}} rows × {{cols}} columns',
        sizeEmpty: 'empty',
        moreRows: '… {{n}} more rows not converted.',
        moreCols: '… {{n}} more columns not converted.',
        notes: {
          empty: 'Sheet “{{name}}” is empty — skipped.',
          budget: 'Sheet “{{name}}” not converted: the file reached the {{max}}-cell limit.',
          rows: 'Sheet “{{name}}”: only the first {{kept}} rows converted, {{n}} rows left out ({{max}} cells per sheet).',
          cols: 'Sheet “{{name}}”: only the first {{kept}} columns converted, {{n}} columns left out.',
          header: 'Sheet “{{name}}”: unsure whether the first row is a header — using 1 header row.',
          unstyled: '.xls file: cell colours and fonts cannot be read — the table keeps data, merged cells and column widths.',
          images: '{{count}} pictures could not be read — skipped.',
        },
      },
      /** Office chart → chilldoc chart — APM-CVT-c. */
      chart: {
        title: 'Chart',
        category: 'Category',
        value: 'Value',
        series: 'Series {{n}}',
        notes: {
          percent: 'Chart “{{title}}”: 100 % stacking drawn as stacked columns of the real numbers (.chilldoc has no 100 % stacking).',
          unstacked: 'Chart “{{title}}”: stacking not kept — series drawn side by side.',
          vertical: 'Chart “{{title}}”: stacked horizontal bars drawn as stacked vertical columns.',
          secondaryAxis: 'Chart “{{title}}”: secondary axis not kept — all series share one axis.',
          series: 'Chart “{{title}}”: only the first {{max}} series kept, {{count}} left out.',
          points: 'Chart “{{title}}”: only the first {{max}} points kept, {{count}} left out.',
          rings: 'Chart “{{title}}”: a multi-ring doughnut keeps its first ring only, {{count}} rings left out.',
        },
      },
      /** `.ppt` 97–2003 → slides (APM-CVT-e). */
      ppt: {
        fallback:
          'The file index could not be read — slides were read in stored order (fallback mode): slide order or text may differ from PowerPoint.',
        lost: {
          images: 'EMF / WMF / TIFF / PICT pictures could not be embedded and were left out: {{count}}.',
        },
        sourceKept:
          'From a .ppt only text, text box positions and PNG / JPEG / BMP pictures are taken. Shapes, groups, backgrounds, slide masters, character formatting, animations and speaker notes are not in the new file. The original .ppt is untouched.',
        errors: {
          encrypted:
            'This PowerPoint file is password-protected (encrypted) — open it in PowerPoint, remove the password or save it as .pptx, then convert again.',
          invalid:
            'Could not read this .ppt — it is damaged or not a PowerPoint 97–2003 presentation.',
        },
      },
      pick: {
        label: 'Pick what to convert',
        count: '{{count}} / {{total}} selected',
        all: 'Select all',
        none: 'Clear selection',
        max: 'At most {{max}} items per run.',
      },
      notes: { frontMatter: 'The header block at the top of the file (front matter) was dropped.' },
      progress: {
        label: 'Conversion progress',
        download: 'Downloading · {{done}} / {{total}}',
        downloadUnknown: 'Downloading · {{done}}',
        read: 'Reading the file',
        unit: {
          byte: 'Converting · {{done}} / {{total}}',
          page: 'Converting page {{done}} / {{total}}',
          sheet: 'Converting sheet {{done}} / {{total}}',
          slide: 'Converting slide {{done}} / {{total}}',
          block: 'Converting block {{done}} / {{total}}',
          image: 'Processing image {{done}} / {{total}}',
          region: 'Processing region {{done}} / {{total}}',
        },
        convertUnknown: 'Converting…',
        images: 'Processing image {{done}} / {{total}}',
        imagesUnknown: 'Processing images…',
        ai: 'AI is working {{done}} / {{total}}',
        aiUnknown: 'AI is working…',
        save: 'Saving · {{percent}} %',
        saveUnknown: 'Saving…',
      },
      errors: {
        code: 'error code: {{code}}',
        tooLarge: 'The file is larger than {{max}} MB — it cannot be converted in the browser.',
        fetchFailed: 'Could not download the file — network or storage configuration.',
        http: 'Storage returned error {{status}} while downloading the file.',
        password: 'The file is password-protected — open it in its own app, save a copy without a password and convert that.',
        browser: 'This browser lacks a feature needed to read the file — try a recent Chrome, Edge or Firefox.',
        invalid: 'Could not read this file — it is damaged or not in the expected format.',
        unsupported: 'This file type cannot be converted yet.',
        reload: 'Could not load the converter — please reload the page.',
        reloadPage: 'Reload page',
        convertFailed: 'Conversion failed — try another range or convert again.',
        outputTooLarge: 'The new file is {{size}}, over the {{max}} limit — pick fewer pages, sheets or slides and try again.',
        saveFailed: 'Could not save: {{message}}',
      },
      ai: {
        toggle: 'Use AI for the hard parts',
        hint: 'AI will handle: {{parts}}. The plain version stays for you to pick.',
        hard: {
          pdfFormula: '{{count}} formulas to LaTeX',
          pdfTable: '{{count}} ruled tables to real tables',
          pdfAmbiguous: '{{count}} regions that may be a figure or text',
          question: '{{count}} unsure headings / list depths',
          omml: '{{count}} Word equations to LaTeX',
          sheetHeader: '{{count}} sheets with unclear header rows',
        },
        runLabel: 'Convert to .chilldoc — hard parts',
        runDone: 'AI changed {{count}} blocks — see the "With AI" tab in the convert dialog',
        failed: 'AI could not finish — the plain version is kept. {{message}}',
        uploadFailed: 'Could not upload the hard regions to storage — try again later.',
        tooMany: '{{count}} more regions stay as images (at most {{max}} per AI run).',
        tooManyQuestions: '{{count}} more heading / list questions keep their default (at most {{max}} per AI run).',
        tabs: { label: 'Version to save', plain: 'Plain', ai: 'With AI' },
        legend: 'Yellow outline: {{count}} blocks changed by AI. Create file and Open to edit use the selected version.',
        legendNone: 'AI changed no blocks — both versions are the same.',
        office: {
          formulasLeft_one: 'AI could not read {{count}} formula for sure — its placeholder stays for you to retype.',
          formulasLeft_other: 'AI could not read {{count}} formulas for sure — their placeholders stay for you to retype.',
          tooManyFormulas_one: '{{count}} more formula keeps its "could not import" placeholder (at most {{max}} per AI run).',
          tooManyFormulas_other: '{{count}} more formulas keep their "could not import" placeholder (at most {{max}} per AI run).',
          tooManySheets_one: '{{count}} more sheet keeps one header row (at most {{max}} per AI run).',
          tooManySheets_other: '{{count}} more sheets keep one header row (at most {{max}} per AI run).',
        },
      },
      run: {
        start: 'Start converting',
        setupHint: 'Pick the page range and whether to use AI for the hard parts, then press Start converting.',
        aiHint:
          'AI only runs where the converter cannot decide on its own — formulas, ruled tables, regions that may be picture or text, heading levels. The plain version is kept for you to choose.',
        batchLabel: 'Pages per AI run',
        batchOption: '{{count}}',
        batchHint:
          'More pages per run ⇒ fewer AI runs, lower cost. Each run takes at most {{regions}} regions and {{questions}} questions — more than that is split automatically.',
        pause: 'Pause',
        pauseHint: 'Press Pause to preview the part converted so far.',
        resume: 'Resume',
        stopHere: 'Stop here',
        restart: 'Convert again',
        paused: 'Paused — this is the part converted so far. Resume to finish, or Stop here to keep just this part.',
        page: 'Page {{page}} — {{done}} / {{total}} pages',
        aiCount: 'AI {{done}} / {{total}} runs',
        aiUpload: 'AI run {{batch}}/{{batches}} (pages {{first}}–{{last}}): sending image {{done}}/{{total}}',
        aiWaiting: 'AI run {{batch}}/{{batches}} (pages {{first}}–{{last}}): working on {{regions}} regions, {{questions}} questions',
        aiLabel: 'AI: hard parts of pages {{first}}–{{last}}',
        aiLabelPart: 'AI: hard parts of pages {{first}}–{{last}} (run {{call}}/{{calls}})',
        aiStopped: 'AI stopped from page {{page}} ({{message}}) — the rest is converted without AI.',
        stoppedAt: 'Stopped at page {{last}} — the file has pages {{from}}–{{last}} (picked {{from}}–{{to}}).',
      },
    },
    folder: {
      all: 'All',
      mine: 'My files',
      task: 'Tasks',
      todo: 'To-dos',
      document: 'Project documents',
      bug: 'Bug evidence',
      project: 'Project (unattached)',
    },
    searchLabel: 'Search by file name',
    searchPlaceholder: 'Search by name…',
    typeLabel: 'Filter by type',
    allTypes: 'All types',
    type: {
      image: 'Image',
      video: 'Video',
      audio: 'Audio',
      document: 'Word document',
      spreadsheet: 'Spreadsheet',
      presentation: 'Presentation',
      pdf: 'PDF',
      code: 'Source code',
      text: 'Text',
      archive: 'Archive',
      other: 'Other',
    },
    linkType: 'External link',
    /** `.chilldoc` sits in the `document` group (filter) but is not a Word file (QA 2026-10-13). */
    typeChilldoc: 'Chill document',
    sortLabel: 'Sort',
    sort: {
      createdAt_desc: 'Newest',
      createdAt_asc: 'Oldest',
      name_asc: 'Name A → Z',
      name_desc: 'Name Z → A',
      size_desc: 'Largest',
      size_asc: 'Smallest',
    },
    viewLabel: 'View',
    view: {
      details: 'Details',
      list: 'List',
      medium: 'Medium icons',
      large: 'Large icons',
      tiles: 'Tiles',
    },
    col: {
      name: 'Name',
      location: 'Location',
      type: 'Type',
      size: 'Size',
      uploadedAt: 'Uploaded',
      actions: 'Actions',
    },
    used: '{{size}} used',
    newDocument: 'New document',
    newDocumentFormat: {
      chilldoc: {
        label: 'Chill document (.chilldoc)',
        hint: 'Richest: text colour, fonts, tables with formulas, outline…',
      },
      markdown: {
        label: 'Markdown (.md)',
        hint: 'What markdown supports: bold, italic, lists, images…',
      },
      text: {
        label: 'Plain text (.txt)',
        hint: 'Text only, no formatting.',
      },
    },
    margins: {
      label: 'Left and right margins',
      left: 'Left margin (mm)',
      right: 'Right margin (mm)',
      leftShort: 'Left',
      rightShort: 'Right',
    },
    pages: {
      toggle: 'A4 pages like Word',
      pageNumber: 'Page {{n}}',
    },
    ruler: {
      label: 'Margin and indent ruler',
      toggle: 'Show/hide ruler',
      firstLine: 'First line indent (mm)',
      hanging: 'Hanging indent (mm)',
      leftIndent: 'Left indent (mm)',
      rightIndent: 'Right indent (mm)',
      value: '{{label}}: {{mm}} mm',
    },
    layout: {
      button: 'Page margins and indents',
      page: 'Page margins',
      pageHelp: 'Your own margins — used for every document you open, not saved in the file.',
      pageHelpDocument:
        'This document’s margins — saved in the file, so everyone who opens it sees them.',
      paragraph: 'Paragraph indent',
      paragraphHelp: 'Saved in the file, applied to every selected paragraph.',
      noBlock: 'Put the cursor in a paragraph or heading to indent it.',
      special: 'Special',
      specialNone: 'None',
      specialFirst: 'First line',
      specialHanging: 'Hanging',
      by: 'By (mm)',
    },
    mermaid: {
      alt: 'Mermaid diagram',
      loading: 'Drawing Mermaid diagram',
      empty: 'This Mermaid block has no code yet.',
      error: 'Could not draw the Mermaid code: {{message}}',
      loadFailed: 'Could not load the Mermaid renderer.',
      imageFailed: 'the browser could not display the diagram image',
      retry: 'Retry',
      convert: 'Convert to drawing',
      converting: 'Converting…',
      convertHint:
        'To drag blocks, change font size and fill colour with the mouse. The original code is kept in the drawing.',
      unsupported:
        'The "{{kind}}" diagram type is display-only. Flowchart, sequence, class, ER and state diagrams can be converted.',
      fixFirst: 'Fix the error in the code before converting.',
      tooLarge:
        'The diagram is too large for a drawing area (max 1600×2000 px, smallest font 12) — try the Vertical direction.',
      cannotConvert: 'This diagram cannot be converted into an editable drawing yet.',
      convertFailed: 'Could not convert: {{message}}',
      backToCode: 'Back to Mermaid code',
      toolbar: 'Mermaid diagram options',
      showCode: 'Show code',
      hideCode: 'Hide code',
      codeEmpty: 'The block has no code yet — type Mermaid code here.',
      codeBroken: 'The code has an error — fix it to collapse.',
      layout: 'Layout',
      layouts: {
        elk: 'ELK layered',
        elkTree: 'ELK tree',
        elkForce: 'ELK force',
        dagre: 'Dagre (Mermaid built-in)',
      },
      layoutDefault: '{{name}} (default)',
      fallback:
        'The "{{layout}}" layout cannot lay out this diagram (usually self-loops or subgraphs) — showing it with {{to}}.',
      directionFixed: 'The force layout has no direction.',
      viewOnly: 'Only changes this view — open the editor to save it to the file.',
      direction: 'Direction',
      horizontal: 'Horizontal',
      vertical: 'Vertical',
      size: 'Display size',
      sizes: {
        fit: 'Fit page',
        max75: 'Up to 75%',
        max50: 'Up to 50%',
        actual: 'Actual size (scroll)',
      },
    },
    drawing: {
      insertButton: 'Insert drawing area',
      insert: 'Create and open the editor',
      size: 'Drawing area size',
      width: 'Width (px)',
      height: 'Height (px)',
      presets: { small: 'Small', medium: 'Medium', large: 'Large', portrait: 'Portrait' },
      shapes: {
        triangle: 'Triangle',
        rightTriangle: 'Right triangle',
        pentagon: 'Pentagon',
        hexagon: 'Hexagon',
        parallelogram: 'Parallelogram',
        trapezoid: 'Trapezoid',
        star: 'Star',
        arrowRight: 'Block arrow right',
        arrowLeft: 'Block arrow left',
        arrowUp: 'Block arrow up',
        arrowDown: 'Block arrow down',
        arrowBoth: 'Double arrow',
        chevron: 'Chevron',
        plus: 'Plus',
        cylinder: 'Cylinder (database)',
        document: 'Document',
        cloud: 'Cloud',
        calloutRect: 'Callout',
        calloutRound: 'Round callout',
      },
      editorTitle: 'Drawing area',
      style: {
        label: 'Style',
        keep: 'Keep as is',
        sketch: 'Hand-drawn',
        sales: 'Sales',
        scientific: 'Scientific',
        minimal: 'Minimal',
      },
      minFont: 'Minimum font size (px)',
      advanced: 'Advanced',
      palette: {
        label: 'Colour palette',
        style: 'Style colours',
        ocean: 'Ocean',
        forest: 'Forest',
        sunset: 'Sunset',
        pastel: 'Pastel',
        colorblind: 'Colour-blind safe',
      },
      selectFirst: 'Select some text (a list, headings or steps) first.',
      previewLoading: 'Building the diagram…',
      fitsAt: 'Fits the area — font size {{size}} px.',
      notFit: 'At {{size}} px the diagram needs a {{width}}×{{height}} area.',
      cannotGrow:
        'At {{size}} px even the largest area is not enough — shorten the labels or drop some points.',
      grow: 'Enlarge the area to {{width}}×{{height}}',
      insertDiagram: 'Insert into the document',
      ai: {
        button: 'Draw a diagram with AI',
        title: 'Draw a diagram with AI',
        previewTitle: 'Diagram suggested by AI',
        help: 'AI reads the selected text and suggests boxes and arrows; the diagram is laid out to fit the area at your chosen font size. Preview it before inserting.',
        type: 'Diagram type',
        types: {
          auto: 'Let AI choose',
          flow: 'Process',
          tree: 'Hierarchy',
          mindmap: 'Mind map',
          cycle: 'Cycle',
          timeline: 'Timeline',
          comparison: 'Comparison',
        },
        prompt: 'Extra instructions (optional)',
        promptPlaceholder: 'E.g. highlight the review step, merge small steps',
        run: 'Draw diagram',
        running: 'Drawing…',
        done: '{{count}} boxes',
        shorten: 'Shorten labels with AI',
        shortening: 'Shortening…',
        partial:
          'AI did not fix every problem (over capacity or stray arrows) — the faulty parts were trimmed; check before inserting.',
        tooLong: 'The selection is longer than {{max}} characters — select less.',
      },
      frameName: 'Drawing area',
      edit: 'Double-click to edit the drawing',
      empty: 'Empty drawing area',
      emptyEdit: 'Empty drawing area — double-click to draw',
      loading: 'Loading drawing…',
      loadingEditor: 'Loading the drawing editor…',
      loadFailed: 'Could not load the drawing editor — check your connection and try again.',
      tooLarge:
        'The drawing is too large ({{size}} KB, max {{max}} KB) — remove some shapes and save again.',
      confirmDiscard: 'The drawing has unsaved changes. Close and discard them?',
    },
    objects: {
      menu: 'Object options',
      layoutButton: 'Layout options',
      editDrawing: 'Edit drawing',
      cut: 'Cut',
      copy: 'Copy',
      paste: 'Paste',
      delete: 'Delete',
      copyBlocked: 'The browser blocked copying from the menu — use Ctrl+C or Ctrl+X.',
      pasteBlocked: 'The browser blocked clipboard access — use Ctrl+V.',
      wrap: 'Wrap text',
      wraps: {
        inline: 'In line with text',
        square: 'Square',
        tight: 'Tight',
        through: 'Through',
        topBottom: 'Top and bottom',
        behind: 'Behind text',
        front: 'In front of text',
      },
      align: 'Align',
      aligns: { left: 'Left', center: 'Center', right: 'Right' },
      alignWrapped: 'Wrapped text can only place the object on the left or the right.',
      size: 'Size',
      sizePercent: '{{percent}}% of page width',
      sizeOriginal: 'Original size',
      sizeLoading: 'The image has not finished loading.',
      rotate: 'Rotate and flip',
      rotateRight: 'Rotate right 90°',
      rotateLeft: 'Rotate left 90°',
      flipX: 'Flip horizontal',
      flipY: 'Flip vertical',
      border: 'Border',
      rounded: 'Rounded corners',
      order: 'Order',
      toFront: 'Bring to front',
      toBack: 'Send to back',
      orderHint: 'Only applies to Behind text and In front of text.',
      describe: 'Describe image',
      removeBackground: 'Remove background…',
      reset: 'Reset formatting',
    },
    imageDescription: {
      title: 'Describe image',
      hint: 'The description travels with the document: AI uses it to pick images for slides and to place them without cropping the subject.',
      summary: 'What the image shows',
      summaryPlaceholder: 'e.g. Ha Long Bay from above, many limestone islands',
      kind: 'Kind',
      kinds: {
        photo: 'Photo',
        diagram: 'Diagram',
        chart: 'Chart',
        screenshot: 'Screenshot',
        logo: 'Logo',
        text: 'Text image',
        other: 'Other',
      },
      textHeavy: 'Text that must stay readable (never crop)',
      focal: 'Main subject at',
      focalX: { left: 'left', center: 'center', right: 'right' },
      focalY: { top: 'top', center: 'middle', bottom: 'bottom' },
      ai: 'Describe with AI',
      aiRunning: 'AI is looking at the image…',
      aiDone: 'AI described it — review, then Save.',
      aiFailed: 'AI could not describe this image. You can type it yourself.',
      none: 'No description yet.',
      save: 'Save',
    },
    contextMenu: {
      label: 'Editor menu',
      cut: 'Cut',
      copy: 'Copy',
      paste: 'Paste',
      pastePlain: 'Paste as plain text',
      smartPaste: 'Smart paste…',
      clipboardBlocked: 'The browser blocked clipboard access — use Ctrl+C, Ctrl+X, Ctrl+V.',
      clearFormat: 'Clear text formatting',
      selectBlock: 'Select paragraph',
      insertEquation: 'Insert equation',
      insertTable: 'Insert 3 × 3 table',
      toOrdered: 'Convert to numbered list',
      removeBullet: 'Remove bullet',
      toBullet: 'Convert to bulleted list',
      removeNumbering: 'Remove numbering',
      editEquation: 'Edit equation',
      toInline: 'Make inline equation',
      toBlock: 'Make display equation',
      copyLatex: 'Copy LaTeX',
      latexCopied: 'LaTeX copied',
      deleteEquation: 'Delete equation',
      openFile: 'Open file',
      removeFile: 'Remove from document (file is kept)',
    },
    listNumbering: {
      menu: 'List numbering',
      continueFrom: 'Continue numbering after',
      none: 'No numbered list above',
      restart: 'Restart at 1',
    },
    crossRef: {
      button: 'Cross-reference',
      title: 'Insert cross-reference',
      hint: 'Pick a heading, bookmark, figure or table. Numbers and text update as the document changes.',
      search: 'Search by text',
      showLabel: 'Show',
      show: { text: 'Text', number: 'Number', both: 'Both' },
      targets: 'Reference targets',
      groups: { heading: 'Headings', bookmark: 'Bookmarks', figure: 'Figures', table: 'Tables' },
      untitled: '(no text yet)',
      noTargets: 'This document has no headings, bookmarks or captions to reference yet.',
      noMatch: 'No target matches.',
      pickOne: 'Pick a target to insert.',
      preview: 'Will insert: {{text}}',
      insert: 'Insert reference',
      section: 'section {{number}}',
      figure: 'Figure',
      table: 'Table',
      broken: '⚠ broken reference',
      brokenHint: 'The target of this reference was deleted. Delete the reference or insert it again.',
      jump: 'Click to go to the referenced place',
      jumpEditing: 'Ctrl + click to go to the referenced place',
      bookmarkButton: 'Bookmark',
      bookmarkTitle: 'Add bookmark',
      bookmarkHint: 'Places a named point at the cursor for cross-references to point to.',
      bookmarkName: 'Bookmark name',
      bookmarkAdd: 'Add bookmark',
      captionButton: 'Add caption',
      captionHint: 'Figure / table numbers count themselves in document order.',
      caption: { figure: 'Figure caption', table: 'Table caption' },
    },
    findReplace: {
      button: 'Find and replace (Ctrl + F / Ctrl + H)',
      region: 'Find and replace',
      find: 'Find in document',
      replaceWith: 'Replace with',
      toggleReplace: 'Show / hide replace (Ctrl + H)',
      previous: 'Previous match (Shift + Enter)',
      next: 'Next match (Enter)',
      close: 'Close (Esc)',
      replace: 'Replace',
      replaceAll: 'Replace all',
      replaceHint: 'Replace the selected match and find the next one (Enter)',
      replaceAllHint: 'Replace every match — one undo reverts them all (Ctrl + Alt + Enter)',
      position: '{{current}}/{{total}}',
      total: '{{count}} matches',
      none: 'No matches',
      replacedAll: 'Replaced {{count}} matches',
      matchCase: 'Match case',
      wholeWord: 'Whole words only',
      matchDiacritics: 'Match diacritics',
    },
    docImport: {
      button: 'Import from file…',
      title: 'Import from file',
      loading: 'Reading file…',
      building: 'Building content…',
      buildingPages: 'Building page {{done}}/{{total}}…',
      preview: 'Preview of imported content',
      nothing: 'Nothing to import in this range.',
      insert: 'Import',
      insertAi: 'Import with AI',
      ai: {
        title: 'Import with AI',
        pending: '{{regions}} image regions and {{questions}} questions for AI to decide',
        asking: '{{count}} spots for AI to decide',
        running: 'AI is reading formulas and unclear regions…',
        applied: 'AI read {{count}} formulas',
        revert: 'Back to the non-AI version',
        nothing: 'Nothing for AI to decide — imported as usual.',
        editedDuring: 'You edited the imported block, so the AI result was not applied.',
        uploadFailed: 'Could not upload the formula images — try again later.',
        tooMany: '{{count}} more formulas kept as images (at most {{max}} per run).',
        tooManyQuestions: '{{count}} more heading / list questions keep their default (at most {{max}} per run).',
      },
      pages: {
        from: 'From page',
        to: 'To page',
        of: 'of {{count}} pages',
        strip: 'Pick pages',
        pick: 'Page {{page}} — click: first page, Shift + click: last page',
      },
      sections: {
        from: 'From section',
        to: 'To section',
        intro: 'Introduction',
        whole: 'The document has no headings — the whole document is imported.',
      },
      summary: {
        heading: '{{count}} headings',
        paragraph: '{{count}} paragraphs',
        listItem: '{{count}} list items',
        list: '{{count}} lists',
        figure: '{{count}} figures',
        formula: '{{count}} formulas (as images)',
        code: '{{count}} code blocks',
        table: '{{count}} tables',
        unsupported: '{{count}} not imported',
      },
      warnings: {
        scanned: '{{count}} pages have no text layer (scan) — imported as images.',
        tooLarge: '{{count}} images too large after compression — dropped.',
        outline: 'Built {{count}} headings from the PDF table of contents.',
        tocLinks: 'The contents page became {{count}} clickable links to headings.',
      },
      errors: {
        unsupportedType: 'Only PDF and .docx can be imported.',
        tooLarge: 'The file is larger than {{max}} MB — it cannot be read in the browser.',
        invalid: 'This file could not be read.',
      },
      unsupported: {
        equation:
          '{{count}} Word equations were not imported — Word stores them in a format the app cannot read; paste a screenshot instead.',
        chart: '{{count}} Word charts were not imported — paste a screenshot instead.',
        object:
          '{{count}} embedded objects (OLE / shapes) were not imported — paste a screenshot instead.',
      },
      unsupportedBlock: {
        equation: '[Not imported: equation]',
        chart: '[Not imported: chart]',
        object: '[Not imported: embedded object]',
      },
    },
    smartPaste: {
      button: 'Smart paste',
      title: 'Smart paste',
      source: 'Pasted content source',
      sources: {
        markdown: 'md code',
        markdownPreview: 'md preview',
        word: 'Word',
        excel: 'Excel',
        other: 'Other',
      },
      hint: 'Press Ctrl + V to paste here',
      readButton: 'Paste from clipboard',
      blocked: "The browser doesn't allow reading the clipboard here — press Ctrl + V.",
      readFailed: "Couldn't read the clipboard — allow it in the browser, or press Ctrl + V.",
      detected: 'Detected: {{source}}',
      pasteAgain: 'Ctrl + V again to replace',
      preview: 'Pasted content preview',
      nothing: 'Nothing to paste when read as {{source}} — try another tab.',
      droppedImages: '{{count}} images could not be pasted — use "Insert image".',
      insert: 'Insert now',
      aiTitle: 'Format the pasted content',
      aiDone: 'Reformatted {{count}} blocks',
      aiRunning: 'AI is formatting the pasted content…',
      aiSkip: 'Skip AI',
      aiApplied: 'Formatted by AI',
      aiLocal: 'Formatted from context',
      aiAsking: 'Formatted from context — {{count}} uncertain points',
      askAi: 'Ask AI',
      aiAnswered: 'AI answered {{count}} points',
      keep: 'Keep',
      revert: 'Back to plain paste',
      editedDuring:
        'You edited the pasted content while AI was running, so the AI result was not applied.',
      reformat: 'Reformat with AI',
    },
    math: {
      insertButton: 'Insert equation (Alt + =)',
      title: 'Equation',
      field: 'Equation editor',
      help: 'Type like in Word: / for a fraction, ^ for a power, _ for a subscript, sqrt for a root. Click the keyboard icon in the field for the math keyboard.',
      display: 'Layout',
      insert: 'Insert',
      update: 'Update',
      edit: 'Double-click to edit the equation',
      empty: 'Empty equation',
      loading: 'Loading equation…',
      loadingEditor: 'Loading the equation editor…',
      loadFailed: 'Could not load the equation editor — check your connection and try again.',
    },
    docSettings: {
      button: 'Settings and sharing',
      tabConfig: 'Settings',
      tabShare: 'Sharing',
      text: 'Text',
      baseSize: 'Default font size',
      headings: 'Headings',
      headingSize: '{{name}} size',
      headingColor: '{{name}} colour',
      headingFont: '{{name}} font',
      defaultColor: 'Default colour',
      defaultFont: 'Default font',
      reset: 'Reset document settings',
      plainNote:
        'Text and heading styles are saved only in Chill documents (.chilldoc). This document only has page margins, kept on your device.',
      shareNeedsSave: 'Save the document to My files to share it.',
      customize: 'Customize',
      collapse: 'Collapse',
      page: 'Page',
      marginsRow: 'Margins & indents',
      marginsSummary: 'Left {{left}} mm · Right {{right}} mm',
      unsaved: 'Unsaved changes',
      unsavedTitle: 'The settings have unsaved changes.',
      stay: 'Stay',
      discard: 'Discard changes',
      saveAndClose: 'Save and close',
      lists: {
        title: 'Lists',
        numbering: 'Numbering',
        numberingPresets: 'Numbering presets',
        bullets: 'Bullets',
        bulletPresets: 'Bullet presets',
        custom: 'Custom',
        default: 'Default',
        level: 'Level {{level}}',
        format: 'Number style of {{name}}',
        pattern: 'Marker pattern of {{name}}',
        insertToken: 'Insert {{token}}',
        patternHint:
          '{1} {2} {3} are the numbers of levels 1, 2, 3 — e.g. “{1}.{2}.” gives 1.2., “{2})” gives b). Levels deeper than {{levels}} use level {{levels}}.',
        symbol: 'Symbol of {{name}}',
        symbolsFor: 'Pick a symbol for {{name}}',
        pickSymbol: 'Pick',
        formats: {
          decimal: '1, 2, 3',
          'lower-alpha': 'a, b, c',
          'upper-alpha': 'A, B, C',
          'lower-roman': 'i, ii, iii',
          'upper-roman': 'I, II, III',
        },
      },
    },
    versions: {
      saveAs: 'Save as a new version',
      saveAsWithCount: 'Save as a new version — this file has {{count}} versions',
      saveAsHint:
        'The current copy of “{{name}}” is kept as the previous version; what you are editing becomes the new version.',
      note: 'Version note (optional)',
      notePlaceholder: 'e.g. sent to my manager for review',
      save: 'Save version',
      saved_one: 'Saved {{name}}',
      saved_other: 'Saved {{name}} — version {{count}}',
      count_one: '{{count}} version',
      count_other: '{{count}} versions',
      menu: 'Versions',
      title: 'Versions of “{{name}}”',
      number: 'Version {{n}}',
      representative: 'Main',
      panelTitle: 'Versions',
      current: 'Open',
      newVersion: 'Save as a new version',
      newVersionHint: 'The new version goes to the top and becomes the main version.',
      newVersionHintProject:
        'The current version is kept. See all versions under “Versions & review”.',
      dragHint:
        'Drag to reorder — the top version is the main one, opened by double-clicking in Files.',
      dragHandle: 'Drag to reorder version {{n}}',
      openVersion: 'Open version {{n}}',
      switchConfirm: 'Unsaved changes will be lost. Open version {{n}}?',
      switchConfirmAction: 'Discard and open',
      cannotDeleteOpen: 'Open now — switch to another version to delete it',
      confirmDeleteRepresentative:
        'Delete version {{n}}? It is the main version — the next one takes its place.',
      reordered: 'Version {{n}} is now the main version',
      open: 'Open',
      restore: 'Restore',
      delete: 'Delete version',
      restoreNote: 'Restored from version {{n}}',
      restored: 'Restored version {{n}} as the latest version',
      deleted: 'Deleted version {{n}}',
      confirmDelete: 'Delete version {{n}}? This cannot be undone.',
      loadError: 'Could not load the versions',
      readOnly: 'Version {{n}} — read only',
      deleteAll_one: 'Delete this file?',
      deleteAll_other: 'Delete this file and all {{count}} of its versions?',
    },
    saveAs: {
      title: 'Save as',
      name: 'File name',
      folder: 'Save to folder',
    },
    shared: {
      folder: 'Shared with me',
      empty: 'Nothing has been shared with you yet',
      emptyHelp: 'When someone shares a file or document with you, it shows up here.',
      by: 'Shared by {{name}}',
      mode: { copy: 'Can save a copy', read: 'Read only', suggest: 'Can suggest edits' },
      suggestOpen: 'Suggest edits',
      expires: 'Expires {{time}}',
      stale: 'This is the version the owner shared earlier — the document has a newer version.',
      copy: 'Save a copy to My files',
      copied: 'Saved "{{name}}" to My files',
      openMine: 'Open My files',
      signInToCopy: 'Sign in to save a copy',
      signIn: 'Sign in',
      signInTitle: 'Sign in to open this link',
      signInHelp: 'This link is only for people with a ChillPM account.',
      gone: 'This link no longer works',
      goneHelp:
        'It may have expired or been revoked, or the file was deleted. Ask the sender for a new link.',
      orgOnly: 'Shared within the organization only',
      orgOnlyHelp:
        'This link only opens for members of the organization of the person who created it.',
    },
    fileShare: {
      menu: 'Share',
      title: 'Share "{{name}}"',
    },
    docShare: {
      createLink: 'Create share link',
      invite: 'Invite people',
      summary: {
        none: 'Not shared with anyone yet.',
        public_one: '{{count}} public link',
        public_other: '{{count}} public links',
        chillpm_one: '{{count}} link for ChillPM accounts',
        chillpm_other: '{{count}} links for ChillPM accounts',
        org_one: '{{count}} link within the organization',
        org_other: '{{count}} links within the organization',
        people_one: '{{count}} person invited',
        people_other: '{{count}} people invited',
      },
      peopleTitle: 'Shared with',
      linksTitle: 'Share links',
      noLinks: 'No links yet.',
      external: 'Outside organization',
      permissionOf: 'Permission of {{name}}',
      revoke: 'Revoke',
      revokePerson: 'Revoke access for {{name}}',
      revokePersonConfirm: 'Revoke access to this file for {{name}}?',
      mode: { copy: 'Can save a copy', read: 'Read only', suggest: 'Suggest', edit: 'Edit' },
      modeLong: {
        copy: 'Copy to their files',
        read: 'Read only',
        suggest: 'Suggest edits',
        edit: 'Edit',
      },
      soon: 'Coming soon',
      suggestChilldocOnly: 'Chill documents only',
      suggestHelp:
        'They edit in suggesting mode: every save is a new version of the document, and you accept or reject each suggestion in the Review pane.',
      audience: {
        org: 'Within organization',
        chillpm: 'ChillPM accounts',
        public: 'Public on the web',
      },
      scope: { text: 'Text', images: 'Images', tables: 'Tables', presentation: 'Presentation' },
      scopeAll: 'Whole document',
      expired: 'Expired',
      expiresAt: 'Expires {{time}}',
      noExpiry: 'No expiry',
      opens_one: '{{count}} open',
      opens_other: '{{count}} opens',
      stale:
        'This link shows an older version — the document was saved after the link was created.',
      update: 'Update content',
      updated: 'The link now shows the latest version',
      copyLink: 'Copy link',
      copied: 'Link copied',
      copyFailed: "The browser didn't allow copying — select the link and copy it by hand: {{url}}",
      deleteLink: 'Delete link',
      deleteLinkConfirm: 'Delete this link? People holding it will no longer be able to open it.',
      revoked: 'Link deleted',
      modeLabel: 'Share type',
      scopeLabel: 'Scope',
      snapshotNote:
        'A partial share link shows the last saved version. After saving again, press "Update content" in the link list.',
      expiryLabel: 'Expiry',
      expiry: {
        '1': '1 day',
        '7': '7 days',
        '30': '30 days',
        none: 'No expiry',
        date: 'Pick a date',
      },
      expiryDate: 'Expiry date',
      audienceLabel: 'Who can open',
      publicWarning: 'Anyone with the link can open it, without signing in.',
      share: 'Share',
      personLabel: 'Recipient',
      personPlaceholder: 'Search your organization, or type an email',
      changePerson: 'Change',
      inviteByEmail: 'Invite {{email}} — they need a ChillPM account with this email.',
      emailNotFound: 'No ChillPM account uses the email {{email}}.',
      permissionLabel: 'Permission',
      sendInvite: 'Invite',
      invited: 'Shared with {{name}}',
    },
    drafts: {
      folder: 'Drafts',
      saving: 'Saving draft…',
      savedAt: 'Draft saved at {{time}}',
      failedRetry: 'Draft not saved — retry',
      limitShort: 'You already have {{limit}} drafts — this document is not auto-saved.',
      limitReached:
        'You already have {{limit}} drafts, so this document was not auto-saved. Press Save to give it a name, or discard old drafts in the Drafts folder of Files.',
      cleanUp: 'Clean up drafts',
      closeFailed:
        'The draft could not be saved, so the editor stayed open — check your connection and retry.',
      empty: 'No drafts yet',
      emptyHelp:
        'New documents without a name are saved here automatically a few seconds after you type.',
      untitled: 'Untitled document',
      editedAt: 'edited {{time}}',
      open: 'Open',
      discard: 'Discard draft',
      confirmDiscard: 'Discard draft “{{title}}”? Its content will be lost.',
      discarded: 'Draft discarded.',
    },
    fileRef: {
      insertButton: 'Insert my file',
      pickTitle: 'Choose files to insert',
      pickList: 'Files',
      insert_one: 'Insert {{count}} file',
      insert_other: 'Insert {{count}} files',
      open: 'Open “{{name}}”',
      missing: 'This file is gone — deleted, or you can no longer see it.',
      missingShort: 'gone',
    },
    outline: {
      label: 'Outline',
      toggle: 'Show/hide outline',
      empty: 'No headings yet — use "Heading level" to add one.',
      levels: 'Show up to level',
      showLevel: 'Show up to level {{n}}',
      showAll: 'Show all levels',
      collapse: 'Collapse “{{title}}”',
      expand: 'Expand “{{title}}”',
    },
    userFolder: {
      listLabel: 'My folders',
      new: 'New folder',
      namePlaceholder: 'Folder name',
      created: 'Folder “{{name}}” created.',
      deleted: 'Folder deleted. Its files are back in My files.',
      confirmDelete_one:
        'Delete folder “{{name}}”? Its {{count}} file goes back to My files — nothing is deleted.',
      confirmDelete_other:
        'Delete folder “{{name}}”? Its {{count}} files go back to My files — nothing is deleted.',
      fileCount_one: '{{count}} file',
      fileCount_other: '{{count}} files',
      open: 'Open',
      rename: 'Rename',
      delete: 'Delete folder',
      actionsFor: 'Actions for folder {{name}}',
      dropInto: 'Drop into “{{name}}”',
      moveTo: 'Move to…',
      moveTitle: 'Move “{{name}}” to',
      moveTitleMany_one: 'Move {{count}} file to',
      moveTitleMany_other: 'Move {{count}} files to',
      moved_one: 'Moved {{count}} file.',
      moved_other: 'Moved {{count}} files.',
      root: 'My files (no folder)',
      here: 'Current',
      noneYet: 'No folders yet — create one from the sidebar with "New folder".',
      breadcrumb: 'Folder path',
    },
    editor: {
      titleNew: 'New document',
      titleEdit: 'Edit document',
      nameLabel: 'Document name',
      untitled: 'Untitled document',
      nameEmpty: 'Give the document a name.',
      nameTooLong: 'The name is longer than 255 characters.',
      nameSlash: 'The name cannot contain / or \\.',
      contentLabel: 'Document content',
      markdownSource: 'Show Markdown source',
      markdownSourceLabel: 'Markdown source of the document',
      placeholder: 'Start writing…',
      saving: 'Saving…',
      loading: 'Opening the document…',
      loadFailed: 'Could not open the document for editing.',
      confirmDiscard: 'The document has unsaved changes. Close and discard them?',
      imagesPending: 'Wait for the images to finish uploading before saving.',
      saved: 'Saved “{{name}}”.',
      savedAsNewVersion:
        'Saved “{{name}}” as version {{n}} — the previous version was submitted, approved or saved by someone else, so it was kept.',
      nameAndSave: 'Name and save',
      suggestBanner:
        'You are suggesting edits: what you type and delete becomes suggestions the owner can accept or reject. Every save is a new version.',
      savedSuggestion: 'Saved your suggestions as version {{n}} of “{{name}}”.',
      suggestStale: {
        message:
          'The owner just saved a new version, so your suggestions were not saved. Suggestions must be made on the latest version.',
        reload: 'Open the latest version',
        copy: 'Save my suggestions as my own copy',
        copyName: '{{name}} (my suggestions)',
        copied: 'Saved the copy “{{name}}” to My files.',
        confirmReload:
          'Opening the latest version discards your unsaved suggestions here (a saved copy is kept). Continue?',
      },
      storageOff: 'File storage is not configured yet.',
      toolbar: 'Editor toolbar',
      fileGroup: 'File',
      saveHint: 'Save (Ctrl + S)',
      saveUnsaved: 'Save — unsaved changes',
      rename: 'Rename',
      renameCurrent: 'Rename — {{name}}',
      collapseToolbar: 'Collapse toolbar',
      expandToolbar: 'Expand toolbar',
      moreInGroup: 'More buttons in {{group}}',
      fontSize: 'Font size',
      fontSizeHint: 'Type a size ({{min}}–{{max}}) and press Enter; leave empty for the default',
      headingN: 'Heading {{level}}',
      defaultFontShort: 'Default',
      normalStyle: 'Normal',
      notEditable: "This file can't be opened in the document editor.",
      mixedStyles: 'Mixed styles',
      groups: {
        history: 'Actions',
        style: 'Styles',
        font: 'Font',
        text: 'Character',
        paragraph: 'Paragraph',
        insert: 'Insert',
        draw: 'Draw',
        review: 'Review',
        view: 'View',
      },
    },
    /** Word/PowerPoint files opened in the Chill editor — APM-WDE-b. */
    officeEditor: {
      openFailed: 'This file could not be opened for editing. Download it to open in Office.',
      saveNote: '{{from}} file — Save will write “{{name}}”.',
      lossyBanner: 'Saving from here will drop: {{list}}. The current file stays in Versions.',
      lossyConfirm:
        'This file has {{list}} — the editor cannot keep them, saving drops them (the current file stays in Versions). Save anyway?',
      lossyDismiss: 'Hide warning',
      lostBanner:
        'The last save could not keep: {{list}} — that part changed structure, so Chill left the original in place. The previous file stays in Versions.',
      patchFallback:
        'The original file changed since it was opened, so it could not be patched — the saved copy was rebuilt from the Chill content.',
      lossy: {
        headerFooter: 'headers/footers',
        footnotes: 'footnotes',
        comments: 'comments',
        trackChanges: 'tracked changes',
        textBoxes: 'text boxes',
        columns: 'multi-column layout',
        fields: 'fields and page numbers',
        equations: 'equations (kept as LaTeX text)',
        charts: 'charts',
        objects: 'embedded objects',
        macros: 'macros',
        styles: 'page formatting of the legacy Word file',
        notes: 'speaker notes',
        smartArt: 'SmartArt',
        media: 'video/audio',
        animations: 'animations and transitions',
        shapes: 'shapes',
        links: 'links',
        contentControls: 'content controls (dates, check boxes…)',
        formulas: 'Excel formulas (only computed values kept)',
      },
    },
    /** Spreadsheet editor (Univer) — APM-XLE-c. */
    sheetEditor: {
      title: 'Edit spreadsheet “{{name}}”',
      loading: 'Opening spreadsheet…',
      openFailed: 'This spreadsheet could not be opened for editing. Download it to open in Excel.',
      save: 'Save',
      saving: 'Saving…',
      saved: 'Saved “{{name}}”.',
      saveFailed: 'Save failed: {{message}}',
      saveVersion: 'Save as another version',
      versionNote: 'What changed? (optional)',
      versionSaved: 'Saved as version {{version}}.',
      close: 'Close',
      confirmDiscard: 'The spreadsheet has unsaved changes. Close and discard them?',
      xlsNote: '.xls file — Save will write “{{name}}”.',
      lossyBannerRebuild:
        'You inserted, deleted or moved rows or columns, or changed sheets, so this save rebuilds the file and drops: {{list}}. The current file stays in Versions.',
      lossyConfirmRebuild:
        'You inserted, deleted or moved rows or columns, or changed sheets, so saving rebuilds the file and drops {{list}} (the current file stays in Versions). Save anyway?',
      lossyBanner: 'Saving from here will drop: {{list}}. The current file stays in Versions.',
      lossyConfirm:
        'This spreadsheet has {{list}} — the editor cannot keep them, saving drops them (the current file stays in Versions). Save anyway?',
      lossyDismiss: 'Hide warning',
      lossy: {
        charts: 'charts',
        pivot: 'pivot tables',
        macros: 'macros',
        images: 'images/drawings',
        conditionalFormatting: 'conditional formatting',
        dataValidation: 'data validation',
        comments: 'cell comments',
        hyperlinks: 'cell hyperlinks',
        richText: 'per-run text formatting in cells',
        chartsheets: 'chart sheets',
        brokenCharts: 'charts with parts missing from the file (cannot be copied intact)',
        lambdaFunctions: 'LET/LAMBDA formulas',
        dynamicArrays: 'dynamic arrays (saved as fixed-range array formulas)',
        tables: 'tables (Format as Table)',
        printSettings: 'print settings (paper size, print area, header/footer)',
        autoFilter: 'filters',
        protection: 'sheet/workbook protection',
        externalLinks: 'links to other workbooks',
        slicers: 'slicers',
        sparklines: 'sparklines',
        threadedComments: 'threaded comments',
        strictOoxml: 'Strict Open XML format',
      },
    },
    preview: {
      unsupported: 'This format can’t be previewed yet',
      unsupportedHelp: 'Download it to open with an app on your device.',
      errorTitle: 'Preview unavailable',
      loading: 'Opening file…',
      /** Per-preview link — APM-PVLINK. */
      copyLink: 'Copy link',
      linkCopied: 'Link copied — anyone allowed to view this file sees it when they open the link.',
      copyFailed: 'Couldn’t copy automatically — copy this link by hand: {{link}}',
      linkUnavailable: 'File not found, or you don’t have permission to view it.',
      toolbar: {
        label: 'Preview tools',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        zoomMenu: 'Zoom: {{percent}}',
        fit: 'Fit',
        fitShort: 'Fit · {{percent}}',
        actualSize: 'Actual size (100%)',
        textLarger: 'Larger text',
        textSmaller: 'Smaller text',
        textReset: 'Default size',
        prevPage: 'Previous page (PageUp)',
        nextPage: 'Next page (PageDown)',
        goToPage: 'Go to page',
        pageOf: '/ {{total}}',
      },
      outline: {
        toggle: 'Show/hide outline',
        title: 'Outline',
        slide: 'Slide {{n}}',
        resize: 'Drag to resize the outline (double-click: default)',
      },
      search: {
        open: 'Find in file (Ctrl + F)',
        placeholder: 'Find in file',
        count: '{{current}} / {{total}}',
        none: 'No results',
        searching: 'Searching…',
        next: 'Next result (Enter)',
        prev: 'Previous result (Shift + Enter)',
        close: 'Close search (Esc)',
      },
      error: {
        tooLarge: 'The file is larger than {{limit}} — too big to preview in the page.',
        fetchFailed:
          'Couldn’t read the file content. If Download still works, the storage may not allow this page to read files (CORS) — tell your system administrator.',
        http: 'The storage returned error {{status}} while reading the file.',
        media: 'The browser can’t display or play this file (unsupported format or codec).',
        library: 'The viewer failed to open this file.',
        chilldocInvalid: 'This document is damaged — its content cannot be read.',
        chilldocNewer:
          'This document was made by a newer version of the app. Reload the page to update, then open it again.',
        browser:
          'This browser is too old to preview PDFs (Chrome 125, Safari or iOS 18 or later needed). Download it, or update your browser.',
        password:
          'This PDF is password-protected — download it to open with an app on your device.',
      },
      downloadOnly: {
        ppt: 'The legacy PowerPoint format (.ppt) has no usable open-source viewer. Download it, or save it as .pptx to preview it in the page.',
      },
      pdfPage: 'Page {{page}} of {{total}}',
      sheetTabs: 'Sheets',
      sheetChart: 'Chart: {{title}}',
      sheetChartUntitled: 'Chart',
      sheetChartSeries: 'Series {{n}}',
      sheetChartUnsupported:
        'This chart type (Excel 2016 and later) cannot be previewed yet — download to open it in Excel.',
      sheetXlsNote:
        '.xls files show data only, without colours or formatting — save as .xlsx to see everything.',
      docNote: 'The .doc preview is approximate — layout may differ from the original.',
      truncated: 'Showing the first {{shown}} of {{total}} lines — download to see everything.',
      empty: 'The file is empty.',
      htmlSource: 'Source',
      htmlPage: 'Page',
      htmlPageNote: 'The page runs in an isolated frame: no scripts, no external resources.',
      missingImage: 'Image no longer available',
      resizeTableColumn:
        'Drag to resize column {{column}} — this view only, the file is unchanged. Left/right arrows to adjust, double-click to reset.',
      gridLabel: 'Data table of {{name}}',
      gridSize: '{{rows}} rows · {{columns}} columns',
    },
    actionsFor: 'Actions for {{name}}',
    open: 'Open',
    openLink: 'Open link',
    download: 'Download',
    openOrigin: 'Go to source',
    edit: 'Edit',
    delete: 'Delete',
    cannotDeleteDocument: 'This is a document version — manage it under Documents.',
    deleteAtOrigin: 'Remove this file where it is attached.',
    confirmDelete:
      'Delete “{{name}}”? The file is removed from storage and can no longer be downloaded.',
    deleted: 'File deleted.',
    empty: 'No files here yet',
    emptyHelp: 'Files you upload in this place will show up here.',
    emptyHelpUpload: 'Click Upload or drop files here — any format is accepted.',
    noMatch: 'No matching files',
    noMatchHelp: 'Try removing a filter or changing the search term.',
    uploadQueue: 'Upload progress',
    uploading: 'Uploading {{done}}/{{total}} files…',
    uploadFinished: 'Upload finished',
    uploadFailed: 'Failed',
  },
  orgStorage: {
    title: 'Organisation storage',
    subtitle: 'How much of its file storage quota the organisation uses, per project.',
    ofQuota: 'of a {{quota}} quota',
    help: 'Includes uploads in progress. Once the quota is used up no more files can be uploaded — delete some on a project’s Files page, or ask the system operator to raise the quota.',
    project: 'Project',
    used: 'Used',
    org: 'Organisation',
    quotaTitle: 'Storage quota per organisation',
    quotaSubtitle: 'Set how much file storage each organisation gets. Default 1 GB.',
    quotaHelp:
      'Fullest first. Lowering a quota below current usage deletes nothing — it only blocks uploads until the organisation frees space.',
    quotaGb: 'Quota (GB)',
    quotaFor: 'Quota for {{name}}, in GB',
    quotaSaved: 'Quota saved.',
  },
  users: {
    title: 'Users',
    lineManager: 'Line manager',
    department: 'Department',
    projects: 'Projects',
    status: 'Status',
    orgRole: {
      SUPER_ADMIN: 'System admin',
      ORG_ADMIN: 'Organisation admin',
      DIRECTOR: 'Director',
      LEADER: 'Team leader',
      EMPLOYEE: 'Employee',
      // Not positions: the shell labels these sessions too (APM-PSPACE-g).
      GUEST: 'Guest collaborator',
      CLIENT: 'Client',
    },
    subtitle: 'Invite people, assign roles and line managers, record skills.',
    invite: 'Invite',
    invited: 'Account created with a set-password link.',
    updated: 'Updated.',
    displayName: 'Display name',
    unsaved: 'Unsaved changes',
    role: 'Role',
    noManager: '— no manager —',
    active: 'Active',
    disabled: 'Locked',
    activeHint: 'Can enter this organization and work normally.',
    disabledHint:
      'Cannot enter this organization until unlocked. Other organizations they belong to are not affected.',
    lock: 'Lock access now…',
    unlock: 'Unlock',
    lockConfirmAction: 'Lock',
    lockConfirm:
      'Lock {{name}} in this organization? They are signed out of it right away and cannot come back until unlocked. Other organizations they belong to are not affected.',
    unlockConfirm: 'Unlock {{name}}? They can enter the organization again right away.',
    locked: '{{name}} is locked.',
    unlocked: '{{name}} is unlocked.',
    skills: 'Skills',
    addSkill: '+ Add skill',
    removeSkill: 'Click to remove',
    googleLinkHelp:
      'Added. Send this sign-in link to the invitee and ask them to sign in with Google using the email you entered.',
    linkHelp:
      'Send this link to the invitee. It is single-use and expires in 1 hour. (An email module will replace this.)',
    level: { learning: 'learning', proficient: 'proficient', expert: 'expert' },
  },
  department: {
    title: 'Departments',
    subtitle:
      'Shared across the organisation. A catalogue rather than free text, so filtering by department never silently misses people.',
    label: 'Department',
    none: '— no department —',
    add: 'Add',
    placeholder: 'Engineering, Sales, Operations…',
    enable: 'enable',
    disable: 'disable',
    empty: 'No departments yet.',
    help: 'Disable instead of delete: people who left a department must still be traceable to where they were.',
  },
  techStack: {
    title: 'Skills catalogue',
    subtitle:
      'Shared across the organisation. Velocity is measured per skill, so names must be consistent.',
    add: 'Add',
    placeholder: 'Node.js, Go, React…',
    enable: 'enable',
    disable: 'disable',
    help: 'Disable rather than delete: recorded skills and velocity history must stay resolvable.',
  },
  taskTypeCatalog: {
    proposed:
      'Proposal sent. Organisation admins have been notified — the type becomes usable once approved.',
    alreadyPending: '“{{name}}” has already been proposed and is waiting for an admin.',
    proposeTitle: 'Propose a new work type',
    proposeHelp:
      'The new type goes into the shared organisation catalogue and can only be used after an organisation admin approves it.',
    propose: 'Send proposal',
    statusPending: 'Waiting for admin approval',
    statusRejected: 'Rejected: {{note}}',
    pendingTitle_one: '{{count}} proposal waiting for approval',
    pendingTitle_other: '{{count}} proposals waiting for approval',
    proposedBy: 'Proposed by {{name}} · {{project}}',
    noProject: 'while creating a project',
    approve: 'Approve',
    reject: 'Reject',
    rejectReason: 'Reason for rejecting',
    approved: 'Work type approved.',
    rejected: 'Proposal rejected.',
    pendingBadge_one: '{{count}} request waiting for approval',
    pendingBadge_other: '{{count}} requests waiting for approval',
    colorN: 'Colour {{n}}',
    colorLabel: 'Background colour',
    colorFor: 'Background colour of {{name}}',
    changeColor: 'Change colour',
    usedOrgType:
      'The organisation already has “{{name}}” — the shared type was enabled instead of creating a copy. Click Save to keep it.',
    title: 'Work type catalogue',
    subtitle:
      'Shared across the organisation. Each project picks the types it uses from here, and velocity is measured per type.',
    add: 'Add',
    placeholder: 'Backend, Testing, Design…',
    rename: 'Name of work type {{name}}',
    taskCount_one: '{{count}} task',
    taskCount_other: '{{count}} tasks',
    inactive: 'disabled',
    enable: 'Enable',
    disable: 'Disable',
    adminHelp:
      'Renaming keeps every existing task and velocity figure. Disable instead of deleting: a disabled type is hidden from new projects, while old tasks keep their type.',
    emptyCatalog: 'The organisation has no work types yet. An org admin can add them in Admin.',
    projectTitle: 'Work types',
    projectSubtitle: 'Pick the work types this project uses to create and classify tasks.',
    projectHelp:
      'The catalog is managed by the organisation — ask an org admin to add or rename a type.',
    removeWarning_one:
      'Removing {{count}} type from the project: existing tasks keep it, new tasks just can’t pick it.',
    removeWarning_other:
      'Removing {{count}} types from the project: existing tasks keep them, new tasks just can’t pick them.',
    needOne: 'Pick at least one work type.',
    createLabel: 'Work types',
    createHelp:
      'Uses every organisation type by default. Click a type to leave it out; you can change this later in project settings.',
    // Personal space — APM-PSPACE-o: the owner adds/edits, nobody approves.
    personalAddTitle: 'Add a work type',
    personalAddHelp: 'Usable right away — no approval needed.',
    personalAdd: 'Add',
    personalAdded: 'Added “{{name}}” — usable right away.',
    personalProjectHelp:
      'Your own catalogue: add new types below; rename, recolour or turn them off in the list.',
    personalManageTitle: 'Your work types',
  },
  matrix: {
    title: 'Permission matrix',
    subtitle: 'Generated from the same lookup the system enforces — not copied from the docs.',
    permission: 'Permission',
    legend: {
      yes: 'granted',
      no: 'not granted',
      project_member: 'only in projects you belong to',
      own: 'only your own items',
      direct_report: 'only your direct reports',
      report_chain: 'your whole reporting branch, any depth',
    },
  },
  audit: {
    title: 'Activity log',
    subtitle: 'Every change in the organisation, with who did it and when.',
    when: 'When',
    event: 'Event',
    actor: 'Actor',
    aggregate: 'Subject',
    filterPlaceholder: 'Filter by event type, e.g. task.status_changed',
    loadMore: 'Load more',
    empty: 'No activity yet',
    archivedNotice: '{{count}} older months moved to S3 archive and are not listed here',
    sensitiveTitle: 'Performance data access',
    sensitiveSubtitle: 'Who read whose performance profile. Every read is logged, no exceptions.',
    viewed: 'viewed the profile of',
    noSensitive: 'No access recorded yet',
    noSensitiveHelp: 'Every read of personal performance data will appear here.',
  },
  // APM-ORGINT-e — copy button inside step-by-step guides (`NumberedSteps`).
  setupSteps: {
    copy: 'Copy',
    copied: 'Copied.',
    copyFailed: 'Could not copy — select it and copy by hand.',
  },
  // APM-ORGINT-e — per-organisation "Allow internal addresses" switch (super admin), plan-org-integrations §5.4.
  orgNetwork: {
    title: 'Internal addresses per organisation',
    subtitle:
      'Which organisations may connect sync sources / git servers that live on an internal network (company GitLab, Redmine).',
    intro:
      'Organisation admins enter their own sync source and git server addresses. By default ChillPM refuses to call internal addresses (such as 10.x, 192.168.x, localhost). Organisations with GitLab / Redmine on the company network can be allowed here, one by one.',
    risk:
      'Allowing it means that organisation’s admins can make the ChillPM server call into the internal network it runs in. Only allow organisations you trust, and turn it off when no longer needed.',
    resume:
      'Sync connections / git repositories that stopped because they were blocked **do not restart on their own** after you allow it: a PM has to press "Sync now" (or run it by hand) on that connection.',
    search: 'Search organisations',
    empty: 'No organisation matches.',
    demo: 'Demo',
    personal: 'Personal space',
    usage: '{{providers}} sync sources · {{hosts}} git servers',
    switchFor: 'Allow internal addresses for {{name}}',
    on: 'Internal addresses allowed',
    off: 'Internal addresses blocked',
    confirmOn:
      'Allow "{{name}}" to connect to internal addresses? This organisation’s admins will be able to make the ChillPM server call into the internal network — only allow it if you trust them.',
    confirmOnDemo:
      '"{{name}}" is the demo organisation — anyone can sign in without a password. Allowing it here opens a way for anyone to probe the server’s internal network. Allow anyway?',
    confirmOnButton: 'Allow',
    turnedOn:
      'Internal addresses allowed for {{name}}. Connections that stopped because they were blocked need a PM to press "Sync now" to run again.',
    turnedOff: 'Internal addresses blocked for {{name}} — its internal sources / servers can no longer be reached.',
  },
  adminNav: {
    back: 'All admin sections',
    group: {
      setup: 'System setup',
      people: 'Users and permissions',
      org: 'Organization and calendar',
      catalog: 'Shared catalogues',
      cost: 'Cost and AI',
      monitor: 'Monitoring and audit',
    },
  },
  health: {
    stalled: 'Stalled jobs',
    title: 'System health',
    subtitle: 'Diagnostics. Unlike /readyz, this page also checks S3 and the AI key.',
    database: 'Database',
    storage: 'Storage',
    ai: 'AI',
    version: 'Version',
    environment: 'Environment',
    uptime: 'Uptime',
    minutes: 'min',
    jobQueue: 'Job queue',
    pending: 'pending',
    running: 'running',
    failed: 'failed',
    dead: 'dead',
    staleJobs: 'Oldest job has waited {{minutes}} min — the worker may be down',
    eventLog: 'Event log',
    state: { configured: 'configured', not_configured: 'not configured' },
  },
  todos: {
    quickEdit: {
      priority: 'Priority: {{value}} — click to change',
      date: 'Change the date of “{{title}}”',
      startTab: 'Start',
      dueTab: 'Due',
      clearDue: 'Remove due date',
      noDueForRepeat: 'Repeating to-dos have no due date — only the start date can change.',
    },
    quick: {
      title: 'To-dos to do now',
      undo: 'Undo',
      undoLabel: 'Undo — not done: {{title}}',
    },
    subtitle: 'Your own to-dos, outside any project — only you can see them.',
    create: 'New to-do',
    listLabel: 'To-do list',
    group: { now: 'To do now', later: 'Not yet', done: 'Done' },
    empty: {
      now: 'Nothing to do right now.',
      later: 'Nothing scheduled for the coming days.',
      done: 'Nothing done yet.',
    },
    doneTruncated: 'Showing the {{shown}} most recent of {{total}} done to-dos.',
    complete: 'Mark done: {{title}}',
    reopen: 'Mark not done: {{title}}',
    drag: 'Drag to reorder: {{title}}',
    open: 'Open: {{title}}',
    attachments: '{{count}} attachment(s)',
    dueTitle: 'Due: {{value}}',
    overdue: 'Overdue',
    starts: 'Starts {{value}}',
    priorityTitle: 'Priority: {{value}}',
    repeat: {
      daily: 'Repeats daily',
      dailyEvery: 'Repeats every {{count}} days',
      weekly: 'Repeats weekly on {{days}}',
      weeklyEvery: 'Repeats every {{count}} weeks on {{days}}',
      monthly: 'Repeats monthly',
      monthlyEvery: 'Repeats every {{count}} months',
      yearly: 'Repeats yearly',
      yearlyEvery: 'Repeats every {{count}} years',
    },
    weekday: { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' },
    form: {
      createTitle: 'New to-do',
      editTitle: 'Edit to-do',
      title: 'Title',
      titlePlaceholder: 'What needs doing…',
      description: 'Description',
      descriptionPlaceholder: 'Notes, links, small checklist…',
      attachments: 'Attachments',
      upload: 'Attach files',
      uploading: 'Uploading {{count}} file(s)…',
      uploadError: 'Could not upload {{name}}: {{message}}',
      download: 'Download {{name}}',
      removeFile: 'Remove {{name}}',
      priority: 'Priority',
      startDate: 'Do on',
      withTime: 'Set a time',
      startTime: 'Time',
      hasDue: 'Due date',
      dueDate: 'Due on',
      dueTime: 'Due time',
      repeat: 'Repeat',
      dueBlocked: 'Repeating to-dos have no due date — untick Repeat to set one.',
      repeatBlocked: 'To-dos with a due date do not repeat — untick Due date to repeat.',
      freqLabel: 'Frequency',
      freq: { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' },
      every: 'Every',
      unit: { daily: 'day(s)', weekly: 'week(s)', monthly: 'month(s)', yearly: 'year(s)' },
      weekdays: 'On',
      preview: 'Next three: {{dates}}',
      save: 'Save',
      delete: 'Delete',
      confirmDelete: 'Delete to-do "{{title}}"?',
    },
    saveError: 'Could not save: {{message}}',
  },
  myTasks: {
    moreProjects: '+{{count}} more projects',
    taskCount: '{{count}} tasks',
    dayOff: {
      off_shift: 'Day off',
      holiday: 'Public holiday',
      absence: 'On leave',
      left: 'No longer working',
    },
    subtitle: 'Tasks assigned to you, gathered from every project you belong to.',
    viewSwitch: 'Switch view',
    view: { list: 'List', calendar: 'Calendar' },
    filter: {
      status: 'Status',
      open: 'Not done',
      priority: 'Priority',
      overdue: 'Overdue',
      sort: 'Sort',
      toggle: 'Filters',
    },
    sort: {
      focus: 'In progress & upcoming',
      dueDate: 'By due date',
      priority: 'By priority',
      project: 'By project',
      updatedAt: 'Recently updated',
      assignee: 'By person',
    },
    blocked: 'Blocked',
    noDueDate: 'No due date',
    bugs: {
      title: 'Bugs',
      group: {
        fix: 'Bugs to fix ({{count}})',
        reopened: 'Bugs sent back ({{count}})',
        verify: 'Waiting for my retest ({{count}})',
      },
      due: 'due {{date}}',
      reopenedTimes: 'Sent back ×{{count}}',
      markFixed: 'Fixed',
      pass: '✓ Resolved',
      fail: '✗ Still broken',
      failReason: 'What is still wrong',
      failPlaceholder: 'What you still see — the fixer needs to know what differs from last time',
      sendBack: 'Send back',
      fixedToast: 'Marked as fixed — waiting for the reporter to retest.',
      passedToast: 'Bug closed.',
      failedToast: 'Sent back to the fixer.',
      openHint: 'Open the bug in the project Testing tab',
      loadError: 'Could not load your bugs.',
    },
    progressOnlyInProgress:
      'At {{value}}%. Move it to "In progress" to edit by hand — "Ready" and "Done" change progress by changing column.',
    emptyTitle: 'No tasks yet',
    emptyHelp: 'Nothing is assigned to you matching the current filters.',
    unitSwitch: 'Choose calendar view',
    unit: { week: 'Week', month: 'Month' },
    prevPeriod: 'Previous period',
    nextPeriod: 'Next period',
    today: 'Today',
    moreItems: '+{{count}} more',
    team: {
      tabsLabel: 'Whose tasks',
      tab: { me: 'My tasks', todo: 'To-do', team: 'My team’s tasks' },
      subtitle:
        'Tasks assigned to your direct reports, across every project — including ones you are not in.',
      filter: { member: 'Person', project: 'Project', all: 'All' },
      audited:
        'Every time this list is opened, it is recorded in the access log of each person in it.',
      truncated:
        'Showing the first {{count}} tasks only. Pick a person or a project to see them all.',
      emptyTitle: 'No tasks',
      emptyHelp: 'No team tasks match the current filters.',
      calendar: {
        off: '(off)',
        allOff: 'Whole team off',
        moreMembers: '+{{count}} more people',
        hint: 'Counts open tasks only. Click a label to open that person’s tasks in that project.',
        audited:
          'Every time this calendar is opened, it is recorded in the access log of each person with tasks in it.',
        truncated:
          'The team has more than {{count}} tasks in this period, so the counts may be incomplete. Use the week view for full counts.',
      },
      detail: {
        project: 'Project',
        status: 'Status',
        progress: 'Progress',
        readOnlyNote:
          'Read-only: you cannot edit tasks in this project, so progress and comments are unavailable here.',
      },
    },
  },
  workflow: { todo: 'To do', in_progress: 'In progress', done: 'Done' },
  priority: { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' },
  calendar: {
    placeholder: 'dd/mm/yyyy',
    open: 'Open calendar',
    clear: 'Clear date',
    today: 'Today',
    openTime: 'Open time list',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    now: 'Now',
    weekday: { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' },
  },
  privacy: {
    title: 'Who viewed my performance record',
    subtitle:
      'Personal performance data is used to evaluate people, so every read is logged and you are entitled to see that list.',
    emptyTitle: 'Nobody has viewed your record',
    emptyHelp: 'When someone opens your performance data, that access will appear here.',
    unknownViewer: 'Unknown',
    column: { viewer: 'Viewer', scope: 'Scope', reason: 'Reason', when: 'When' },
  },
  coordination: {
    matrixTitle: 'People – work matrix',
    matrixHelp: 'Who is carrying what in this project. Darker cell = more tasks.',
    workloadTitle: 'Workload over time',
    workloadHelp:
      'Summed across every project, including ones you are not on — so it shows numbers only, never task names.',
    workloadRestricted: 'Assignment permission required',
    workloadRestrictedHelp:
      'Only PMs and direct managers can see a member’s cross-project workload.',
    crossProjectNote:
      'The dashed line is daily capacity. Red bars are days already committed beyond capacity, counting work in other projects.',
    person: 'Member',
    total: 'Total',
    unassigned: 'Unassigned',
    hoursShort: '({{hours}}h)',
    committedTotal: '{{hours}} hours committed',
    overloadedDays: '{{count}} overloaded day(s)',
    dayTooltip: '{{date}}: {{committed}}/{{capacity}} hours',
    cellTooltip: '{{count}} task(s) · {{hours}} estimated hours',
    noMembers: 'No members yet',
    noMembersHelp: 'Add members to the project to see how work is distributed.',
    invite: 'Invite members',
    activityRestricted: 'Only people working on the project can see this',
    activityRestrictedHelp:
      'Team update activity is shown to the PM and project members, not to the Viewer role.',
  },
  collaboration: {
    detailTabs: 'Task details',
    tab: { comments: 'Comments', activity: 'History', attachments: 'Attachments' },
    commentPlaceholder: 'Write a comment… type @ to mention someone',
    mentionSuggestions: 'People you can mention',
    send: 'Send',
    sendHint: 'Ctrl/⌘ + Enter to send',
    edited: 'edited',
    deleteComment: 'Delete comment',
    unknownAuthor: 'Unknown',
    syncedFrom: 'from {{source}}',
    syncedFromTitle: 'Comment synced from {{source}} — the name shown is who wrote it there',
    noComments: 'No comments yet',
    noCommentsHelp: 'Be the first to discuss this task.',
    noActivity: 'No changes yet',
    moreActivity: 'Older changes are not shown here.',
    noAttachments: 'No files yet',
    noAttachmentsHelp: 'Files attached to this task will appear here.',
    download: 'Download',
    removeAttachment: 'Remove attachment',
    addAttachment: 'Attach file',
    uploading: 'Uploading…',
    actor: { user: 'User', ai: 'AI', system: 'System' },
    // History line coming from a sync run — APM-SYNC-h (plan-project-sync §5.6).
    syncActor: 'Synced from {{source}}',
    // History line caused by a git status rule — APM-GIT-h.
    gitActor: 'Git',
  },
  period: {
    label: 'Period:',
    previous: 'Previous period',
    next: 'Next period',
    kind: { month: 'Month', quarter: 'Quarter', year: 'Year' },
    monthLabel: '{{month}}/{{year}}',
    quarterLabel: 'Q{{quarter}} {{year}}',
    yearLabel: '{{year}}',
    hint: 'Maintenance projects run continuously, so they are viewed by period. A task shows in every period its dates **overlap** — one spanning two periods appears in both.',
  },
  activity: {
    'task.created': 'created the task',
    'task.checklist_updated': 'updated the task checklist',
    'task.updated': 'edited the details',
    'task.status_changed': 'changed the status',
    'task.assigned': 'changed the assignee',
    'task.dates_changed': 'changed the schedule',
    'task.progress_updated': 'updated progress',
    'task.estimate_changed': 'changed the estimate',
    'task.story_points_changed': 'changed the story points',
    'task.priority_changed': 'changed the priority',
    'task.blocked': 'marked it blocked',
    'task.unblocked': 'removed the blocked flag',
    'task.deleted': 'deleted the task',
    'task.restored': 'restored the task',
    'task.kind_changed': 'changed the milestone type',
    'task.attachment_added': 'attached a file',
    'task.attachment_removed': 'removed an attachment',
    'comment.created': 'commented',
    'comment.updated': 'edited a comment',
    'comment.deleted': 'deleted a comment',
    'task.group_changed': 'moved the task to another group',
    'task.reordered': 'reordered tasks',
    'dependency.added': 'linked a dependency',
    'dependency.removed': 'removed a dependency',
    'task_group.created': 'created a task group',
    'task_group.renamed': 'renamed a task group',
    'task_group.deleted': 'deleted a task group',
    'sprint.created': 'created a sprint',
    'sprint.started': 'started a sprint',
    'sprint.closed': 'closed a sprint',
    'sprint.updated': 'edited a sprint',
    'project.created': 'created the project',
    'project.updated': 'edited the project',
    'project.methodology_changed': 'changed the project type',
    'project.baseline_set': 'set the baseline',
    'project.tab_layout_changed': 'rearranged the tab bar',
    'project_sync.connected': 'connected the project to a sync source',
    'project_sync.configured': 'changed sync settings',
    'project_sync.mapping_changed': 'changed sync mappings',
    'project_sync.key_rotated': 'replaced the sync source API key',
    'project_sync.paused': 'paused sync',
    'project_sync.resumed': 'resumed sync',
    'project_sync.disconnected': 'disconnected a sync source',
    'project_sync.released': 'released the external project of a sync source',
    // Git tab — APM-GIT-d (docs/plan-git-integration.md §4.1).
    'git_connection.created': 'created a git server connection',
    'git_connection.token_rotated': 'replaced the token of a git connection',
    'git_connection.revoked': 'revoked a git connection',
    'git_connection.status_changed': 'changed the status of a git connection',
    'git_connection.access_changed': 'updated the write access of a git connection',
    'git_repository.linked': 'linked a git repository to the project',
    'git_repository.settings_changed': 'changed the settings of a git repository',
    'git_repository.unlinked': 'unlinked a git repository from the project',
    'git_repository.webhook_changed': 'changed the webhook mode of a git repository',
    // Linking a whole group — APM-GIT-s.
    'git_repository_group.linked': 'linked a git repository group to the project',
    'git_repository_group.unlinked': 'unlinked a git repository group from the project',
    'git_repository_group.repository_skipped': 'skipped a suggested repository of a git group',
    'git_repository_group.repository_unskipped': 'un-skipped a repository of a git group',
    'git_settings.changed': 'changed the Git tab settings (branch name format, rules)',
    'task.git_link_added': 'manually added a git link',
    'task.git_link_hidden': 'hid a git link',
    'task.git_link_override_removed': 'removed a manual git link decision',
    'issue.git_link_added': 'manually added a git link',
    'issue.git_link_hidden': 'hid a git link',
    'issue.git_link_override_removed': 'removed a manual git link decision',
    'task.git_branch_created': 'created a git branch from this item',
    'task.git_change_request_created': 'created a merge request from this item',
    'issue.git_branch_created': 'created a git branch from this issue',
    'issue.git_change_request_created': 'created a merge request from this issue',
    'task.progress_commits_reported': 'reported commits for a progress update',
    'task.progress_commit_removed': 'removed a commit from a progress report',
    'task.progress_review_completed': 'AI reviewed the commits of a progress report',
    'task.progress_review_acknowledged': 'acknowledged the AI commit review',
    'task.progress_review_requeued': 'asked AI to review the commits again',
    'git_release_note.suggested': 'sent an AI suggestion for the release notes for review',
    'git_release_note.approved': 'approved the release notes',
    'git_release_note.edited': 'wrote / edited the release notes',
    // Status change caused by a git rule — APM-GIT-h (plan-git-integration §6.2). Reads
    // right after "Git": "Git moved this to Done — MR !45 “Fix login” was merged into main".
    // `{{ref}}` is "MR !45" / "PR #45" — the git server's own notation, not translated.
    gitRule: {
      moved: 'moved this to {{status}} — {{reason}}',
      cr_merged: '{{ref}} “{{title}}” was merged into {{branch}}',
      cr_opened: '{{ref}} “{{title}}” was opened',
      cr_approved: '{{ref}} “{{title}}” was approved',
      deployed: '{{ref}} “{{title}}” was deployed to production',
      work_started_branch: 'branch {{branch}} was pushed',
      work_started_commit: 'commit {{sha}} is the first commit for this task',
      work_started_change_request: '{{ref}} “{{title}}” was linked to this task',
    },
    'workflow_status.created': 'added a column',
    'workflow_status.updated': 'edited a column',
    'workflow_status.deleted': 'deleted a column',
    'membership.added': 'added a member',
    'membership.removed': 'removed a member',
    'membership.role_changed': 'changed a member role',
    'document.created': 'created a document',
    'document.status_changed': 'changed a document status',
    'document.updated': 'edited a document',
    'document.version_added': 'added a document version',
    'document.deleted': 'deleted a document',
    'document.restored': 'restored a document',
    'raid.created': 'logged a RAID item',
    'raid.updated': 'edited a RAID item',
    'objective.created': 'created an objective',
    'key_result.created': 'created a key result',
    'key_result.value_updated': 'updated a key result',
    // Decision sheets — APM-DSHEET.
    'decision_sheet.created': 'created a decision sheet',
    'decision_sheet.updated': 'edited a decision sheet',
    'decision_sheet.deleted': 'deleted a decision sheet',
    'decision_sheet.opened': 'opened a sheet for deciders',
    'decision_sheet.closed': 'closed a decision sheet',
    'decision_sheet.reopened': 'reopened a decision sheet',
    'decision_sheet.deciders_changed': 'changed the deciders of a sheet',
    'decision_sheet.group_upserted': 'edited a question group in a sheet',
    'decision_sheet.group_removed': 'removed a question group from a sheet',
    'decision_sheet.question_upserted': 'edited a question in a sheet',
    'decision_sheet.question_removed': 'removed a question from a sheet',
    'decision_sheet.questions_reordered': 'reordered the questions of a sheet',
    'decision_sheet.answered': 'decided a question in a sheet',
    'decision_sheet.answer_cleared': 'cleared a decision in a sheet',
    'decision_sheet.exported': 'exported a sheet as a document',
    'decision_sheet.raid_recorded': 'recorded decided questions in RAID',
    field: {
      title: 'Title',
      description: 'Description',
      startDate: 'Start',
      dueDate: 'Due',
      estimateHours: 'Estimate (h)',
      storyPoints: 'Story points',
      progressPct: 'Progress (%)',
      priority: 'Priority',
      statusName: 'Status',
      assigneeName: 'Assignee',
      category: 'Status category',
      kind: 'Item type',
      name: 'Name',
      blockReason: 'Blocked reason',
      checklist: 'Checklist',
      rule: 'Repeat schedule',
      taskStatusRules: 'Who can change status',
      taskScheduleRules: 'Who can change dates',
      minTaskDurationMinutes: 'Minimum time before Done (minutes)',
      actualStartAt: 'Actual start',
      actualStartDeclared: 'Start time declared',
      targets: 'SLA targets',
      commitment: 'Time commitment',
      screens: 'Screens',
      stats: 'Change summary',
      order: 'Order',
      hidden: 'Hidden',
      statusChange: 'Who can change status',
      done: 'Who can mark done',
      scheduleChange: 'Who can reschedule',
      freq: 'Frequency',
      weekdays: 'Weekdays',
      monthDay: 'Day of month',
      intervalDays: 'Every (days)',
      endDate: 'Ends',
      responseMinutes: 'Response (min)',
      resolveMinutes: 'Resolution (min)',
      roundTheClock: 'Count off-hours',
      mode: 'Type',
      value: 'Amount',
      added: 'Added',
      removed: 'Removed',
      modified: 'Modified',
      renamed: 'Renamed',
      moved: 'Moved',
      replaced: 'Replaced',
      changes: 'Total changes',
      page: 'Page',
      groupPath: 'Group',
      text: 'Text',
      weight: 'Weight',
    },
    /** Enum values inside before/after objects — `activity.value.<key>.<value>`. */
    value: {
      statusChange: {
        members: 'any member',
        assignee_or_creator: 'assignee or creator',
        roles: 'selected roles',
      },
      done: {
        pm: 'PM only',
        roles_and_pm: 'PM and selected roles',
        assignee_or_creator_and_pm: 'PM, assignee or creator',
        not_assignee_and_pm: 'PM or anyone but the assignee',
        anyone: 'anyone',
      },
      scheduleChange: {
        pm: 'PM only',
        assignee_or_creator: 'assignee or creator',
        roles: 'selected roles',
      },
      mode: { hours: 'hours / week', percent: '% of time' },
      freq: {
        daily: 'daily',
        weekly: 'weekly',
        monthly: 'monthly',
        interval: 'every N days',
      },
    },
    /** Before/after objects drawn as lists instead of JSON — 2026-10-06. */
    structured: {
      added: 'added',
      removed: 'removed',
      renamed: 'renamed',
      checked: 'done',
      unchecked: 'unmarked',
      changed: 'changed',
      unchanged: '{{count}} unchanged',
      more: '+{{count}} more',
      empty: 'empty',
      lastDay: 'last day of month',
    },
  },
  tabLayout: {
    quickEdit: 'Arrange tab bar',
    // APM-GIT-p — default-hidden tab (Git): prompt to switch it on right where setup finished.
    hiddenNotice: 'The {{name}} tab is hidden on the project tab bar. Switch it on so the team can see it.',
    hiddenNoticeAskPm: 'The {{name}} tab is hidden on the project tab bar — ask the PM to switch it on under Tab bar.',
    showTab: 'Show the {{name}} tab',
    shownToast: 'The {{name}} tab is now shown.',
    title: 'Tab bar',
    subtitle: 'Reorder the project tabs, and switch off the ones your team does not use.',
    moveUp: 'Move {{name}} up',
    moveDown: 'Move {{name}} down',
    toggle: 'Show {{name}} in the tab bar',
    pinnedBadge: 'Always on',
    pinnedReason: 'Core tab — can be reordered but not switched off.',
    save: 'Save tab bar',
    reset: 'Undo',
    saved: 'Tab bar updated',
    futureHint: 'Pages added later show up at the end on their own; nothing to switch on here.',
  },
  aiPanel: {
    needKey:
      'This project has no AI key yet, so the feature cannot run. Add one under Settings → “Project AI key”, then come back.',
    hasResult: 'has a result',
    needParams: 'Fill in the field above first.',
    contentMismatch:
      'This result came from an earlier version and can no longer be read. Run it again.',
    dismiss: 'Dismiss',
    cost: '{{value}} USD',
  },
  delayRisk: {
    open: 'Automatic risk analysis',
    title: 'Why the delay risk score looks like this',
    help: 'The indicator table below already has the numbers. This adds the words: what is pulling the score down, and what to do first.',
    run: 'Explain the risk score',
    prompt: 'Explain this project delay risk score and suggest at most three things to do first.',
  },
  inferDeps: {
    title: 'Missing dependencies',
    help: 'Reads descriptions and comments to find dependencies nobody declared. Each proposal quotes a real sentence as evidence.',
    run: 'Find missing dependencies',
    prompt: 'Find dependencies between tasks in this project that have not been declared.',
    none: 'No missing dependencies found.',
    cycles: 'Dropped {{count}} proposals because they form a cycle',
  },
  budgetAi: {
    open: 'Automatic budget estimate',
    title: 'Explain the budget estimate',
    help: 'The total above is computed by the system. This says where the money sits, which assumptions hold it up, and what data is missing.',
    run: 'Explain the estimate',
    prompt:
      'Explain this budget estimate: where the money sits, which assumptions it rests on, what data is missing.',
    noConfidence: 'confidence not established',
    missing: 'Missing data',
  },
  whatIf: {
    title: 'What if we had done it differently',
    help: 'State a hypothesis; the system re-simulates and compares it with what actually happened.',
    run: 'Simulate',
    placeholder: 'e.g. what if we had added a person to the payment integration back then?',
    p50: 'Shift (P50)',
    p85: 'Shift (P85)',
    affected: 'Tasks affected',
    verdict: 'Verdict',
    verdictName: { better: 'better', worse: 'worse', same: 'no different' },
    days: '{{value}} days',
    lesson: 'Lesson learned',
    appliesWhen: 'Applies when: {{value}}',
    seed: 'Simulation seed: {{value}} — the same seed gives the same result.',
  },
  decisionDig: {
    title: 'Why did we choose that',
    help: 'Answers “why did we choose that back then” from the trail the project already left: Decision entries in RAID, task comments, and uploaded documents. It reconstructs the context as it was then — which sprint was running, who owned it, what constrained it — rather than answering from today. If it finds nothing it says so and offers to record the decision, instead of inventing a plausible reason.',
    run: 'Look up the decision',
    placeholder: 'e.g. why did the project pick MySQL over PostgreSQL?',
    contextAt: 'Context rebuilt as of {{value}}',
    backfill: 'No recorded decision found — worth writing one down',
    sources: 'Built from {{count}} sources',
  },
  scopeNego: {
    title: 'Scope negotiation',
    help: 'Give a new constraint, get back a few options with real simulation numbers and which objectives they touch.',
    run: 'Build options',
    placeholder: 'e.g. the client wants delivery two weeks earlier with no extra people',
    recommended: 'recommended',
    days: 'P50 {{p50}} days · P85 {{p85}} days',
    cuts: 'cuts {{count}} tasks',
    defers: 'defers {{count}} tasks',
  },
  taskSummary: {
    title: 'Summarise this task',
    help: 'Where it stands, who is waiting on whom, what was decided, what is still open, and the next step.',
    run: 'Summarise',
    prompt: 'Summarise the state of task {{key}} and its comment thread.',
    waitingOn: 'Waiting on',
    decisions: 'Decided',
    openQuestions: 'Still open',
    nextStep: 'Next step',
    sources: 'Built from {{count}} comments and events',
  },
  handoverBrief: {
    title: 'Handover brief for someone going away',
    help: 'Pick a person and get each of their tasks: where it stands, what it waits on, and who should take it.',
    run: 'Build handover brief',
    prompt:
      'Build a handover brief for this person: where each task stands, what it waits on, who should take it.',
    noTasks: 'This person has no open tasks.',
    nextStep: 'Next step',
    waitingOn: 'Waiting on: {{value}}',
    proposed: 'Suggested owner: {{name}}',
    risks: 'Handover risks',
  },
  rebalanceAi: {
    open: 'Handle late tasks',
    noLateTasks: 'No task is past its deadline right now — nothing to handle.',
    title: 'This task is late — what does it affect, and what can you do?',
    helpIntro: 'Pick a task that is past its deadline. AI will tell you:',
    helpDates: 'Which important dates will be pushed back (such as a handover or launch date).',
    helpWaiting: 'Which other tasks have to wait for this one, and so will be late too.',
    helpOptions:
      'What your options are — for example pushing back the tasks that follow, or handing some work to someone with free time. For each: how many days things move, who is affected, and why it estimates that.',
    helpNote:
      'AI only explains — it does not change the schedule. You pick an option and edit the schedule yourself.',
    run: 'Analyse',
    prompt: 'Analyse this late task: what it affects and how to handle it.',
    pickTask: '— pick a late task —',
    cause: {
      early: '{{key}} finished {{days}} working days early',
      late: '{{key}} is {{days}} working days late',
    },
    milestones: 'Important dates that will be pushed back',
    shift: 'moves {{value}} working days',
    noShift: 'no schedule change',
    touches: 'affects {{count}} people',
    moves: '{{count}} tasks to move',
    solverTitle: 'Options suggested by the system (no AI needed)',
    noOption: 'The system found no option for this task — review the schedule on the Gantt.',
    moveDate: 'move from {{from}} to {{to}}',
    moveAssign: 'hand over to {{name}}',
    moveUnschedule: 'take off the schedule (not deleted)',
  },

  dod: {
    help: 'An item must tick every box below before it can move to Done. Editing this list applies to every item in the project.',
    title: 'Definition of Done',
    subtitle: 'What a task must satisfy before it counts as done.',
    helpEmpty:
      'No items yet — and that is a normal state. Add the first one and from then on work must tick them all before reaching Done.',
    placeholder: 'For example: reviewed, tests written',
    add: 'Add item',
    remove: 'Remove “{{text}}”',
    retired: 'retired',
  },
  announce: {
    open: 'Compose',
    title: 'Send an announcement',
    help: 'This lands straight in people\u2019s inboxes and cannot be unsent — read it once more first.',
    audience: 'Send to',
    toReports: 'People who report to me',
    toOrg: 'The whole organisation',
    toProject: 'Members of {{name}}',
    counting: 'Counting recipients…',
    blocked: {
      direct_reports: 'Nobody lists you as their line manager, so there is nobody to send to.',
      organization: 'You cannot send announcements to the whole organisation.',
      project: 'You cannot edit this project, so you cannot announce to it.',
    },
    recipients_one: 'Goes to {{count}} person',
    recipients_other: 'Goes to {{count}} people',
    subject: 'Subject',
    subjectPlaceholder: 'Maintenance tonight',
    body: 'Message',
    bodyPlaceholder: 'The system pauses from 22:00 to 23:00.',
    left: '{{count}} characters left',
    send: 'Send',
    sendTo_one: 'Send to {{count}} person',
    sendTo_other: 'Send to {{count}} people',
    sent_one: 'Sent to {{count}} person',
    sent_other: 'Sent to {{count}} people',
  },
  notifications: {
    // Realtime toast when a notification's sentence lacks its parameters — see `toastText`.
    fallback: 'You have a new notification — open the bell to read it',
    'task_type.proposed': '{{by}} proposed the work type “{{name}}” — needs your approval',
    'project_approval.requested': 'A new project is waiting for your approval',
    'project_approval.approved': 'Your project was approved',
    'project_approval.rejected': 'Your project was rejected',
    'project_approval.decided_by_other':
      'Someone else decided on a project that was waiting for you',
    'project_approval.request_received':
      '{{by}} opened the project “{{name}}” — needs your approval',
    'project_approval.accepted': 'Your project “{{name}}” was approved by {{by}}',
    'project_approval.declined': 'Your project “{{name}}” was rejected by {{by}}: {{note}}',
    'project_approval.accepted_by_other': '{{by}} approved “{{name}}” on your behalf',
    'project_approval.declined_by_other': '{{by}} rejected “{{name}}” on your behalf: {{note}}',
    'task_type.approved': 'The work type “{{name}}” you proposed was approved',
    'task_type.rejected': 'The work type “{{name}}” you proposed was rejected: {{note}}',
    'announcement.sent': '{{title}}',
    'comment.mentioned': 'You were mentioned in a comment',
    'task.assigned': 'A task was assigned to you',
    'task.unassigned': 'One of your tasks went to someone else',
    'task.edited_one': 'Someone edited a task you are working on',
    'task.edited_other': 'Someone edited a task you are working on {{count}} times',
    'membership_request.invited': 'You were invited to join a project',
    'membership_request.applied': '{{name}} applied to join {{project}} — please review',
    'client_request.created': 'Client {{name}} asked to join {{project}} — please review',
    'client_request.accepted': 'You were approved to join {{project}}',
    'client_request.declined': 'Your request to join {{project}} was not approved',
    'guest_request.created': 'Collaborator {{name}} asks to join {{project}} — needs your approval',
    'guest_request.accepted': 'You have been approved to join {{project}}',
    'guest_request.declined': 'Your request to join {{project}} was not approved',
    'org_join_request.created': '{{name}} asked to join the organisation — needs your approval',
    'org_join_request.accepted':
      'You were approved into {{org}} — switch organisation at the top to enter',
    'org_join_request.declined': 'Your request to join {{org}} was not approved',
    'offboarding.scheduled': '{{name}} leaves after {{date}} — arrange handover and reassign work',
    'offboarding.rescheduled': '{{name}} changed their last working day to {{date}}',
    'offboarding.cancelled': '{{name}} is no longer leaving',
    'offboarding.handover_received':
      'You took over from {{name}}: {{files}} files, {{reports}} direct reports',
    'offboarding.handover_done': 'Handover from {{name}} is complete',
    'offboarding.handover_failed':
      'Handover from {{name}} is not complete — open the plan to see why',
    'membership_request.answered.accepted': 'Your invitation was accepted',
    'membership_request.answered.declined': 'Your invitation was declined: {{note}}',
    'membership_request.finalized.accepted': 'Your request to join a project was approved',
    'membership_request.finalized.declined':
      'Your request to join a project was declined: {{note}}',
    'raid.risk_high': 'A risk just crossed the high threshold in your project',
    'raid.signals_detected': 'New risk signals in your project',
    'handover.suggested':
      'A teammate will be away with {{openTasks}} open tasks — draft a handover?',
    'sprint.ending_soon':
      'Sprint «{{name}}» ({{project}}) ends on {{endDate}} — unfinished tasks: {{openTasks}}. Extend it if needed; it closes automatically once past due',
    'sprint.last_day':
      'Today is the last day of sprint «{{name}}» ({{project}}) — unfinished tasks: {{openTasks}}. It closes automatically tonight and unfinished tasks go to the backlog',
    'sprint.auto_closed':
      'Sprint «{{name}}» ({{project}}) closed automatically at its end date — unfinished tasks moved to the backlog: {{carriedOver}}',
    'project.member_left': 'A member left your project',
    'task.due_soon': 'A task of yours is due soon',
    'design_file.remote_version_detected':
      'Figma file “{{name}}” has a new version — capture it to compare?',
    // Project sync — APM-SYNC-e: the source key died ⇒ automatic runs stopped.
    'project_sync.failed':
      'Sync of “{{project}}” with {{source}} stopped — the API key is no longer valid, paste a new one in project settings',
    // APM-SYNC-alert: every other failed run, and runs that could not import some tasks.
    'project_sync.error':
      'Sync of «{{project}}» with {{source}} is failing: {{reason}} — open the project to review and retry',
    'project_sync.skipped':
      'Sync of «{{project}}» with {{source}}: {{count}} tasks could not be imported ({{reason}}), {{imported}} imported — open the project to review and fix',
    'project_sync.pending':
      'Sync of «{{project}}» with {{source}}: {{count}} source values could not be mapped, {{tasks}} tasks placed temporarily — open the sync result to map them',
    // Git tab — APM-GIT-d: the git server rejected the token ⇒ the repository stopped syncing; and other lasting failures.
    'git_sync.failed':
      'Repository «{{repository}}» of «{{project}}» stopped syncing from {{host}} — the token is no longer valid, paste a new one under Project settings → Git repositories',
    'git_sync.error':
      'Repository «{{repository}}» of «{{project}}» cannot sync from {{host}}: {{reason}} — open the Git tab to see',
    // Git tab — APM-GIT-j: token about to expire (14 days before, once); default branch red for more than 2 hours (once per red spell).
    git_token_expiring:
      'The git token «{{label}}» of «{{project}}» expires on {{date}} ({{days}} days left) — paste a new one under Project settings → Git repositories',
    // APM-PCRV: AI rà commit đã báo thấy thấp — chỉ cảnh báo PM, việc không đổi.
    git_progress_review_unrelated:
      'Commits {{reporter}} reported for {{taskKey}} “{{task}}” look unrelated (AI review) — {{summary}}',
    git_progress_review_partial:
      'Commits {{reporter}} reported for {{taskKey}} “{{task}}” are only partly related (AI review) — {{summary}}',
    git_default_pipeline_failed:
      'Branch {{branch}} of «{{repository}}» («{{project}}») has been red for {{hours}} hours — open the Git tab to see the failed pipeline',
    // APM-GIT-s-b: a new repository in a linked group — only a suggestion, the PM approves.
    git_group_repo_suggested:
      'Group «{{group}}» has a new repository: {{repository}} — open the settings of «{{project}}» → Git repositories to link or skip it',
    'task.due_overdue': 'A task of yours is overdue',
    // APM-TLOW — an in-progress task has ≤ 10% of its time left.
    'task.time_running_out':
      '{{taskKey}} «{{title}}» is running out of time — due {{deadline}}, 90% of the time since you started is used',
    'schedule.impact_detected': 'A schedule change affects your project plan',
    'schedule.drift_detected':
      '{{late}} task(s) late and {{early}} finished early — review what comes next',
    'schedule.drift_late.one':
      '«{{project}}»: {{task}} is {{days}} working days late — move the due date or rework the schedule after it',
    'schedule.drift_late.many':
      '«{{project}}»: {{count}} tasks are late ({{tasks}}) — open the Tasks tab to review the schedule',
    'schedule.drift_early.one':
      '«{{project}}»: {{task}} finished {{days}} working days early — later tasks may be pulled in',
    'schedule.drift_early.many':
      '«{{project}}»: {{count}} tasks finished early ({{tasks}}) — later tasks may be pulled in',
    'ai_budget.soft_limit_reached': 'Your organisation crossed the AI spend warning threshold',
    'issue.state.triaged': '{{key}} “{{title}}” was triaged',
    'issue.state.in_progress': '{{key}} “{{title}}” is being worked on',
    'issue.state.waiting_customer': '{{key}} “{{title}}” is waiting for your reply',
    'issue.state.waiting_third_party': '{{key}} “{{title}}” is waiting on a third party',
    'issue.state.closed': '{{key}} “{{title}}” was closed',
    'issue.resolved': '{{key}} “{{title}}” is resolved — confirm or reopen',
    'issue.reopened': '{{key}} “{{title}}” was reopened by the reporter',
    'issue.watcher_added': '{{key}} “{{title}}” was shared with you',
    // Issue conversation — APM-ISC-c. `note` is the start of the comment.
    'issue.replied': 'The team replied on {{key}} “{{title}}”: {{note}}',
    'issue.customer_replied': 'The reporter replied on {{key}} “{{title}}”: {{note}}',
    'issue.internal_comment': 'Internal note on {{key}} “{{title}}”: {{note}}',
    'issue.assigned': '{{key}} “{{title}}” was assigned to you',
    // Testing bug flow — APM-TEST2-b. `actor` is who clicked, `project` the project name.
    'bug.assigned': '{{actor}} assigned you bug #{{code}} · {{project}}: {{title}}',
    'bug.fixed': '{{actor}} fixed bug #{{code}} · {{project}}: {{title}} — waiting for your retest',
    'bug.still_failing':
      '{{actor}} retested bug #{{code}} · {{project}}: {{title}} — still failing, needs another fix',
    'bug.closed': '{{actor}} verified bug #{{code}} · {{project}}: {{title}} — the bug is closed',
    // Decision sheets — APM-DSHEET-i. `name` is the inviter, `count` the number of questions.
    'decision_sheet.invited':
      '{{name}} invited you to decide sheet «{{title}}» ({{count}} questions)',
    // Document submitted for review — the recipient is the PM. `version` is the latest version number.
    'document.review_requested':
      '{{name}} submitted document «{{title}}» (version {{version}}) for review · {{project}}',
    // Mentioned (@) in a document — APM-CD2-f. `name` is who mentioned.
    'document.mentioned': '{{name}} mentioned you in document «{{title}}»',
    'document.suggested': '{{name}} suggested edits to document «{{title}}» (version {{version}})',
    // AI key model lifecycle — APM-MDL-b/-d.
    'ai.model_update_available': 'Key «{{label}}» has new models: {{models}}',
    'ai.model_recommendation': 'Model recommendation for key «{{label}}»: {{summary}}',
    // Deadline / milestone moved — APM-DL-a. Dates arrive pre-formatted as dd/MM/yyyy.
    'schedule.deadline_changed.project.moved':
      'The deadline of project «{{name}}» moved from {{from}} to {{to}}',
    'schedule.deadline_changed.project.set': 'Project «{{name}}» now has a deadline: {{to}}',
    'schedule.deadline_changed.project.cleared':
      'Project «{{name}}» no longer has a deadline (was {{from}})',
    'schedule.deadline_changed.sprint.moved':
      '«{{project}}»: the end of sprint «{{name}}» moved from {{from}} to {{to}}',
    'schedule.deadline_changed.milestone.moved':
      '«{{project}}»: milestone «{{name}}» moved from {{from}} to {{to}}',
    'schedule.deadline_changed.milestone.set':
      '«{{project}}»: milestone «{{name}}» is now dated {{to}}',
    'schedule.deadline_changed.milestone.cleared':
      '«{{project}}»: milestone «{{name}}» lost its date (was {{from}})',
    'schedule.deadline_changed.many':
      '«{{project}}»: {{count}} deadlines and milestones just moved — check your tasks',
    'ai.alert.error_rate':
      'AI run error rate over the last 6 hours is {{value}}%, above the {{threshold}}% threshold',
    'ai.alert.cost_per_day':
      'AI spend today is {{value}} USD, above the {{threshold}} USD threshold',
    'timesheet.timer_overdue':
      'Your timer is running on overdue «{{taskKey}}» — update progress or move the date?',
    'timesheet.session_capped':
      'Your timer ran past 12 hours and stopped itself — check today’s hours',
    'timesheet.overtime_capped':
      '{{hours}} hours of overtime recorded and the timer stopped itself. Still working? Press Continue.',
    'timesheet.day_capped':
      'You have logged {{hours}} hours today, so the timer stopped itself — pick it up tomorrow.',
    // Direct manager notice (no manager ⇒ directors) — APM-TREM-e. `range` is preformatted dates.
    'absence.requested': '{{name}} requests leave {{range}} — awaiting your approval',
    'absence.retracted': '{{name}} withdrew the leave request {{range}}',
    'ai.alert.stuck_runs': '{{value}} AI runs are stuck, above the threshold of {{threshold}}',

    // Organisation merge / split — APM-ORGMS-c/e/f. `updated` is the toast text (SSE type),
    // the rest are inbox `titleKey`s.
    'org_restructure.updated': 'An organisation merge / split proposal changed',
    'okr.report_due': 'Key results in your OKRs need a value: {{count}}',
    'org_restructure.invited':
      '{{by}} invites your organisation to merge with “{{org}}” — open the invitation',
    'org_restructure.partner_joined':
      '“{{partner}}” accepted the merge proposal with “{{initiator}}” — your approval is needed',
    'org_restructure.config_changed':
      'The merge configuration changed (version {{version}}) — earlier approvals no longer count, please approve again',
    'org_restructure.rejected': '{{by}} rejected the organisation merge proposal: {{reason}}',
    'org_restructure.cancelled': 'The organisation merge proposal was cancelled ({{by}})',
    'org_restructure.expired':
      'The organisation merge proposal expired — not enough approvals in 14 days',
    'org_restructure.executed': '“{{source}}” has merged into “{{target}}”',
    'org_restructure.failed':
      'Merging “{{source}}” into “{{target}}” did not finish — an administrator is on it',

    // Inbox UI — APM-NOTI-c. Keys above are the *content* of each notification
    // type; the ones below are the screen's own wording.
    title: 'Notifications',
    filterUnread: 'Unread',
    markAllRead: 'Mark all as read',
    markAllReadShort: 'Read all',
    composeShort: 'Compose',
    empty: 'No notifications yet',
    taskPopupLabel: 'Task detail',
    emptyUnread: 'You are all caught up',
    emptyHelp: 'Assignments, mentions and upcoming deadlines show up here.',
    unreadDot: 'Unread',
    targetGone: 'This item has been deleted',
    bellEmpty: 'Notifications',
    bellWithCount_one: '{{count}} unread notification',
    bellWithCount_other: '{{count}} unread notifications',
    previous: 'Previous',
    next: 'Next',
  },
  levelRate: {
    title: 'Rates by level',
    subtitle:
      'Default hourly rate for each staff level. Each project PM either uses it or sets a custom rate.',
    hint: 'This is the organisation rate card, not a payroll table: it answers "what does a Senior 1 cost per hour". Changing a rate keeps the old one so past reports do not move.',
    level: {
      JUNIOR_1: 'Junior 1',
      JUNIOR_2: 'Junior 2',
      SENIOR_1: 'Senior 1',
      SENIOR_2: 'Senior 2',
    },
    perHour: '/hour',
    notSet: 'not set',
    save: 'Save',
    saved: 'Rate card saved.',
    since: 'since {{date}}',
    userLevel: 'Level',
    userLevelNone: 'Not set',
  },
  projectAiKey: {
    title: 'Project AI key',
    subtitle: 'Attach your key to the project so the team shares it and the cost lands here.',
    intro:
      'The project uses this key for every AI feature inside it, including for people who have their own key. Your key is not copied — the project only points at it.',
    noneAttached: 'No key attached. Everyone uses their own key.',
    owner: 'owned by {{name}}',
    inactive: "The owner deactivated this key, so the project falls back to each person's own key.",
    pick: 'Your keys',
    mine: 'in use',
    attach: 'Attach to project',
    replace: 'Switch to this key',
    detach: 'Detach from project',
    noKeysOfMine: 'You have no verified key yet. Add one and hit "Test" in My settings first.',
    warning:
      "Note: every AI call in this project bills this key — the owner's money, not the caller's.",
    spent: 'This project has spent {{value}} USD on AI.',
    saved: 'Project key saved.',
  },
  testImport: {
    open: 'Import cases from file',
    title: 'Import test cases from a file',
    subtitle:
      'Excel or CSV. Files in the template format import directly; for your own layout, pick the columns or let AI detect them. Nothing is saved until you click Import.',
    dropHere: 'Drop a file here, or choose one from your computer',
    formats:
      '.xlsx, .xls, .csv — up to 10 MB. The file is read in your browser only and never uploaded.',
    pickFile: 'Choose file',
    reading: 'Reading file…',
    tooBig: 'The file is larger than 10 MB — split it and import each part.',
    emptyFile: 'The file has no sheet with data.',
    unreadable: 'This file could not be read. Save it as .xlsx or .csv and try again.',
    templateTitle: 'No file yet? Use the template',
    templateHelp:
      'Ten columns in order, with two example rows and a guide sheet. Fill it in and it imports right away, no AI needed.',
    downloadTemplate: 'Download template',
    sheet: 'Sheet',
    otherFile: 'Choose another file',
    showMapping: 'Pick columns',
    hideMapping: 'Hide columns',
    source: {
      template: 'Template format',
      guess: 'Not the template — columns guessed from their names, please check',
      manual: 'Columns picked by you',
      ai: 'Columns suggested by AI — check before importing',
    },
    aiRun: 'Normalize with AI',
    aiPrompt: 'Map the columns of this test case file to the standard template',
    aiApplied: 'AI picked the columns — review the preview before clicking Import.',
    aiMismatch: 'The AI result had an unexpected shape — please pick the columns manually.',
    headerRow: 'Header row',
    noHeaderRow: 'No header row',
    rowOption: 'Row {{n}}: {{text}}',
    groupRows: 'Rows with a single filled cell are group names',
    noColumn: '— none —',
    needTitleColumn:
      'No column was recognised as “Case title”. Pick it above, or click “Normalize with AI” to let AI detect the columns.',
    noRows: 'There are no data rows below the header row.',
    tickAll: 'Select all',
    tickRow: 'Select row {{n}}',
    autoCode: 'auto',
    col: { row: 'Row', kind: 'Kind', priority: 'Priority', note: 'Notes' },
    issue: {
      missingTitle: 'Missing title',
      codeExists: 'Code already in project',
      codeDuplicate: 'Duplicate of a row above',
      truncated: 'Some cells were cut',
    },
    summary: '{{count}} cases to import · {{skipped}} rows skipped',
    tooMany: 'At most {{max}} cases per import — untick some rows or split the file.',
    import: 'Import {{count}} cases',
    imported: 'Imported {{count}} test cases.',
    downloadNormalised: 'Download normalized file',
    normalisedFileName: 'normalized-test-cases.xlsx',
    columns: {
      code: 'Case code',
      suite: 'Group',
      title: 'Case title',
      kind: 'Kind (UI/API)',
      priority: 'Priority (High/Medium/Low)',
      preconditions: 'Preconditions',
      steps: 'Steps',
      expected: 'Expected result',
      requirementRef: 'Related rule',
      endpoint: 'Endpoint',
    },
    guide: {
      code: 'Optional. Leave blank to get a code based on the project key. Codes already in the project are skipped.',
      suite: 'Optional. Groups that do not exist yet are created by name.',
      title: 'Required. One short sentence saying what is checked.',
      kind: '“UI” or “API”. Blank means UI.',
      priority: '“High”, “Medium” or “Low”. Blank means Medium.',
      preconditions: 'State that must exist before testing (signed in, data present…).',
      steps: 'One step per line, numbered 1., 2., …',
      expected: 'Something visible on screen, or a returned value.',
      requirementRef: 'Requirement / business rule / related task key.',
      endpoint: 'API cases only, e.g. POST /api/v1/login.',
    },
    template: {
      sheet: 'Test cases',
      guideSheet: 'Guide',
      guideColumn: 'Column',
      guideMeaning: 'How to fill',
      fileName: 'test-case-template.xlsx',
    },
    example1: {
      code: 'AUTH-01',
      suite: 'Sign in',
      title: 'Sign in with a correct email and password',
      kind: 'UI',
      priority: 'High',
      preconditions: 'An activated account exists',
      steps: '1. Open the sign-in page\n2. Enter a correct email and password\n3. Click Sign in',
      expected: 'The Overview screen opens with the user name at the top right',
      requirementRef: 'BR-01',
      endpoint: '',
    },
    example2: {
      code: '',
      suite: 'Sign in',
      title: 'Sign-in API returns 401 for a wrong password',
      kind: 'API',
      priority: 'Medium',
      preconditions: '',
      steps: '1. Send POST with a wrong password',
      expected: 'Status 401, body contains error code INVALID_CREDENTIALS',
      requirementRef: 'BR-02',
      endpoint: 'POST /api/v1/auth/login',
    },
  },
  testing: {
    evidenceFiles: 'Images / videos from your device',
    evidenceHelp:
      'Up to 10 files. Images (PNG, JPG, GIF, WebP) up to 5 MB, videos (MP4, WebM, MOV) up to 100 MB.',
    evidenceImageTooBig: 'image is larger than 5 MB',
    evidenceVideoTooBig: 'video is larger than 100 MB',
    evidenceWrongType: 'only images or videos are accepted',
    evidenceDeleted: 'This file was deleted on the Files page',
    title: 'Testing',
    flowLine:
      'Run each case in each environment; when a cell fails, report the bug right there. A test round re-runs the whole case set — developers do not wait for a round to fix bugs.',
    sectionRun: 'Run tests',
    sectionRunRound: 'Run tests · Round {{number}}',
    sectionBugsOpen: 'Bugs · {{n}} open',
    report: {
      progressTitle: 'Test progress by round',
      progressSubtitle: 'Counted by cell: each case × each environment is one cell',
      yieldTitle: 'Fix effectiveness across rounds',
      yieldSubtitle: 'How many dev marked fixed, and how many the retest confirmed',
      convergenceTitle: 'Convergence',
      convergenceSubtitle: 'Bugs remaining and newly found, by round',
      latestRound: 'Round {{number}} — latest',
      roundShort: 'Round {{number}}',
      cardProgress: 'Run',
      cardRemaining: 'Bugs left',
      cardNew: 'New bugs this round',
      fixYield: 'Actually fixed',
      reopenRate: 'Reopened',
      remaining: 'Bugs remaining',
      newBugs: 'New bugs',
      overdue: 'Past dev deadline',
      avgFix: 'Average fix time',
      hours: '{{value}} h',
      notEnoughData: 'not enough data',
      bySeverity: 'By severity (left/total)',
      noRounds: 'No test rounds to report on yet.',
      noFixData:
        'No round has any bug marked fixed by dev yet, so effectiveness cannot be computed.',
    },
    viewingRound: 'Viewing',
    roundLabel: 'Round {{number}}',
    roundOpen: 'open',
    openRound: 'Open new round',
    openFirstRound: 'Open the first round',
    closeRound: 'Close round',
    reopenRound: 'Reopen round',
    reopenRoundHelp:
      'Only the latest round can be reopened — reopening lets results be recorded again.',
    closeRoundHelp: 'Once closed, no more results can be recorded into this round.',
    closeBeforeOpen: 'Close the open round before opening a new one.',
    roundOpened: 'New round opened.',
    roundClosed: 'Round closed.',
    roundReopened: 'Test round reopened',
    confirm: {
      close: {
        title: 'Close round {{number}}?',
        warning:
          'Once closed, nobody can record results into this round, and its numbers flow into reports and the Gantt markers. It can be reopened, but only while no newer round exists.',
        action: 'Close round',
      },
      reopen: {
        title: 'Reopen round {{number}}?',
        warning:
          'This round counts as final right now. Reopening means any result added afterwards changes the numbers reports and round-to-round comparisons already use.',
        action: 'Reopen round',
      },
      delete: {
        title: 'Delete round {{number}}?',
        warning:
          'Deleting is permanent: every result cell in this round and every per-bug retest result inside it are gone for good. The bugs themselves stay. The next round you open takes back the number just freed.',
        action: 'Delete round',
      },
    },
    roundDeleted: 'Test round deleted',
    deleteRound: 'Delete round',
    deleteRoundHelp: 'Only the latest round can be deleted. Every result in it is lost.',
    noRounds: 'No test rounds yet',
    noRoundsHelp:
      'A test round is one pass over the case set. Bugs found belong to this round, and once dev has fixed them you open a new round to retest.',
    sectionCases: 'Test cases',
    sectionReport: 'Reports',
    sectionBugs: 'Bugs',
    noCases: 'No test cases yet',
    noCasesHelp:
      'Add the first case at the bottom. Later cases can be duplicated from earlier ones.',
    noSuite: 'Ungrouped',
    noEnvironments: 'No test environments yet',
    noEnvironmentsHelp:
      'An environment is where you run the test — a browser, or a phone. With those in place you can record a result per place.',
    addDefaultEnvironments: 'Use the 6 common environments',
    environments: {
      title: 'Test environments: {{active}}/{{total}} in use',
      help: 'Each environment in use is a column in the results grid.',
      manageHelp:
        'Each environment in use is a column in the results grid. Click a name to turn it off/on — it is disabled, not deleted, so past results are kept.',
      turnOff: 'Click to turn off — this environment will no longer be a grid column',
      turnOn: 'Click to use this environment again',
      addPlaceholder: 'Add an environment, e.g. iPad Safari',
      add: 'Add',
      seeded: 'Added {{count}} test environments.',
      readyTitle: 'Environments are ready',
      readyHelp:
        'Next: add test cases (button at the bottom, or generate with AI), then open a test round in the bar above. Each environment above then becomes a column for recording results.',
    },
    colCase: 'Test case',
    colAll: 'Everywhere',
    allPass: 'All pass',
    allPassHelp: 'Record "pass" for every environment of this case in one click.',
    cycleHelp: 'Click to cycle: not run → pass → fail → blocked → skipped',
    priorityHigh: 'High priority',
    preconditions: 'Preconditions',
    steps: 'Steps',
    expected: 'Expected result',
    endpoint: 'Endpoint',
    requirementRef: 'Related rule',
    caseCode: 'Case code',
    caseTitle: 'Case title',
    caseTitlePlaceholder: 'Guest not signed in clicks Vote',
    addCase: 'Add case',
    caseAdded: 'Test case added.',
    caseUpdated: 'Test case saved.',
    editCase: 'Edit',
    caseCodeLocked:
      'The code cannot change — the team refers to this case by it. Duplicate the case if you need a different code.',
    caseKind: 'Case kind',
    kind: { ui: 'UI', api: 'API' },
    priorityLabel: 'Priority',
    priority: { high: 'High', medium: 'Medium', low: 'Low' },
    caseSuite: 'Group',
    newSuite: '+ New group…',
    newSuiteName: 'New group name',
    newSuitePlaceholder: 'Authentication',
    preconditionsPlaceholder: 'Browser signed out, cookies cleared',
    stepsCasePlaceholder: '1. Open the nominee list\n2. Click Vote',
    stepsCaseHelp: 'One step per line. Someone else must be able to follow it without asking.',
    expectedPlaceholder: 'Sign-in prompt appears, no vote recorded',
    tool: 'Tool',
    requirementRefHelp:
      'The business rule this case checks — so you can trace back when requirements change.',
    status: { none: 'Not run', pass: 'Pass', fail: 'Fail', blocked: 'Blocked', skipped: 'Skipped' },
    reportBug: 'Report a bug',
    bugReported: 'Bug reported.',
    bugTitle: 'What is wrong',
    bugTitlePlaceholder: 'Tooltip text is cut off on mobile',
    stepsToReproduce: 'How to see this bug again',
    stepsPlaceholder: '1. Open the detail page\n2. Click Vote\n3. Look at the tooltip',
    stepsHelp:
      'The most important part of a bug. A developer cannot fix what they cannot reproduce, and leaving this out usually costs one extra round.',
    noStepsWarning: 'This bug has no reproduction steps — dev will struggle and may send it back.',
    severityLabel: 'Severity',
    severity: { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' },
    environment: 'Where it happens',
    anyEnvironment: 'Anywhere / not sure',
    category: 'Which area',
    categoryPlaceholder: 'UI - detail page',
    attachment: 'Screenshot or video',
    save: 'Save',
    noBugs: 'No bugs reported yet',
    noBugsHelp:
      'When a test goes wrong, hit "Report a bug" so it lands in the list and the reports.',
    allBugsClosed: 'Nothing left to fix',
    bugState: {
      open: 'New',
      fixing: 'Being fixed',
      retest: 'Awaiting retest',
      closed: 'Done',
      reopened: 'Reopened',
      rejected: 'Not a bug',
    },
    fixTasks: 'Fix tasks',
    noFixTasks: 'No task linked. A bug fixed the same day does not need one.',
    fixTaskType: 'Task type',
    createFixTask: 'Create fix task',
    fixTaskCreated: 'Fix task created and linked to this bug.',
    unlink: 'Unlink',
    unlinkHelp: 'Unlinks only — the task is not deleted, it may be fixing other bugs too.',
    fixTaskHelp:
      'A fix task is planned and counted in burndown like any other. But moving it to Done does NOT close the bug — only a tester retest can.',
    bugsFixedHere: 'Bugs this task fixes',
    taskDoneNotClosed:
      'Moving this task to Done does not close the bugs above. A tester must retest and confirm them clean.',
    assigneeOptional: 'Fixer (optional)',
    assigneeHelp:
      'Pick someone now and the bug goes straight to Fixing. Leave it empty and it waits in To assign.',
    assigneeSelfOnly:
      'You can only take it yourself — assigning someone else needs test-management permission.',
    dueDate: 'Fix by',
    board: {
      filterLabel: 'Filter',
      mine: 'My work',
      all: 'All',
      anySeverity: 'Any severity',
      anyEnvironment: 'Any environment',
      mineEmpty: 'Nothing is waiting on you — showing all bugs.',
      noMatch: 'No bugs match the filter.',
      column: {
        assign: 'To assign',
        fix: 'Fixing',
        verify: 'To verify',
        done: 'Done',
      },
      columnEmpty: 'Empty',
      expandDone: 'Open the Done column',
      collapseDone: 'Collapse the Done column',
      doneCollapsed: '{{n}} finished bugs — click the column title to see them.',
      reopened: 'Sent back · time {{n}}',
      due: 'due {{date}}',
      waitAssign: 'Nobody has it yet',
      waitVerify: 'waiting for {{name}} to verify',
      rejected: 'Not a bug',
      unknownUser: 'Someone who left the project',
    },
    detail: {
      waiting: {
        assign: 'Waiting on: the PM to assign a fixer',
        fix: 'Waiting on: {{name}} to fix',
        fixDue: 'Waiting on: {{name}} to fix — due {{date}}',
        verify: 'Waiting on: {{name}} to verify',
        closed: 'Done — the bug is closed',
        rejected: 'Concluded: not a bug',
      },
      action: {
        assign: 'Assign to…',
        take: 'I will take it',
        fixed: 'Fixed',
        pass: '✓ It is fixed',
        fail: '✗ Still broken',
        reopen: 'Reopen',
        more: 'More actions',
        reject: 'Not a bug',
        giveBack: 'Give back',
        confirm: 'Confirm',
        cancel: 'Never mind',
      },
      noAction: 'This step is not yours.',
      fixedComment: 'Note for whoever verifies (optional)',
      fixedCommentPlaceholder: 'What was fixed, and where',
      failComment: 'What do you still see?',
      failCommentPlaceholder: 'Mobile Chrome still lets me save',
      failCommentHelp: 'Required — the fixer needs to know how this time differs from the last.',
      reason: 'Reason',
      rejectPlaceholder: 'Works as specified in section …',
      reopenPlaceholder: 'The new build shows it again',
      done: {
        assigned: 'Assigned.',
        taken: 'You have the bug.',
        givenBack: 'Given back — the bug is waiting to be assigned.',
        fixed: 'Marked fixed — the bug now waits to be verified.',
        pass: 'Bug closed.',
        fail: 'Sent back to the fixer.',
        rejected: 'Concluded: not a bug.',
        reopened: 'Bug reopened.',
        saved: 'Saved.',
      },
      fieldAssignee: 'Fixer',
      fieldDue: 'Fix by',
      fieldSeverity: 'Severity',
      fieldEnvironment: 'Environment',
      fieldReporter: 'Reported by',
      fieldSource: 'From case',
      sourceLine: '{{code}} · Round {{round}}',
      noDue: 'Not set',
      noSource: 'Reported by hand, not from a cell',
      unassigned: 'Unassigned',
      description: 'Description, actual result',
      evidence: 'Images, videos',
      history: 'History',
      event: {
        reported: '{{actor}} reported the bug',
        reportedAssigned: '{{actor}} reported the bug and assigned {{assignee}}',
        assigned: '{{actor}} assigned {{assignee}}',
        taken: '{{actor}} took it',
        unassigned: '{{actor}} unassigned it',
        fixed: '{{actor}}: Fixed',
        wont_fix: "{{actor}}: proposed won't fix",
        verified_pass: '{{actor}}: ✓ It is fixed',
        verified_fail: '{{actor}}: ✗ Still broken',
        returned: 'Sent back to {{assignee}}',
        rejected: '{{actor}}: Not a bug',
        reopened: '{{actor}} reopened it',
        someone: 'Someone',
        round: 'Round {{number}}',
      },
      plan: 'Add to the plan (create a fix task)',
    },
    failCell: {
      title: 'Failed: {{code}} · {{place}}',
      noEnvironment: 'no environment',
      modeNew: 'Report a new bug',
      modeExisting: 'Link an existing bug',
      modeOnly: 'Just mark it failed',
      actual: 'What actually happened',
      actualPlaceholder: 'Clicking Save does nothing, the spinner keeps going',
      expected: 'Expected',
      stepsFromCase: 'Steps (from the case)',
      pickBug: 'Which bug',
      noOpenBugs: 'There is no open bug to link — report a new one.',
      onlyHelp:
        'The cell turns Failed without a bug. Use it when the bug was already reported elsewhere.',
      description: 'Expected: {{expected}}\nActual: {{actual}}',
      reported: 'Bug reported and linked to the cell.',
      linked: 'Marked failed and linked to bug #{{code}}.',
      marked: 'Marked failed.',
    },
  },
  testGen: {
    title: 'Generate cases with AI',
    open: 'Generate cases with AI',
    help: 'Pick the work to cover; the AI reads each task and the documents attached to it, then proposes a set of cases. Finished work usually has documents, so its cases go deeper; unfinished work still yields main-flow cases.',
    run: 'Generate cases',
    prompt: 'Generate test cases for the work I selected.',
    groupFilter: 'Task group',
    allGroups: 'All groups',
    noGroup: 'Ungrouped',
    pickGroup: 'Select all',
    dropGroup: 'Clear all',
    pickedCount: '{{n}}/{{max}} tasks selected',
    noTasks: 'No task matches this filter.',
    doneHint: 'has documents ⇒ richer cases',
    category: { todo: 'To do', in_progress: 'In progress', done: 'Done' },
    why: 'Based on',
    gaps: 'Where the AI says it lacked data',
    accept: 'Create {{n}} cases',
    thenOpenRound: 'Open a new test round afterwards',
    created: 'Added {{n}} cases to the suite.',
  },
  staffing: {
    title: 'Hire rates and speed',
    subtitle:
      'What the project pays per hour for each person, and their speed against a standard mid-level baseline. This is not salary.',
    member: 'Member',
    hireRate: 'Hire rate (/hour)',
    speed: 'Speed factor',
    notSet: 'not set',
    save: 'Save',
    saved: 'Saved.',
    loadFailed: 'Could not load the staffing table.',
    bucket: { simple: 'Easy work', standard: 'Medium work', hard: 'Hard work' },
    bucketHint: 'Easy: up to 4h · Medium: 4-16h · Hard: over 16h',
    factorHint:
      '1.00 is the standard mid-level pace. Below 1 is faster, above 1 is slower. A task estimate means hours for someone at that standard pace.',
    learned: 'Measured: {{value}}',
    learnedSamples: '({{count}} tasks)',
    learnedNone: 'not enough data',
    learnedHint:
      'Measured from completed work, for reference only. It never feeds the cost calculation on its own — only the factor you set does.',
    noLevel: 'No level set',
    useCustom: 'Custom rate',
    noRate: "No hire rate — this person's work cannot be costed yet.",
    rateFrom: {
      custom: 'In use: {{value}}/hour (custom rate for this project).',
      level: 'In use: {{value}}/hour (level default, set by the organisation).',
      none: 'No hire rate yet.',
    },
    autofill: 'Autofill by level',
    autofillHint:
      'Fill empty hire rates and speed factors with estimates based on each person’s level. Filled cells stay as they are.',
    autofillNothing: 'No empty cells left to fill by level.',
    autofillConfirm:
      'Fill {{rates}} empty hire rates and {{speeds}} empty speed factors with level-based estimates? Filled cells stay as they are, and every cell can be edited.',
    autofillSkipped: 'Skipping people without a level: {{names}}.',
    unleveledTitle_one: '{{count}} person has no level, so no hourly rate: {{names}}.',
    unleveledTitle_other: '{{count}} people have no level, so no hourly rate: {{names}}.',
    unleveledHint:
      'Auto-fill does not guess a rate for them. An org admin assigns levels in the Org chart, or pick "Custom rate" on each person\'s row and type it in.',
    unleveledLink: 'Open Org chart',
    autofillApply: 'Fill and save',
    autofillNote: 'Initial estimate for level {{level}}',
    empty: 'This project has no members yet.',
    perHour: '{{value}} VND/h',
    noRateShort: 'No rate',
    /** Table — user request 2026-10-15. */
    effective: 'In force',
    rateMode: 'How the hire rate is set',
    /** The table's select — the figure is in the In force column. */
    byLevel: 'By level',
  },
  designControl: {
    narration: {
      aiButton: 'Narrate with AI',
      aiLabel: 'Narrate design changes',
      aiResult_one: 'AI narrated {{count}} change',
      aiResult_other: 'AI narrated {{count}} changes',
      applied_one: 'Added {{count}} narration for review.',
      applied_other: 'Added {{count}} narrations for review.',
      summary: 'Summary',
      write: 'Write a narration',
      summaryPlaceholder: 'Which change matters most to users?',
      changePlaceholder: 'What does this change mean for users?',
    },
    ai: {
      button: 'Describe with AI',
      label: 'Describe design screens',
      result_one: 'AI described {{count}} screen',
      result_other: 'AI described {{count}} screens',
      apply: 'Add for review',
      applied_one: 'Added {{count}} description for review — open the screen to approve it.',
      applied_other: 'Added {{count}} descriptions for review — open each screen to approve.',
    },
    compare: {
      title: 'Compare snapshots',
      open: 'Compare',
      base: 'Base',
      head: 'Head',
      swap: 'Swap',
      back: 'Back to file',
      pending: 'Comparing the two snapshots…',
      failed: 'The comparison failed. Press again to recompute.',
      stats:
        '{{modified}} screens changed · {{added}} added · {{removed}} removed · {{changes}} changes',
      noChanges: 'The two snapshots are identical.',
      viewed: 'Viewed',
      renamedFrom: 'Renamed from “{{name}}”',
      movedFrom: 'Moved from {{path}}',
      maybeReplaced: 'May replace a removed screen with the same name',
      truncated: 'Too many changes — only the first 200 rows are shown.',
      mode2up: '2-up',
      modeSwipe: 'Swipe',
      modeOnion: 'Onion skin',
      noImage: 'No image',
      pickTwo: 'Pick two finished snapshots to compare.',
      narrationBy: 'Narration',
    },
    change: {
      text_changed: 'Text “{{before}}” → “{{after}}”',
      node_added: 'Added “{{after}}”',
      node_removed: 'Removed “{{before}}”',
      style_changed: '“{{node}}”: {{property}} {{before}} → {{after}}',
      layout_changed: '“{{node}}”: {{property}} {{before}} → {{after}}',
      component_swapped: '“{{node}}” swapped from {{before}} to {{after}}',
      shown: 'Showed “{{node}}”',
      hidden: 'Hid “{{node}}”',
      times: '(×{{count}})',
    },
    property: {
      text: 'text',
      fills: 'fill',
      strokes: 'stroke',
      strokeWeight: 'stroke width',
      effects: 'effects',
      cornerRadius: 'corner radius',
      opacity: 'opacity',
      fontFamily: 'font',
      fontWeight: 'weight',
      fontSize: 'font size',
      lineHeight: 'line height',
      letterSpacing: 'letter spacing',
      textAlign: 'alignment',
      position: 'position',
      size: 'size',
      parent: 'container',
      direction: 'direction',
      gap: 'gap',
      padding: 'padding',
      justify: 'main-axis alignment',
      align: 'cross-axis alignment',
      wrap: 'wrap',
    },
    tab: {
      emptyTitle: 'No Figma file linked yet',
      step1: 'An org admin adds a Figma token (Admin › Figma).',
      step2: 'Paste a Figma file link — with node-id, only that part is used (a page, a section).',
      step3: 'Press Capture: each screen’s description and image are stored.',
      notBackup:
        'A snapshot is each screen’s JSON and image — enough to review, describe and compare, but it can NOT be reopened in Figma. Figma keeps the original and its version history.',
      askWriter: 'Ask the PM or someone who can edit designs to link a file.',
      linkLabel: 'Figma file link',
      link: 'Link file',
      linking: 'Reading file…',
      linked:
        'Linked: {{screens}} screens; the first capture uses about {{calls}} Figma API calls.',
      alreadyLinked: 'This file is already linked — opening it.',
      noSnapshot: 'No snapshot yet',
      screenCount_one: '{{count}} screen',
      screenCount_other: '{{count}} screens',
      remoteUpdate: 'Newer version on Figma',
      viewerBadge: 'View-only token',
      viewerWarning:
        'This token can only view the file — Figma allows 20 reads a month, about one capture. Use a token from someone with a Full or Dev seat.',
      allFiles: 'All files',
      openInFigma: 'Open in Figma',
      snapshot: 'Snapshot',
      capture: 'Capture latest',
      estimate: 'Last capture used {{calls}} Figma API calls',
      estimateFirst: 'First capture — the Figma API call count shows when it is done',
      pickVersion: 'Pick an older version…',
      captureQueued: 'Capture queued. This page updates when it is done.',
      captureExists: 'This version already has a snapshot.',
      captureHint: 'Press Capture to store the current version of the file.',
      queuedHint: 'Reading Figma and storing images. This page updates by itself.',
      exportMd: 'Download descriptions (.md)',
      search: 'Find a screen…',
      showHidden: 'Also show components, fragments and hidden screens',
      noMatch: 'No screen matches.',
      noImage: 'No image',
    },
    status: {
      queued: 'Capturing',
      fetching: 'Reading Figma',
      rendering: 'Storing images',
      ready: 'Done',
      partial: 'Done, some images failed',
      failed: 'Capture failed',
    },
    kind: { screen: 'Screen', component: 'Component', fragment: 'Fragment' },
    textStatus: { ai_suggested: 'AI suggestion', approved: 'Approved', manual: 'Written by hand' },
    screen: {
      description: 'Description',
      none: 'No description for this screen at this version.',
      write: 'Write a description',
      edit: 'Edit',
      approve: 'Approve',
      save: 'Save description',
      saved: 'Description saved.',
      template:
        'Purpose: …\n\nMain parts (top → bottom):\n- …\n\nUser actions:\n- …\n\nStates / variants:\n- …',
      hide: 'Hide from the list',
      show: 'Show in the list',
      downloadJson: 'Download JSON',
    },
    figma: {
      title: 'Figma',
      subtitle:
        "Your organization's Figma token, used by the Design tab to read files, capture screens and compare versions.",
      seatWarning:
        'Use a token from someone with a Full or Dev seat in the team that owns the files. Figma limits calls by the seat of whoever created the token: a View seat gets only 20 reads a month — about one capture.',
      scopes:
        'When creating the token in Figma (Settings › Security › Personal access tokens), three read scopes are enough: file_content:read, file_metadata:read, file_versions:read.',
      none: 'No token yet. The Design tab cannot read any Figma file.',
      active: 'In use: {{label}} · •••• {{last4}}',
      account: 'Figma account: {{handle}}',
      verifiedAt: 'Last checked: {{at}}',
      notVerified: 'Not checked with Figma yet.',
      verify: 'Check again',
      verifying: 'Checking…',
      revoke: 'Revoke',
      confirmRevoke:
        'Revoke this token? The Design tab cannot capture new versions until another token is added.',
      revoked: 'Token revoked.',
      history: '{{count}} revoked tokens',
      replaceHint: 'Saving a new token revokes the one in use.',
      label: 'Label',
      labelPlaceholder: "e.g. Hang's Full seat",
      token: 'Personal access token',
      testFile: 'A file link to test with (optional)',
      testFilePlaceholder: 'https://www.figma.com/design/…',
      save: 'Save and check',
      saving: 'Checking…',
      testOk: 'Token works — account {{handle}}.',
      testOkFile: 'Token works — account {{handle}}, can read “{{file}}”.',
      testSavedUnverified: 'Saved, but could not check with Figma: {{reason}}',
      testFileFailed: 'Token works (account {{handle}}) but cannot read the test file: {{reason}}',
    },
  },
  timesheet: {
    errors: {
      TIMESHEET_TASK_DONE: 'This task is done — it can no longer be timed.',
      TIMESHEET_TASK_NOT_MINE:
        'This task is assigned to someone else — you can only time your own work.',
      TIMESHEET_DAY_CAPPED: 'You have already logged {{hours}} hours today — pick it up tomorrow.',
    },
    title: 'Timesheet',
    person: 'Person',
    me: 'Me',
    total: 'Total',
    avgPerDay: 'Avg/day',
    noEntry: 'No time logged',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    thisWeek: 'This week',
    hoursShort: '{{hours}}h',
    showDetail: 'Show detail',
    hideDetail: 'Collapse',
    weekTotal: '{{hours}}h total',
    noFutureWeek: 'That week has not happened yet',
    scheduledFooter: 'Your schedule this week: {{hours}} hours',
    overtimeFooter: 'Overtime this week: {{hours}} hours',
    overtimeCappedNote: '— {{hours}} of it stopped by the system, worth a check',
    overtimeBadge: '{{hours}}h OT',
    regularGroup: 'In hours',
    overtimeGroup: 'Overtime',
    overtimeTitle: 'Overtime (off-shift): {{hours}} hours',
    overtimeWindow: 'Last 90 days: {{hours}} hours of overtime across {{days}} days',
    overtimeHelpLabel: 'overtime',
    entryDetail: {
      open: 'See what each time block of {{key}} achieved',
      title: '{{key}} · {{date}}',
      person: 'Tracked by: {{name}}',
      minutes: '{{count}} min',
      dayTotal: 'Whole day',
      session: 'Block {{from}} – {{to}}',
      running: 'running',
      progress: 'Progress',
      progressValue: '{{from}}% → {{to}}%',
      checklist: 'Checklist',
      checklistChecked: '+{{count}} items',
      checklistUnchecked: '−{{count}} unticked',
      checklistNone: 'No checklist',
      checklistNoChange: 'No change',
      completedLabel: 'Completed',
      completedYes: 'Completed at {{time}}',
      completedNo: 'Not completed in this block',
      noSession: 'No tracked block of this task left on that day.',
      graceNote: 'Counted within the tracked block plus {{minutes}} minutes after it stopped. These are task changes, whoever made them.',
    },
    weekday: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
    create: {
      title: 'Quick create and start working',
      taskType: 'Task type',
      issueKind: 'Issue type',
      issueKinds: {
        incident: 'Incident',
        request: 'Request',
        problem: 'Problem',
      },
      titleLabel: 'Title',
      description: 'Description (optional)',
      estimateHours: 'Estimate (hours)',
      dueDate: 'Due date',
      impact: 'Impact',
      urgency: 'Urgency',
      scale: {
        '1': 'Low',
        '2': 'Medium',
        '3': 'High',
      },
      startNow: 'Start the timer right after creating',
      createOnly: 'Create',
      created: 'Created',
    },
    fab: {
      running: 'Working on: {{title}}',
      paused: 'Paused: {{title}}',
      idle: 'Working hours, nothing started',
      off: 'Outside working hours',
    },
    stopConfirm: {
      title: 'Stop the timer?',
      body: 'You are timing “{{title}}”. Finishing it will stop the timer.',
      confirm: 'Finish and stop timer',
    },
    panel: {
      working: 'Working on',
      switchTo: 'Switch to',
      quickCreate: 'Quick create',
      newTask: 'New task',
      newIssue: 'New issue',
      pause: 'Pause',
      stop: 'Stop',
      resume: 'Resume',
      start: 'Start',
      nothingRunning: 'Pick a task or create a new one to start working',
      nothingToSwitch: 'Nothing in progress',
      todayTotal: 'Time worked today: {{hours}} hours',
      nextShift: 'Next shift at {{time}}',
      offHours: 'Outside working hours — start anyway if you are working late',
      resumeOffer: 'A new shift started. Resume «{{title}}»?',
      overdue_one: 'This task is {{count}} day overdue — update progress or move the date',
      overdue_other: 'This task is {{count}} days overdue — update progress or move the date',
      countdownDays: '{{days}}d {{hours}}h left',
      countdownClock: '{{clock}} left',
      countdownOver: 'Past due',
      dueAt: '· due {{date}}',
      openTask: 'Open task',
      progressOnlyOnActive: 'Switch to this task before updating its progress',
      project: 'Project',
      titlePlaceholder: 'What needs doing',
      createAndStart: 'Create and start',
      noProject: 'You are not in any project yet',
      started: 'Timer started',
      paused_toast: 'Paused',
      stopped: 'Stopped',
    },
  },
  teamReport: {
    /** Delegation — APM-TWRD-d. */
    delegation: {
      title: 'Delegate reporting',
      hint: 'Pick one person on your team to create and finalize team work reports for you — e.g. while you are on leave.',
      assign: 'Delegate',
      change: 'Change person',
      revoke: 'Revoke',
      none: 'Not delegated to anyone.',
      since: 'since {{date}}',
      until: 'until {{date}}',
      untilRevoked: 'until revoked',
      pick: 'Delegate',
      pickPlaceholder: '— Pick someone on your team —',
      endsAt: 'Ends on (optional)',
      seesData:
        'This person sees the whole team’s data as you do: work done, workload, logged hours. Every view is logged.',
      usesYourAi:
        'AI for the reports they write runs on your AI key and counts against your budget.',
      oneAtATime: 'One person at a time — picking someone new revokes the previous one.',
      save: 'Save delegation',
      granted: 'Delegated',
      revoked: 'Delegation revoked',
      history: 'Delegation history ({{count}})',
      historyRow: '{{name}} · {{from}} → {{to}}',
      actingFor: 'You are handling team work reports for {{name}}',
      aiOf: 'AI runs on {{name}}’s AI',
      aiOffOf: '{{name}} has no AI set up — you can still write the narrative yourself',
      createFor: 'Create a report for {{name}}',
      createTitleFor: 'Create a team work report for {{name}}',
      byline: '{{delegate}} on behalf of {{manager}}',
      runWithAi: 'Write with {{name}}’s AI',
      readOnlyNow: 'Your delegation has ended — you can only view this report now.',
      listBy: 'by {{name}}',
    },
    memberStats: {
      label: 'Per-member figures',
      member: 'Member',
      tasksDone: 'Tasks done',
      issuesResolved: 'Issues done',
      workload: 'Workload',
      workloadHelp: '{{committed}} assigned hours / {{capacity}} working hours in the period',
      loggedHours: 'Hours logged',
      avgPerDay: 'Avg / full day',
      avgHelp: '{{hours}} over {{days}} full working days — half days, leave and holidays excluded',
      text: '{{name}}: {{tasks}} tasks done · {{issues}} issues done · workload {{workload}} · logged {{logged}} · avg {{avg}}/full day',
    },
    projectOverview: {
      priority: 'Priority',
      status: 'Status',
      progress: 'Complete',
      progressValue: '{{pct}}% ({{done}}/{{total}} tasks)',
      weekDelta: 'vs last week',
      weekDeltaHelp: '{{pct}}% seven days before the end of the period',
      period: 'Start – End',
      pm: 'PM',
      members: 'Members',
      membersCount: '{{count}} people',
      membersTitle: 'Project members ({{count}})',
      noPm: 'No PM yet',
      narrative: 'Details',
      text: '{{status}} · Complete {{progress}} · vs last week {{delta}} · {{period}} · PM: {{pm}} · {{members}}',
    },
    title: 'Team work reports',
    subtitle:
      'What your direct reports got done in a period — figures captured at creation, narrative written by you or drafted by AI.',
    topSummary: 'Summary by project',
    sections: {
      mine: 'Reports I created',
      aboutMe: 'Reports about me',
      managed: 'Teams I manage indirectly',
    },
    ai: {
      generate: 'Write summary with AI',
      title: 'Ask AI to draft the narrative',
      promptHelp: 'Leave empty and the AI uses the note saved when the report was created.',
      replaceConfirm:
        'This report already has a narrative. Replace it with the AI draft? Manual edits will be lost.',
      run: 'Draft',
      replace: 'Replace narrative',
      ready: 'AI drafted the narrative — review and edit it before finalising',
      readyPartial: 'AI drafted part of the narrative — see the warning on the report',
    },
    tabs: { projects: 'By project', people: 'By person' },
    detail: { low: 'Brief', medium: 'Standard', high: 'Detailed' },
    status: {
      finalized: 'Finalised {{date}}',
      draftEmpty: 'Draft, no narrative yet',
    },
    source: {
      ai: 'Draft written by AI',
      ai_edited: 'AI draft, edited',
      manual: 'Hand-written draft',
    },
    viewTitle: 'Team work report — {{name}}',
    backToList: 'Back to reports',
    capturedAt: 'Figures captured at {{at}}',
    truncated:
      'Figures were truncated for exceeding a cap (200 items per person per project, or 2 MB per report) — the oldest items are missing from the table.',
    copy: 'Copy as text',
    copied: 'Report copied',
    copyFailed: 'Could not copy — the browser only allows it over https or localhost.',
    writeNarrative: 'Write summary',
    editNarrative: 'Edit narrative',
    finalize: 'Finalise',
    discard: 'Discard draft',
    finalizeTitle: 'Finalise report?',
    finalizeConfirm:
      'It cannot be edited after finalising. Your superiors and each member (their own part only) will be able to see it.',
    discardTitle: 'Discard draft?',
    discardConfirm:
      'The draft and its narrative will be discarded. Figures can be captured again at any time.',
    finalized: 'Report finalised',
    discarded: 'Draft discarded',
    notFound: 'Report not found, or you do not have access to it.',
    noProjects: 'No activity was recorded in this period.',
    noNarrative: 'No summary for this project yet.',
    noActivity: 'No recorded activity in this period.',
    refs: 'References:',
    hours: '{{hours}} h',
    counts: '{{done}} done · {{ongoing}} in progress · {{issues}} issues · {{hours}} h',
    facts: {
      toggle: 'Figures',
      item: 'Item',
      state: 'State',
      hours: 'Hours',
      doneAt: 'Done {{date}}',
      ongoing: 'In progress ({{status}})',
      resolvedAt: 'Resolved {{date}}',
      workedOfEstimate: '{{worked}} / est. {{estimate}}',
      hoursOnly: 'Tracked hours only, no completed tasks or resolved issues.',
      progressColumn: 'Progress',
      progress: '{{from}}% → {{to}}% ({{delta}})',
      progressTo: '{{to}}%',
      checklistDone_one: '{{count}} checklist item done',
      checklistDone_other: '{{count}} checklist items done',
    },
    editor: {
      help: 'Leave a box empty to skip that item. The figures below do not change with the narrative.',
      summary: 'Top summary — one box per project, one line per piece of work',
      summaryHelp:
        'Pre-filled from the figures: work done, checklist items done, work continued. Each line becomes a bullet; empty lines are skipped. No progress numbers in this part.',
      summaryFor: 'Summary for project {{name}}',
      overview: 'Overall summary for the team',
      projectSummary: 'What the team achieved on this project',
      highlights: 'Highlights — one per line (optional)',
      highlightsFor: 'Highlights for {{name}}',
      personOverview: 'What this person achieved in the period',
      personProject: '{{name}} on {{project}}',
      save: 'Save narrative',
      saved: 'Narrative saved',
      needOverview: 'An overall summary for the team is required.',
      needSummary: 'At least one top summary line is required.',
      needProjectSummary: 'Project {{name}} has highlights but no summary.',
      needPersonOverview: '{{name}} has per-project text but no overall summary.',
      tooLong:
        'Some text is too long (max {{max}} characters per paragraph; 500 characters per line and 50 lines for highlights and for each top summary box).',
    },
    list: {
      period: 'Period',
      manager: 'Created by',
      detail: 'Detail',
      members: 'People',
      status: 'Status',
      createdAt: 'Created',
      emptyMine: 'No reports yet',
      emptyMineHelp: 'Click "New report" to summarise your team’s work for a period.',
      emptyAboutMe: 'No reports about you yet',
      emptyAboutMeHelp:
        'When your line manager finalises a report that includes you, your part shows up here. Only the line manager of a team can create reports — line managers are set in the org chart.',
    },
    create: {
      open: 'New report',
      title: 'New team work report',
      period: 'Period',
      periodRange: 'Period: {{period}}',
      from: 'From',
      to: 'To',
      presets: {
        thisWeek: 'This week',
        lastWeek: 'Last week',
        thisMonth: 'This month',
        lastMonth: 'Last month',
        custom: 'Custom',
      },
      detail: 'Narrative detail',
      detailHelp: {
        low: 'A few sentences per project, one sentence per person per project.',
        medium: 'A paragraph and key results per project, a few sentences per person.',
        high: 'Every significant item, with references to each task.',
      },
      members: 'Members ({{count}})',
      membersUnavailable:
        'Could not load the preview — the report still includes all your direct reports.',
      noMembers: 'You have no active direct reports.',
      note: 'Extra instructions for AI (optional)',
      notePlaceholder: 'E.g. emphasise work related to the September release',
      auditNotice: 'Creating and opening a report writes an entry to each member’s access log.',
      submit: 'Create report',
      created: 'Report created',
      needPeriod: 'Pick both a start and an end date.',
      futureTo: 'The end date cannot be after today.',
    },
    text: {
      title: 'Team work report — {{name}}',
      period: 'Period: {{period}}',
      done: '[Done]',
      ongoing: '[In progress]',
      resolved: '[Resolved issue]',
    },
  },
  pptxTransitions: {
    groups: {
      subtle: 'Subtle',
      exciting: 'Exciting',
      dynamic: 'Dynamic content',
    },
    kinds: {
      none: 'None',
      morph: 'Morph',
      fade: 'Fade',
      push: 'Push',
      wipe: 'Wipe',
      split: 'Split',
      reveal: 'Reveal',
      cut: 'Cut',
      randomBar: 'Random bars',
      shape: 'Shape',
      uncover: 'Uncover',
      cover: 'Cover',
      flash: 'Flash',
      fallOver: 'Fall over',
      drape: 'Drape',
      curtains: 'Curtains',
      wind: 'Wind',
      prestige: 'Prestige',
      fracture: 'Fracture',
      crush: 'Crush',
      peelOff: 'Peel off',
      pageCurlDouble: 'Page curl',
      airplane: 'Airplane',
      origami: 'Origami',
      dissolve: 'Dissolve',
      checker: 'Checkerboard',
      blinds: 'Blinds',
      clock: 'Clock',
      ripple: 'Ripple',
      honeycomb: 'Honeycomb',
      glitter: 'Glitter',
      vortex: 'Vortex',
      shred: 'Shred',
      switch: 'Switch',
      flip: 'Flip',
      gallery: 'Gallery',
      doors: 'Doors',
      cube: 'Cube',
      box: 'Box',
      comb: 'Comb',
      zoom: 'Zoom',
      random: 'Random',
      pan: 'Pan',
      ferris: 'Ferris wheel',
      conveyor: 'Conveyor',
      window: 'Window',
      flythrough: 'Fly through',
      rotate: 'Rotate',
      orbit: 'Orbit',
    },
    options: {
      smoothly: 'Smoothly',
      throughBlack: 'Through black',
      fromRight: 'From right',
      fromLeft: 'From left',
      fromBottom: 'From bottom',
      fromTop: 'From top',
      fromBottomRight: 'From bottom-right',
      fromBottomLeft: 'From bottom-left',
      fromTopRight: 'From top-right',
      fromTopLeft: 'From top-left',
      verticalOut: 'Vertical out',
      verticalIn: 'Vertical in',
      horizontalOut: 'Horizontal out',
      horizontalIn: 'Horizontal in',
      smoothlyFromRight: 'Smoothly from right',
      smoothlyFromLeft: 'Smoothly from left',
      throughBlackFromRight: 'Through black from right',
      throughBlackFromLeft: 'Through black from left',
      vertical: 'Vertical',
      horizontal: 'Horizontal',
      circle: 'Circle',
      diamond: 'Diamond',
      plus: 'Plus',
      left: 'Left',
      right: 'Right',
      up: 'Up',
      down: 'Down',
      doubleLeft: 'Double left',
      doubleRight: 'Double right',
      singleLeft: 'Single left',
      singleRight: 'Single right',
      clockwise: 'Clockwise',
      counterclockwise: 'Counterclockwise',
      wedge: 'Wedge',
      center: 'Center',
      in: 'In',
      out: 'Out',
      byObject: 'Objects',
      byWord: 'Words',
      byChar: 'Characters',
    },
    gallery: 'Transition',
    effectOptions: 'Effect options',
    customOption: 'As in the file (not in the list)',
    timing: 'Timing',
    duration: 'Duration (seconds)',
    onClick: 'Advance on mouse click',
    after: 'Advance after (seconds)',
    afterSeconds: 'Seconds before the slide advances',
    preview: 'Preview',
    applyToAll: 'Apply to all',
    applyToAllHint: 'Use this effect, duration and advance on every slide',
    unknown: 'This slide uses the effect “{{name}}”, which the editor does not know — it is kept on save.',
    playedAsFade:
      'Preview and the slide show here play this effect as a fade; PowerPoint plays it properly.',
  },
  pptxAnimations: {
    add: 'Add an effect',
    target: 'Shape',
    noShapes: 'This slide has no shape to animate yet.',
    class: 'Effect group',
    effect: 'Effect',
    option: 'Option',
    addButton: 'Add',
    list_one: 'Effects on this slide ({{count}})',
    list_other: 'Effects on this slide ({{count}})',
    empty: 'This slide has no effects yet.',
    mainSequence: 'In click order',
    triggerShape: 'On click of “{{name}}”',
    triggerNone: 'In the slide’s click order',
    row: '{{shape}}: {{effect}}',
    shapeFallback: 'Shape {{id}}',
    custom: 'Custom effect',
    customHint:
      'A custom effect is not in the table, so its type stays as it is; its timing and order can still change, or it can be deleted.',
    fixedType: 'This kind of effect cannot change type here.',
    details: 'Selected effect',
    preview: 'Effect preview',
    replay: 'Play again',
    type: 'Type',
    start: 'Start',
    duration: 'Duration (seconds)',
    delay: 'Delay (seconds)',
    repeat: 'Repeat',
    autoReverse: 'Rewind when done',
    trigger: 'Trigger',
    moveUp: 'Move earlier',
    moveDown: 'Move later',
    delete: 'Delete effect',
    dragHint: 'Drag and drop to reorder within one sequence',
    starts: {
      onClick: 'On click',
      withPrev: 'With previous',
      afterPrev: 'After previous',
    },
    repeats: {
      times: '{{n}} times',
      untilNextClick: 'Until next click',
      untilEndOfSlide: 'Until end of slide',
    },
    classes: {
      entr: 'Entrance',
      emph: 'Emphasis',
      exit: 'Exit',
      path: 'Motion path',
    },
    entr: {
      appear: 'Appear',
      fade: 'Fade',
      fly: 'Fly In',
      float: 'Float In',
      split: 'Split',
      wipe: 'Wipe',
      shape: 'Shape',
      wheel: 'Wheel',
      randomBars: 'Random Bars',
      grow: 'Grow & Turn',
      zoom: 'Zoom',
      swivel: 'Swivel',
      bounce: 'Bounce',
      blinds: 'Blinds',
      box: 'Box',
      checkerboard: 'Checkerboard',
      diamond: 'Diamond',
      dissolve: 'Dissolve In',
      peek: 'Peek In',
      strips: 'Strips',
      expand: 'Expand',
      rise: 'Rise Up',
      spinner: 'Spinner',
    },
    emph: {
      pulse: 'Pulse',
      colorPulse: 'Color Pulse',
      teeter: 'Teeter',
      spin: 'Spin',
      growShrink: 'Grow/Shrink',
      desaturate: 'Desaturate',
      darken: 'Darken',
      lighten: 'Lighten',
      transparency: 'Transparency',
      objectColor: 'Object Color',
      complementaryColor: 'Complementary Color',
      lineColor: 'Line Color',
      fillColor: 'Fill Color',
      fontColor: 'Font Color',
      underline: 'Underline',
      boldFlash: 'Bold Flash',
      wave: 'Wave',
    },
    exit: {
      appear: 'Disappear',
      fade: 'Fade',
      fly: 'Fly Out',
      float: 'Float Out',
      split: 'Split',
      wipe: 'Wipe',
      shape: 'Shape',
      wheel: 'Wheel',
      randomBars: 'Random Bars',
      grow: 'Shrink & Turn',
      zoom: 'Zoom',
      swivel: 'Swivel',
      bounce: 'Bounce',
      dissolve: 'Dissolve Out',
    },
    path: {
      lines: 'Lines',
      arcs: 'Arcs',
      turns: 'Turns',
      shapes: 'Shapes',
      loops: 'Loops',
      custom: 'Custom path',
    },
    mediacall: {
      play: 'Play',
      pause: 'Pause',
      stop: 'Stop',
    },
    options: {
      b: 'Bottom',
      lb: 'Bottom left',
      l: 'Left',
      lt: 'Top left',
      t: 'Top',
      rt: 'Top right',
      r: 'Right',
      rb: 'Bottom right',
      horzIn: 'Horizontal in',
      horzOut: 'Horizontal out',
      vertIn: 'Vertical in',
      vertOut: 'Vertical out',
      in: 'In',
      out: 'Out',
      horz: 'Horizontal',
      vert: 'Vertical',
      across: 'Across',
      down: 'Down',
      up: 'Up',
      left: 'Left',
      right: 'Right',
      lu: 'Up left',
      ru: 'Up right',
      ld: 'Down left',
      rd: 'Down right',
      spokes1: '1 spoke',
      spokes2: '2 spokes',
      spokes3: '3 spokes',
      spokes4: '4 spokes',
      spokes8: '8 spokes',
    },
    cmd: {
      add: 'Add effect',
      delete: 'Delete effect',
      move: 'Reorder effect',
      timing: 'Change effect timing',
      trigger: 'Change effect trigger',
      preset: 'Change effect type',
    },
  },
  pptxShow: {
    play: 'Slide show',
    fromStart: 'From beginning',
    fromCurrent: 'From current slide',
    openWindow: 'Open slide show window',
    blocked: 'The browser blocked the show window after asking for screen access.',
    retry: 'Show on second screen',
    blockedPlain: 'The browser blocked the pop-up — allow pop-ups for this site and try again.',
    windowTitle: 'Slide show',
    endOfShow: 'End of slide show — click to exit.',
    fullscreen: 'Click to present full screen',
    goto: 'Go to slide {{n}}',
    presenter: {
      current: 'Now showing',
      upNext: 'Next slide',
      lastSlide: 'This is the last slide.',
      notes: 'Notes',
      noNotes: 'This slide has no notes.',
      elapsed: 'Elapsed',
      clock: 'Time',
      position: 'Slide {{n}} / {{total}} · step {{step}} / {{steps}}',
      prev: 'Back',
      next: 'Next',
      end: 'End show',
    },
  },
  // Callout box of `.chilldoc` (APM-CD2-a).
  chilldocCallout: {
    insert: 'Callout',
    changeKind: 'Change callout type (now: {{kind}})',
    remove: 'Remove box, keep content',
    kinds: {
      note: 'Note',
      tip: 'Tip',
      warning: 'Warning',
      danger: 'Danger',
      success: 'Success',
    },
  },
  // Reading / focus mode of `.chilldoc` (APM-CD2-a).
  documentReader: {
    preparing: 'Preparing reading pages…',
    loadingPage: 'Loading page {{n}}…',
    pageFailed: 'Could not load this page.',
    loadFailed: 'Could not open this document for reading.',
    storageUnavailable:
      'Temporary browser storage is unavailable or full. Allow site storage and try again.',
    page: 'Page {{n}} / {{total}}',
    goToPage: 'Go to page',
    bilingual: 'Bilingual',
    language: 'Translation language',
    edit: 'Edit',
    marginLeft: 'Left margin',
    marginRight: 'Right margin',
    translatePage: 'Translate missing blocks',
    redoPage: 'Retranslate page',
    redoConfirm: 'Replace AI translations on this page? Manual translations will be kept.',
    replaceManualConfirm: 'Replace this manual translation with an AI translation?',
    editTranslation: 'Edit translation',
    manual: 'Translate manually',
    redoBlock: 'Retranslate block',
    translateRest: 'Translate remaining pages',
    restConfirm: 'Translate missing blocks in {{count}} pages, starting at the current page?',
    batch: {
      "title": "Translate pages",
      "description": "{{total}} pages · Translate into {{lang}}",
      "scope": "Choose pages",
      "remaining": "From the current page to the end ({{start}}–{{total}})",
      "all": "Entire document",
      "custom": "Page ranges or list",
      "pages": "Pages to translate",
      "placeholder": "For example: 2-5, 8, 12-15",
      "hint": "Separate pages with commas and ranges with a dash. Duplicate pages are translated only once.",
      "checking": "Checking pages for missing translations…",
      "summary": "{{count}} of {{selected}} selected pages need translation.",
      "list": "Pages: {{pages}}",
      "nothing": "The selected pages have no passages left to translate.",
      "missingOnly": "Translate only missing passages in the selected pages. Fully translated pages are skipped.",
      "start": "Start translating {{count}} pages",
      "running": "Translating",
      "stopping": "Stopping after the current pass…",
      "stopped": "Stopped",
      "done": "Completed",
      "failed": "Stopped due to an error",
      "currentPage": "Translating page {{page}}",
      "failedPage": "Error on page {{page}}",
      "progressLabel": "Page translation progress",
      "progress": "Completed {{completed}} / {{total}} pages",
      "stopHint": "Stop after the current pass; saved translations are kept. You can hide this dialog and reopen it from the bottom bar.",
      "hide": "Hide dialog",
      "chooseAgain": "Choose other pages",
      "viewProgress": "Progress {{completed}} / {{total}}",
      "incomplete": "The translation pass did not return all results. Received translations have been kept.",
      "rangeErrors": {
        "empty": "Enter at least one page or page range.",
        "syntax": "Invalid page list. For example: 2-5, 8, 12-15.",
        "bounds": "Page numbers must be between 1 and {{total}}.",
        "reversed": "A range must start with a page at or before its end."
      }
    },
    stop: 'Stop',
    checklist: 'Checklist item',
    imageUnavailable: 'Image unavailable',
    chartUnavailable: 'No saved chart data',
    attachment: 'Attachment',
    reference: 'Reference',
    slideBreak: 'Slide break',
    unsupported: 'Unsupported content: {{type}}',
  },
  chilldocReading: {
    enter: 'Reading mode',
    title: 'Reading mode',
    bar: 'Reading mode controls',
    focus: 'Focus',
    focusHint: 'Focus on nearby passages, about one third of a page',
    outline: 'Outline',
    outlineHint: 'Show the outline on the left — click an entry to go to that heading',
    exit: 'Exit',
    rulerHint:
      '{{label}}: {{mm}} mm — drag to change, double-click to go back to the starting margin. While reading only; the document is not changed.',
  },
  // Bilingual in reading mode (APM-BIL-b, plan-chilldoc-bilingual).
  chilldocBilingual: {
    toggle: 'Bilingual',
    toggleHint: 'Show a translation side by side — translate page by page when you ask',
    needsSave: 'Save the document first to translate it',
    lang: 'Translate to',
    langs: {
      vi: 'Tiếng Việt',
      en: 'English',
      ja: '日本語',
      ko: '한국어',
      'zh-Hans': '中文 (简体)',
      fr: 'Français',
      de: 'Deutsch',
      th: 'ไทย',
      es: 'Español',
    },
    column: 'Translation · {{lang}}',
    columnLabel: 'Translation column',
    cellLabel: 'Translation of block {{n}}',
    retranslateHint: 'Translate this block again with AI',
    manual: 'Translate myself',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    manualLabel: 'Translation of the block',
    manualPlaceholder: 'Type the translation of this block… (Ctrl+Enter to save)',
    translating: 'Translating…',
    sourceChanged: 'Source changed',
    sourceChangedHint: 'This translation was made for the earlier text: {{text}}',
    byAi: 'AI · {{date}}',
    byPerson: '{{name}} · {{date}}',
    otherStyle: 'translated in the {{style}} style',
    tooLong: 'This block is too long to translate in one go — split it into shorter blocks.',
    object: 'No text to translate',
    loadFailed: 'Could not load the existing translations.',
    aiUnavailable: 'AI is not available — you can still translate each block yourself.',
    aiLabel: 'Translate a document block',
    sameLanguage: 'This passage is already in the target language — nothing to translate',
    langSource: '{{lang}} (source language)',
    rest: {
      button: 'Translate the rest',
      hint: 'Translate every untranslated passage, about one A4 page per AI pass',
      confirm: 'Translate {{blocks}} passages in {{count}} AI passes?',
      confirmCapped:
        'Translate the first {{blocks}} passages in {{count}} AI passes? Press again for the rest.',
      start: 'Translate',
      progress: 'Translating pass {{n}}/{{total}}…',
      stop: 'Stop',
      stopped: 'Stopped after pass {{n}}/{{total}}.',
      failed: 'Stopped at pass {{n}}/{{total}} on an error — see the failed passage.',
      done: 'Translated {{count}} passes.',
    },
    page: {
      label: 'Page {{n}} / {{total}}',
      untranslated: 'Not translated',
      blocks_one: '{{count}} passage',
      blocks_other: '{{count}} passages',
      progress: '{{done}}/{{total}} translated',
      translate: 'Translate page',
      translateHint: 'Translate every passage of this page in one AI pass',
      translateMissing_one: 'Translate the missing passage',
      translateMissing_other: 'Translate the missing {{count}} passages',
      translateMissingHint:
        'Translate the untranslated (or changed) passages of this page in one AI pass',
      retranslate: 'Translate page again',
      retranslateHint: 'Translate the AI passages of this page again — your own translations stay',
      retranslateConfirm_one:
        'Replace {{count}} AI translation on this page? Your own translations stay.',
      retranslateConfirm_other:
        'Replace {{count}} AI translations on this page? Your own translations stay.',
      retranslateStart: 'Translate again',
      translating: 'Translating the page…',
    },
    blockLabel: 'Passage {{n}}',
    blockActions: 'Actions for selected passage {{n}}',
    retranslateBlock: 'Translate this block again',
    errors: {
      stale:
        'This document was just saved as a new copy elsewhere (another tab or device) — close and reopen it to keep translating.',
      notFound:
        'Could not find the document to attach the translation to — it may have been deleted or you can no longer view it. Close it and reopen it from the list.',
    },
    style: {
      label: 'Style',
      detecting: 'Detecting the kind of document…',
      auto: 'detected',
      autoGenre: 'detected ({{genre}})',
      manual: 'your choice',
      fallback: 'default',
      change: 'Change',
      redetect: 'Detect again',
      redetected: 'Detected again: {{style}}',
      redetectFailed: 'Could not detect again — keeping the current style.',
      menu: 'Translation style',
    },
    styles: {
      'formal-administrative': {
        label: 'Formal – administrative',
        description: 'Full sentences, formal address, the shape of official documents',
      },
      'legal-precise': {
        label: 'Legal – word for word',
        description: 'Close to every word, legal terms kept, no paraphrasing',
      },
      'technical-precise': {
        label: 'Technical – terms kept',
        description: 'Domain terms kept, short and clear sentences',
      },
      'business-professional': {
        label: 'Business – professional',
        description: 'Polite and concise, written for decision makers',
      },
      academic: {
        label: 'Academic',
        description: 'Objective and rigorous, citations kept as they are',
      },
      'marketing-fluent': {
        label: 'Marketing – fluent, engaging',
        description: 'Reads as if written natively, appeal over word-for-word',
      },
      neutral: {
        label: 'Neutral',
        description: 'Clear, neither formal nor casual',
      },
      conversational: {
        label: 'Conversational',
        description: 'Close to speech, short sentences',
      },
    },
    genres: {
      administrative: 'Administrative document',
      legal: 'Legal document',
      technical: 'Technical document',
      business: 'Business document',
      academic: 'Academic text',
      marketing: 'Marketing',
      meeting: 'Meeting minutes',
      casual: 'Letter or note',
    },
  },
  // Tables and charts on slides (APM-CD2-n).
  docSlideObjects: {
    table: 'Table',
    chart: 'Chart',
    fromRegion: 'From section',
    edit: 'Edit table / chart',
    edit_table: 'Edit table',
    edit_chart: 'Edit chart',
    editData: 'Edit data',
    needObject: 'Select a table or chart to edit',
    needChart: 'Select a chart to edit its data',
    apply: 'Apply',
    copyNote:
      'A copy — not linked to the table / chart in the text: editing one does not change the other.',
    regionEmpty: 'This slide’s text section has no tables or charts yet.',
    regionTable: 'Table {{n}} ({{rows}} rows × {{cols}} columns)',
    regionChart: 'Chart {{n}}',
    tableBroken: 'Broken table — cannot be read',
    chartBroken: 'Broken chart — cannot be read',
    chartTableLocked:
      'The data lives inside the chart box (a copy) — change the numbers with "Edit data" in the Slide group.',
    sample: {
      title: 'New chart',
      category: 'Category',
      value: 'Value',
      item: 'Item {{n}}',
    },
  },
  // Shapes, arrows and diagrams in slides (APM-CD2-m).
  docSlideShapes: {
    insert: 'Shape',
    diagram: 'Diagram',
    fill: 'Fill color',
    line: 'Outline color',
    needShape: 'Select a shape first',
    rotate: 'Rotate 90°',
    rotateHandle: 'Drag to rotate (Shift: 15° steps)',
    flipH: 'Flip horizontal',
    flipV: 'Flip vertical',
    library: 'Shape library',
    search: 'Search shapes…',
    noMatch: 'No matching shapes',
    themeColors: 'Theme colors',
    fixedColors: 'Fixed colors',
    noColor: 'No color',
    diagramFull: 'Not enough room on this slide for the diagram (at most {{max}} boxes per slide)',
    arrange: {
      button: 'Arrange',
      needTwo: 'Shift+click to select 2 or more boxes',
      needThree: 'Distributing needs 3 or more boxes',
      selected: '{{count}} boxes selected',
      left: 'Align left',
      center: 'Align center',
      right: 'Align right',
      top: 'Align top',
      middle: 'Align middle',
      bottom: 'Align bottom',
      horizontal: 'Distribute horizontally',
      vertical: 'Distribute vertically',
    },
    colors: {
      accent: 'Accent',
      surface: 'Accent background',
      title: 'Title color',
      text: 'Text color',
      muted: 'Muted',
      background: 'Slide background',
    },
    groups: {
      lines: 'Lines',
      basic: 'Basic shapes',
      arrows: 'Block arrows',
      flowchart: 'Flowchart',
      callouts: 'Callouts',
      stars: 'Stars and banners',
    },
    presets: {
      line: 'Line',
      arrow: 'Arrow',
      doubleArrow: 'Double arrow',
      rect: 'Rectangle',
      roundRect: 'Rounded rectangle',
      ellipse: 'Oval',
      triangle: 'Triangle',
      rtTriangle: 'Right triangle',
      diamond: 'Diamond',
      parallelogram: 'Parallelogram',
      trapezoid: 'Trapezoid',
      pentagon: 'Pentagon',
      hexagon: 'Hexagon',
      octagon: 'Octagon',
      plus: 'Plus',
      heart: 'Heart',
      cloud: 'Cloud',
      rightArrow: 'Right arrow',
      leftArrow: 'Left arrow',
      upArrow: 'Up arrow',
      downArrow: 'Down arrow',
      leftRightArrow: 'Left-right arrow',
      chevron: 'Chevron',
      homePlate: 'Pentagon arrow',
      bentArrow: 'Bent arrow',
      uturnArrow: 'U-turn arrow',
      flowChartProcess: 'Flowchart: process',
      flowChartAlternateProcess: 'Flowchart: alternate process',
      flowChartDecision: 'Flowchart: decision',
      flowChartInputOutput: 'Flowchart: data',
      flowChartDocument: 'Flowchart: document',
      flowChartTerminator: 'Flowchart: terminator',
      flowChartConnector: 'Flowchart: connector',
      flowChartMagneticDisk: 'Flowchart: database',
      wedgeRectCallout: 'Rectangular callout',
      wedgeRoundRectCallout: 'Rounded callout',
      wedgeEllipseCallout: 'Oval callout',
      cloudCallout: 'Cloud callout',
      star4: '4-point star',
      star5: '5-point star',
      star6: '6-point star',
      star8: '8-point star',
      star12: '12-point star',
      irregularSeal1: 'Explosion',
    },
    diagrams: {
      process: 'Process',
      processHint: 'Sequential steps, arrows left to right',
      cycle: 'Cycle',
      cycleHint: 'Steps in a circle, the last returns to the first',
      hierarchy: 'Two-level hierarchy',
      hierarchyHint: 'One box on top, branches below (pick the number of branches)',
      count: '{{kind}} — {{count}}',
    },
    diagramText: {
      step: 'Step {{n}}',
      root: 'Topic',
      child: 'Branch {{n}}',
    },
  },
  // Background removal — APM-BGR-c, docs/plan-bg-removal.md §7.
  bgRemoval: {
    title: 'Remove background',
    apply: 'Use the cut-out image',
    modeLabel: 'Removal mode',
    modes: { auto: 'Automatic', flat: 'Solid background', select: 'Select area' },
    flatHint:
      'For diagrams, text, logos and screenshots: removes exactly the background colour and keeps every letter and line. For photos choose "Automatic".',
    flat: {
      color: 'Background colour',
      tolerance: 'Sensitivity',
      keepInside: 'Keep background-coloured areas inside shapes',
    },
    autoHint:
      'Finds the subject and removes the whole background. Not right? Switch to "Select area" to mark what to keep or paint fixes.',
    selectHint:
      'Click keep points (+) on what to keep, or drag a box around it. Clicking only remove points (−) on an extra object erases that object from the automatic result. Keep / erase brushes fix single spots.',
    toolsLabel: 'Selection tools',
    tools: {
      keep: 'Keep point',
      drop: 'Remove point',
      box: 'Box',
      brushKeep: 'Keep brush',
      brushErase: 'Erase brush',
    },
    brushSize: 'Brush size',
    undo: 'Undo last action',
    clear: 'Clear all points, box and brush strokes',
    softness: 'Edges',
    softnessLevels: { sharp: 'Sharp', normal: 'Normal', soft: 'Soft' },
    holdOriginal: 'Hold to see original',
    previewLabel: 'Image with background removed',
    preview: {
      open: 'Preview',
      title: 'Preview the cut-out image',
      background: 'Preview background',
      backgrounds: { checker: 'Checkerboard', white: 'White', dark: 'Dark', color: 'Colour' },
      info: '{{width}} × {{height}} px · {{size}} KB',
      back: 'Back to editing',
      use: 'Use this image',
      adjust: 'Make it stand out',
      adjustModes: { none: 'Keep as is', invert: 'Invert light/dark', outline: 'Outline' },
      outlineColor: 'Outline colour',
      outlineColors: { auto: 'Auto', white: 'White', black: 'Black' },
      outlineWidth: 'Thickness',
      adjusting: 'Adjusting colours…',
      adjustFailed: 'Could not adjust this image.',
    },
    // AI subject pick — APM-BGR-d. AI only places points; the cut still runs on this device.
    ai: {
      label: 'Pick the subject with AI',
      toggle: 'Use AI to pick the subject',
      hint: 'Hint for the AI (optional)',
      hintPlaceholder: 'Hint (optional): keep the mug, remove the hand holding it…',
      ask: 'Ask AI',
      running: 'AI is looking at the image…',
      picked: 'AI picked: {{subject}}. Check the points on the image, fix them if needed, then press ✓.',
      lowConfidence: 'The AI is not sure which subject — check carefully before applying.',
      failed: 'The AI could not find a subject. Place points yourself or try again.',
      manual: 'Select the area myself',
    },
    device: { webgpu: 'Using GPU', wasm: 'Using CPU — slower' },
    status: {
      load: 'Opening image…',
      download: 'Downloading background-removal model ({{mb}} MB, first time only) — {{percent}}%',
      auto: 'Removing background… (runs on this device, can take up to a minute)',
      encode: 'Preparing area selection…',
      final: 'Building the full-size image…',
      saving: 'Refining edges and saving…',
    },
    error: {
      gif: 'GIF images cannot have their background removed.',
      download_failed: 'Could not download the background-removal model. Check your connection and reopen.',
      out_of_memory: 'Not enough memory to process this image. Close some tabs and try again.',
      no_image: 'Lost the image being processed. Close and reopen.',
      no_embedding: 'Area selection is not ready. Close and reopen.',
      too_large: 'The cut-out image is too large to store in the document.',
      cancelled: 'Cancelled.',
      failed: 'Background removal failed. Try again or use another image.',
      apply: 'Could not save the image into the document.',
    },
  },
  docSectionWrite: {
    menu: 'Write in detail with AI…',
    title: 'Write in detail with AI',
    ai: 'Write a section in detail with AI',
    target: 'Section: {{name}}',
    selection: 'Selected blocks',
    untitled: '(untitled section)',
    detailLabel: 'Level of detail',
    detail: { more: 'More detail (≈ 2×)', much: 'Much more detail (≈ 4×)' },
    instruction: 'Extra request (optional)',
    instructionPlaceholder: 'e.g. add a worked example of a page reference string, write for first-year students',
    hint: 'AI keeps all of your points and uses general knowledge to expand them. The result appears as suggestions — check before accepting.',
    pending: 'This section still has pending suggestions. Accept or reject them in the Review pane first.',
    write: 'Write',
    running: 'AI is writing…',
    done: 'Section written',
    applied: 'The draft was added as suggestions — see the Review pane.',
    partial: 'The draft is shorter than requested.',
    touched: 'The section changed while AI was writing, so the result was not applied — run it again.',
    applyFailed: 'Could not apply the draft to this section.',
    tooLong: 'The section is too long — select a smaller part and try again.',
    noSection: 'Put the cursor in a heading or a list item (or select a few paragraphs) and try again.',
    author: 'AI · {{name}}',
    manual: 'Write it yourself',
  },
  docSlides: {
    regionLabel: 'Section {{index}} · {{count}} slides',
    removeRegion: 'Remove section break',
    // Read-only slide grid (viewer, convert dialog preview — APM-CVT-d).
    readGrid: {
      label: 'Slides of section {{index}}',
      present: 'Present from slide {{n}}',
    },
    lost: 'The previous region is full — {{count}} slides of the deleted region were dropped',
    undo: 'Undo',
    toggle: 'Presentation',
    panel: {
      title: 'Presentation pane',
      resize: 'Drag to resize (double-click: default width)',
      toolbar: 'Presentation tools',
      createRegion: 'Add section break',
      createRegionNo: 'There is already a section break right before this paragraph',
      noRegionHere: 'Put the cursor inside a section',
      regionFull: 'This section already has {{max}} slides',
      deckFull: 'This document already has {{max}} slides',
      addSlide: 'Add slide',
      generate: 'Create slides',
      edit: 'Edit slide',
      editHint: 'Edit slide (double-click or start typing also works)',
      fullView: 'Full view',
      settings: 'Presentation settings',
      aspect: 'Aspect ratio',
      theme: 'Theme',
      slideN: 'Slide {{n}}',
      menu: 'Slide actions',
      changeLayout: 'Change layout',
      duplicate: 'Duplicate',
      delete: 'Delete',
      deleted: 'Slide deleted',
      regenerate: 'Regenerate',
      staleNotice: '{{region}} changed since its slides were created — they may be out of date',
      regionHeader: 'Section {{index}}',
      emptyRegion: 'No slides yet',
      opening: 'Opening',
      closing: 'Closing',
      emptyOpening: 'Room for a title slide — click "Create slides"',
      emptyClosing: 'Room for a closing slide — click "Create slides"',
      editTab: 'Edit',
      slidesTab: 'Slides',
    },
    groups: { slide: 'Slide' },
    menu: {
      label: 'Slide box actions',
      duplicate: 'Duplicate box',
      editText: 'Edit text',
      replaceImage: 'Replace image…',
      removeBackground: 'Remove background…',
      bringToFront: 'Bring to front',
      sendToBack: 'Send to back',
      nothingToPaste: 'The clipboard has no image, box or text to paste.',
    },
    edge: {
      agenda: 'Agenda',
      summary: 'Summary',
      thanks: 'Thank you',
      questions: 'Q&A',
      presenterLegend: 'Shown on the slides',
      name: 'Presenter',
      organization: 'Organization',
      date: 'Date',
    },
    full: {
      canvas: 'Slide editor',
      position: 'Slide {{n}} / {{total}}',
      prev: 'Previous slide',
      next: 'Next slide',
      exit: 'Back to split view',
      addText: 'Add text box',
      insertImage: 'Insert picture',
      sizeNeedsText: 'Select a box with text to change the font size',
      deleteBox: 'Delete box',
      docImage: 'Image from document',
      docImageHint: 'Use an image already in the document — the slide shares it, no copy',
      useDocImage: 'Use this image',
      pasteFull: 'This slide already has {{max}} boxes — remove one, then paste.',
      pasteOtherDoc: 'This image belongs to another document — use "Insert image" to add it here.',
    },
    generate: {
      title: 'Create slides for section {{index}}',
      titleOpening: 'Create opening slides',
      titleClosing: 'Create closing slides',
      previewTitle: 'Slide preview',
      count: 'Number of slides',
      // Model tier for this run — user request 2026-10-30.
      modelTier: 'AI model',
      // Level of detail relative to the content being edited — user request 2026-10-30.
      detail: 'Level of detail',
      detailLevel: {
        more: 'High — keep most points, figures, examples',
        medium: 'Medium (default)',
        less: 'Low — core points only',
      },
      shortForMore:
        'This region is short — slides cannot be more detailed than what it already says. To add content to a section: right-click in it › Write in detail with AI.',
      tier: {
        strong: 'Good — tightest writing, costs most',
        standard: 'Medium (default)',
        small: 'Low — fast, cheap',
      },
      auto: 'Automatic',
      modeLabel: 'Section already has slides',
      mode: {
        add: 'Add after existing slides',
        replace: 'Replace generated slides (keep manual ones)',
      },
      prompt: 'Extra instructions (optional)',
      promptPlaceholder: 'E.g. formal tone, emphasise the numbers',
      empty: 'This section has no content to turn into slides.',
      manual: 'Create slides from section (no AI)',
      ai: 'Create with AI',
      running: 'AI is writing slides…',
      done: '{{count}} slides suggested',
      sampled: 'The section is longer than 12,000 characters — AI only read the beginning.',
      partial:
        'AI did not meet every requirement — slides were trimmed to fit, review before inserting.',
      back: 'Back',
      insert: 'Insert {{count}} slides',
      replace: 'Replace with {{count}} slides',
      capped: 'Only room for {{count}} slides in this section / document — the rest was dropped.',
    },
    present: {
      play: 'Present',
      fromStart: 'From the beginning',
      fromCurrent: 'From the selected slide',
      noSlides: 'There are no slides to present yet',
      stage: 'presentation',
      position: 'Slide {{n}} / {{total}}',
      empty: 'No slides left',
      windowTitle: 'Presentation',
      waiting: 'Connecting to the editor…',
      openFromEditor:
        'Start the presentation from the document editor (the ▶ button in the presentation pane).',
      openWindow: 'Open presentation window',
      blocked: 'The browser blocked the presentation window after asking for screen access.',
      retry: 'Present on the second screen',
      blockedPlain: 'The browser blocked the pop-up — allow pop-ups for this site and try again.',
      controls: 'Presentation controls',
      prev: 'Previous',
      next: 'Next',
      stop: 'End',
      live: 'Presenting',
      fullscreen: 'Click to present full screen',
    },
    layoutPicker: 'Choose a layout',
    layouts: {
      title: 'Title slide',
      titleContent: 'Title and content',
      sectionHeader: 'Section header',
      twoContent: 'Two content',
      comparison: 'Comparison',
      titleOnly: 'Title only',
      blank: 'Blank',
      contentCaption: 'Content with caption',
      pictureCaption: 'Picture with caption',
      contentPicture: 'Content with picture',
      titlePicture: 'Title with picture',
    },
    themes: {
      light: 'Light',
      dark: 'Dark',
      office: 'Office',
      warm: 'Warm',
      minimal: 'Minimal',
      contrast: 'High contrast',
      glass: 'Liquid Glass',
      glassDark: 'Liquid Glass dark',
    },
    placeholder: {
      title: 'Click to add title',
      subtitle: 'Click to add subtitle',
      heading: 'Click to add heading',
      body: 'Click to add text',
      caption: 'Click to add caption',
      text: 'Click to add text',
      image: 'Insert picture',
      shape: 'Text in shape',
      table: 'Empty table — double-click to edit',
      chart: 'Empty chart',
    },
    gap: {
      menu: 'Actions for the gap between slides',
      empty: 'Gap in a section with no slides — click to select, then Ctrl+V to paste',
      before: 'Gap before slide {{n}}',
      after: 'Gap after slide {{n}}',
      between: 'Gap between slides {{a}} and {{b}}',
      newSlide: 'New slide',
      fromPptx: 'Insert slides from a PowerPoint file…',
      pptxSoon: 'Pasting a PowerPoint file here is not supported yet — open the file and copy slides, or paste slide pictures.',
      officeUnreadable: 'Could not read the PowerPoint slides on the clipboard — pasted a picture of the slide instead.',
      nothing: 'The clipboard has no slides or pictures',
      full: 'No room left — at most {{region}} slides per section and {{deck}} per document.',
      pasted: 'Pasted {{count}} slides',
      capped: 'Out of room in this section / document — {{count}} slides were dropped.',
      imagesLost: 'Dropped {{count}} pictures that could not be read (another document, or an Office temp file).',
      deleted: 'Deleted {{count}} slides',
      copySlide: 'Copy slide',
      copySlides: 'Copy {{count}} slides',
    },
    pptx: {
      title: 'Insert slides from a PowerPoint file',
      fromDevice: 'Upload from device',
      fromMyFiles: 'My files',
      choose: 'Pick a .pptx file to see its slides, then tick the ones to insert.',
      otherFile: 'Choose another file',
      loading: 'Opening the file…',
      invalid: 'Could not open the file — it is damaged or not a PowerPoint (.pptx) file.',
      notPptx: 'Slides can only be inserted from a .pptx file.',
      tooLarge: 'The file is larger than {{size}} — it cannot be opened here yet.',
      empty: 'The file has no slides.',
      grid: 'Slides in the file',
      slide: 'Slide {{n}}',
      selectAll: 'Select all',
      clearAll: 'Clear selection',
      selected: '{{count}} of {{total}} slides selected',
      insert: 'Insert {{count}} slides',
      failed: 'Could not convert the slides of this file.',
      lost: 'Not carried over: {{what}}.',
      lostKinds: {
        charts: '{{count}} charts',
        smartArt: 'the graphics of {{count}} SmartArt (text kept)',
        shapes: '{{count}} drawings',
        images: '{{count}} EMF / WMF / TIFF pictures',
        media: '{{count}} videos / audio clips',
        background: 'backgrounds and decorations of {{count}} slides',
        animations: 'animations and transitions of {{count}} slides',
        notes: 'speaker notes of {{count}} slides',
      },
    },
    imagePlaceholder: 'Double-click to insert a picture',
    imageMissing: 'Picture no longer available',
    overflow: 'Text overflows the box',
  },
  docChart: {
    loading: 'Loading chart…',
    loadFailed: 'Could not load the chart renderer. Reload the page to try again.',
    broken: 'Broken chart — its settings could not be read.',
    empty: 'No data to plot yet.',
    figureLabel: 'Chart: {{title}}',
    select: 'Select chart "{{title}}"',
    toolbar: 'Chart actions',
    edit: 'Edit',
    delete: 'Delete',
    resize: 'Drag to resize',
    lostAt: 'Data source missing — showing the numbers saved at {{at}}',
    lostRecent: 'Data source missing — showing the latest numbers',
    lostEmpty: 'Data source missing — no saved numbers to show.',
    keepNumbers: 'Keep current numbers',
    keepNumbersHint: 'Unlink from the table and keep the chart with the numbers shown',
    pickTable: 'Choose a table',
    other: 'Other',
    blank: '(blank)',
    countLabel: '{{header}} (count)',
    million: 'M',
    billion: 'B',
    trillion: 'T',
    createManual: 'Create chart (no AI)',
    noTable: 'This document has no table yet — insert a data table first.',
    insight: {
      title: 'Data insight',
      previewTitle: 'Data insight — preview',
      help: 'AI reads this scope, the whole table and the document, then suggests charts: it picks the type, columns and aggregation. Numbers are computed from the table by the app; nothing changes in the document until you click Insert.',
      scopeRange: 'Range {{address}} — {{rows}} rows × {{cols}} columns',
      scopeTable: 'Whole table — {{rows}} rows × {{cols}} columns',
      readByRows: ' — each row read as a series (metric × time table)',
      labelBorrowed: ' — plus label column {{column}} on the left',
      findings: 'Data findings',
      findingsHint: '— read by AI from the table; double-check the numbers',
      insertFindings: 'Insert the findings as a list under the charts',
      count: 'Number of charts',
      prompt: 'Extra instructions (optional)',
      promptPlaceholder: 'E.g. focus on profit by quarter',
      run: 'Create charts',
      running: 'Creating…',
      done: 'Suggested charts: {{count}}',
      manual: 'Suggest without AI',
      manualNotice:
        'Suggested without AI — picked by fixed rules from the column types, no commentary.',
      manualFewer:
        'The data only supports {{count}} distinct charts (you asked for {{requested}}).',
      partial: 'AI did not finish — some invalid charts were dropped.',
      none: 'No chart fits this scope.',
      back: 'Back',
      insert: 'Insert charts ({{count}})',
      editOne: 'Edit chart "{{title}}"',
      blocked: {
        too_few_rows: 'Needs at least 2 data rows.',
        no_columns: 'No numeric or label column to chart.',
      },
    },
    dialog: {
      editTitle: 'Edit chart',
      createTitle: 'Create chart (no AI)',
      previewTitle: 'Edit preview chart',
      type: 'Chart type',
      title: 'Title',
      insight: 'Insight (one sentence under the chart)',
      source: 'Data source',
      table: 'Table',
      pickTable: '— Pick a table —',
      tableN: 'Table {{n}} ({{rows}} rows × {{cols}} columns)',
      tableUnder: 'Table {{n}} — {{heading}} ({{rows}} rows × {{cols}} columns)',
      tableLocked: 'Table follows the Data insight scope — it cannot be changed here.',
      staticSource: 'Static chart — drawn from kept numbers. Pick a table to link it again.',
      relinkHint:
        'The source table is gone. Pick a table so the chart draws from its numbers again.',
      range: 'Range',
      wholeTable: 'Whole table',
      rangePart: 'A range',
      rangeAddress: 'Range address',
      rangePlaceholder: 'e.g. B2:D10',
      headerRows: 'Header rows',
      category: 'Label column',
      scatterX: 'X column (number)',
      noCategory: '(None — one point per row)',
      pickColumn: '— Pick a column —',
      missingColumn: 'Column {{letter}} (no longer in the table)',
      columnLetter: 'Column {{letter}}',
      columnOption: '{{letter}} — {{header}} ({{kind}})',
      group: 'Group rows with the same label',
      series: 'Series',
      seriesN: 'Series {{n}}',
      column: 'Number column',
      aggregate: 'Aggregate',
      seriesLabel: 'Label',
      seriesKind: 'Draw as',
      kindBar: 'Bar',
      kindLine: 'Line',
      axis: 'Axis',
      axisLeft: 'Left',
      axisRight: 'Right',
      addSeries: 'Add series',
      removeSeries: 'Remove series {{n}}',
      color: 'Color',
      colorOfSeries: 'Series {{n}} color',
      colorOf: 'Color series {{n}} {{color}}',
      defaultColors: 'Default colors',
      sort: 'Sort',
      limit: 'Only the top',
      limitCount: 'Rows to keep',
      limitRows: 'rows',
      xLabel: 'Category axis label',
      yLabel: 'Value axis label',
      width: 'Width (px)',
      height: 'Height (px)',
      preview: 'Preview',
      noPreview: 'Pick a table to see a preview.',
      fixErrors: 'Fix these to save:',
      save: 'Save',
      insert: 'Insert',
      apply: 'Apply',
      defaultTitle: 'New chart',
      pieKept: 'A {{type}} chart has one series — kept the first, dropped {{count}} more.',
      movedToX: 'Column {{column}} became the X axis of the scatter chart.',
      suggestions: 'Suggestions',
      suggestionsHint: 'click a chart to use it',
      useSuggestion: 'Use suggestion "{{title}}"',
      firstRowHeader: 'First row is a header',
      firstColumnLabels: 'First column holds labels',
      labelColumns: 'Label columns',
      switchRowsColumns: 'Switch rows/columns',
      switchHint:
        'Each row becomes a series (instead of each column) — like Excel\'s "Switch Row/Column"',
      seriesInColumns: 'Each number column is a series',
      seriesInRows: 'Each row is a series',
      switched: 'Reading direction switched — suggestions refreshed for it.',
      labelFrom:
        'The selection has only number columns — using column {{column}} on the left as labels.',
      advanced: 'More options',
    },
    types: {
      bar: 'Column',
      stacked_bar: 'Stacked column',
      horizontal_bar: 'Bar',
      line: 'Line',
      area: 'Area',
      pie: 'Pie',
      donut: 'Donut',
      scatter: 'Scatter',
      combo: 'Column + line',
    },
    aggregates: {
      none: 'None',
      sum: 'Sum',
      avg: 'Average',
      count: 'Count',
      min: 'Minimum',
      max: 'Maximum',
    },
    sorts: {
      none: 'Table order',
      value_desc: 'Value, high to low',
      value_asc: 'Value, low to high',
      category: 'By label',
    },
    kinds: {
      number: 'number',
      percent: '%',
      currency: 'money',
      date: 'time',
      category: 'group',
      text: 'text',
      empty: 'empty',
    },
    issues: {
      title_required: 'The title is empty.',
      table_required: 'No source table picked.',
      range_invalid: 'The range address is not valid — write it like B2:D10.',
      range_outside: 'The range is outside the table.',
      column_out_of_table: 'The column is no longer in the table.',
      column_out_of_range: 'The column is outside the chosen range.',
      pie_series_count: 'A {{type}} chart has exactly one series.',
      category_required: 'This chart type needs a label column.',
      x_label_missing: 'The category axis label is missing.',
      y_label_missing: 'The value axis label is missing.',
      group_without_category: 'Grouping by label needs a label column.',
      group_needs_aggregate: 'Grouping by label needs an aggregate.',
      aggregate_without_group: 'An aggregate only applies when grouping by label.',
      combo_series_count: 'A column + line chart needs at least 2 series.',
      combo_kind_missing: 'Choose whether to draw it as a column or a line.',
      right_axis_not_combo: 'The right axis is only for column + line charts.',
      series_duplicate: 'Same as another series (same column, same aggregate).',
      series_not_numeric:
        'This is not a number column — pick a number column, or group by label and Count.',
      scatter_x_not_numeric: 'The X column of a scatter chart must be a number column.',
    },
    warnings: {
      unreadable_cells: '{{count}} cells are not numbers — skipped.',
      negative_in_pie: '{{count}} negative slices — a pie cannot draw them, dropped.',
      points_capped: '{{total}} points — only the first 200 are drawn.',
      column_missing: 'Column {{columns}} is no longer in the table.',
      total_rows_skipped: 'Skipped {{count}} total rows at the end of the range.',
    },
  },
  /** Decision sheets — APM-DSHEET-c, docs/plan-decision-sheet.md. */
  decisionSheet: {
    blockTitle: 'Decision sheets',
    blockEmpty:
      'No sheets yet. A decision sheet is a set of multiple-choice questions for the BA / client to settle point by point.',
    create: 'New sheet',
    createBlank: 'Blank sheet',
    createBlankHint: 'An empty sheet — add groups and questions in the editor',
    newTitle: 'New decision sheet',
    created: 'Sheet created',
    progress: '{{answered}}/{{total}} decided',
    updatedAt: 'Updated {{at}}',
    noDeciders: 'No answerers chosen yet',
    open: 'Open sheet {{title}}',
    status: { draft: 'Draft', open: 'Open', closed: 'Closed' },
    eyebrow: 'Decision sheet',
    back: 'Back to documents',
    legend: 'Legend',
    origin: { documented: 'In the documents', new: 'Not covered' },
    originHint: {
      documented: 'The source documents mention it, but not clearly enough',
      new: 'Raised by the author — the source documents do not mention it',
    },
    recommended: 'recommended',
    recommendedHint: "The author's recommendation",
    multipleHint: 'You can pick several options',
    totals: {
      label: 'Sheet summary',
      answered: 'Decided',
      recommended: 'Matched recommendation',
      pending: 'Pending',
    },
    filter: {
      label: 'Filter questions',
      all: 'All',
      open: 'Pending',
      answered: 'Decided',
      link: 'Via link',
    },
    /** Outsiders who answered via a public link — line under "Deciders" (APM-DSX). */
    linkRespondents: {
      line: 'Answered via link:',
      item: '{{name}} ({{count}} questions)',
    },
    viewResult: 'See results',
    groupCount: '{{answered}}/{{total}} decided',
    ungrouped: 'Ungrouped',
    questionStatus: { open: 'Pending', answered: 'Decided' },
    sourceRef: 'Source',
    context: 'Situation',
    asks: 'To decide',
    options: 'Options for question {{code}}',
    other: 'Other',
    otherDetail: 'None of the above — explain in the note',
    otherNeedsNote: '"Other" needs a note — nothing is saved until you add one.',
    note: 'Note',
    notePlaceholder: 'Extra note (optional)',
    otherNotePlaceholder: 'Describe the chosen option (required)',
    clear: 'Clear',
    trace: 'Chose {{key}} · {{at}} · {{name}}',
    traceImported: 'Chose {{key}} · {{at}} · imported from an outside source',
    /** Outsider via a public link — APM-DSX-b, plan §11.3. */
    traceLink: 'Chose {{key}} · {{at}} · {{who}} · {{via}}',
    respondent: {
      viaLink: 'via link',
      viaLinkUnverified: 'via link (unverified)',
      decidedBy: '{{name}} ({{email}}, via link)',
    },
    someone: 'Someone',
    history: 'History',
    historyTitle: 'History of {{code}}',
    historyEmpty: 'Nobody has answered this question yet.',
    historyChose: 'Chose {{key}}. {{title}}',
    historyCleared: 'Cleared',
    historyImported: 'imported from an outside source',
    historyViewAt: 'View the sheet at this time',
    onlyDeciders: 'Only deciders can choose: {{names}}',
    onlyDecidersNone: 'This sheet has no deciders — nobody can choose yet.',
    closedNotice: 'The sheet is closed — every answer is locked. Reopen it to continue.',
    draftNotice:
      'This sheet is a draft — deciders cannot see it yet. Open it so they can start choosing.',
    asOfNotice: 'Viewing the sheet as of {{at}} — read only.',
    asOfExit: 'Back to now',
    conflictChose:
      '{{name}} just chose {{key}} at {{time}} on {{code}} — your choice was not saved. Review and choose again if needed.',
    conflictCleared: 'Someone just cleared {{code}} — your choice was not saved.',
    deciders: 'Deciders',
    decidersLine: 'Answerers: {{names}}',
    decidersTitle: 'Choose answerers',
    decidersHint:
      'Chosen members can answer (decide) right in the app. Authors are not answerers by default — add yourself to decide.',
    decidersSave: 'Save answerers',
    decidersSaved: 'Answerers saved',
    decidersLimit: 'At most {{max}} answerers.',
    decidersEmpty: 'The project has no members yet.',
    /** "Choose answerers" dialog — project members + public link. */
    answerers: {
      action: 'Choose answerers',
      title: 'Choose answerers',
      tabs: { members: 'Project members', link: 'Outsiders via link' },
      openNow: 'Open sheet now',
    },
    /** "Can't open the sheet yet" dialog — reasons come from the server (409 DECISION_SHEET_NOT_READY). */
    openProblems: {
      title: "Can't open the sheet yet",
      intro: 'Fix the items below, then click "Open sheet" again:',
      noQuestions: 'The sheet has no questions.',
      addQuestions: 'Add questions',
      noAnswerers:
        'No answerers yet — choose project members, or create an "Answer" link for outsiders.',
    },
    transition: { open: 'Open sheet', close: 'Close sheet', reopen: 'Reopen' },
    transitioned: { open: 'Sheet opened', close: 'Sheet closed', reopen: 'Sheet reopened' },
    openNotReady: 'A sheet needs at least one question and one answerer before it can open.',
    openNeedsDeciders:
      'A sheet needs at least one answerer before it can open: pick members and save (the sheet opens right away), or create an "Answer" link in "Outsiders via link" and click "Open sheet now".',
    openNeedsQuestions: 'The sheet has no questions yet — add some in Edit mode first.',
    /** View mode opened in its own window, styled like the share page. */
    openWindow: 'Open in new window',
    standalone: {
      signIn: 'Sign in',
      switchAccount: 'Switch account',
      canAnswer: 'You are an answerer of this sheet — pick your answers below.',
      viewOnly: 'View only — only the sheet answerers can choose answers.',
      notOpen: 'The sheet is not open for answers — all choices are locked.',
      signInTitle: 'Sign in to view this sheet',
      signInHint:
        'This sheet belongs to a project — sign in with an account that is a project member to view and answer. Outsiders answer through a share link.',
    },
    resultTitle: 'Decided results',
    resultCopy: 'Copy',
    resultCopied: 'Results copied',
    resultCopyFailed: 'Could not copy — select the text and copy it by hand.',
    result: {
      matchesRecommended: 'matches recommendation',
      unanswered: 'NOT DECIDED',
      note: 'Note:',
    },
    empty: 'This sheet has no questions yet.',
    emptyFiltered: 'No question matches the filter.',
    download: 'Download JSON',
    /** Editor — APM-DSHEET-d; in-place Edit mode — APM-DSX-e. */
    editor: {
      modeLabel: 'Sheet mode',
      modeView: 'View',
      modeEdit: 'Edit',
      closedNoEdit: 'The sheet is closed — reopen it to edit.',
      openNotice:
        'The sheet is open — you can edit it, except deleting answered questions and deleting the chosen option.',
      sheetTitle: 'Sheet title',
      sheetDescription: 'Description',
      sheetDescriptionHint: 'Add a description (optional)',
      allowOther: 'Offer "Other" (note required)',
      deleteSheet: 'Delete sheet',
      deleteSheetConfirm: 'Delete "{{title}}"? The answer history stays in the project log.',
      deleteSheetConfirmOpen:
        'Delete "{{title}}"? The sheet is open — answerers (including via a link) will no longer be able to open it. The answer history stays in the project log.',
      sheetDeleted: 'Sheet deleted',
      dragHint:
        'Click any text to edit — saved when you leave the question. Drag the handle on the left to reorder or move a question to another group.',
      dragHandle: 'Drag to reorder the question',
      addGroup: 'Add group',
      newGroupTitle: 'New group',
      groupCode: 'Group code',
      groupCodeEdit: 'Group code {{code}} — click to edit',
      deleteGroup: 'Delete group',
      deleteGroupCode: 'Delete group {{code}}',
      deleteGroupConfirm: 'Delete group {{code}}?',
      groupNotEmpty: 'The group still has questions — move them out before deleting it.',
      duplicateGroup: 'Duplicate group code {{code}}',
      groupTitle: 'Group title',
      groupNote: 'Group note',
      groupNoteHint: 'Group note (optional)',
      highlight: 'Highlight label',
      highlightHint: 'e.g. Affects KPI / money',
      addQuestion: 'Add question',
      deleteQuestion: 'Delete question',
      deleteQuestionCode: 'Delete question {{code}}',
      deleteQuestionConfirm: 'Delete question {{code}}?',
      discardDraft: 'Discard unsaved question',
      duplicateCode: 'Duplicate question code {{code}} — codes must be unique in the sheet',
      code: 'Question code',
      codeEdit: 'Question code {{code}} — click to edit',
      questionTitle: 'Question',
      questionTitlePlaceholder: 'Question to decide',
      origin: 'Origin',
      sourceRefHint: 'Where in the source documents, e.g. "File 2, sheet 3, A34–A35" — or "None."',
      contextHint: 'Context — why this needs deciding (optional)',
      askLine: 'Point to decide {{index}}',
      askHint: 'One point to decide — Enter adds a line',
      addAsk: 'Add a point to decide',
      optionTitle: 'Option {{key}} title',
      optionTitlePlaceholder: 'Option title',
      optionDetail: 'Option {{key}} description',
      optionDetailHint: 'One-line description (optional)',
      addOption: 'Add option',
      optionLimit: '{{max}} options — the maximum.',
      removeOption: 'Remove option {{key}}',
      minOptions: 'At least {{min}} options are required',
      recommendToggle: 'Option {{key}} is recommended',
      multiple: 'Allow several answers',
      multipleLocked: 'Several options are chosen — clear the answer before switching back to one',
      optionInUse: 'Chosen answer — cannot be removed',
      answeredLock:
        'Answered {{key}} — clear the answer in View mode before deleting the question; option {{key}} cannot be removed.',
      otherPreview: 'Deciders also see this choice — turn it on / off at the top of the sheet.',
      saving: 'Saving…',
      saved: 'Saved',
      saveFailed: 'Failed — retry',
      unsaved: 'Not saved',
      needTitle: 'Not saved: the question is empty.',
      needCode: 'Not saved: the question code is empty.',
      needOptionTitle: 'Not saved: option {{key}} has no title.',
    },
    /** AI drafting / extraction and suggestion review — APM-DSHEET-g/h, plan §5.4–5.5. */
    ai: {
      draftMenu: 'AI draft from documents',
      draftMenuHint: 'Pick 1–5 project documents; AI drafts the open questions',
      draftUnavailable: 'AI is not enabled — use "Blank sheet" or "Import JSON" above',
      draftTitle: 'AI draft a sheet from documents',
      draftIntro:
        'Pick up to {{max}} documents (SRS, minutes, sitemap). AI reads them and drafts the questions still to decide, each citing where it came from. The result is a draft for you to review.',
      documentsLabel: 'Documents ({{count}}/{{max}})',
      noDocuments: 'This project has no documents yet',
      noDocumentsHint:
        'Add documents in the Project documents block, or use "Blank sheet" / "Import JSON".',
      goalLabel: 'Goal (optional)',
      goalHint: 'E.g. what the BA has not decided yet that affects billing',
      run: 'Draft with AI',
      running: 'AI is running…',
      extractRun: 'Extract with AI',
      pasteIntro:
        'Paste text copied from a page or its HTML source (e.g. a sheet built by an external AI agent such as Claude, ChatGPT or Gemini). Pasting a valid apm-decision-sheet JSON file creates the sheet directly, without AI.',
      pastePlaceholder: 'Paste the content here…',
      pasteCount: '{{count}}/{{max}} characters (CSS removed)',
      pasteTooLong:
        'The content is longer than {{max}} characters even without CSS — paste less and try again.',
      reviewIntro:
        'This is an AI suggestion; no sheet exists yet. Rename it, drop questions you do not need, then press "Create draft sheet" — or "Discard".',
      partial: 'The AI stopped early (run limit reached) — some questions may be missing.',
      unverified: 'What the AI could not verify itself:',
      unreadable:
        'This suggestion can no longer be read as a sheet (likely an older version) — discard it and run again.',
      titleLabel: 'Sheet name',
      summary: 'Keeping {{kept}}/{{total}} questions · {{groups}} groups · {{answers}} answers',
      questionsLabel: 'Questions suggested by AI',
      include: 'Keep question {{code}}',
      answered: '← chosen',
      afterCreate:
        'The sheet is created as a draft — keep editing questions, options and groups in the editor before opening it to deciders.',
      accept: 'Create draft sheet',
      discard: 'Discard',
      created: 'Created a draft sheet from the AI suggestion',
      discarded: 'Suggestion discarded',
    },
    /** Import dialog — APM-DSHEET-d, plan §5.2–5.3. */
    import: {
      menu: 'Import JSON / paste content',
      menuHint: 'An apm-decision-sheet file, e.g. drafted by an external AI agent',
      title: 'Import a decision sheet',
      tab: { json: 'File / paste JSON', paste: 'Paste text / HTML' },
      pickFile: 'Choose a .json file',
      downloadSample: 'Download sample JSON',
      sampleHint:
        'A sample covering every case, with instructions inside the file — give it to an external AI agent (Claude, ChatGPT, Gemini…) with your documents to draft a sheet in the right format.',
      orPaste: 'or paste the JSON below',
      textLabel: 'JSON content',
      tooLarge: 'The file is larger than {{max}} KB.',
      notJson: 'Could not read JSON: {{reason}}',
      issues: 'The file has {{count}} problem(s) — fix them all and import again:',
      preview: '"{{title}}" · {{groups}} groups · {{questions}} questions · {{answers}} answers',
      create: 'Create draft sheet',
      guideToggle: 'How to get it from an external AI agent',
      guideIntro:
        'The server cannot read share links of external AI agents (Claude, ChatGPT, Gemini…). For a sheet an agent built, paste this into the conversation that built the page so it adds a JSON export button — or give it the sample JSON file above:',
      guideThen:
        'Then click "Copy JSON for ChillPM" on that page and paste it into the box below (or download the file and use "Choose a .json file").',
      guideCopied: 'Prompt copied',
      claudePrompt:
        'Add two buttons side by side: "Copy JSON for ChillPM" (copies the JSON straight to the clipboard with navigator.clipboard.writeText, falling back to a pre-selected text box when the browser blocks it, and shows "Copied") and "Download JSON for ChillPM" (downloads a .json file). Both export the questions (and the chosen answers) in the apm-decision-sheet v1 format, same content, indented with 2 spaces. Rules: output ONE valid JSON object and keep the English field names; omit optional fields you do not have (do not write null); a group "highlight" is a short text label, not true/false; each question has 2–8 options, option keys are one capital letter A–W, at most one option with "recommended": true; a question that allows several answers at once (checkboxes, "select all that apply") gets "multiple": true and NO "recommended"; do not add an "Other" option yourself — in "answers" use key "X" with a required "note"; for a "multiple" question with several choices, "key" is the keys joined by commas, e.g. "A,C" or "B,X"; only list answers that were actually decided. Structure (? = optional):',
    },
    /** Export as a project document — APM-DSHEET-e, plan §6.1. */
    exportDoc: {
      action: 'Export as project document',
      exporting: 'Exporting…',
      created: 'Exported as a project document',
      versioned: 'Exported — version {{version}} of the document',
      changeNote: 'Re-exported from the decision sheet',
      exportedAt: 'Exported at {{at}} (version {{version}})',
      exportedAtChilldoc: 'Exported .chilldoc at {{at}} (version {{version}})',
      exportedAtMd: 'Exported .md at {{at}} (version {{version}})',
      open: 'Open document',
      gone: 'The previously exported .chilldoc document was deleted — the next export creates a new one.',
      goneMd:
        'The previously exported .md document was deleted — the next .md export creates a new one.',
      // Format dialog — APM-DSX.
      dialogTitle: 'Export as project document',
      dialogHint:
        'Each format is its own project document. Exporting again adds a new version to that document.',
      formats: {
        chilldoc: {
          title: 'ChillPM document (.chilldoc)',
          hint: 'Nicely formatted tables, opens and edits right in the app.',
        },
        md: {
          title: 'Markdown (.md)',
          hint: 'Readable anywhere — good for Git, a wiki or handing to an AI.',
        },
      },
      openShort: 'Open',
      submit: 'Export',
      // Only a closed sheet exports — APM-DSX, user 2026-10-07.
      notClosed: {
        title: 'Cannot export yet',
        open: 'The sheet is open — answerers can still change their answers. Close the sheet to export the final document.',
        draft:
          'The sheet has not been opened for answers — nothing is decided yet. Open it, wait for the answers, then close it to export the final document.',
        close: 'Close sheet',
      },
      meta: 'Exported at {{at}} · {{answered}}/{{total}} decided',
      columns: {
        code: 'Code',
        question: 'Question',
        answer: 'Answer',
        recommended: 'Recommended option',
        note: 'Note',
        decidedBy: 'Decided by',
        at: 'At',
      },
    },
    /** Record decided questions as RAID decisions — APM-DSHEET-f, plan §6.2. */
    raid: {
      action: 'Record in RAID',
      title: 'Record decisions in RAID',
      hint: 'Each decided question becomes a Decision item in RAID: the decider is who decided, the decision day is the day it was decided. A recorded question whose answer changed becomes a new decision — the old one stays as it was.',
      empty: 'No question has been decided yet.',
      submit: 'Record {{count}} question(s) in RAID',
      recorded: 'recorded',
      changed: 'changed after recording',
      noDecider: 'Unknown decider — cannot be recorded in RAID.',
      done: 'Recorded {{count}} decision(s) in RAID',
      partial: 'Recorded {{done}} question(s); {{failed}} could not be recorded:',
      syncFailed:
        'The decisions were created in RAID but the sheet has not registered them ({{reason}}) — check the RAID tab before recording again to avoid duplicates.',
      openRaid: 'Open {{code}} in RAID',
      note: 'Note: {{note}}',
      fromSheet: 'From sheet "{{title}}"',
      replaces: 'Replaces decision {{ref}}',
      respondent: 'Answered by: {{name}} ({{email}}, via link)',
    },
    /** Share the sheet through a public link — APM-DSX-b, plan §11.3. */
    share: {
      action: 'Share',
      title: 'Share the sheet by link',
      hint: 'Anyone with the link can open the sheet, no account needed. People who are not signed in give their name and email before answering — their answers show "via link (unverified)".',
      mode: 'People with the link can',
      modes: { answer: 'Answer', view: 'View only' },
      expiry: 'Link expires',
      expiries: { d7: '7 days', d30: '30 days', d90: '90 days', none: 'Never' },
      create: 'Create link',
      created: 'Link created and copied to the clipboard',
      newLink: 'New link',
      copy: 'Copy link',
      copied: 'Link copied',
      copyFailed: 'Could not copy — select the link and copy it by hand.',
      links: 'Links',
      empty: 'No links yet.',
      limit: '{{max}} active links already — revoke one before creating another.',
      expiresAt: 'expires {{at}}',
      noExpiry: 'no expiry',
      expired: 'expired',
      revokedTag: 'revoked',
      answers_one: '{{count}} answer',
      answers_other: '{{count}} answers',
      lastUsed: 'last {{at}}',
      createdBy: 'by {{name}}',
      revoke: 'Revoke link',
      revokeConfirm: 'Revoke this link? Anyone holding it will no longer be able to open it.',
      revoked: 'Link revoked',
    },
    /** Public page `/d/$token` — APM-DSX-b. */
    public: {
      from: '{{project}} · {{org}}',
      expires: 'Link expires {{at}}',
      whoTitle: 'Who are you?',
      whoHint:
        'Give your name and email so the sheet owner knows who answered. No account needed; the email is not verified.',
      name: 'Full name',
      namePlaceholder: 'Jane Doe',
      email: 'Email',
      emailPlaceholder: 'you@company.com',
      whoRequired: 'Fill in a name and a valid email above to choose.',
      whoReady: 'Your answers will read: {{who}} · via link (unverified).',
      signInPrompt: 'Already have a ChillPM account?',
      signIn: 'Sign in to answer with your account',
      whoSave: 'Save',
      whoSaveHint: 'Press Save to use this name and email when choosing.',
      whoChange: 'Change',
      demoNote: 'You are using a demo account — demo accounts cannot answer.',
      demoSwitch: 'Sign out to use another account',
      signedInAs: 'Answering as {{name}}',
      viewOnly: 'This link is view-only — it cannot be used to answer.',
      closed: 'The sheet is closed — all answers are locked.',
      draft: 'The sheet is not open for answers yet — the owner is still editing it.',
      unavailableTitle: 'This link no longer works',
      unavailable: 'The link was revoked, has expired or is wrong. Ask the sender for a new one.',
      poweredBy: 'Decision sheet on ChillPM',
    },
    /** Assign answering — a task carrying the sheet, APM-DSX-d, plan §11.2. */
    assign: {
      action: 'Create task',
      title: 'Create a task to answer the sheet',
      hint: 'Create a task for the person who will answer this sheet. The assignee becomes a decider automatically; the task does not close itself when the answers are in.',
      tabs: { create: 'New task', link: 'Existing task' },
      taskTitle: 'Task title',
      suggestedTitle: 'Answer sheet: {{title}}',
      assignee: 'Assignee',
      assigneeNone: 'Unassigned',
      due: 'Due',
      taskType: 'Task type',
      description: 'Answer the decision sheet "{{title}}": {{url}}',
      submit: 'Assign',
      noTypes: 'The project has no task types — add one in project settings first.',
      search: 'Search tasks by key or title',
      noTasks: 'No matching tasks.',
      linkSubmit: 'Attach task',
      done: 'Assigned {{code}}',
      doneDecider: 'Assigned {{code}} — {{name}} is now a decider on the sheet',
      linkFailed: 'Task {{code}} was created but could not be attached to the sheet: {{reason}}',
      retryLink: 'Try attaching again',
    },
    /** Tasks carrying the sheet, on the sheet page — APM-DSX-d. */
    tasks: {
      title: 'Answering tasks',
      unassigned: 'Unassigned',
      done: 'Done',
      open: 'Open task {{code}}',
      unlink: 'Detach task {{code}}',
      unlinkConfirm: 'Detach task {{code}} from the sheet? The task stays, and so do the deciders.',
      unlinkLabel: 'Detach',
      unlinked: 'Task detached',
    },
    /** "Decision sheet" block in task detail — APM-DSX-d. */
    taskBlock: {
      title: 'Decision sheets',
      open: 'Open sheet',
      notDecider:
        'The assignee is not a decider on this sheet yet, so they cannot answer — ask the sheet owner to add them.',
      hint: 'Move the task to Done yourself once every question is answered — it does not close on its own.',
    },
  },
  documentSharePage: {
    eyebrow: 'Shared document',
    logo: 'Sign in to ChillPM',
    signIn: 'Sign in',
    poweredBy: 'Shared via ChillPM',
    viewOnly: 'View only',
    version: 'Version v{{no}}',
    pending: 'This version is being prepared for sharing — reload the page in a few minutes.',
    noApproved: 'No version of this document has been approved for viewing yet.',
    state: { approved: 'Approved', review: 'In review', draft: 'Draft', old: 'Older version' },
    signInTitle: 'Sign in to view this document',
    signInHelp: 'The sharer requires signing in with a ChillPM account to view this document.',
    orgOnly: 'Shared within the organization only',
    orgOnlyHelp: 'The signed-in account is not part of this project’s organization.',
    gone: 'This link no longer opens',
    goneHelp: 'Sharing was turned off, or the document no longer exists.',
  },
  publicShell: {
    navLabel: 'Navigation',
    tryIt: 'Try it',
    openApp: 'Open the app',
    about: 'About ChillPM',
    outline: 'Outline',
    outlineShow: 'Show outline',
    outlineHide: 'Hide outline',
    outlineProgress: '{{answered}}/{{total}} answered',
    answered: 'Answered',
    unanswered: 'Not answered',
    loading: 'Opening the decision sheet…',
  },
  pptxEditor: {
    title: 'Presentation “{{name}}”',
    loading: 'Opening the presentation…',
    openFailed: 'This presentation could not be opened. Download it to open in PowerPoint.',
    empty: 'This file has no slides.',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved “{{name}}”.',
    saveFailed: 'Save failed: {{message}}',
    saveVersion: 'Save as a new version',
    versionNote: 'What changed? (optional)',
    versionSaved: 'Saved as version {{version}}.',
    close: 'Close',
    confirmDiscard: 'The presentation has unsaved changes. Close and discard them?',
    toolbar: 'Slide editing tools',
    slides: 'Slides',
    slideLabel: 'Slide {{n}}',
    hiddenSlide: 'hidden slide',
    prev: 'Previous slide',
    next: 'Next slide',
    counter: '{{n}} / {{total}}',
    notes: 'Speaker notes',
    noNotes: 'This slide has no notes.',
    panelsLabel: 'Side panels',
    undo: 'Undo',
    redo: 'Redo',
    undoStep: 'Undo: {{step}}',
    redoStep: 'Redo: {{step}}',
    flipH: 'Flip horizontal',
    flipV: 'Flip vertical',
    selectionLayer: 'Select and drag shapes on the slide',
    textEditor: 'Edit shape text',
    commandFailed: 'Could not do that: {{message}}',
    insert: 'Insert',
    textBox: 'Text box',
    shape: 'Shape',
    shapes: {
      rect: 'Rectangle',
      roundRect: 'Rounded rectangle',
      ellipse: 'Oval',
      triangle: 'Triangle',
      rightArrow: 'Right arrow',
      star5: '5-point star',
      line: 'Line',
    },
    image: 'Picture',
    imageTooLarge: 'The picture is larger than {{max}} MB — shrink it before inserting.',
    imageUnsupported: 'Only PNG, JPEG, GIF or BMP pictures can be inserted.',
    table: 'Table',
    tableSize: '{{rows}} rows × {{cols}} columns',
    deleteShape: 'Delete selected shape',
    arrange: 'Arrange',
    bringToFront: 'Bring to front',
    sendToBack: 'Send to back',
    bringForward: 'Bring forward',
    sendBackward: 'Send backward',
    group: 'Group (Shift + click to select more shapes)',
    ungroup: 'Ungroup',
    format: 'Shape format',
    fillColor: 'Fill colour',
    lineColor: 'Outline colour',
    lineWidth: 'Outline width (pt)',
    noFill: 'No fill',
    noLine: 'No outline',
    styleDefault: 'From style',
    apply: 'Apply',
    slideMenu: 'Slide {{n}} actions',
    newSlide: 'New slide',
    newSlideOn: 'New slide with layout',
    duplicateSlide: 'Duplicate slide',
    deleteSlide: 'Delete slide',
    hideSlide: 'Hide in slide show',
    showSlide: 'Show slide again',
    moveUp: 'Move up',
    moveDown: 'Move down',
    notesPlaceholder: 'Click to add speaker notes',
    cmd: {
      move: 'Move',
      resize: 'Resize',
      rotate: 'Rotate',
      flip: 'Flip',
      editText: 'Edit text',
      insertTextBox: 'Insert text box',
      insertShape: 'Insert shape',
      insertImage: 'Insert picture',
      replaceImage: 'Change picture',
      insertTable: 'Insert table',
      delete: 'Delete shape',
      bringToFront: 'Bring to front',
      sendToBack: 'Send to back',
      bringForward: 'Bring forward',
      sendBackward: 'Send backward',
      group: 'Group',
      ungroup: 'Ungroup',
      fill: 'Change fill',
      line: 'Change outline',
      newSlide: 'New slide',
      duplicateSlide: 'Duplicate slide',
      deleteSlide: 'Delete slide',
      moveSlide: 'Reorder slides',
      hideSlide: 'Hide slide',
      showSlide: 'Show slide',
      editNotes: 'Edit notes',
      transition: 'Change transition',
      transitionAll: 'Apply transition to all',
    },
    panels: {
      animations: 'Animations',
      transitions: 'Transitions',
      design: 'Design',
    },
    master: {
      open: 'Master',
      openHint: 'Master view: edit the master and layouts — slides that use them follow',
      close: 'Close Master view',
      bar: 'Master view tools',
      tree: 'Masters and layouts',
      master: 'Master',
      masterN: 'Master {{n}}',
      masterLabel: 'Master “{{name}}”',
      layout: 'Layout',
      layoutLabel: 'Layout “{{name}}”',
      unnamed: 'Untitled layout',
      usedBy: 'Used by {{n}} slide(s)',
      unused: 'Not used by any slide',
      addLayout: 'Add layout',
      copyName: '{{name}} (copy)',
      newLayoutName: 'New layout',
      rename: 'Rename',
      name: 'Name',
      deleteLayout: 'Delete layout',
      blocked: {
        inUse: 'Cannot delete: {{n}} slide(s) use this layout.',
        lastLayout: 'Cannot delete the master’s last layout.',
        notLayout: 'Select a layout to delete.',
      },
      addPlaceholder: 'Add placeholder',
      placeholderOnLayout: 'Select a layout to add a placeholder.',
      placeholders: {
        obj: 'Content',
        body: 'Text',
        pic: 'Picture',
        chart: 'Chart',
        tbl: 'Table',
        title: 'Title',
        dt: 'Date',
        ftr: 'Footer',
        sldNum: 'Slide number',
      },
      prompts: {
        obj: 'Click to add content',
        body: 'Click to add text',
        pic: 'Click to add a picture',
        chart: 'Click to add a chart',
        tbl: 'Click to add a table',
        title: 'Click to edit title',
      },
      textStyles: 'Text styles',
      textStylesTitle: 'Master text styles',
      textStylesHint:
        'Empty fields inherit. Slides with their own formatting keep it. Switching style or level applies the level being edited.',
      style: 'Text style',
      styles: {
        titleStyle: 'Title',
        bodyStyle: 'Body',
        otherStyle: 'Other',
      },
      level: 'Level',
      levelN: 'Level {{n}}',
      size: 'Font size (pt)',
      font: 'Font',
      color: 'Font colour (RRGGBB)',
      colorPicker: 'Pick font colour',
      themeColor: 'Theme colour “{{name}}”',
      bullet: 'Bullet',
      bulletNone: 'None',
      bulletChar: 'Character',
      bulletCharLabel: 'Bullet character',
      marL: 'Left margin (pt)',
      indent: 'First-line indent (pt)',
      inherit: 'Inherit',
      invalid: 'Invalid value: {{field}}',
      fields: {
        size: 'font size (1–4000 pt)',
        color: 'colour (6 hex digits, e.g. 1F4E79)',
        font: 'font',
        bullet: 'bullet (exactly one character)',
        marL: 'left margin (0–4032 pt)',
        indent: 'first-line indent (−4032–4032 pt)',
      },
      cmd: {
        addLayout: 'Add layout',
        rename: 'Rename layout',
        deleteLayout: 'Delete layout',
        addPlaceholder: 'Add placeholder',
        textStyle: 'Change master text style',
      },
    },
    design: {
      layout: 'Slide layout',
      layoutHint:
        'Text in placeholders is kept. A placeholder with no place in the new layout stays where it was; an empty one is removed.',
      themeColors: 'Theme colours',
      themeColorsHint: 'Every use of a theme colour on every slide follows.',
      slots: {
        dk1: 'Dark 1 (text)',
        lt1: 'Light 1 (background)',
        dk2: 'Dark 2',
        lt2: 'Light 2',
        accent1: 'Accent 1',
        accent2: 'Accent 2',
        accent3: 'Accent 3',
        accent4: 'Accent 4',
        accent5: 'Accent 5',
        accent6: 'Accent 6',
        hlink: 'Hyperlink',
        folHlink: 'Followed hyperlink',
      },
      noTheme: 'This slide has no theme.',
      apply: 'Apply',
      discard: 'Discard changes',
      themeFonts: 'Theme fonts',
      headingFont: 'Heading font',
      bodyFont: 'Body font',
      background: 'Background',
      backgroundTarget: 'Background of',
      backgroundColor: 'Background colour',
      targets: {
        slide: 'This slide',
        layout: 'Layout',
        master: 'Master',
      },
      origin: {
        shape: 'Uses its own background.',
        layout: "Follows the layout's background.",
        master: "Follows the master's background.",
        theme: "Follows the theme's background.",
        default: 'Uses the default background (white).',
      },
      notSolid: 'The current background is a picture or gradient — pick a colour to replace it.',
      applyAll: 'Apply to all',
      reset: 'Reset',
      resetHint: 'Remove its own background and inherit again',
      slideSize: 'Slide size',
      sizes: {
        wide: 'Widescreen 16:9',
        standard: 'Standard 4:3',
        custom: 'Custom',
      },
      currentSize: 'Now: {{w}} × {{h}} cm',
      width: 'Width (cm)',
      height: 'Height (cm)',
      sizeInvalid: 'The size must be between 2.54 and 142.24 cm.',
      applySize: 'Change size',
      scaleTitle: 'Change slide size',
      scaleQuestion: 'Changing to {{w}} × {{h}} cm. What should happen to the content?',
      maximize: 'Maximize',
      maximizeHint: 'Content fills the slide and may run past an edge.',
      fit: 'Ensure fit',
      fitHint: 'Shapes and text shrink so everything stays on the slide.',
      palettes: 'Built-in palettes',
      paletteNames: {
        office: 'Office',
        office2007: 'Office 2007–2010',
        blue: 'Blue',
        green: 'Green',
        redOrange: 'Red Orange',
        violet: 'Violet',
        yellow: 'Yellow',
        grayscale: 'Grayscale',
      },
      fontPairs: 'Font pairs',
      fontSample: 'Body text',
      backgroundSwatches: 'Suggested backgrounds',
      pale: 'Pale {{name}}',
      phRoles: {
        title: 'Title',
        content: 'Content',
        picture: 'Picture',
      },
      cmd: {
        colors: 'Change theme colours',
        fonts: 'Change theme fonts',
        background: 'Change background',
        backgroundAll: 'Apply background to all',
        slideSize: 'Change slide size',
        changeLayout: 'Change slide layout',
      },
    },
    devCompare: {
      title: 'New slide renderer vs pptx-renderer (dev page)',
      slide: 'Slide {{n}}',
      ours: 'New renderer',
      theirs: 'pptx-renderer',
      fallback: 'Drawn by the fallback layer: {{list}}',
      noFallback: 'No shape went through the fallback layer.',
      loading: 'Opening the file…',
    },
  },
  chilldocTasks: {
    createTask: 'Create task',
    createTitle: 'Create a task from this checklist line',
    title: 'Title (the line text)',
    emptyLine: 'The line is empty — type something before creating a task.',
    project: 'Project',
    noProjects: 'You are not in any project where you may create tasks.',
    taskType: 'Task type',
    assignee: 'Assignee',
    unassigned: 'Unassigned',
    dueDate: 'Due date',
    created: 'Created task "{{title}}" and linked it to the line.',
    checkbox: 'Mark done: {{text}}',
    emptyText: 'empty line',
    unavailable: 'Not visible',
    unavailableHint: 'The task was deleted or you are not allowed to see it.',
    loading: 'Loading task status, try again in a moment.',
    openTask: 'Open {{key}}: {{title}}',
    due: 'Due {{date}}',
    noDoneStatus: 'This task\'s project has no "Done" column to move it into.',
    noTodoStatus: 'This task\'s project has no "To do" column to move it back to.',
    toggleFailed: 'Could not change the status of {{key}}: {{reason}}',
  },
  // `@` mentions and `#` task / issue references in `.chilldoc` — APM-CD2-f.
  chilldocMentions: {
    peopleTitle: 'Mention someone',
    refsTitle: 'Task / issue',
    loading: 'Loading…',
    empty: 'No matches.',
    refUnavailable: 'Deleted, or you do not have access to it.',
    openRef: 'Open {{code}}: {{title}}',
    notified: 'Notified {{names}}.',
    notMember: '{{names}} cannot see this document (not in the project) — no notification sent.',
    notShared: '{{names}} do not have access to this document yet — no notification sent.',
    shareAction: 'Share (view only)',
    sharedAndNotified: 'Shared with and notified {{names}}.',
    shareFailed: 'Could not share: {{reason}}',
    notifyFailed: 'Saved, but the mention notifications were not sent: {{reason}}',
  },
  chilldocTemplates: {
    fromTemplate: 'From a template…',
    fromTemplateHint: 'Meeting minutes, plans, reports… with the project name and date filled in',
    pickerTitle: 'New document from a template',
    blank: 'Blank document',
    blankHint: 'Start from an empty page',
    builtinGroup: 'Built-in',
    orgGroup: 'Your organization',
    orgEmpty: 'Your organization has no templates of its own yet.',
    orgEmptyAdmin:
      'Open the ⋯ menu of a .chilldoc file on the Files page and choose “Make an organization template”.',
    orgLoadFailed: 'Could not load the organization templates.',
    unavailable: 'The content of this template cannot be read.',
    opening: 'Opening the template…',
    openFailed: 'Could not open the template “{{name}}”.',
    byAuthor: 'Added by {{name}}',
    projectHint: 'The project name {{name}}, today, your name and the project members are filled in.',
    noProjectHint:
      'Today and your name are filled in; the project name and members are left as underlined blanks.',
    manage: 'Manage template {{name}}',
    edit: 'Edit details',
    remove: 'Remove template',
    confirmRemove: 'Remove the template “{{name}}”? Documents already created from it are not affected.',
    removed: 'Removed the template “{{name}}”.',
    setAsTemplate: 'Make an organization template',
    createTitle: 'Make “{{name}}” an organization template',
    editTitle: 'Edit template “{{name}}”',
    name: 'Template name',
    description: 'Description (optional)',
    category: 'Category',
    nameEmpty: 'The template name cannot be empty.',
    nameTooLong: 'The template name can be at most {{max}} characters.',
    descriptionTooLong: 'The description can be at most {{max}} characters.',
    create: 'Make template',
    save: 'Save',
    created: 'Added the template “{{name}}” for the whole organization.',
    updated: 'Saved the template “{{name}}”.',
    categories: {
      meeting: 'Meeting',
      plan: 'Plan',
      proposal: 'Proposal',
      spec: 'Spec',
      report: 'Report',
      other: 'Other',
    },
    builtin: {
      meetingMinutes: {
        name: 'Meeting minutes',
        description: 'Attendees, discussion, decisions and action items that can become tasks',
      },
      projectPlan: {
        name: 'Project plan',
        description: 'Goals, scope, milestones, risks and resources',
      },
      proposal: {
        name: 'Proposal for approval',
        description: 'Grounds, proposal, budget and recommendation',
      },
      requirementsSpec: {
        name: 'Requirements specification (SRS)',
        description: 'Functional and non-functional requirements, users and interfaces',
      },
      weeklyReport: {
        name: 'Weekly report',
        description: 'Done, in progress, blockers and next week’s plan',
      },
      postProjectReview: {
        name: 'Post-project review',
        description: 'Results against goals, lessons learned, improvements',
      },
    },
  },
  placeholder: {
    comingSoon: 'This screen arrives in a later sprint.',
    dashboard: 'Project overview, burndown and RAID land here (S8).',
    myTasks: 'Your cross-project tasks land here (S5).',
    projects: 'The project list lands here (S5).',
    reports: 'Reports and charts land here (S8).',
  },
};
